import React, { Fragment, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  Box,
  Typography,
  Button,
  ButtonBase,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Pagination,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  IconButton,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormGroup,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Avatar,
  Stack,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Menu,
  Switch,
  Slider,
  Select,
  MenuItem,
  InputLabel,
  Collapse,
  TextField,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  FilterList as FilterIcon,
  ThumbUp as ThumbUpIcon,
  ViewModule as ViewModuleIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  HelpOutline as HelpOutlineIcon,
  ExpandMore as ExpandMoreIcon,
  LocationOn as LocationOnIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Edit as EditIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  AutoAwesome as AutoAwesomeIcon,
  Apartment as ApartmentIcon,
  Bookmark as BookmarkIcon,
  ShoppingCart as ShoppingCartIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Visibility as VisibilityIcon,
  Description as DescriptionIcon,
  MenuBook as MenuBookIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Remove as RemoveIcon,
  Add as AddIcon,
  School as SchoolIcon,
  Campaign as CampaignIcon,
  Podcasts as PodcastsIcon,
  OndemandVideo as OndemandVideoIcon,
  InfoOutlined as InfoIcon,
  MapOutlined as MapIcon,
  TrendingUp as TrendingUpIcon,
  FolderOpenOutlined as FolderIcon,
  PeopleOutlined as PeopleIcon,
  FolderShared as FolderSharedIcon,
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon,
  Delete as DeleteIcon,
  LocalOffer as LocalOfferIcon,
  PermMediaOutlined as PermMediaIcon,
  CollectionsOutlined as CollectionsIcon,
  VideocamOutlined as VideocamIcon,
  ThreeDRotationOutlined as ThreeDRotationIcon,
  DonutLarge as DonutLargeIcon,
  Search as SearchIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import {
  ComparableUnit,
  ReferenceUnit,
  ProjectDetails,
  DistrictOption,
  TopViewingReferenceUnit,
  TowerViewUnit,
  SitePlanData,
  AmenitiesResponse,
  ProjectMembersResponse,
  ProjectMemberInfo,
  BestByUnitTypeResponse,
  TopViewingSitePlan,
  ProjectFactsheet,
  ProjectBrochure,
  ProjectEdmFile,
  ProjectEbookFile,
  PriceMatrixSimulationUnit,
  PodcastVideoRecord,
  ProjectResourceLink,
  ThumbsupUnit,
  PricePromoUnit,
  DemandSupplyResponse,
  ProjectMediaItem,
  DroneView,
} from '../../services/api';
import { api, buildSitePlanImageProxyUrl } from '../../services/api';
import {
  isDuplicateProjectResourceLink,
  isResourceLinkAlreadyExistsError,
  RESOURCE_LINK_DUPLICATE_INFO,
} from '../../utils/projectResourceLinkDuplicate';
import { DownloadFileRow, type DownloadFileRowProps, isPdfFile, isPreviewableVideo } from '../file/fileVisuals';
import FilePreviewDialog from '../../dialogs/FilePreviewDialog';
import VirtualTourDialog from '../../dialogs/VirtualTourDialog';
import ProjectPodcastVideosDialog from '../podcast/ProjectPodcastVideosDialog';
import ProjectTrainingVideosDialog from '../podcast/ProjectTrainingVideosDialog';
import {
  logFactsheetViewed,
  logProjectTrainingClick,
  logProjectDetailsPreviewed,
  logProjectDetailsDownloaded,
} from '../../services/analytics';
import { ProjectMap, ProjectLandLotMap, DemandSupplyPanel, DemandSupplyMap } from '../map';
import { NewBadge, TAB_NEW_BADGE_SX } from '../common';
import { ProjectShareLinkButton } from '../share-links/ProjectShareLinkButton';
import { SelectedDemandSupplyTypes } from '../../../types';
import EventRegistrationDialog from '../../../common/dialogs/EventRegistrationDialog';
import { buildBestByUnitTypeFromTowerUnits } from './bestByUnitTypeFromTowerUnits';
import { getSitePlan3dsLockedProjectRole, isSitePlan3dRestrictedProject } from './sitePlan3dRestrictedProjects';
import { getSitePlanViews } from './sitePlanViews';
import SitePlanViewsSlider from './SitePlanViewsSlider';
import SitePlanDroneView from './SitePlanDroneView';
import type { SolarTower } from './solarScene';
import { draftFloorGroups, mergeFloorGroupStackUnits } from './sitePlanFloorGroups';
import { EMPTY_STOREY_COLOR, padStoreys } from './storeyLadder';
import {
  useIsMobile,
  useScrollableContainerStyles,
  useMobileOptimizedStyles,
  useGPUAcceleration,
  useIsIPad,
  useIsIPadPro129,
  useThrottle,
} from './TopViewingSitePlanDetail/mobile-hooks';
import { isSuperUpgradeBedroomMatch } from './superUpgradeMatching';
import { isUnitStatusNotReleased } from '../../../utils/formatting';

const SolarSitePlan = React.lazy(() => import('./SolarSitePlan'));
// Admin-only: loaded on demand so the pricing model never ships to non-admins.
const DeveloperInsightsSection = React.lazy(() => import('./developerInsights/DeveloperInsightsSection'));

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// ─── Project Media helpers (replicated for Super Picker Media tab) ───
type MediaSubTabId = 'gallery' | 'videos' | 'virtualTour';

const MEDIA_PREVIEW_IMAGE_EXT = /\.(jpe?g|png|gif|webp|bmp|svg)$/i;
const MEDIA_PREVIEW_VIDEO_EXT = /\.(mp4|webm|mov|m4v|ogg|ogv)$/i;

function normalizeMediaType(type: string | null | undefined): string {
  return (type || '').trim().toLowerCase();
}

function defaultMediaExt(type: string): string {
  if (type === 'image') return 'jpg';
  if (type === 'video') return 'mp4';
  return 'html';
}

function hasPreviewableMediaExt(name: string, type: string): boolean {
  if (type === 'image') return MEDIA_PREVIEW_IMAGE_EXT.test(name);
  if (type === 'video') return MEDIA_PREVIEW_VIDEO_EXT.test(name);
  return /\.[a-z0-9]{1,8}$/i.test(name);
}

function filenameFromMediaUrl(
  url: string | null | undefined,
  mediaNumber: number,
  type: string,
): string {
  const fallback = `media-${mediaNumber}.${defaultMediaExt(type)}`;
  if (!url) return fallback;
  try {
    const u = new URL(url);
    for (const key of ['fileName', 'filename', 'file']) {
      const param = u.searchParams.get(key);
      if (param && param.trim()) {
        const decoded = decodeURIComponent(param.trim());
        if (hasPreviewableMediaExt(decoded, type)) return decoded;
      }
    }
    const base = u.pathname.split('/').filter(Boolean).pop();
    if (base) {
      const decoded = decodeURIComponent(base);
      if (hasPreviewableMediaExt(decoded, type)) return decoded;
    }
  } catch {
    const base = url.split(/[\\/?#]/).filter(Boolean).pop();
    if (base && hasPreviewableMediaExt(base, type)) return base;
  }
  return fallback;
}

function mediaPreviewUrl(item: ProjectMediaItem, type: string): string | undefined {
  if (type === 'image') {
    return item.url || item.imageLarge || item.imageMedium || item.imageSmall || undefined;
  }
  return item.url || undefined;
}

function isStreamPageUrl(url: string): boolean {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    return (
      host === 'youtube.com'
      || host === 'm.youtube.com'
      || host === 'youtu.be'
      || host === 'vimeo.com'
      || host === 'player.vimeo.com'
    );
  } catch {
    return false;
  }
}

/** Files tab is PDF-only (same rule as Project Essentials ProjectResourcesDialog). */
function filterPdfFileGroups<T extends { items: Array<{ filename: string; displayName: string }> }>(
  groups: T[],
): T[] {
  return groups
    .map((g) => ({
      ...g,
      items: g.items.filter((f) => {
        const name = (f.filename || f.displayName || '').trim();
        return name ? isPdfFile(name) : false;
      }),
    }))
    .filter((g) => g.items.length > 0);
}

function mapProjectMediaToListItem(
  item: ProjectMediaItem,
  options?: {
    onOpenVirtualTour?: (tour: { title: string; url: string }) => void;
  },
): DownloadFileRowProps & { id: string } {
  const type = normalizeMediaType(item.type);
  const url = mediaPreviewUrl(item, type);
  const filename = filenameFromMediaUrl(url ?? item.url, item.mediaNumber, type);
  const previewImage =
    type === 'image'
      ? (item.url
        || item.imageMedium
        || item.imageLarge
        || item.imageSmall
        || item.videoThumbnail
        || null)
      : (item.imageMedium
        || item.imageLarge
        || item.imageSmall
        || item.videoThumbnail
        || null);
  const displayName = (item.description || '').trim() || undefined;

  if (type === 'ivt') {
    const externalLink = (item.externalLink || '').trim();
    const linkToCopy = externalLink || item.url || '';
    const title = displayName || 'Virtual tour';
    return {
      id: String(item.mediaNumber),
      filename: filename || `virtual-tour-${item.mediaNumber}.html`,
      displayName: title,
      subtitle: 'Virtual tour',
      url: linkToCopy || undefined,
      previewImage,
      disablePreview: true,
      actionMode: 'copy',
      copyText: linkToCopy,
      typeLabel: 'Click to Open',
      onClick: () => {
        const href = linkToCopy.trim();
        if (!href) return;
        options?.onOpenVirtualTour?.({ title, url: href });
      },
    };
  }

  const base: DownloadFileRowProps & { id: string } = {
    id: String(item.mediaNumber),
    filename,
    displayName,
    url,
    previewImage,
    sizeBytes: item.size ?? undefined,
    forceBlobDownload: true,
  };

  if (type === 'video' && url && !isPreviewableVideo(filename) && isStreamPageUrl(url)) {
    return {
      ...base,
      forceBlobDownload: false,
      disablePreview: true,
      onClick: () => window.open(url, '_blank', 'noopener,noreferrer'),
    };
  }

  return base;
}

// Re-export types needed
type SortDirection = 'default' | 'asc' | 'desc';
type TopViewingColumnKey = 'projectName' | 'blockNumber' | 'floor' | 'stack' | 'unitTypeDescription' | 'bedroom' | 'bathroom' | 'areaSqft' | 'price' | 'dps' | 'psf' | 'outlay' | 'installment' | 'distance';
type PriceMatrixGridColumnKey = 'unitNumber' | 'floor' | 'stack' | 'bedroom' | 'areaSqft' | 'price' | 'psf' | 'status';
type TopViewingCompColumnKey = 'projectName' | 'projectCategory' | 'blockNumber' | 'floor' | 'stack' | 'unitTypeDescription' | 'bedroom' | 'bathroom' | 'areaSqft' | 'price' | 'psf' | 'dps' | 'outlay' | 'installment' | 'distance';
type ProjectDetailsTab = 'details' | 'amenities' | 'superRatio' | 'members' | 'media' | 'files';

/**
 * The project panel is a tall subtree holding a live map, and it sits above the
 * unit tables, price matrix and site plan. Animating its height reflowed and
 * repainted all of that on every frame, which no amount of GPU hinting fixes, so
 * the toggle is instant (`timeout: 0`) and the rotating chevron carries the
 * motion. This window only covers the single reflow the toggle costs, during
 * which the header's looping animations are paused to free up paint budget.
 */
const PROJECT_SECTION_SETTLE_MS = 240;
type SelectedAmenityTypes = {
  mrtLrt: boolean;
  schools: boolean;
  supermarkets: boolean;
  preschools: boolean;
};
type ColumnVisibilityState = Record<string, boolean>;
type BudgetProjectMatchedBrBucket = 'br1' | 'br2' | 'br3' | 'br4' | 'br5' | 'ph';
type TopViewingAvailableUnitFilters = {
  projectName: string;
  blockNumber: string;
  floorMin: string;
  floorMax: string;
  stackMin: string;
  stackMax: string;
  stackSelected: string[];
  unitTypeDescriptionSelected: string[];
  bedroomMin: string;
  bedroomMax: string;
  bathroomMin: string;
  bathroomMax: string;
  areaMin: string;
  areaMax: string;
  priceMin: string;
  priceMax: string;
  dpsMin: string;
  dpsMax: string;
  psfMin: string;
  psfMax: string;
  outlayMin: string;
  outlayMax: string;
  installmentMin: string;
  installmentMax: string;
};
type TowerViewBlockData = { floors: number[]; stacks: number[]; grid: Map<string, TowerViewUnit | ComparableUnit> };

/** One storey of one stack, as painted onto the 3D tower's side faces. */
type SitePlanStoreyCell = {
  unit: TowerViewUnit | ComparableUnit;
  floor: number;
  sold: boolean;
  notReleased: boolean;
  /** Filtered out by the active bedroom / unit-type / stack filters. */
  dimmed: boolean;
  /** Inside the active blinker price range. */
  highlighted: boolean;
  bedroomColor: string;
};

/**
 * Module-scope twin of the `normalizeStackKey` prop. The prop is re-created on
 * every parent render, so memos that depend on it never actually memoise; the
 * 3D storey cells are far too expensive to rebuild that often.
 */
const padSitePlanStackKey = (stack: string | number | null | undefined): string => {
  if (stack == null) return '';
  const raw = String(stack).trim();
  if (!raw) return '';
  return /^\d+$/.test(raw) ? raw.padStart(2, '0') : raw;
};

/** Tower / 3D storey fill per bedroom count; -1 is the penthouse bucket. */
const BEDROOM_COLORS_DARK: Record<number, string> = {
  1: '#1565c0',
  2: '#2e7d32',
  3: '#6d4c41',
  4: '#8e24aa',
  5: '#00897b',
  [-1]: '#f9a825',
};
const BEDROOM_COLORS_LIGHT: Record<number, string> = {
  1: '#64b5f6',
  2: '#81c784',
  3: '#a1887f',
  4: '#e1bee7',
  5: '#4db6ac',
  [-1]: '#ffe082',
};

const TOP_VIEWING_AVAILABLE_UNIT_COLUMN_KEYS = [
  'projectName',
  'unitNumber',
  'blockNumber',
  'floor',
  'stack',
  'unitTypeDescription',
  'bedroom',
  'bathroom',
  'areaSqft',
  'price',
  'psf',
  'dps',
  'outlay',
  'installment',
  'distance',
] as const;

const TOP_VIEWING_COMPARABLE_UNIT_COLUMN_KEYS = [
  'projectName',
  'projectCategory',
  'blockNumber',
  'unitNumber',
  'floor',
  'stack',
  'unitTypeDescription',
  'bedroom',
  'bathroom',
  'areaSqft',
  'price',
  'psf',
  'dps',
  'outlay',
  'installment',
  'distance',
] as const;

const TOP_VIEWING_BUDGET_UNIT_COLUMN_KEYS = [
  'projectName',
  'projectCategory',
  'blockNumber',
  'unitNumber',
  'floor',
  'stack',
  'bedroom',
  'bathroom',
  'areaSqft',
  'price',
  'psf',
  'dps',
  'outlay',
  'installment',
  'distance',
  'district',
  'region',
  'tenure',
  'completedDate',
  'nearestMrt',
  'walkingTimeToMrt',
] as const;

const createInitialColumnVisibility = (
  columns: readonly string[],
  options?: { showPsfColumnByDefault?: boolean; hiddenColumns?: string[]; hideProjectNameByDefault?: boolean },
): ColumnVisibilityState => {
  const showPsf = options?.showPsfColumnByDefault !== false;
  const hideProjectNameByDefault = options?.hideProjectNameByDefault !== false;
  const hiddenColumns = new Set(options?.hiddenColumns ?? []);
  return columns.reduce<ColumnVisibilityState>((acc, column) => {
    if (column === 'blockNumber') {
      acc[column] = false;
    } else if (column === 'projectName' && hideProjectNameByDefault) {
      acc[column] = false;
    } else if (hiddenColumns.has(column)) {
      acc[column] = false;
    } else if (column === 'psf') {
      acc[column] = showPsf;
    } else {
      acc[column] = true;
    }
    return acc;
  }, {});
};

const PRICE_SIMULATION_WATERMARK = `url("data:image/svg+xml,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="560" height="260" viewBox="0 0 560 260">
    <g transform="rotate(-30 280 130)">
      <text
        x="280"
        y="130"
        text-anchor="middle"
        dominant-baseline="middle"
        font-size="40"
        font-weight="900"
        font-family="sans-serif"
        letter-spacing="4"
        fill="rgba(255,255,255,0.24)"
        stroke="rgba(8,15,30,0.42)"
        stroke-width="6"
        paint-order="stroke"
      >GUESS-TIMATED PRICES</text>
      <text
        x="280"
        y="130"
        text-anchor="middle"
        dominant-baseline="middle"
        font-size="40"
        font-weight="900"
        font-family="sans-serif"
        letter-spacing="4"
        fill="rgba(255,255,255,0.16)"
      >GUESS-TIMATED PRICES</text>
    </g>
  </svg>`
)}")`;

const formatColumnVisibilityLabel = (column: string): string => {
  switch (column) {
    case 'projectName':
      return 'Project Name';
    case 'projectCategory':
      return 'Project Category';
    case 'unitNumber':
      return 'Unit';
    case 'blockNumber':
      return 'Block';
    case 'areaSqft':
      return 'Area';
    case 'bathroom':
      return 'Bath';
    case 'psf':
      return 'PSF';
    case 'unitTypeDescription':
      return 'Unit Type';
    case 'nearestMrt':
      return 'Nearest MRT';
    case 'walkingTimeToMrt':
      return 'Walking Time';
    case 'completedDate':
      return 'TOP';
    default:
      return column.replace(/([A-Z])/g, ' $1').replace(/^./, (value) => value.toUpperCase());
  }
};

interface ColumnVisibilityMenuProps {
  anchorEl: HTMLElement | null;
  columns: readonly string[];
  getLabel: (column: string) => string;
  onClose: () => void;
  onOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onReset: () => void;
  onToggle: (column: string) => void;
  visibility: ColumnVisibilityState;
}

const ColumnVisibilityMenu: React.FC<ColumnVisibilityMenuProps> = ({
  anchorEl,
  columns,
  getLabel,
  onClose,
  onOpen,
  onReset,
  onToggle,
  visibility,
}) => (
  <>
    <Button size="small" variant="outlined" onClick={onOpen}>
      Columns
    </Button>
    <Menu
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Box sx={{ minWidth: 220, px: 1.5, py: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Show Columns
          </Typography>
          <Button size="small" onClick={onReset}>
            Reset
          </Button>
        </Box>
        <Divider sx={{ mb: 1 }} />
        <FormGroup>
          {columns.map((column) => (
            <FormControlLabel
              key={column}
              control={(
                <Checkbox
                  size="small"
                  checked={visibility[column] !== false}
                  onChange={() => onToggle(column)}
                />
              )}
              label={<Typography variant="body2">{getLabel(column)}</Typography>}
              sx={{ my: 0.25 }}
            />
          ))}
        </FormGroup>
      </Box>
    </Menu>
  </>
);

interface TopViewingSitePlanDetailProps {
  // AI Summary
  onAISummaryClick?: () => void;
  /** When true, tower view uses high-contrast colors and explicit "Available"/"SOLD" labels for PDF capture */
  isPdfCapturing?: boolean;
  /** Export project report as PDF */
  onExportProjectReport?: () => void | Promise<void>;
  /** When true, hide shortlist star buttons (for external users) */
  hideShortlist?: boolean;
  ceaNumber?: string | null;

  // Navigation Panel
  topViewingSitePlans: TopViewingSitePlan[];
  selectedTopViewingSitePlan: string | null;
  handleSelectTopViewingSitePlan: (
    projectName: string,
    options?: { forceRefresh?: boolean },
  ) => void | Promise<void>;
  setSelectedTopViewingSitePlan: (plan: string | null) => void;
  /** When opened from Project Essential, back navigates there instead of the SP list. */
  fromProjectEssential?: boolean;
  onBackToProjectEssential?: () => void;

  // Project Details
  projectDetails: ProjectDetails | null;
  referenceUnit: ReferenceUnit | null;
  selectedProject: string;
  projectDetailsLoading: boolean;
  projectSectionExpanded: boolean;
  setProjectSectionExpanded: (expanded: boolean) => void;
  projectDetailsTab: ProjectDetailsTab;
  setProjectDetailsTab: (tab: ProjectDetailsTab) => void;
  fetchAmenities: (projectName: string, radius: number) => void;
  fetchProjectMembers: (projectId: string) => void;
  amenitiesRadius: number;
  setAmenitiesRadius: (radius: number) => void;
  selectedAmenityTypes: SelectedAmenityTypes;
  setSelectedAmenityTypes: (updater: ((prev: SelectedAmenityTypes) => SelectedAmenityTypes) | SelectedAmenityTypes) => void;
  amenitiesLoading: boolean;
  amenitiesData: AmenitiesResponse | null;
  fetchDemandSupply: (
    projectName: string,
    radius: number,
    minRadius?: number,
    nationwide?: boolean,
  ) => void;
  demandSupplyRadius: number;
  /** Inner edge of the Super Ratio scan ring in meters; 0 scans from the centre. */
  demandSupplyMinRadius?: number;
  onDemandSupplyRadiusChange: (radius: number, minRadius: number) => void;
  /** Whole-island Super Ratio scan — ignores both radius bounds while on. */
  demandSupplyNationwide?: boolean;
  onDemandSupplyNationwideChange?: (nationwide: boolean) => void;
  demandSupplyData: DemandSupplyResponse | null;
  demandSupplyLoading: boolean;
  selectedDemandSupplyTypes: SelectedDemandSupplyTypes;
  setSelectedDemandSupplyTypes: (updater: ((prev: SelectedDemandSupplyTypes) => SelectedDemandSupplyTypes) | SelectedDemandSupplyTypes) => void;
  projectMembersLoading: boolean;
  projectMembers: ProjectMembersResponse | null;
  canViewProjectMembers?: boolean;
  onMemberTap?: (member: ProjectMemberInfo) => void;
  disableSetReferenceAction?: boolean;
  disableAddToCompareAction?: boolean;
  isAdminUser?: boolean;
  isProjectDeptUser?: boolean;
  /** Session roles, upper-cased — unlocks per-project grants such as MAP-3DS-THOMSON. */
  userRoles?: ReadonlySet<string>;
  canAccessSuperRatio?: boolean;
  /** Show the header "Share Link" button (internal agents — same gate as My Shareable Links). */
  canCreateShareLink?: boolean;
  canAccessNearbyDeal?: boolean;
  /** Site-plan 3D (massing only, fixed lighting) — ADMIN / PROJECT-DEPT / ACCELERATOR or the MAP-3D role. */
  canAccessSitePlan3d?: boolean;
  /** Site-plan 3DS (3D + the solar rig) — ADMIN / PROJECT-DEPT / ACCELERATOR or the MAP-3DS role. */
  canAccessSitePlan3dSun?: boolean;
  priceMatrixSimulationSession?: {
    sheetId: string;
    sheetUrl: string | null;
    units: PriceMatrixSimulationUnit[];
    statusOverrides: Record<string, string | null>;
    updatedAt: string;
    matchedUnits: number;
    unmatchedUnits: string[];
    syncEnabled?: boolean;
  } | null;
  priceMatrixSimulationLoading?: boolean;
  priceMatrixSimulationRefreshing?: boolean;
  priceMatrixSimulationError?: string | null;
  onLoadPriceMatrixSimulation?: (projectId: string) => void | Promise<void>;
  onRefreshPriceMatrixSimulation?: (projectId: string) => void | Promise<void>;
  onTogglePriceMatrixSimulationSync?: (projectId: string, enabled: boolean) => void | Promise<void>;
  onSetPriceMatrixSimulationStatus?: (projectId: string, unitId: number, status: string | null) => void | Promise<void>;
  onApplyPriceMatrixSimulationStatuses?: (
    projectId: string,
    updates: Array<{ unitId: number; status: string | null }>
  ) => void | Promise<void>;
  onCancelPriceMatrixSimulation?: (projectId: string) => void | Promise<void>;
  getPhotoUrl: (photo?: string | null | undefined) => string | undefined;
  shouldShowSitePlanButton: boolean;
  handleOpenSitePlan: () => void;
  handleOpenTowerView: (unit: ComparableUnit | ReferenceUnit) => void;

  // Site Plan Viewer
  sitePlanData: SitePlanData | null;
  sitePlanLoading: boolean;
  sitePlanShowPoints: boolean;
  setSitePlanShowPoints: (show: boolean) => void;
  sitePlanShowHeatMap: boolean;
  setSitePlanShowHeatMap: (show: boolean) => void;
  sitePlanBedroomFilter: number[];
  setSitePlanBedroomFilter: (filter: number[]) => void;
  sitePlanUnitTypeFilter: number[];
  sitePlanUnitTypeFilterForMatching: number[];
  sitePlanUnitTypeFilterExpandedUnitTypes: Set<string>;
  setSitePlanUnitTypeFilter: (filter: number[]) => void;
  sitePlanUnitTypes: Array<{ id: number; unitType?: string; description?: string; unitTypeCode?: string; bedroom?: number; size?: number }>;
  sitePlanBlinkingStacks: Set<string>;
  sitePlanStackSoldPercentages: Map<string, number>;
  sitePlanProjectName: string;
  handleOpenStackAnalysis: (projectName: string, stackNumber: string, blockNumber?: string) => void;
  isMobileSitePlan: boolean;
  sitePlanDotSize: number;
  sitePlanDotBorder: string;
  sitePlanDotShadow: string;
  sitePlanDotFontSize: string;
  shouldHideNonMatchingSitePlanStack: (stack: string | number | null | undefined, blockNumber?: string | null) => boolean;
  getHeatMapColor: (percentage: number) => string;
  normalizeStackKey: (stack: string | number | null | undefined) => string;
  sitePlanMenuOpen: boolean;
  setSitePlanMenuOpen: (open: boolean) => void;
  sitePlanStackBedrooms: Map<string, number>;
  sitePlanStackFilter: string[];
  setSitePlanStackFilter: (filter: string[]) => void;
  sitePlanStacksOptions: string[];

  // Tower View
  topViewingTowerZoom: number;
  setTopViewingTowerZoom: React.Dispatch<React.SetStateAction<number>>;
  topViewingTowerDisplayOptions: Array<'bedroom' | 'price' | 'psf' | 'size'>;
  setTopViewingTowerDisplayOptions: (opts: Array<'bedroom' | 'price' | 'psf' | 'size'>) => void;
  /** When false (ProjectSheet.ShowPsfInTowerViewInd === 0), PSF is hidden by default in Available Units and Best Match column menus. */
  showPsfColumnByDefault?: boolean;
  topViewingTowerBedroomColor: boolean;
  setTopViewingTowerBedroomColor: (on: boolean) => void;
  towerViewData: { blocks: Map<string, TowerViewBlockData>; blockNumbers: string[] };
  towerViewUnits: TowerViewUnit[];
  towerViewUnitTypes: string[];
  topViewingBestPxBedroomSitePlan: SitePlanData | null;
  topViewingBestByUnitTypeData: BestByUnitTypeResponse | null;
  topViewingBestByUnitTypeLoading: boolean;
  isTopViewingIndicativeBeforeLaunch: boolean;
  topViewingFilteredAvailableUnits: TowerViewUnit[];
  topViewingDetailedAvailableUnits: Array<TowerViewUnit & { outlay: number; installment: number; floor?: number; floorLabel?: string | null; stack?: number; distance?: number | null }>;
  topViewingSortedUnits: Array<TowerViewUnit & { outlay: number; installment: number; floor?: number; floorLabel?: string | null; stack?: number; distance?: number | null }>;
  topViewingSortConfig: { column: TopViewingColumnKey | null; direction: SortDirection };
  topViewingPopoverAnchor: { element: HTMLElement; column: TopViewingColumnKey } | null;
  topViewingAvailableUnitFilters: TopViewingAvailableUnitFilters;
  setTopViewingAvailableUnitFilters: React.Dispatch<React.SetStateAction<TopViewingAvailableUnitFilters>>;
  topViewingUnitMenuAnchor: { element: HTMLElement; unit: TowerViewUnit | ComparableUnit; mouseX: number; mouseY: number } | null;
  setTopViewingUnitMenuAnchor: (anchor: { element: HTMLElement; unit: TowerViewUnit | ComparableUnit; mouseX: number; mouseY: number } | null) => void;
  handleTopViewingOpenPopover: (e: React.MouseEvent<HTMLElement>, col: TopViewingColumnKey) => void;
  handleTopViewingClosePopover: () => void;
  handleTopViewingSortChange: (col: TopViewingColumnKey, dir: SortDirection) => void;
  handleTopViewingFilterChange: (col: TopViewingColumnKey, val: string) => void;
  handleTopViewingRangeFilterChange: (col: string, bound: 'min' | 'max', val: string) => void;
  hasTopViewingActiveFilter: (col: TopViewingColumnKey) => boolean;
  getTopViewingSortDirection: (col: TopViewingColumnKey) => SortDirection;
  topViewingAvailableUnitsViewMode: 'availableUnits' | 'bestPxBedroom' | 'bestPsfBedroom';
  setTopViewingAvailableUnitsViewMode: (mode: 'availableUnits' | 'bestPxBedroom' | 'bestPsfBedroom') => void;
  topViewingCompViewMode: 'unit' | 'project';
  setTopViewingCompViewMode: (mode: 'unit' | 'project') => void;

  // Floor Plan
  topViewingFloorPlanOpen: boolean;
  setTopViewingFloorPlanOpen: (open: boolean) => void;
  topViewingFloorPlanUrl: string | null;
  setTopViewingFloorPlanUrl: (url: string | null) => void;
  topViewingFloorPlanLoading: boolean;
  topViewingFloorPlanUnit: TowerViewUnit | ComparableUnit | null;

  // Reference Unit & Comparable
  topViewingReferenceUnit: TopViewingReferenceUnit | null;
  setTopViewingReferenceUnit: (unit: TopViewingReferenceUnit | null) => void;
  topViewingBlinkerChoiceUnit: TopViewingReferenceUnit | null;
  setTopViewingBlinkerChoiceUnit: (unit: TopViewingReferenceUnit | null) => void;
  topViewingBlinkerMinTolerance: number;
  setTopViewingBlinkerMinTolerance: (v: number) => void;
  setTopViewingBlinkerMinPriceOverride: (v: number | null) => void;
  topViewingBlinkerTolerance: number;
  setTopViewingBlinkerTolerance: (v: number) => void;
  setTopViewingBlinkerMaxPriceOverride: (v: number | null) => void;
  topViewingBlinkerSuperUpgrade: boolean;
  setTopViewingBlinkerSuperUpgrade: (v: boolean) => void;
  topViewingBlinkerRange: { base: number; min: number; max: number; minPercent: number; maxPercent: number } | null;
  handleTopViewingIndicativeReference: (params: { projectName: string; bedroom: number; price: number; sizeRange: { min: number; max: number }; priceLabel?: string }) => void;
  handleTopViewingGuesstimationReference: (params: {
    projectName: string;
    price: number;
    bedroom?: number;
    areaSqft?: number;
    sizeRange?: { min: number; max: number };
  }) => void;
  topViewingComparableUnits: ComparableUnit[];
  topViewingComparableUnitsLoading: boolean;
  topViewingAreaTolerance: number;
  setTopViewingAreaTolerance: (v: number) => void;
  topViewingToleranceSliderValue: number;
  setTopViewingToleranceSliderValue: (v: number) => void;
  topViewingToleranceModalOpen: boolean;
  setTopViewingToleranceModalOpen: (open: boolean) => void;
  topViewingComparableSectionOpen: boolean;
  setTopViewingComparableSectionOpen: React.Dispatch<React.SetStateAction<boolean>>;
  topViewingCompFilters: { projectName: string[]; projectCategory: string[]; blockNumber: string; unitTypeDescription: string[] };
  setTopViewingCompFilters: (filters: { projectName: string[]; projectCategory: string[]; blockNumber: string; unitTypeDescription: string[] }) => void;
  topViewingCompRangeFilters: Record<string, { min: string; max: string }>;
  /** Same shape as `topViewingCompRangeFilters`, debounced in parent so min/max typing does not filter on every keystroke. */
  topViewingCompRangeFiltersApplied: Record<string, { min: string; max: string }>;
  setTopViewingCompRangeFilters: React.Dispatch<React.SetStateAction<Record<string, { min: string; max: string }>>>;
  topViewingCompPopoverAnchor: { element: HTMLElement; column: TopViewingCompColumnKey } | null;
  topViewingThumbsUpFilterActive: boolean;
  setTopViewingThumbsUpFilterActive: React.Dispatch<React.SetStateAction<boolean>>;
  topViewingCompSortConfig: { column: TopViewingCompColumnKey | null; direction: 'asc' | 'desc' | null };
  setTopViewingCompSortConfig: (config: { column: TopViewingCompColumnKey | null; direction: 'asc' | 'desc' | null }) => void;
  topViewingFloorPlansToCompare: Array<{ unit: ComparableUnit | ReferenceUnit; imageUrl: string }>;
  setTopViewingFloorPlansToCompare: React.Dispatch<React.SetStateAction<Array<{ unit: ComparableUnit | ReferenceUnit; imageUrl: string }>>>;
  topViewingComparableSectionRef: React.RefObject<HTMLDivElement>;
  /** Scroll target for deep-links that land on All Blocks Tower View. */
  topViewingTowerViewSectionRef?: React.RefObject<HTMLDivElement>;
  topViewingThumbsUpLegendAnchor: HTMLElement | null;
  setTopViewingThumbsUpLegendAnchor: (el: HTMLElement | null) => void;
  handleTopViewingCompOpenPopover: (e: React.MouseEvent<HTMLElement>, col: TopViewingCompColumnKey) => void;
  handleTopViewingCompClosePopover: () => void;
  handleTopViewingCompFilterChange: (col: string, val: string | string[]) => void;
  handleTopViewingCompRangeFilterChange: (col: string, bound: 'min' | 'max', val: string) => void;
  handleTopViewingCompSort: (col: TopViewingCompColumnKey) => void;
  hasTopViewingCompActiveFilter: (col: TopViewingCompColumnKey) => boolean;
  getTopViewingCompColumnLabel: (col: TopViewingCompColumnKey) => string;
  topViewingFilteredComparableUnits: ComparableUnit[];
  topViewingComparableResultProjectNames: string[];
  topViewingComparableResultProjectCategories: string[];
  topViewingComparableResultUnitTypeDescriptions: string[];
  handleTopViewingAddToUnitCompare: (unit: TowerViewUnit | ComparableUnit) => void;
  analyticsCea?: string | null;

  // Budget Section
  topViewingBudgetSectionOpen: boolean;
  setTopViewingBudgetSectionOpen: (open: boolean) => void;
  topViewingBudgetTolerance: number;
  setTopViewingBudgetTolerance: (v: number) => void;
  topViewingBudgetSliderValue: number;
  setTopViewingBudgetSliderValue: (v: number) => void;
  topViewingBudgetUnitsLoading: boolean;
  topViewingBudgetUnitsError: string | null;
  topViewingBudgetRange: { base: number; min: number; max: number; percent: number } | null;
  topViewingBudgetSearchRange: { min: string; max: string };
  handleTopViewingBudgetSearchRangeChange: (bound: 'min' | 'max', value: string) => void;
  topViewingBudgetViewMode: 'unit' | 'project';
  setTopViewingBudgetViewMode: (mode: 'unit' | 'project') => void;
  topViewingBudgetRegionTab: 'ALL' | 'NEARBY' | 'CCR' | 'OCR' | 'RCR' | 'SUPER';
  setTopViewingBudgetRegionTab: (tab: 'ALL' | 'NEARBY' | 'CCR' | 'OCR' | 'RCR' | 'SUPER') => void;
  topViewingBudgetSortedUnits: Array<TowerViewUnit & { outlay: number; installment: number; floor?: number; stack?: number; distance?: number | null; superScore?: number; superReasons?: string[] }>;
  topViewingBudgetPopoverAnchor: { element: HTMLElement; column: string } | null;
  topViewingBudgetSortConfig: { column: string | null; direction: SortDirection };
  topViewingBudgetFilters: { projectName: string; blockNumber: string; unitNumber: string };
  topViewingBudgetExclusionFilters: { projectName: string[]; projectCategory: string[]; tenure: string[]; region: string[]; nearestMrt: string[]; district: string[] };
  topViewingBudgetUniqueFilterOptions: { projectName: string[]; projectCategory: string[]; tenure: string[]; region: string[]; nearestMrt: string[]; district: string[] };
  handleTopViewingBudgetExclusionFilterChange: (col: string, value: string, excluded: boolean) => void;
  handleTopViewingBudgetExclusionFilterSet: (col: string, excluded: string[]) => void;
  topViewingBudgetRangeFilters: Record<string, { min: string; max: string }>;
  topViewingOutlayInfoAnchor: HTMLElement | null;
  setTopViewingOutlayInfoAnchor: (el: HTMLElement | null) => void;
  topViewingInstallmentInfoAnchor: HTMLElement | null;
  setTopViewingInstallmentInfoAnchor: (el: HTMLElement | null) => void;
  handleTopViewingBudgetOpenPopover: (e: React.MouseEvent<HTMLElement>, col: string) => void;
  handleTopViewingBudgetClosePopover: () => void;
  handleTopViewingBudgetFilterChange: (col: string, val: string) => void;
  handleTopViewingBudgetRangeFilterChange: (col: string, bound: 'min' | 'max', val: string) => void;
  handleTopViewingBudgetSortChange: (col: string, dir: SortDirection) => void;
  getTopViewingBudgetSortDirection: (col: string) => SortDirection;
  hasTopViewingBudgetActiveFilter: (col: string) => boolean;
  getTopViewingBudgetIconButtonSx: (col: string) => object;
  handleTopViewingBudgetClearFilters: () => void;
  getBudgetProjectCellValue: (projectName: string, key: string) => string;
  topViewingBudgetProjectsForView: string[];
  onOpenBudgetInProjectResearch?: (superMatch?: { bedroom: boolean; size: boolean; floor: boolean }) => void;
  topViewingBudgetNearbyRadiusKm: number;
  setTopViewingBudgetNearbyRadiusKm: (value: number) => void;
  topViewingBudgetShowReferenceProjectUnits: boolean;
  setTopViewingBudgetShowReferenceProjectUnits: (show: boolean) => void;

  // Unit Action Handlers (extracted from inline menu items)
  handleTopViewingUnitMenuViewFloorPlan: (unit: TowerViewUnit | ComparableUnit) => void;
  handleTopViewingUnitMenuSetReference: (unit: TowerViewUnit | ComparableUnit) => void;
  handleTopViewingUnitMenuSetBlinkerChoice: (unit: TowerViewUnit | ComparableUnit) => void;
  handleTopViewingUnitMenuToggleCompare: (unit: TowerViewUnit | ComparableUnit) => void;

  // Utilities
  formatCurrency: (v: number) => string;
  formatNumber: (v: number, decimals?: number) => string;
  getAreaRangeLabel: (area: number | null | undefined, tol: number) => string | null;
  getOutlayFormulaTooltip: (price: number, outlay: number) => React.ReactNode;
  getInstallmentFormulaTooltip: (price: number, installment: number) => React.ReactNode;
  onOpenFinancialParameters?: () => void;
  onOpenNpsInfo?: () => void;
  onOpenDpsInfo?: () => void;
  currentInterestRate?: number;
  loanToValue?: number;
  getIconButtonSx: () => object;
  qualifiesForThumbsUp: (unit: ComparableUnit) => boolean;
  getTopViewingDistanceFromReference: (projectName: string) => number | null;
  projectDetailsMap: Map<string, ProjectDetails>;
  setProjectDetailsMap: React.Dispatch<React.SetStateAction<Map<string, ProjectDetails>>>;

  // Nearby Deal section (thumbsup + price promo units grouped by project)
  nearbyThumbsupGroups: Array<{ projectId: string; projectName: string; isLive: number; units: ThumbsupUnit[] }>;
  nearbyPricePromoGroups: Array<{ projectId: string; projectName: string; isLive: number; latestAt: number; units: PricePromoUnit[] }>;
  pricePromoBestByStackBedroom: Set<number>;
  pricePromoUnitIds: Set<number>;
  handleThumbsupUnitClick: (item: ThumbsupUnit, source?: string) => void;
  handlePricePromoUnitClick: (item: PricePromoUnit, source?: string, section?: string) => void;
  formatThumbsupUnitSqft: (sqft: number | string | null | undefined) => string;
  formatPricePromoDate: (iso: string | null | undefined) => string;

  // Project View (comparable section - project view mode)
  projectViewPopoverAnchor: { element: HTMLElement; column: string } | null;
  setProjectViewPopoverAnchor: (anchor: { element: HTMLElement; column: string } | null) => void;
  projectViewFilters: Record<string, string>;
  setProjectViewFilters: (updater: ((prev: Record<string, string>) => Record<string, string>) | Record<string, string>) => void;
  projectViewRangeFilters: Record<string, { min: string; max: string }>;
  setProjectViewRangeFilters: (updater: ((prev: Record<string, { min: string; max: string }>) => Record<string, { min: string; max: string }>) | Record<string, { min: string; max: string }>) => void;
  projectViewDistrictFilter: string[];
  setProjectViewDistrictFilter: (filter: string[]) => void;
  projectViewDistrictOptions: string[];
  projectViewNumericColumns: Set<string>;

  // Additional utility functions referenced in component
  formatBlockNumber: (blockNumber: string | null) => string;
  normalizeBedroomValue: (value: unknown) => number | null;
  handleOpenSitePlanForProject: (projectName: string) => void;
  handleOpenFloorPlan: (unit: ComparableUnit | ReferenceUnit) => void;
  onOpenFloorPlanCompare?: () => void;
  handleOpenPriceChart: (unitId: number, type: 'price' | 'psf', showOnlyDrop?: boolean, source?: string, bedroom?: number | null) => void;
  handleShortlistAction: (payload: { unitId: number; label: string; floorplan?: string | null }) => void;
  setPricePromoInfoOpen: (open: boolean) => void;
  getPriceDiffColor: (value: number) => string;
  parseUnitNumber: (unitNumber: string, floorNumber?: string | null) => { floor: number | null; stack: number | null; floorLabel: string | null };
  shortlistByUnitId: Map<number, { id: number; unitId: number | null }>;
  shortlistUpdatingUnitId: number | null;
  topViewingProjectsForProjectView: string[];
  topViewingFilteredProjectsForView: string[];
  PROJECT_VIEW_COLUMN_KEYS: readonly string[];
}

/**
 * Compact "quick action" tile used across the project detail header (Factsheet,
 * Brochure, EDMs, eBooks, Agent Access Folder, Podcast Videos, Training Videos,
 * Export Report, etc.). Kept as a single visual atom so the row scales cleanly
 * as more actions are added without regressing to a dropdown / overflow menu.
 *
 * The icon lives in a tinted square on the left; the label is bold; when
 * `count` is provided it renders as a subdued second line so users can scan
 * "how much stuff is behind this button" at a glance.
 */
interface QuickActionTileProps {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  count?: number;
  disabled?: boolean;
  tooltip?: string;
}

const QuickActionTile: React.FC<QuickActionTileProps> = ({
  label,
  icon,
  onClick,
  count,
  disabled,
  tooltip,
}) => {
  const displayLabel = typeof count === 'number' ? `${label} (${count})` : label;

  const button = (
    <Button
      variant="text"
      onClick={onClick}
      disabled={disabled}
      startIcon={icon}
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        letterSpacing: '0.01em',
        borderRadius: 1.5,
        py: { xs: 1.25, sm: 1.25 },
        px: { xs: 0.5, sm: 1 },
        fontSize: { xs: '0.72rem', sm: '0.85rem' },
        lineHeight: 1.15,
        // Stack icon above label on phones; revert to standard horizontal layout from sm upward.
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: { xs: 'center', sm: 'flex-start' },
        alignItems: 'center',
        textAlign: { xs: 'center', sm: 'left' },
        gap: { xs: 0.5, sm: 0 },
        minWidth: 0,
        minHeight: { xs: 72, sm: 0 },
        width: '100%',
        bgcolor: 'var(--color-surface)',
        color: 'var(--color-text-strong)',
        border: '1px solid var(--color-accent)',
        boxShadow: 'none',
        '& .MuiButton-startIcon': {
          color: 'var(--color-accent)',
          mr: { xs: 0, sm: 0.75 },
          ml: 0,
          mb: { xs: 0.25, sm: 0 },
        },
        '&:hover': {
          bgcolor: 'var(--color-accent-soft)',
          borderColor: 'var(--color-accent)',
          boxShadow: 'none',
        },
        '&.Mui-disabled': {
          color: 'var(--color-text-muted)',
          bgcolor: 'var(--color-surface)',
          border: '1px solid var(--color-accent)',
          opacity: 0.55,
          '& .MuiButton-startIcon': { color: 'var(--color-text-muted)' },
        },
      }}
    >
      {displayLabel}
    </Button>
  );

  return tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button;
};

const TopViewingSitePlanDetail: React.FC<TopViewingSitePlanDetailProps> = (props) => {
  const {
    onAISummaryClick,
    isPdfCapturing,
    onExportProjectReport,
    hideShortlist = false,
    ceaNumber,
    topViewingSitePlans,
    selectedTopViewingSitePlan,
    handleSelectTopViewingSitePlan,
    setSelectedTopViewingSitePlan,
    fromProjectEssential = false,
    onBackToProjectEssential,
    projectDetails,
    referenceUnit,
    selectedProject,
    projectDetailsLoading,
    projectSectionExpanded,
    setProjectSectionExpanded,
    projectDetailsTab,
    setProjectDetailsTab,
    fetchAmenities,
    fetchProjectMembers,
    amenitiesRadius,
    setAmenitiesRadius,
    selectedAmenityTypes,
    setSelectedAmenityTypes,
    amenitiesLoading,
    amenitiesData,
    fetchDemandSupply,
    demandSupplyRadius,
    demandSupplyMinRadius = 0,
    onDemandSupplyRadiusChange,
    demandSupplyNationwide = false,
    onDemandSupplyNationwideChange,
    demandSupplyData,
    demandSupplyLoading,
    selectedDemandSupplyTypes,
    setSelectedDemandSupplyTypes,
    projectMembersLoading,
    projectMembers,
    canViewProjectMembers = true,
    disableSetReferenceAction = false,
    disableAddToCompareAction = false,
    isAdminUser = false,
    isProjectDeptUser = false,
    userRoles,
    canAccessSuperRatio = isAdminUser,
    canCreateShareLink = false,
    canAccessNearbyDeal = isAdminUser,
    canAccessSitePlan3d: canAccessSitePlan3dGrant = isAdminUser,
    canAccessSitePlan3dSun: canAccessSitePlan3dSunGrant = isAdminUser,
    priceMatrixSimulationSession = null,
    priceMatrixSimulationLoading = false,
    priceMatrixSimulationRefreshing = false,
    priceMatrixSimulationError = null,
    onLoadPriceMatrixSimulation,
    onRefreshPriceMatrixSimulation,
    onTogglePriceMatrixSimulationSync,
    onSetPriceMatrixSimulationStatus,
    onApplyPriceMatrixSimulationStatuses,
    onCancelPriceMatrixSimulation,
    getPhotoUrl,
    shouldShowSitePlanButton,
    handleOpenSitePlan,
    handleOpenTowerView,
    sitePlanData,
    sitePlanLoading,
    sitePlanShowPoints,
    setSitePlanShowPoints,
    sitePlanShowHeatMap,
    setSitePlanShowHeatMap,
    sitePlanBedroomFilter,
    setSitePlanBedroomFilter,
    sitePlanUnitTypeFilter,
    sitePlanUnitTypeFilterForMatching,
    sitePlanUnitTypeFilterExpandedUnitTypes,
    setSitePlanUnitTypeFilter,
    sitePlanUnitTypes,
    sitePlanBlinkingStacks,
    sitePlanStackSoldPercentages,
    sitePlanProjectName,
    handleOpenStackAnalysis,
    isMobileSitePlan,
    sitePlanDotSize,
    sitePlanDotBorder,
    sitePlanDotShadow,
    sitePlanDotFontSize,
    shouldHideNonMatchingSitePlanStack,
    getHeatMapColor,
    normalizeStackKey,
    sitePlanMenuOpen,
    setSitePlanMenuOpen,
    sitePlanStackBedrooms,
    sitePlanStackFilter,
    setSitePlanStackFilter,
    sitePlanStacksOptions,
    topViewingTowerZoom,
    setTopViewingTowerZoom,
    topViewingTowerDisplayOptions,
    setTopViewingTowerDisplayOptions,
    showPsfColumnByDefault = true,
    topViewingTowerBedroomColor,
    setTopViewingTowerBedroomColor,
    towerViewData,
    towerViewUnits,
    towerViewUnitTypes,
    topViewingBestPxBedroomSitePlan,
    topViewingBestByUnitTypeData,
    topViewingBestByUnitTypeLoading,
    isTopViewingIndicativeBeforeLaunch,
    topViewingFilteredAvailableUnits,
    topViewingDetailedAvailableUnits,
    topViewingSortedUnits,
    topViewingSortConfig,
    topViewingPopoverAnchor,
    topViewingAvailableUnitFilters,
    setTopViewingAvailableUnitFilters,
    topViewingUnitMenuAnchor,
    setTopViewingUnitMenuAnchor,
    handleTopViewingOpenPopover,
    handleTopViewingClosePopover,
    handleTopViewingSortChange,
    handleTopViewingFilterChange,
    handleTopViewingRangeFilterChange,
    hasTopViewingActiveFilter,
    getTopViewingSortDirection,
    topViewingAvailableUnitsViewMode,
    setTopViewingAvailableUnitsViewMode,
    topViewingCompViewMode,
    setTopViewingCompViewMode,
    topViewingFloorPlanOpen,
    setTopViewingFloorPlanOpen,
    topViewingFloorPlanUrl,
    setTopViewingFloorPlanUrl,
    topViewingFloorPlanLoading,
    topViewingFloorPlanUnit,
    topViewingReferenceUnit,
    setTopViewingReferenceUnit,
    topViewingBlinkerChoiceUnit,
    setTopViewingBlinkerChoiceUnit,
    topViewingBlinkerMinTolerance,
    setTopViewingBlinkerMinTolerance,
    setTopViewingBlinkerMinPriceOverride,
    topViewingBlinkerTolerance,
    setTopViewingBlinkerTolerance,
    setTopViewingBlinkerMaxPriceOverride,
    topViewingBlinkerSuperUpgrade,
    setTopViewingBlinkerSuperUpgrade,
    topViewingBlinkerRange,
    handleTopViewingIndicativeReference,
    handleTopViewingGuesstimationReference,
    topViewingComparableUnits,
    topViewingComparableUnitsLoading,
    topViewingAreaTolerance,
    setTopViewingAreaTolerance,
    topViewingToleranceSliderValue,
    setTopViewingToleranceSliderValue,
    topViewingToleranceModalOpen,
    setTopViewingToleranceModalOpen,
    topViewingComparableSectionOpen,
    setTopViewingComparableSectionOpen,
    topViewingCompFilters,
    setTopViewingCompFilters,
    topViewingCompRangeFilters,
    topViewingCompRangeFiltersApplied,
    setTopViewingCompRangeFilters,
    topViewingCompPopoverAnchor,
    topViewingThumbsUpFilterActive,
    setTopViewingThumbsUpFilterActive,
    topViewingCompSortConfig,
    setTopViewingCompSortConfig,
    topViewingFloorPlansToCompare,
    setTopViewingFloorPlansToCompare,
    topViewingComparableSectionRef,
    topViewingTowerViewSectionRef,
    topViewingThumbsUpLegendAnchor,
    setTopViewingThumbsUpLegendAnchor,
    handleTopViewingCompOpenPopover,
    handleTopViewingCompClosePopover,
    handleTopViewingCompFilterChange,
    handleTopViewingCompRangeFilterChange,
    handleTopViewingCompSort,
    hasTopViewingCompActiveFilter,
    getTopViewingCompColumnLabel,
    topViewingFilteredComparableUnits,
    topViewingComparableResultProjectNames,
    topViewingComparableResultProjectCategories,
    topViewingComparableResultUnitTypeDescriptions,
    handleTopViewingAddToUnitCompare,
    analyticsCea,
    topViewingBudgetSectionOpen,
    setTopViewingBudgetSectionOpen,
    topViewingBudgetTolerance,
    setTopViewingBudgetTolerance,
    topViewingBudgetSliderValue,
    setTopViewingBudgetSliderValue,
    topViewingBudgetUnitsLoading,
    topViewingBudgetUnitsError,
    topViewingBudgetRange,
    topViewingBudgetSearchRange,
    handleTopViewingBudgetSearchRangeChange,
    topViewingBudgetViewMode,
    setTopViewingBudgetViewMode,
    topViewingBudgetRegionTab,
    setTopViewingBudgetRegionTab,
    topViewingBudgetSortedUnits,
    topViewingBudgetPopoverAnchor,
    topViewingBudgetSortConfig,
    topViewingBudgetFilters,
    topViewingBudgetExclusionFilters,
    topViewingBudgetUniqueFilterOptions,
    handleTopViewingBudgetExclusionFilterChange,
    handleTopViewingBudgetExclusionFilterSet,
    topViewingBudgetRangeFilters,
    topViewingOutlayInfoAnchor,
    setTopViewingOutlayInfoAnchor,
    topViewingInstallmentInfoAnchor,
    setTopViewingInstallmentInfoAnchor,
    handleTopViewingBudgetOpenPopover,
    handleTopViewingBudgetClosePopover,
    handleTopViewingBudgetFilterChange,
    handleTopViewingBudgetRangeFilterChange,
    handleTopViewingBudgetSortChange,
    getTopViewingBudgetSortDirection,
    hasTopViewingBudgetActiveFilter,
    getTopViewingBudgetIconButtonSx,
    handleTopViewingBudgetClearFilters,
    getBudgetProjectCellValue,
    topViewingBudgetProjectsForView,
    onOpenBudgetInProjectResearch,
    topViewingBudgetNearbyRadiusKm,
    setTopViewingBudgetNearbyRadiusKm,
    topViewingBudgetShowReferenceProjectUnits,
    setTopViewingBudgetShowReferenceProjectUnits,
    handleTopViewingUnitMenuViewFloorPlan,
    handleTopViewingUnitMenuSetReference,
    handleTopViewingUnitMenuSetBlinkerChoice,
    handleTopViewingUnitMenuToggleCompare,
    formatCurrency,
    formatNumber,
    getAreaRangeLabel,
    getOutlayFormulaTooltip,
    getInstallmentFormulaTooltip,
    onOpenFinancialParameters,
    onOpenNpsInfo,
    onOpenDpsInfo,
    currentInterestRate,
    loanToValue = 75,
    getIconButtonSx,
    qualifiesForThumbsUp,
    getTopViewingDistanceFromReference,
    projectDetailsMap,
    setProjectDetailsMap,
    nearbyThumbsupGroups,
    nearbyPricePromoGroups,
    pricePromoBestByStackBedroom,
    pricePromoUnitIds,
    handleThumbsupUnitClick,
    handlePricePromoUnitClick,
    formatThumbsupUnitSqft,
    formatPricePromoDate,
    projectViewPopoverAnchor,
    setProjectViewPopoverAnchor,
    projectViewFilters,
    setProjectViewFilters,
    projectViewRangeFilters,
    setProjectViewRangeFilters,
    projectViewDistrictFilter,
    setProjectViewDistrictFilter,
    projectViewDistrictOptions,
    projectViewNumericColumns,
    formatBlockNumber,
    normalizeBedroomValue,
    handleOpenSitePlanForProject,
    handleOpenFloorPlan,
    onOpenFloorPlanCompare,
    handleOpenPriceChart,
    handleShortlistAction,
    setPricePromoInfoOpen,
    getPriceDiffColor,
    parseUnitNumber,
    shortlistByUnitId,
    shortlistUpdatingUnitId,
    topViewingProjectsForProjectView,
    topViewingFilteredProjectsForView,
    PROJECT_VIEW_COLUMN_KEYS,
    onMemberTap,
  } = props;

  // Project members search state
  const [projectMembersSearch, setProjectMembersSearch] = React.useState<string>('');

  const flattenedProjectMembers = React.useMemo(() => {
    if (!projectMembers) return [];
    const result: Array<{ role: string; member: ProjectMemberInfo }> = [];
    projectMembers.members.forEach((roleGroup) => {
      roleGroup.members.forEach((member) => {
        result.push({ role: roleGroup.role, member });
        // PCs and Taggers with a manager are nested under their Lead IC / IC,
        // not listed as their own role group — surface them for search too.
        const managerName = member.name || 'N/A';
        (member.pcs || []).forEach((pc) => {
          result.push({ role: `PC · under ${managerName}`, member: pc });
        });
        (member.taggers || []).forEach((tagger) => {
          result.push({ role: `Tagger · under ${managerName}`, member: tagger });
        });
      });
    });
    return result;
  }, [projectMembers]);

  const filteredProjectMembers = React.useMemo(() => {
    const query = projectMembersSearch.trim().toLowerCase();
    if (!query) return flattenedProjectMembers;
    return flattenedProjectMembers.filter(({ member }) => {
      const name = (member.name || '').toLowerCase();
      const cea = (member.ceaNumber || '').toLowerCase();
      const mobile = (member.mobile || '').toLowerCase();
      return name.includes(query) || cea.includes(query) || mobile.includes(query);
    });
  }, [flattenedProjectMembers, projectMembersSearch]);

  const [districtOptionsByCode, setDistrictOptionsByCode] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    let mounted = true;
    const loadDistricts = async () => {
      try {
        const districts = await api.getDistricts();
        if (!mounted) return;
        const next: Record<string, string> = {};
        districts.forEach((district: DistrictOption) => {
          const no = String(district.no ?? '').trim();
          const noDigitsMatch = no.match(/\d{1,2}/);
          const noDigits = noDigitsMatch ? noDigitsMatch[0] : '';

          const rawName = (district.name ?? district.generalLocation ?? '').trim();
          const nameDigitsMatch = rawName.match(/D?\s*(\d{1,2})/i);
          const nameDigits = nameDigitsMatch ? nameDigitsMatch[1] : '';

          const digits = noDigits || nameDigits;
          if (!digits) return;

          const displayCode = `D${digits.padStart(2, '0')}`;
          const cleanedName = rawName.replace(/^\s*D{0,2}0*\d+\s*-\s*/i, '').trim();
          const finalName = cleanedName || rawName;
          if (!finalName) return;

          next[displayCode] = finalName;
        });
        setDistrictOptionsByCode(next);
      } catch (error) {
        console.error('Failed to load district options for budget filter:', error);
      }
    };
    loadDistricts();
    return () => {
      mounted = false;
    };
  }, []);

  const getDistrictFilterOptionLabel = React.useCallback((value: string): string => {
    const rawValue = String(value ?? '').trim();
    if (!rawValue) return '(empty)';
    const digitsMatch = rawValue.match(/\d{1,2}/);
    const digits = digitsMatch ? digitsMatch[0] : rawValue;
    const displayCode = `D${digits.padStart(2, '0')}`;
    const districtName = districtOptionsByCode[displayCode];
    if (!districtName) return displayCode;
    return `${displayCode} - ${districtName}`;
  }, [districtOptionsByCode]);

  const formatLiveStatusTimestamp = (value?: string | Date | null): string => {
    if (!value) {
      return 'N/A';
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'N/A';
    }
    const pad = (num: number) => num.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  // iPad & Mobile Performance Optimizations
  const theme = useTheme();
  const isLightTheme = theme.palette.mode === 'light';
  const blinkerAccentColor = isLightTheme ? '#1b5e20' : '#ffeb3b';
  const blinkerAccentRgb = isLightTheme ? '27, 94, 32' : '255, 235, 59';
  const blinkerValueLabelTextColor = isLightTheme ? '#ffffff' : '#1f2937';
  const blinkerKeyframeName = isLightTheme ? 'blinkerChoiceBorderLight' : 'blinkerChoiceBorderDark';
  const isMobile = useIsMobile();
  const isIPad = useIsIPad();
  const blinkerRangeTouchFriendly = isMobile || isMobileSitePlan || isIPad;
  const blinkerRangeControlIconSx = React.useMemo(
    () =>
      ({
        color: 'var(--color-text-muted)',
        ...(blinkerRangeTouchFriendly
          ? {
              minWidth: 44,
              minHeight: 44,
              p: 0.65,
              borderRadius: 999,
              border: 'none',
              bgcolor: 'rgba(255, 255, 255, 0.05)',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
              '&:active': { bgcolor: 'rgba(255, 255, 255, 0.14)' },
              '&.Mui-disabled': { opacity: 0.4, bgcolor: 'rgba(255, 255, 255, 0.03)' },
            }
          : { p: 0.25 }),
      }) as const,
    [blinkerRangeTouchFriendly],
  );
  const blinkerRangeStepGlyphRem = blinkerRangeTouchFriendly ? '1.05rem' : '0.75rem';
  const blinkerRangeEditGlyphRem = blinkerRangeTouchFriendly ? '1.1rem' : '0.8rem';
  /** Phone / mobile site-plan panel: vertically center label vs icon row; iPad keeps prior alignment */
  const blinkerRangeRowAlignItems = !isIPad && (isMobile || isMobileSitePlan) ? 'center' : 'flex-start';
  const blinkerRangeButtonClusterPt = !isIPad && (isMobile || isMobileSitePlan) ? 0 : 0.1;
  const isTabletLandscape = useMediaQuery('(min-width:900px) and (max-width:1366px) and (orientation: landscape)');
  const scrollableStyles = useScrollableContainerStyles();
  const mobileOptimizedStyles = useMobileOptimizedStyles();
  const gpuAccelerationStyles = useGPUAcceleration();
  const TOP_VIEWING_PAGE_SIZE = 20;
  const SITE_PLAN_MIN_SCALE = 0.8;
  // Project currently on screen. Declared here because the 3D grants below are
  // scoped per project.
  const currentSitePlan = React.useMemo(() => {
    return topViewingSitePlans.find((item) => item.projectName === selectedTopViewingSitePlan) ?? null;
  }, [topViewingSitePlans, selectedTopViewingSitePlan]);
  const currentProjectId = currentSitePlan?.projectId || projectDetails?.id || null;
  // Per-project lockdown: on a restricted project 3D and 3DS are ADMIN-only,
  // though everyone opens them everywhere else. Mirrored
  // server-side for 3DS by requireAdminOrSitePlan3dSun; plain 3D calls no gated
  // endpoint, so this is the only gate for it.
  const currentProjectName = currentSitePlan?.projectName || projectDetails?.name || null;
  const sitePlan3dRestrictedProject = isSitePlan3dRestrictedProject(currentProjectId, currentProjectName);
  const canAccessSitePlan3d = sitePlan3dRestrictedProject
    ? isAdminUser
    : canAccessSitePlan3dGrant;
  // Narrower lockdown: some projects keep 3D open but hold 3DS back for ADMIN,
  // PROJECT-DEPT and the project's own grant role.
  const sitePlan3dsLockedProjectRole = getSitePlan3dsLockedProjectRole(currentProjectId);
  const canAccessSitePlan3dSun = sitePlan3dsLockedProjectRole
    ? isAdminUser || isProjectDeptUser || Boolean(userRoles?.has(sitePlan3dsLockedProjectRole))
    : sitePlan3dRestrictedProject
      ? isAdminUser
      : canAccessSitePlan3dSunGrant;
  // Two 3D grants, each with its own role: `3d` is the bare massing model and
  // `3ds` adds the solar rig (sun, shadows, time of day). The toggle only offers
  // what the viewer holds, and the effective mode falls back to 2D so a stale
  // selection can never render a scene the viewer lost access to.
  // `views` is the curated view-map slider, offered to everyone on projects
  // that have slides (see sitePlanViews.ts).
  const sitePlanViewSlides = React.useMemo(() => getSitePlanViews(currentProjectId), [currentProjectId]);
  // `drone` is the aerial tour an admin attached in Manage Drone View, offered
  // to everyone on projects that have an active one.
  const [sitePlanDroneView, setSitePlanDroneView] = React.useState<DroneView | null>(null);
  React.useEffect(() => {
    setSitePlanDroneView(null);
    if (!currentProjectId) return undefined;
    let cancelled = false;
    api.getDroneView(String(currentProjectId))
      .then((view) => {
        if (!cancelled) setSitePlanDroneView(view);
      })
      .catch(() => {
        // No drone view available (or migration 079 not applied) — the tab stays hidden.
      });
    return () => {
      cancelled = true;
    };
  }, [currentProjectId]);
  const [sitePlanViewModeSelection, setSitePlanViewMode] = React.useState<'2d' | '3d' | '3ds' | 'drone' | 'views'>('2d');
  const sitePlanViewMode: '2d' | '3d' | '3ds' | 'drone' | 'views' =
    (sitePlanViewModeSelection === '3d' && canAccessSitePlan3d)
      || (sitePlanViewModeSelection === '3ds' && canAccessSitePlan3dSun)
      || (sitePlanViewModeSelection === 'drone' && sitePlanDroneView)
      || (sitePlanViewModeSelection === 'views' && sitePlanViewSlides.length > 0)
      ? sitePlanViewModeSelection
      : '2d';
  const sitePlanIs3d = sitePlanViewMode === '3d' || sitePlanViewMode === '3ds';
  // Edited plans (Super Picker) can be compared against the developer's original in 2D.
  // Unit points are plotted on the edited image, so they are hidden on the original.
  const sitePlanOriginalUrl = sitePlanData?.originalImageUrl || null;
  const [sitePlanOriginalSelected, setSitePlanShowOriginal] = React.useState(false);
  const sitePlanShowOriginal = sitePlanOriginalSelected && !!sitePlanOriginalUrl && sitePlanViewMode === '2d';
  React.useEffect(() => { setSitePlanShowOriginal(false); }, [sitePlanData?.imageUrl]);
  // Storeys per stack derived from tower-view units; keys are `${blockDigits}-${normalizedStack}`
  // (preferred) and bare normalized stack (fallback), matching the sold-percentage lookups.
  const sitePlanStackFloorCounts = React.useMemo(() => {
    const counts = new Map<string, number>();
    let maxFloors = 0;
    towerViewData.blocks.forEach((blockData, blockKey) => {
      const blockDigits = String(blockKey).replace(/\D/g, '');
      blockData.stacks.forEach((stackNum) => {
        let topFloor = 0;
        blockData.floors.forEach((floor) => {
          if (floor > topFloor && blockData.grid.has(`${floor}-${stackNum}`)) topFloor = floor;
        });
        if (topFloor <= 0) return;
        const normStack = normalizeStackKey(stackNum);
        if (blockDigits) counts.set(`${blockDigits}-${normStack}`, topFloor);
        counts.set(normStack, Math.max(counts.get(normStack) ?? 0, topFloor));
        if (topFloor > maxFloors) maxFloors = topFloor;
      });
    });
    return { counts, maxFloors };
  }, [towerViewData, normalizeStackKey]);
  // Block of each stack, for plotted stacks saved without a block number. Only
  // stacks that appear in exactly one block are mapped, so a shared stack
  // number never lands on the wrong block.
  const sitePlanStackBlocks = React.useMemo(() => {
    const blocks = new Map<string, string | null>();
    towerViewData.blocks.forEach((blockData, blockKey) => {
      if (!blockKey || blockKey === 'Default') return;
      blockData.stacks.forEach((stackNum) => {
        const norm = normalizeStackKey(stackNum);
        blocks.set(norm, blocks.has(norm) && blocks.get(norm) !== blockKey ? null : String(blockKey));
      });
    });
    return blocks;
  }, [towerViewData, normalizeStackKey]);
  const SITE_PLAN_3D_DEFAULT_TILT = 55;
  // Orbit control for the 3D site plan. Angles live in refs and are applied as
  // inline styles on the tilted plane so dragging never re-renders this component.
  const sitePlanPlaneRef = React.useRef<HTMLDivElement | null>(null);
  const sitePlan3dAnglesRef = React.useRef({ tilt: SITE_PLAN_3D_DEFAULT_TILT, rotateZ: 0 });
  const sitePlan3dDragRef = React.useRef<{ pointerId: number; startX: number; startY: number; startTilt: number; startRotateZ: number } | null>(null);
  const sitePlan3dDragMovedRef = React.useRef(false);
  const applySitePlan3dTransform = React.useCallback((withTransition: boolean) => {
    const plane = sitePlanPlaneRef.current;
    if (!plane) return;
    const { tilt, rotateZ } = sitePlan3dAnglesRef.current;
    plane.style.transition = withTransition ? 'transform 0.4s ease' : 'none';
    plane.style.transform = `rotateX(${tilt}deg) rotateZ(${rotateZ}deg)`;
    // Counter-rotation for the stack labels so they stay upright while the map spins.
    plane.style.setProperty('--site-plan-3d-rz', `${rotateZ}deg`);
  }, []);
  // Turntable showcase: on entering 3D the plan drifts anticlockwise on its own
  // until the viewer takes over by dragging.
  const SITE_PLAN_3D_SPIN_DEG_PER_SEC = 7;
  const SITE_PLAN_3D_TRANSITION_MS = 400;
  const sitePlan3dSpinRafRef = React.useRef<number | null>(null);
  const sitePlan3dSpinDelayRef = React.useRef<number | null>(null);
  const sitePlan3dSpinLastTsRef = React.useRef<number | null>(null);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const stopSitePlan3dAutoSpin = React.useCallback(() => {
    if (sitePlan3dSpinDelayRef.current != null) {
      window.clearTimeout(sitePlan3dSpinDelayRef.current);
      sitePlan3dSpinDelayRef.current = null;
    }
    if (sitePlan3dSpinRafRef.current != null) {
      window.cancelAnimationFrame(sitePlan3dSpinRafRef.current);
      sitePlan3dSpinRafRef.current = null;
    }
    sitePlan3dSpinLastTsRef.current = null;
  }, []);
  const startSitePlan3dAutoSpin = React.useCallback((delayMs: number) => {
    stopSitePlan3dAutoSpin();
    if (prefersReducedMotion || !sitePlanPlaneRef.current) return;
    const step = (ts: number) => {
      const last = sitePlan3dSpinLastTsRef.current;
      sitePlan3dSpinLastTsRef.current = ts;
      if (last != null) {
        // Time-based so the speed holds regardless of frame rate; the clamp keeps a
        // backgrounded tab from jumping the map on return.
        const deltaSec = Math.min(ts - last, 100) / 1000;
        const current = sitePlan3dAnglesRef.current;
        // Anticlockwise: CSS rotateZ is clockwise-positive, so wind it down.
        sitePlan3dAnglesRef.current = {
          ...current,
          rotateZ: current.rotateZ - SITE_PLAN_3D_SPIN_DEG_PER_SEC * deltaSec,
        };
        applySitePlan3dTransform(false);
      }
      sitePlan3dSpinRafRef.current = window.requestAnimationFrame(step);
    };
    sitePlan3dSpinDelayRef.current = window.setTimeout(() => {
      sitePlan3dSpinDelayRef.current = null;
      sitePlan3dSpinRafRef.current = window.requestAnimationFrame(step);
    }, delayMs);
  }, [applySitePlan3dTransform, prefersReducedMotion, stopSitePlan3dAutoSpin]);
  const resetSitePlan3dView = React.useCallback(() => {
    sitePlan3dAnglesRef.current = { tilt: SITE_PLAN_3D_DEFAULT_TILT, rotateZ: 0 };
    applySitePlan3dTransform(true);
    startSitePlan3dAutoSpin(SITE_PLAN_3D_TRANSITION_MS);
  }, [applySitePlan3dTransform, startSitePlan3dAutoSpin]);
  React.useEffect(() => {
    // Entering either mode starts from the default camera; clearing the inline
    // styles lets the sx transition animate the tilt/untilt between modes.
    sitePlan3dAnglesRef.current = { tilt: SITE_PLAN_3D_DEFAULT_TILT, rotateZ: 0 };
    sitePlan3dDragRef.current = null;
    const plane = sitePlanPlaneRef.current;
    if (plane) {
      plane.style.transition = '';
      plane.style.transform = '';
      plane.style.setProperty('--site-plan-3d-rz', '0deg');
    }
    if (sitePlanIs3d) {
      // Hold off until the tilt transition lands, then drift.
      startSitePlan3dAutoSpin(SITE_PLAN_3D_TRANSITION_MS + 100);
    } else {
      stopSitePlan3dAutoSpin();
    }
    return stopSitePlan3dAutoSpin;
  }, [sitePlanViewMode, startSitePlan3dAutoSpin, stopSitePlan3dAutoSpin]);
  const handleSitePlan3dPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!sitePlanIs3d) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    // Any touch on the plan hands control to the viewer.
    stopSitePlan3dAutoSpin();
    if (sitePlan3dDragRef.current) {
      // A second touch point starts a pinch — hand the gesture back to the zoom library.
      sitePlan3dDragRef.current = null;
      return;
    }
    sitePlan3dDragMovedRef.current = false;
    sitePlan3dDragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startTilt: sitePlan3dAnglesRef.current.tilt,
      startRotateZ: sitePlan3dAnglesRef.current.rotateZ,
    };
  };
  const handleSitePlan3dPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = sitePlan3dDragRef.current;
    if (!drag || e.pointerId !== drag.pointerId) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!sitePlan3dDragMovedRef.current) {
      if (Math.hypot(dx, dy) <= 4) return;
      sitePlan3dDragMovedRef.current = true;
      e.currentTarget.setPointerCapture?.(e.pointerId);
    }
    sitePlan3dAnglesRef.current = {
      // Drag-left must spin the near edge left, which is clockwise seen from above;
      // CSS rotateZ is clockwise-positive, so the horizontal delta is negated.
      rotateZ: drag.startRotateZ - dx * 0.4,
      tilt: Math.min(80, Math.max(5, drag.startTilt - dy * 0.25)),
    };
    applySitePlan3dTransform(false);
  };
  const handleSitePlan3dPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = sitePlan3dDragRef.current;
    if (!drag || e.pointerId !== drag.pointerId) return;
    sitePlan3dDragRef.current = null;
  };
  const handleSitePlan3dClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    // A drag that rotated the map must not fire the stack-analysis click underneath.
    if (sitePlan3dDragMovedRef.current) {
      sitePlan3dDragMovedRef.current = false;
      e.stopPropagation();
      e.preventDefault();
    }
  };
  const isIPadPro129 = useIsIPadPro129();
  const [sectionUnitMenuAnchor, setSectionUnitMenuAnchor] = React.useState<{
    unit: TowerViewUnit | ComparableUnit;
    mouseX: number;
    mouseY: number;
    mode: 'comparable' | 'budget';
  } | null>(null);
  const [topViewingFormulaAnchor, setTopViewingFormulaAnchor] = React.useState<{
    element: HTMLElement;
    content: React.ReactNode;
  } | null>(null);
  const suppressNextSectionUnitMenuRef = React.useRef(false);
  const suppressNextSectionUnitMenuTimeoutRef = React.useRef<number | null>(null);
  const referenceUnitLabelSx = {
    color: 'rgba(var(--color-text-rgb), 0.65)',
    whiteSpace: 'nowrap',
    fontSize: isIPadPro129 ? '0.9rem' : '0.75rem',
    lineHeight: isIPadPro129 ? 1.5 : 1.4,
  } as const;
  const referenceUnitValueSx = {
    fontWeight: 600,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    minWidth: 0,
    fontSize: isIPadPro129 ? '0.9rem' : '0.75rem',
    lineHeight: isIPadPro129 ? 1.55 : 1.4,
  } as const;
  const referenceInfoGridColumns = isIPadPro129 ? 'minmax(84px, 112px) minmax(80px, 1fr)' : 'minmax(72px, 96px) minmax(64px, 1fr)';
  const suppressNextSectionUnitMenu = React.useCallback(() => {
    suppressNextSectionUnitMenuRef.current = true;
    if (suppressNextSectionUnitMenuTimeoutRef.current !== null) {
      window.clearTimeout(suppressNextSectionUnitMenuTimeoutRef.current);
    }
    suppressNextSectionUnitMenuTimeoutRef.current = window.setTimeout(() => {
      suppressNextSectionUnitMenuRef.current = false;
      suppressNextSectionUnitMenuTimeoutRef.current = null;
    }, 500);
  }, []);
  const openTopViewingFormulaPopover = React.useCallback((event: React.MouseEvent<HTMLElement>, content: React.ReactNode) => {
    suppressNextSectionUnitMenu();
    event.stopPropagation();
    setSectionUnitMenuAnchor(null);
    setTopViewingUnitMenuAnchor(null);
    setTopViewingFormulaAnchor({ element: event.currentTarget, content });
  }, [setTopViewingUnitMenuAnchor, suppressNextSectionUnitMenu]);
  const closeTopViewingFormulaPopover = React.useCallback(() => {
    setTopViewingFormulaAnchor(null);
  }, []);
  const isTopViewingFormulaCellTarget = React.useCallback((target: EventTarget | null) => {
    return target instanceof HTMLElement && Boolean(target.closest('[data-top-viewing-formula-cell="true"]'));
  }, []);

  const [nearbyDealVisible, setNearbyDealVisible] = React.useState<boolean>(false);
  const [nearbyDealSectionOpen, setNearbyDealSectionOpen] = React.useState<boolean>(false);
  const [nearbyDealTab, setNearbyDealTab] = React.useState<'thumbsup' | 'pricePromo'>('thumbsup');

  const [projectInfoExpanded, setProjectInfoExpanded] = React.useState<boolean>(true);
  const [anticipateBlinkerSectionExpanded, setAnticipateBlinkerSectionExpanded] = React.useState<boolean>(true);
  const [referenceUnitSectionExpanded, setReferenceUnitSectionExpanded] = React.useState<boolean>(true);
  const [towerViewSectionExpanded, setTowerViewSectionExpanded] = React.useState<boolean>(true);
  const [priceReferenceSectionExpanded, setPriceReferenceSectionExpanded] = React.useState<boolean>(true);
  const [availableUnitsSectionExpanded, setAvailableUnitsSectionExpanded] = React.useState<boolean>(true);
  const [bestPxBedroomSectionExpanded, setBestPxBedroomSectionExpanded] = React.useState<boolean>(true);
  const [blinkerMinPriceEditAnchor, setBlinkerMinPriceEditAnchor] = React.useState<HTMLElement | null>(null);
  const [blinkerMinPriceInput, setBlinkerMinPriceInput] = React.useState<string>('');
  const [blinkerMaxPriceEditAnchor, setBlinkerMaxPriceEditAnchor] = React.useState<HTMLElement | null>(null);
  const [blinkerMaxPriceInput, setBlinkerMaxPriceInput] = React.useState<string>('');
  const [rightPanelCollapsed, setRightPanelCollapsed] = React.useState<boolean>(true);
  const formatBlinkerPriceInput = React.useCallback((value: string) => {
    const digitsOnly = value.replace(/[^\d]/g, '');
    if (!digitsOnly) return '';
    return Number(digitsOnly).toLocaleString('en-US');
  }, []);
  // Auto-expand right panel when blinker or reference unit is set, auto-collapse when both cleared
  React.useEffect(() => {
    if (topViewingBlinkerChoiceUnit || topViewingReferenceUnit) {
      setRightPanelCollapsed(false);
    } else {
      setRightPanelCollapsed(true);
    }
  }, [topViewingBlinkerChoiceUnit, topViewingReferenceUnit]);

  // Keep All Blocks Tower View open when a blinker is set so matching cells are visible.
  React.useEffect(() => {
    if (topViewingBlinkerChoiceUnit) {
      setTowerViewSectionExpanded(true);
    }
  }, [topViewingBlinkerChoiceUnit]);
  const [refreshing, setRefreshing] = React.useState<boolean>(false);
  const [factsheetModalOpen, setFactsheetModalOpen] = React.useState<boolean>(false);
  const [factsheetData, setFactsheetData] = React.useState<ProjectFactsheet | null>(null);
  const [factsheetLoading, setFactsheetLoading] = React.useState<boolean>(false);
  const [brochureModalOpen, setBrochureModalOpen] = React.useState<boolean>(false);
  const [brochureData, setBrochureData] = React.useState<ProjectBrochure | null>(null);
  const [brochureLoading, setBrochureLoading] = React.useState<boolean>(false);
  const [projectTrainingRegistrationOpen, setProjectTrainingRegistrationOpen] = React.useState<boolean>(false);
  const [hasProjectTrainingEvents, setHasProjectTrainingEvents] = React.useState<boolean>(false);
  const [brochurePdfNumPages, setBrochurePdfNumPages] = React.useState<number>(0);
  const [brochurePdfPageNumber, setBrochurePdfPageNumber] = React.useState<number>(1);
  const [filesFactsheetData, setFilesFactsheetData] = React.useState<ProjectFactsheet | null>(null);
  const [filesBrochureData, setFilesBrochureData] = React.useState<ProjectBrochure | null>(null);
  // EDMs / eBooks tied to the current project via the EDMProject /
  // ProjectLauncherProject join tables. Fetched alongside factsheet
  // + brochure when the Files tab opens and rendered as
  // `DownloadFileRow`s under their own section headings.
  const [filesEdms, setFilesEdms] = React.useState<ProjectEdmFile[]>([]);
  const [filesEbooks, setFilesEbooks] = React.useState<ProjectEbookFile[]>([]);
  // States to drive the quick-list modal for Project EDMs or eBooks.
  // Triggered by clicking the 'EDMs' or 'EBooks' buttons in the Details tab.
  const [fileListDialogOpen, setFileListDialogOpen] = React.useState<boolean>(false);
  const [fileListDialogType, setFileListDialogType] = React.useState<'edm' | 'ebook'>('edm');
  // Drives the project-scoped "Podcast Videos" quick-list dialog,
  // opened from the button beside the EDMs / eBooks buttons. The
  // verified clips tagged to the active project are preloaded on
  // project change so the button can show a count (like EDMs) and the
  // dialog can render without a second fetch.
  const [podcastVideosDialogOpen, setPodcastVideosDialogOpen] = React.useState<boolean>(false);
  const [projectPodcastVideos, setProjectPodcastVideos] = React.useState<PodcastVideoRecord[]>([]);
  // Per-project Training Videos quick-list — same pattern as the
  // Podcast Videos dialog above. The verified rows are preloaded on
  // project change so the button can show a count and the dialog
  // can open without a second fetch.
  const [trainingVideosDialogOpen, setTrainingVideosDialogOpen] = React.useState<boolean>(false);
  const [projectTrainingVideos, setProjectTrainingVideos] = React.useState<PodcastVideoRecord[]>([]);
  const [filesLoading, setFilesLoading] = React.useState<boolean>(false);
  const [filesSubTab, setFilesSubTab] = React.useState<'documents' | 'edms' | 'ebooks' | 'files' | 'agentAccess'>('documents');
  const [filesGrouped, setFilesGrouped] = React.useState<Array<{
    category: string;
    items: Array<{
      id: number;
      filename: string;
      displayName: string;
      category: string;
      url: string | null;
      createdAt: string | null;
      updatedAt: string | null;
    }>;
  }>>([]);
  const [filesGroupedLoading, setFilesGroupedLoading] = React.useState<boolean>(false);
  const [projectMediaItems, setProjectMediaItems] = React.useState<ProjectMediaItem[]>([]);
  const [projectMediaLoading, setProjectMediaLoading] = React.useState(false);
  const [projectMediaError, setProjectMediaError] = React.useState<string | null>(null);
  const [mediaSubTab, setMediaSubTab] = React.useState<MediaSubTabId>('gallery');
  // Agent Access Folder — curated external links per project. Fetched on project
  // change so the button + sub-tab counts stay in sync. `canManageResourceLinks`
  // reflects the server-side permission check (Head / Deputy Head of Project).
  const [projectResourceLinks, setProjectResourceLinks] = React.useState<ProjectResourceLink[]>([]);
  const [projectResourceLinksLoading, setProjectResourceLinksLoading] = React.useState<boolean>(false);
  const [canManageResourceLinks, setCanManageResourceLinks] = React.useState<boolean>(false);
  const [agentAccessDialogOpen, setAgentAccessDialogOpen] = React.useState<boolean>(false);
  const [agentAccessPage, setAgentAccessPage] = React.useState<number>(1);
  // Add / edit form state inside the manage dialog. `editingLink` is null for a
  // fresh add flow; set to the row being edited otherwise.
  const [agentAccessFormOpen, setAgentAccessFormOpen] = React.useState<boolean>(false);
  const [agentAccessEditingId, setAgentAccessEditingId] = React.useState<number | null>(null);
  const [agentAccessFormUrl, setAgentAccessFormUrl] = React.useState<string>('');
  const [agentAccessFormLabel, setAgentAccessFormLabel] = React.useState<string>('');
  const [agentAccessSaving, setAgentAccessSaving] = React.useState<boolean>(false);
  const [agentAccessError, setAgentAccessError] = React.useState<string | null>(null);
  const [agentAccessDuplicateNotice, setAgentAccessDuplicateNotice] = React.useState<boolean>(false);

  const PROJECT_IC_ROLES = ['Lead IC', 'IC'] as const;
  const isCurrentUserProjectIc = React.useMemo(() => {
    const normalizedCea = (ceaNumber || '').trim().toUpperCase();
    if (!normalizedCea || !projectMembers?.members?.length) return false;
    return projectMembers.members.some(
      (group) =>
        PROJECT_IC_ROLES.includes(group.role as (typeof PROJECT_IC_ROLES)[number]) &&
        group.members.some((member) => (member.ceaNumber || '').trim().toUpperCase() === normalizedCea),
    );
  }, [ceaNumber, projectMembers]);

  const openAgentAccessAddLinkDialog = React.useCallback(() => {
    setAgentAccessEditingId(null);
    setAgentAccessFormUrl('');
    setAgentAccessFormLabel('');
    setAgentAccessError(null);
    setAgentAccessDuplicateNotice(false);
    setAgentAccessPage(1);
    setAgentAccessFormOpen(true);
    setAgentAccessDialogOpen(true);
  }, []);
  const [localPreviewOpen, setLocalPreviewOpen] = React.useState<boolean>(false);
  const [localPreviewFile, setLocalPreviewFile] = React.useState<{
    title: string;
    url: string | null;
    filename: string;
  } | null>(null);
  const [virtualTourPreview, setVirtualTourPreview] = React.useState<{
    title: string;
    url: string;
  } | null>(null);
  const [pdfNumPages, setPdfNumPages] = React.useState<number>(0);
  const [pdfPageNumber, setPdfPageNumber] = React.useState<number>(1);
  const [guesstimationDialogOpen, setGuesstimationDialogOpen] = React.useState<boolean>(false);
  const [guesstimationPriceInput, setGuesstimationPriceInput] = React.useState<string>('');
  const [guesstimationSelectedBedroom, setGuesstimationSelectedBedroom] = React.useState<number | null>(null);
  const [guesstimationSelectedSize, setGuesstimationSelectedSize] = React.useState<number | string>('');
  const [guesstimationDialogMode, setGuesstimationDialogMode] = React.useState<'guesstimation' | 'indicative'>('guesstimation');
  const [guesstimationIndicativePriceLabel, setGuesstimationIndicativePriceLabel] = React.useState<string>('');
  const [guesstimationNoUnitsFormOpen, setGuesstimationNoUnitsFormOpen] = React.useState<boolean>(false);
  const [guesstimationNoUnitsBedroom, setGuesstimationNoUnitsBedroom] = React.useState<number>(2);
  const [guesstimationNoUnitsSizeFrom, setGuesstimationNoUnitsSizeFrom] = React.useState<string>('');
  const [guesstimationNoUnitsSizeTo, setGuesstimationNoUnitsSizeTo] = React.useState<string>('');
  const [guesstimationNoUnitsPrice, setGuesstimationNoUnitsPrice] = React.useState<string>('');

  /** Tab `projectDetails` can lag behind the selected site-plan project; map pin may exist only in bulk-loaded map. */
  const projectDetailsForLandLotMap = React.useMemo(() => {
    if (selectedTopViewingSitePlan) {
      if (projectDetails?.name === selectedTopViewingSitePlan) {
        return projectDetails;
      }
      const fromMap = projectDetailsMap.get(selectedTopViewingSitePlan);
      if (fromMap) {
        return fromMap;
      }
    }
    return projectDetails;
  }, [projectDetails, projectDetailsMap, selectedTopViewingSitePlan]);
  const [priceMatrixGridOpen, setPriceMatrixGridOpen] = React.useState<boolean>(false);
  const [priceMatrixGridQuery, setPriceMatrixGridQuery] = React.useState<string>('');
  const [priceMatrixGridPopoverAnchor, setPriceMatrixGridPopoverAnchor] = React.useState<{ element: HTMLElement; column: PriceMatrixGridColumnKey } | null>(null);
  const [priceMatrixGridSortConfig, setPriceMatrixGridSortConfig] = React.useState<Partial<Record<PriceMatrixGridColumnKey, SortDirection>>>({});
  const [priceMatrixGridColumnFilters, setPriceMatrixGridColumnFilters] = React.useState<{
    unitNumber: string;
    status: string;
    floorMin: string;
    floorMax: string;
    stackMin: string;
    stackMax: string;
    bedroomMin: string;
    bedroomMax: string;
    areaSqftMin: string;
    areaSqftMax: string;
    priceMin: string;
    priceMax: string;
    psfMin: string;
    psfMax: string;
  }>({
    unitNumber: '',
    status: '',
    floorMin: '',
    floorMax: '',
    stackMin: '',
    stackMax: '',
    bedroomMin: '',
    bedroomMax: '',
    areaSqftMin: '',
    areaSqftMax: '',
    priceMin: '',
    priceMax: '',
    psfMin: '',
    psfMax: '',
  });
  const [priceMatrixGridPage, setPriceMatrixGridPage] = React.useState<number>(0);
  const [priceMatrixGridRowsPerPage, setPriceMatrixGridRowsPerPage] = React.useState<number>(100);
  const [priceMatrixStatusDrafts, setPriceMatrixStatusDrafts] = React.useState<Record<string, string>>({});
  const [priceMatrixStatusSelectionAnchorIndex, setPriceMatrixStatusSelectionAnchorIndex] = React.useState<number | null>(null);
  const [priceMatrixStatusSelectionRange, setPriceMatrixStatusSelectionRange] = React.useState<{ start: number; end: number } | null>(null);
  const [priceMatrixCheckedUnitIds, setPriceMatrixCheckedUnitIds] = React.useState<Set<number>>(new Set());
  const selectedReferenceProjectName = projectDetails?.name || selectedProject || '';
  const openGuesstimationDialog = useCallback((bedroom: number) => {
    setGuesstimationDialogMode('guesstimation');
    setGuesstimationIndicativePriceLabel('');
    setGuesstimationPriceInput('');
    setGuesstimationSelectedBedroom(bedroom);
    setGuesstimationSelectedSize('');
    setGuesstimationDialogOpen(true);
  }, []);
  const openIndicativeSizeDialog = useCallback((params: { bedroom: number; price: number; priceLabel?: string }) => {
    let smallestSize: number | string = '';
    towerViewUnits.forEach((unit) => {
      const unitBedroom = normalizeBedroomValue(unit.bedroom);
      const size = Number(unit.areaSqft ?? 0);
      if (unitBedroom !== params.bedroom || !Number.isFinite(size) || size <= 0) return;
      if (typeof smallestSize !== 'number' || size < smallestSize) {
        smallestSize = size;
      }
    });
    setGuesstimationDialogMode('indicative');
    setGuesstimationIndicativePriceLabel(params.priceLabel ?? '');
    setGuesstimationPriceInput(String(params.price ?? ''));
    setGuesstimationSelectedBedroom(params.bedroom);
    setGuesstimationSelectedSize(smallestSize);
    setGuesstimationDialogOpen(true);
  }, [towerViewUnits, normalizeBedroomValue]);
  const closeGuesstimationDialog = useCallback(() => {
    setGuesstimationDialogOpen(false);
    setGuesstimationSelectedBedroom(null);
    setGuesstimationSelectedSize('');
    setGuesstimationDialogMode('guesstimation');
    setGuesstimationIndicativePriceLabel('');
  }, []);
  const confirmGuesstimationPrice = useCallback(() => {
    const parsedPrice = Number(guesstimationPriceInput);
    const parsedSize = Number(guesstimationSelectedSize);
    if (!selectedReferenceProjectName || !Number.isFinite(parsedPrice) || parsedPrice <= 0) return;
    if (!guesstimationSelectedBedroom || !Number.isFinite(parsedSize) || parsedSize <= 0) return;
    if (guesstimationDialogMode === 'indicative') {
      handleTopViewingIndicativeReference({
        projectName: selectedReferenceProjectName,
        bedroom: guesstimationSelectedBedroom,
        price: parsedPrice,
        sizeRange: { min: parsedSize, max: parsedSize },
        priceLabel: guesstimationIndicativePriceLabel,
      });
    } else {
      handleTopViewingGuesstimationReference({
        projectName: selectedReferenceProjectName,
        price: parsedPrice,
        bedroom: guesstimationSelectedBedroom,
        areaSqft: parsedSize,
      });
    }
    setGuesstimationDialogOpen(false);
    setGuesstimationPriceInput('');
    setGuesstimationSelectedBedroom(null);
    setGuesstimationSelectedSize('');
    setGuesstimationDialogMode('guesstimation');
    setGuesstimationIndicativePriceLabel('');
  }, [
    selectedReferenceProjectName,
    guesstimationPriceInput,
    guesstimationSelectedBedroom,
    guesstimationSelectedSize,
    guesstimationDialogMode,
    guesstimationIndicativePriceLabel,
    handleTopViewingGuesstimationReference,
    handleTopViewingIndicativeReference,
  ]);
  const openGuesstimationNoUnitsForm = useCallback(() => {
    setGuesstimationNoUnitsBedroom(2);
    setGuesstimationNoUnitsSizeFrom('');
    setGuesstimationNoUnitsSizeTo('');
    setGuesstimationNoUnitsPrice('');
    setGuesstimationNoUnitsFormOpen(true);
  }, []);
  const confirmGuesstimationNoUnitsPrice = useCallback(() => {
    const from = Number(String(guesstimationNoUnitsSizeFrom).replace(/,/g, ''));
    const to = Number(String(guesstimationNoUnitsSizeTo).replace(/,/g, ''));
    const parsedPrice = Number(String(guesstimationNoUnitsPrice).replace(/,/g, ''));
    if (!selectedReferenceProjectName || !Number.isFinite(parsedPrice) || parsedPrice <= 0) return;
    if (!guesstimationNoUnitsBedroom || guesstimationNoUnitsBedroom <= 0) return;
    if (!Number.isFinite(from) || from <= 0 || !Number.isFinite(to) || to <= 0) return;
    handleTopViewingGuesstimationReference({
      projectName: selectedReferenceProjectName,
      price: parsedPrice,
      bedroom: guesstimationNoUnitsBedroom,
      sizeRange: { min: from, max: to },
    });
    setGuesstimationNoUnitsFormOpen(false);
    setGuesstimationNoUnitsSizeFrom('');
    setGuesstimationNoUnitsSizeTo('');
    setGuesstimationNoUnitsPrice('');
  }, [
    selectedReferenceProjectName,
    guesstimationNoUnitsBedroom,
    guesstimationNoUnitsSizeFrom,
    guesstimationNoUnitsSizeTo,
    guesstimationNoUnitsPrice,
    handleTopViewingGuesstimationReference,
  ]);
  const topViewingBestPxProjectName = topViewingReferenceUnit?.projectName || selectedTopViewingSitePlan || sitePlanProjectName || '';
  const topViewingBestPxUnitsByKey = React.useMemo(() => {
    const map = new Map<string, TowerViewUnit>();
    towerViewUnits.forEach((unit) => {
      const projectKey = unit.projectName.toLowerCase();
      const unitKey = unit.unitNumber.toLowerCase();
      const blockKey = (unit.blockNumber || '').toLowerCase();

      if (blockKey) {
        map.set(`${projectKey}::${blockKey}::${unitKey}`, unit);
      }
      map.set(`${projectKey}::::${unitKey}`, unit);
    });
    return map;
  }, [towerViewUnits]);

  const handleTopViewingBestPxUnitClick = React.useCallback(
    (event: React.MouseEvent<HTMLElement>, unitNumber: string, blockNumber?: string | null) => {
      const projectKey = topViewingBestPxProjectName.toLowerCase();
      const unitKey = unitNumber.toLowerCase();
      const blockKey = (blockNumber || '').toLowerCase();
      const unit = (
        (blockKey ? topViewingBestPxUnitsByKey.get(`${projectKey}::${blockKey}::${unitKey}`) : null) ||
        topViewingBestPxUnitsByKey.get(`${projectKey}::::${unitKey}`)
      );
      if (!unit) return;
      setTopViewingUnitMenuAnchor({
        element: event.currentTarget,
        unit,
        mouseX: event.clientX,
        mouseY: event.clientY,
      });
    },
    [setTopViewingUnitMenuAnchor, topViewingBestPxProjectName, topViewingBestPxUnitsByKey]
  );

  const handleOpenFactsheet = React.useCallback(async () => {
    const projectId = currentSitePlan?.projectId;
    if (!projectId) return;
    logFactsheetViewed(
      currentSitePlan?.projectName || selectedTopViewingSitePlan || sitePlanProjectName || selectedProject || null,
      analyticsCea,
      projectId,
    );
    setFactsheetLoading(true);
    setFactsheetModalOpen(true);
    setPdfNumPages(0);
    setPdfPageNumber(1);
    try {
      const data = await api.getProjectFactsheet(projectId);
      setFactsheetData(data);
    } catch {
      setFactsheetData(null);
    } finally {
      setFactsheetLoading(false);
    }
  }, [
    analyticsCea,
    currentSitePlan?.projectId,
    currentSitePlan?.projectName,
    selectedProject,
    selectedTopViewingSitePlan,
    sitePlanProjectName,
  ]);

  const factsheetDownload = React.useMemo(() => {
    if (!factsheetData) return null;

    const mediaUrl = factsheetData.url?.trim() ?? '';
    const imageUrl = factsheetData.imageLarge || factsheetData.imageMedium || factsheetData.imageSmall || null;
    const type = (factsheetData.type ?? '').toLowerCase();

    const extensionFromUrl = (url: string): string | null => {
      try {
        const pathname = new URL(url, window.location.href).pathname.toLowerCase();
        const match = pathname.match(/\.([a-z0-9]+)$/);
        return match?.[1] ?? null;
      } catch {
        return null;
      }
    };

    const isPdf = mediaUrl.toLowerCase().endsWith('.pdf') || type === 'pdf';
    const downloadUrl = isPdf ? mediaUrl : (imageUrl || mediaUrl);
    if (!downloadUrl) return null;

    const extension = extensionFromUrl(downloadUrl);
    const safeExtension = extension && /^[a-z0-9]+$/.test(extension)
      ? extension
      : (isPdf ? 'pdf' : (type.includes('png') ? 'png' : 'jpg'));
    const rawName = currentSitePlan?.projectName || factsheetData.description || 'factsheet';
    const safeBaseName = rawName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'factsheet';

    return {
      url: downloadUrl,
      filename: `${safeBaseName}-factsheet.${safeExtension}`,
    };
  }, [currentSitePlan?.projectName, factsheetData]);

  const handleDownloadFactsheet = React.useCallback(() => {
    if (!factsheetDownload) return;

    const downloadUrl = api.buildFactsheetDownloadUrl(factsheetDownload.url, factsheetDownload.filename);
    window.location.assign(downloadUrl);
  }, [factsheetDownload]);

  const handleOpenBrochure = React.useCallback(async () => {
    const projectId = currentSitePlan?.projectId || projectDetails?.id;
    if (!projectId) return;
    setBrochureLoading(true);
    setBrochureModalOpen(true);
    setBrochurePdfNumPages(0);
    setBrochurePdfPageNumber(1);
    try {
      const data = await api.getProjectBrochure(projectId);
      setBrochureData(data);
    } catch {
      setBrochureData(null);
    } finally {
      setBrochureLoading(false);
    }
  }, [currentSitePlan?.projectId, projectDetails?.id]);

  // The header's looping gold animations repaint continuously, so they're stood
  // down while the toggle's reflow lands.
  const [projectSectionSettling, setProjectSectionSettling] = React.useState(false);
  const projectSectionSettleTimer = React.useRef<number | null>(null);
  const headerLoopPlayState = projectSectionSettling ? 'paused' : 'running';

  const handleProjectSectionToggle = React.useCallback((expanded: boolean) => {
    setProjectSectionExpanded(expanded);
    setProjectSectionSettling(true);
    if (projectSectionSettleTimer.current) {
      window.clearTimeout(projectSectionSettleTimer.current);
    }
    projectSectionSettleTimer.current = window.setTimeout(
      () => setProjectSectionSettling(false),
      PROJECT_SECTION_SETTLE_MS,
    );
  }, [setProjectSectionExpanded]);

  React.useEffect(() => () => {
    if (projectSectionSettleTimer.current) {
      window.clearTimeout(projectSectionSettleTimer.current);
    }
  }, []);

  const handleTabShortcutClick = React.useCallback(async (e: React.SyntheticEvent, tabValue: ProjectDetailsTab) => {
    e.stopPropagation();
    setProjectDetailsTab(tabValue);
    
    const projectId = projectDetails?.id;
    if (tabValue === 'amenities' && projectDetails?.name && projectDetails?.mapLatitude && projectDetails?.mapLongitude) {
      fetchAmenities(projectDetails.name, amenitiesRadius);
    }
    if (tabValue === 'members' && projectId && canViewProjectMembers) {
      fetchProjectMembers(projectId);
    }
    if (tabValue === 'files' && projectId) {
      setFilesLoading(true);
      setFilesFactsheetData(null);
      setFilesBrochureData(null);
      setFilesEdms([]);
      setFilesEbooks([]);
      Promise.all([
        api.getProjectFactsheet(projectId).then((d) => d).catch(() => null),
        api.getProjectBrochure(projectId).then((d) => d).catch(() => null),
        api.getProjectEdms(projectId).then((d) => d.items).catch(() => [] as ProjectEdmFile[]),
        api.getProjectEbooks(projectId).then((d) => d.items).catch(() => [] as ProjectEbookFile[]),
      ]).then(([factsheet, brochure, edms, ebooks]) => {
        setFilesFactsheetData(factsheet);
        setFilesBrochureData(brochure);
        setFilesEdms(edms);
        setFilesEbooks(ebooks);
        setFilesLoading(false);
      }).catch(() => setFilesLoading(false));

      // Grouped Files (per-category) sub-tab data
      setFilesGroupedLoading(true);
      setFilesGrouped([]);
      api.getProjectFilesGrouped(projectId)
        .then((res) => setFilesGrouped(filterPdfFileGroups(res.groups)))
        .catch(() => setFilesGrouped([]))
        .finally(() => setFilesGroupedLoading(false));
    }
    if (tabValue === 'media' && projectId) {
      setProjectMediaLoading(true);
      setProjectMediaError(null);
      setProjectMediaItems([]);
      api.getProjectMedia(projectId)
        .then((res) => setProjectMediaItems(res.items || []))
        .catch((err) => setProjectMediaError(err?.message || 'Failed to load media'))
        .finally(() => setProjectMediaLoading(false));
    }
    
    handleProjectSectionToggle(true);
  }, [
    projectDetails,
    amenitiesRadius,
    canViewProjectMembers,
    fetchAmenities,
    fetchProjectMembers,
    setProjectDetailsTab,
    handleProjectSectionToggle,
  ]);

  const brochureDownload = React.useMemo(() => {
    if (!brochureData) return null;

    const mediaUrl = brochureData.url?.trim() ?? '';
    const imageUrl = brochureData.imageLarge || brochureData.imageMedium || brochureData.imageSmall || null;
    const type = (brochureData.type ?? '').toLowerCase();

    const extensionFromUrl = (url: string): string | null => {
      try {
        const pathname = new URL(url, window.location.href).pathname.toLowerCase();
        const match = pathname.match(/\.([a-z0-9]+)$/);
        return match?.[1] ?? null;
      } catch {
        return null;
      }
    };

    const isPdf = mediaUrl.toLowerCase().endsWith('.pdf') || type === 'pdf';
    const downloadUrl = isPdf ? mediaUrl : (imageUrl || mediaUrl);
    if (!downloadUrl) return null;

    const extension = extensionFromUrl(downloadUrl);
    const safeExtension = extension && /^[a-z0-9]+$/.test(extension)
      ? extension
      : (isPdf ? 'pdf' : (type.includes('png') ? 'png' : 'jpg'));
    const rawName = currentSitePlan?.projectName || projectDetails?.name || brochureData.description || 'brochure';
    const safeBaseName = rawName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'brochure';

    return {
      url: downloadUrl,
      filename: `${safeBaseName}-brochure.${safeExtension}`,
    };
  }, [currentSitePlan?.projectName, projectDetails?.name, brochureData]);

  const handleDownloadBrochure = React.useCallback(() => {
    if (!brochureDownload) return;

    const downloadUrl = api.buildBrochureDownloadUrl(brochureDownload.url, brochureDownload.filename);
    window.location.assign(downloadUrl);
  }, [brochureDownload]);

  React.useEffect(() => {
    let cancelled = false;
    if (!currentProjectId) {
      setHasProjectTrainingEvents(false);
      return;
    }

    api.getRegistrationEvents(undefined, currentProjectId)
      .then((events) => {
        if (!cancelled) setHasProjectTrainingEvents(events.length > 0);
      })
      .catch(() => {
        if (!cancelled) setHasProjectTrainingEvents(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentProjectId]);

  // Automatically fetch Factsheet, Brochure, EDMs and eBooks whenever the active project changes.
  // This ensures the local state is populated immediately, allowing the action buttons
  // to dynamically compute their visibility even if the user hasn't switched to the 'Files' tab yet.
  React.useEffect(() => {
    let cancelled = false;
    const projectId = projectDetails?.id;
    if (!projectId) {
      setFilesFactsheetData(null);
      setFilesBrochureData(null);
      setFilesEdms([]);
      setFilesEbooks([]);
      return;
    }

    Promise.all([
      api.getProjectFactsheet(projectId).then((d) => d).catch(() => null),
      api.getProjectBrochure(projectId).then((d) => d).catch(() => null),
      api.getProjectEdms(projectId).then((d) => d.items).catch(() => [] as ProjectEdmFile[]),
      api.getProjectEbooks(projectId).then((d) => d.items).catch(() => [] as ProjectEbookFile[]),
    ]).then(([factsheet, brochure, edms, ebooks]) => {
      if (!cancelled) {
        setFilesFactsheetData(factsheet);
        setFilesBrochureData(brochure);
        setFilesEdms(edms);
        setFilesEbooks(ebooks);
      }
    }).catch(() => {
      if (!cancelled) {
        setFilesFactsheetData(null);
        setFilesBrochureData(null);
        setFilesEdms([]);
        setFilesEbooks([]);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [projectDetails?.id]);

  // Fetch the Agent Access Folder links + the current user's manage
  // permission whenever the active project changes. Best-effort — a
  // failed load leaves both the count and the CTA in their empty state.
  const reloadProjectResourceLinks = React.useCallback(async () => {
    const projectId = projectDetails?.id || currentSitePlan?.projectId;
    if (!projectId) {
      setProjectResourceLinks([]);
      return;
    }
    try {
      const { items } = await api.getProjectResourceLinks(projectId);
      setProjectResourceLinks(items || []);
    } catch {
      setProjectResourceLinks([]);
    }
  }, [projectDetails?.id, currentSitePlan?.projectId]);

  React.useEffect(() => {
    const projectId = projectDetails?.id || currentSitePlan?.projectId;
    if (!projectId) {
      setProjectResourceLinks([]);
      setCanManageResourceLinks(false);
      return;
    }
    let cancelled = false;
    setProjectResourceLinksLoading(true);
    Promise.all([
      api.getProjectResourceLinks(projectId).catch(() => ({ items: [] as ProjectResourceLink[] })),
      api.getProjectResourceLinkPermissions(projectId).catch(() => ({ canManage: false })),
    ]).then(([listRes, permRes]) => {
      if (cancelled) return;
      setProjectResourceLinks(listRes.items || []);
      setCanManageResourceLinks(Boolean(permRes.canManage));
    }).finally(() => {
      if (!cancelled) setProjectResourceLinksLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [projectDetails?.id, currentSitePlan?.projectId]);

  // Preload the verified podcast videos tagged to the active project so
  // the "Podcast Videos" button can surface a count (mirroring EDMs /
  // eBooks). Admin-only surface — skip the fetch for everyone else.
  React.useEffect(() => {
    let cancelled = false;
    const projectId = currentSitePlan?.projectId || projectDetails?.id;
    if (!isAdminUser || !projectId) {
      setProjectPodcastVideos([]);
      return;
    }
    api
      .listPodcastVideos()
      .then((rows) => {
        if (cancelled) return;
        setProjectPodcastVideos(
          rows.filter((r) => (r.projects ?? []).some((p) => p.id === projectId)),
        );
      })
      .catch(() => {
        if (!cancelled) setProjectPodcastVideos([]);
      });
    return () => {
      cancelled = true;
    };
  }, [currentSitePlan?.projectId, projectDetails?.id, isAdminUser]);

  // Preload verified training videos tagged to the active project so
  // the "Training Videos" button can surface a count alongside the
  // Podcast Videos / EDMs / eBooks buttons. Admin-only surface — skip
  // the fetch for everyone else.
  const loadProjectTrainingVideos = React.useCallback(() => {
    const projectId = currentSitePlan?.projectId || projectDetails?.id;
    if (!isAdminUser || !projectId) {
      setProjectTrainingVideos([]);
      return;
    }
    api
      .listPodcastVideos({ videoType: 'training' })
      .then((rows) => {
        setProjectTrainingVideos(
          rows.filter((r) => (r.projects ?? []).some((p) => p.id === projectId)),
        );
      })
      .catch(() => {
        setProjectTrainingVideos([]);
      });
  }, [currentSitePlan?.projectId, projectDetails?.id, isAdminUser]);
  React.useEffect(() => {
    loadProjectTrainingVideos();
  }, [loadProjectTrainingVideos]);

  const TOP_VIEWING_TOWER_DISPLAY_OPTIONS: Array<'bedroom' | 'price' | 'psf' | 'size'> = ['bedroom', 'price', 'psf', 'size'];
  const [budgetProjectPopoverAnchor, setBudgetProjectPopoverAnchor] = React.useState<{ element: HTMLElement; column: string } | null>(null);
  const [budgetProjectSortConfig, setBudgetProjectSortConfig] = React.useState<{ column: string | null; direction: SortDirection }>({
    column: null,
    direction: 'default',
  });
  const [budgetProjectTextFilters, setBudgetProjectTextFilters] = React.useState<Record<string, string>>({});
  const [budgetProjectRangeFilters, setBudgetProjectRangeFilters] = React.useState<Record<string, { min: string; max: string }>>({});
  const [availableUnitColumnsAnchor, setAvailableUnitColumnsAnchor] = React.useState<HTMLElement | null>(null);
  const [comparableUnitColumnsAnchor, setComparableUnitColumnsAnchor] = React.useState<HTMLElement | null>(null);
  const [budgetUnitColumnsAnchor, setBudgetUnitColumnsAnchor] = React.useState<HTMLElement | null>(null);
  const [availableUnitColumnVisibility, setAvailableUnitColumnVisibility] = React.useState<ColumnVisibilityState>(() => (
    createInitialColumnVisibility(TOP_VIEWING_AVAILABLE_UNIT_COLUMN_KEYS, { showPsfColumnByDefault, hiddenColumns: ['distance'] })
  ));
  const [comparableUnitColumnVisibility, setComparableUnitColumnVisibility] = React.useState<ColumnVisibilityState>(() => (
    createInitialColumnVisibility(TOP_VIEWING_COMPARABLE_UNIT_COLUMN_KEYS, { showPsfColumnByDefault, hideProjectNameByDefault: false })
  ));
  const [budgetUnitColumnVisibility, setBudgetUnitColumnVisibility] = React.useState<ColumnVisibilityState>(() => (
    createInitialColumnVisibility(TOP_VIEWING_BUDGET_UNIT_COLUMN_KEYS, { hideProjectNameByDefault: false })
  ));
  React.useEffect(() => {
    setAvailableUnitColumnVisibility((prev) => ({
      ...prev,
      psf: showPsfColumnByDefault,
    }));
    setComparableUnitColumnVisibility((prev) => ({
      ...prev,
      psf: showPsfColumnByDefault,
    }));
  }, [showPsfColumnByDefault]);
  const [topViewingAvailableUnitsPage, setTopViewingAvailableUnitsPage] = React.useState<number>(0);
  const [topViewingComparableUnitsPage, setTopViewingComparableUnitsPage] = React.useState<number>(0);
  const [topViewingComparableProjectsPage, setTopViewingComparableProjectsPage] = React.useState<number>(0);
  const [topViewingBudgetUnitsPage, setTopViewingBudgetUnitsPage] = React.useState<number>(0);
  const [topViewingBudgetProjectsPage, setTopViewingBudgetProjectsPage] = React.useState<number>(0);
  const [topViewingBudgetSuperMatch, setTopViewingBudgetSuperMatch] = React.useState<{
    bedroom: boolean;
    size: boolean;
    floor: boolean;
  }>({
    bedroom: false,
    size: false,
    floor: false,
  });
  const prevTopViewingReferenceUnitKeyRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const key = topViewingReferenceUnit
      ? `${topViewingReferenceUnit.id ?? ''}|${topViewingReferenceUnit.projectName ?? ''}|${topViewingReferenceUnit.unitNumber ?? ''}`
      : null;
    if (prevTopViewingReferenceUnitKeyRef.current === key) return;
    const isFirst = prevTopViewingReferenceUnitKeyRef.current === null;
    prevTopViewingReferenceUnitKeyRef.current = key;
    if (isFirst) return;
    setTopViewingBudgetSuperMatch({ bedroom: false, size: false, floor: false });
    setTopViewingBudgetShowReferenceProjectUnits(false);
    handleTopViewingBudgetRangeFilterChange('bedroom', 'min', '');
    handleTopViewingBudgetRangeFilterChange('areaSqft', 'min', '');
    handleTopViewingBudgetRangeFilterChange('floor', 'min', '');
  }, [topViewingReferenceUnit, handleTopViewingBudgetRangeFilterChange, setTopViewingBudgetShowReferenceProjectUnits]);
  const [budgetAutoFindPending, setBudgetAutoFindPending] = React.useState(false);
  const [budgetProjectMatchedBrExpand, setBudgetProjectMatchedBrExpand] = React.useState<{
    projectName: string;
    bucket: BudgetProjectMatchedBrBucket;
  } | null>(null);
  const [budgetReferenceProjectRowsBlinking, setBudgetReferenceProjectRowsBlinking] = React.useState(false);
  React.useEffect(() => {
    if (!topViewingBudgetShowReferenceProjectUnits) {
      setBudgetReferenceProjectRowsBlinking(false);
      return;
    }
    setBudgetReferenceProjectRowsBlinking(true);
    const timerId = window.setTimeout(() => {
      setBudgetReferenceProjectRowsBlinking(false);
    }, 2000);
    return () => window.clearTimeout(timerId);
  }, [topViewingBudgetShowReferenceProjectUnits]);
  const [projectViewCategoryFilters, setProjectViewCategoryFilters] = React.useState<{
    projectName: string[];
    projectCategory: string[];
    district: string[];
    region: string[];
    nearestMrt: string[];
    tenure: string[];
  }>({
    projectName: [],
    projectCategory: [],
    district: projectViewDistrictFilter,
    region: [],
    nearestMrt: [],
    tenure: [],
  });
  const [projectViewSortConfig, setProjectViewSortConfig] = React.useState<{ column: string | null; direction: SortDirection }>({
    column: null,
    direction: 'default',
  });
  const PENTHOUSE_FILTER_VALUE = -1;
  const ANY_FILTER_VALUE = 'any';
  const STACK_FILTER_ODD_VALUE = 'ODD';
  const STACK_FILTER_EVEN_VALUE = 'EVEN';
  const getBedroomColor = React.useCallback((bedroom: number | null): string => {
    const palette = isLightTheme ? BEDROOM_COLORS_LIGHT : BEDROOM_COLORS_DARK;
    return bedroom != null && bedroom in palette ? palette[bedroom] : (isLightTheme ? '#cfd8dc' : '#546e7a');
  }, [isLightTheme]);
  const formatTowerFloorLabel = React.useCallback((floor: number): string => (
    floor < 0 ? `B${Math.abs(floor)}` : String(floor).padStart(2, '0')
  ), []);
  const formatTowerUnitDisplay = React.useCallback((unit: TowerViewUnit | ComparableUnit | undefined, floor: number, stack: number): string => {
    if (unit?.unitNumber) {
      return unit.unitNumber.startsWith('#') ? unit.unitNumber : `#${unit.unitNumber}`;
    }
    return `#${formatTowerFloorLabel(floor)}-${String(stack).padStart(2, '0')}`;
  }, [formatTowerFloorLabel]);
  const isPenthouseText = React.useCallback((value: unknown): boolean => {
    const raw = String(value ?? '').trim().toLowerCase();
    if (!raw) return false;
    return raw.includes('penthouse') || raw.includes('penthhouse') || /\bph\b/.test(raw) || raw.endsWith('ph');
  }, []);
  const isPenthouseUnit = React.useCallback((unit: TowerViewUnit | ComparableUnit | undefined): boolean => {
    if (!unit) return false;
    const towerUnit = unit as TowerViewUnit;
    return isPenthouseText(towerUnit.unitTypeDescription) || isPenthouseText(towerUnit.unitType);
  }, [isPenthouseText]);
  const getStackFilterLabel = React.useCallback((value: string): string => {
    if (value === STACK_FILTER_ODD_VALUE) return 'Odd';
    if (value === STACK_FILTER_EVEN_VALUE) return 'Even';
    return value;
  }, []);
  const matchesSelectedStackFilter = React.useCallback((unit: TowerViewUnit | ComparableUnit | undefined): boolean => {
    if (!unit || sitePlanStackFilter.length === 0) return true;
    const parsed = parseUnitNumber(unit.unitNumber, (unit as TowerViewUnit).floorNumber);
    if (parsed.stack == null) return false;
    const normStack = normalizeStackKey(parsed.stack);
    if (!normStack) return false;
    const stackNum = Number(normStack);
    const blockNum = (unit.blockNumber || '').replace(/\D/g, '');
    const compositeKey = blockNum ? `${blockNum}-${normStack}` : null;

    return sitePlanStackFilter.some((selected) => {
      const normalizedSelected = String(selected).trim().toUpperCase();
      if (normalizedSelected === STACK_FILTER_ODD_VALUE) {
        return Number.isFinite(stackNum) && stackNum % 2 === 1;
      }
      if (normalizedSelected === STACK_FILTER_EVEN_VALUE) {
        return Number.isFinite(stackNum) && stackNum % 2 === 0;
      }
      const normalizedStackSelection = normalizeStackKey(selected);
      return normalizedStackSelection === normStack || (compositeKey != null && normalizedStackSelection === compositeKey);
    });
  }, [normalizeStackKey, parseUnitNumber, sitePlanStackFilter]);

  /**
   * Does a unit survive the site-plan bedroom / unit-type / stack filters?
   * Shared by the tower grid and the 3D storey cells so both dim the same rows.
   */
  const sitePlanUnitMatchesFilters = React.useCallback((unit: TowerViewUnit | ComparableUnit | undefined): boolean => {
    if (!unit) return false;
    const hasBedroomFilter = sitePlanBedroomFilter.length > 0;
    const hasUnitTypeFilter = sitePlanUnitTypeFilter.length > 0;
    const hasStackFilter = sitePlanStackFilter.length > 0;
    if (!hasBedroomFilter && !hasUnitTypeFilter && !hasStackFilter) return true;

    const bedroom = 'bedroom' in unit ? normalizeBedroomValue(unit.bedroom) : null;
    const unitIsPenthouse = isPenthouseUnit(unit);
    const projectUnitTypeId = 'projectUnitTypeId' in unit ? (unit as TowerViewUnit).projectUnitTypeId : null;
    const unitType = 'unitType' in unit ? (unit as TowerViewUnit).unitType : null;

    if (hasBedroomFilter) {
      const matchesBedroom = sitePlanBedroomFilter.some((value) => {
        if (value === PENTHOUSE_FILTER_VALUE) return unitIsPenthouse;
        return !unitIsPenthouse && bedroom != null && bedroom === value;
      });
      if (!matchesBedroom) return false;
    }

    // Unit-type match with a fallback: `sitePlanUnitTypes` has one row per
    // description, so an id that only exists on units (e.g. 11061) has no row —
    // the expanded code set (C5, C6) derived from units covers it.
    if (hasUnitTypeFilter) {
      let matchesUnitType = false;
      if (projectUnitTypeId != null && sitePlanUnitTypeFilterForMatching.includes(projectUnitTypeId)) {
        matchesUnitType = true;
      } else if (unitType) {
        const utMatch = sitePlanUnitTypeFilterForMatching
          .map((id) => sitePlanUnitTypes.find((ut) => ut.id === id))
          .filter(Boolean)
          .some((ut) => {
            if (!ut) return false;
            if (ut.unitType === unitType || ut.description === unitType) return true;
            const unitTypes = (ut as unknown as { unitTypes?: string[] }).unitTypes;
            return Array.isArray(unitTypes) && unitTypes.includes(unitType);
          });
        const expandedMatch = sitePlanUnitTypeFilterExpandedUnitTypes.has(unitType.trim());
        matchesUnitType = utMatch || expandedMatch;
      }
      if (!matchesUnitType) return false;
    }

    if (hasStackFilter && !matchesSelectedStackFilter(unit)) return false;

    return true;
  }, [
    PENTHOUSE_FILTER_VALUE,
    isPenthouseUnit,
    matchesSelectedStackFilter,
    normalizeBedroomValue,
    sitePlanBedroomFilter,
    sitePlanStackFilter,
    sitePlanUnitTypeFilter,
    sitePlanUnitTypeFilterExpandedUnitTypes,
    sitePlanUnitTypeFilterForMatching,
    sitePlanUnitTypes,
  ]);
  const sitePlanBedroomFilterOptions = React.useMemo<number[]>(() => {
    const bedroomSet = new Set<number>();
    let hasPenthouse = false;
    const hasUnitTypeFilter = sitePlanUnitTypeFilterForMatching.length > 0;
    towerViewUnits.forEach((unit) => {
      if (!matchesSelectedStackFilter(unit)) return;
      if (hasUnitTypeFilter) {
        let matchesUnitType = false;
        const projectUnitTypeId = unit.projectUnitTypeId != null ? Number(unit.projectUnitTypeId) : null;
        if (projectUnitTypeId != null && sitePlanUnitTypeFilterForMatching.includes(projectUnitTypeId)) {
          matchesUnitType = true;
        } else if (unit.unitType) {
          const unitType = unit.unitType.trim();
          const utMatch = sitePlanUnitTypeFilterForMatching
            .map((id) => sitePlanUnitTypes.find((ut) => ut.id === id))
            .filter((ut): ut is NonNullable<typeof ut> => ut != null)
            .some((ut) => ut.unitType === unit.unitType || ut.description === unit.unitType);
          const expandedMatch = sitePlanUnitTypeFilterExpandedUnitTypes.has(unitType);
          matchesUnitType = utMatch || expandedMatch;
        }
        if (!matchesUnitType) return;
      }
      const unitIsPenthouse = isPenthouseUnit(unit);
      if (unitIsPenthouse) {
        hasPenthouse = true;
        return;
      }
      const bedroom = normalizeBedroomValue(unit.bedroom);
      if (bedroom != null && bedroom > 0) bedroomSet.add(bedroom);
    });
    const options = Array.from(bedroomSet).sort((a, b) => a - b);
    if (hasPenthouse) options.push(PENTHOUSE_FILTER_VALUE);
    return options;
  }, [
    towerViewUnits,
    matchesSelectedStackFilter,
    sitePlanUnitTypeFilterForMatching,
    sitePlanUnitTypeFilterExpandedUnitTypes,
    sitePlanUnitTypes,
    normalizeBedroomValue,
    isPenthouseUnit,
    PENTHOUSE_FILTER_VALUE,
  ]);

  // Prune bedroom selections that are no longer reachable under the active
  // stack / unit-type filters (parity with SuperPickerApp bedroom-options prune).
  React.useEffect(() => {
    if (sitePlanBedroomFilter.length === 0) return;
    const allowed = new Set(sitePlanBedroomFilterOptions);
    const next = sitePlanBedroomFilter.filter((value) => allowed.has(value));
    if (next.length !== sitePlanBedroomFilter.length) {
      setSitePlanBedroomFilter(next);
    }
  }, [sitePlanBedroomFilterOptions, sitePlanBedroomFilter, setSitePlanBedroomFilter]);

  const indicativePriceRows = React.useMemo(() => {
    if (priceMatrixSimulationSession) {
      const priceByBedroom = new Map<number, number[]>();
      towerViewUnits.forEach((unit) => {
        const bedroom = normalizeBedroomValue(unit.bedroom);
        const price = Number(unit.price);
        const status = (unit.unitStatus || '').toString().toLowerCase();
        if (bedroom == null || bedroom <= 0 || !Number.isFinite(price) || price <= 0 || status === 'sold' || isUnitStatusNotReleased(status)) {
          return;
        }
        const list = priceByBedroom.get(bedroom) ?? [];
        list.push(price);
        priceByBedroom.set(bedroom, list);
      });
      return [1, 2, 3, 4, 5]
        .map((bedroom) => {
          const prices = (priceByBedroom.get(bedroom) ?? []).sort((a, b) => a - b);
          return {
            key: `matrix-br${bedroom}`,
            label: `${bedroom}BR`,
            bedroom,
            range: prices.length > 0
              ? { low: prices[0], high: prices[prices.length - 1] }
              : null,
          };
        })
        .filter((row) => row.range != null);
    }
    const indicative = projectDetails?.indicativePrices;
    if (!indicative) return [];
    const rows = [
      { key: 'br1', label: '1BR', bedroom: 1, range: indicative.br1 ?? null },
      { key: 'br2', label: '2BR', bedroom: 2, range: indicative.br2 ?? null },
      { key: 'br3', label: '3BR', bedroom: 3, range: indicative.br3 ?? null },
      { key: 'br4', label: '4BR', bedroom: 4, range: indicative.br4 ?? null },
      { key: 'br5', label: '5BR', bedroom: 5, range: indicative.br5 ?? null },
    ];
    return rows.filter((row) => {
      const low = row.range?.low ?? null;
      const high = row.range?.high ?? null;
      return (low != null && low > 0) || (high != null && high > 0);
    });
  }, [normalizeBedroomValue, priceMatrixSimulationSession, projectDetails?.indicativePrices, towerViewUnits]);

  const indicativeSizeRanges = React.useMemo(() => {
    const ranges = new Map<number, { min: number; max: number }>();
    const sizesByBedroom = new Map<number, number[]>();
    towerViewUnits.forEach((unit) => {
      const bedroom = normalizeBedroomValue(unit.bedroom);
      const size = unit.areaSqft ?? 0;
      if (bedroom == null || bedroom <= 0 || !Number.isFinite(size) || size <= 0) return;
      const list = sizesByBedroom.get(bedroom) ?? [];
      list.push(size);
      sizesByBedroom.set(bedroom, list);
    });
    sizesByBedroom.forEach((sizes, bedroom) => {
      const sorted = sizes.filter((size) => Number.isFinite(size)).sort((a, b) => a - b);
      if (sorted.length === 0) return;
      ranges.set(bedroom, { min: sorted[0], max: sorted[sorted.length - 1] });
    });
    return ranges;
  }, [towerViewUnits, normalizeBedroomValue]);

  const guesstimationBedroomOptions = React.useMemo(() => {
    const sizesByBedroom = new Map<number, Set<number>>();
    towerViewUnits.forEach((unit) => {
      const bedroom = normalizeBedroomValue(unit.bedroom);
      const size = unit.areaSqft ?? 0;
      if (bedroom == null || bedroom <= 0 || !Number.isFinite(size) || size <= 0) return;
      const set = sizesByBedroom.get(bedroom) ?? new Set<number>();
      set.add(size);
      sizesByBedroom.set(bedroom, set);
    });
    const options: Array<{ bedroom: number; label: string; sizes: number[] }> = [];
    sizesByBedroom.forEach((sizeSet, bedroom) => {
      const sizes = Array.from(sizeSet).sort((a, b) => a - b);
      options.push({ bedroom, label: `${bedroom} BR`, sizes });
    });
    return options.sort((a, b) => a.bedroom - b.bedroom);
  }, [towerViewUnits, normalizeBedroomValue]);

  const hasUnitPrices = React.useMemo(
    () => towerViewUnits.some((unit) => Number.isFinite(unit.price) && unit.price > 0),
    [towerViewUnits]
  );
  const getPriceMatrixGridSortDirection = React.useCallback(
    (column: PriceMatrixGridColumnKey): SortDirection => priceMatrixGridSortConfig[column] ?? 'default',
    [priceMatrixGridSortConfig]
  );
  const handlePriceMatrixGridSortChange = React.useCallback((column: PriceMatrixGridColumnKey, direction: SortDirection) => {
    setPriceMatrixGridSortConfig((prev) => {
      if (direction === 'default') {
        const next = { ...prev };
        delete next[column];
        return next;
      }
      return { [column]: direction };
    });
    setPriceMatrixGridPage(0);
  }, []);
  const handlePriceMatrixGridOpenPopover = React.useCallback((event: React.MouseEvent<HTMLElement>, column: PriceMatrixGridColumnKey) => {
    setPriceMatrixGridPopoverAnchor({ element: event.currentTarget, column });
  }, []);
  const handlePriceMatrixGridClosePopover = React.useCallback(() => {
    setPriceMatrixGridPopoverAnchor(null);
  }, []);
  const hasPriceMatrixGridActiveFilter = React.useCallback((column: PriceMatrixGridColumnKey): boolean => {
    switch (column) {
      case 'unitNumber':
        return priceMatrixGridColumnFilters.unitNumber.trim() !== '';
      case 'status':
        return priceMatrixGridColumnFilters.status.trim() !== '';
      case 'floor':
        return priceMatrixGridColumnFilters.floorMin.trim() !== '' || priceMatrixGridColumnFilters.floorMax.trim() !== '';
      case 'stack':
        return priceMatrixGridColumnFilters.stackMin.trim() !== '' || priceMatrixGridColumnFilters.stackMax.trim() !== '';
      case 'bedroom':
        return priceMatrixGridColumnFilters.bedroomMin.trim() !== '' || priceMatrixGridColumnFilters.bedroomMax.trim() !== '';
      case 'areaSqft':
        return priceMatrixGridColumnFilters.areaSqftMin.trim() !== '' || priceMatrixGridColumnFilters.areaSqftMax.trim() !== '';
      case 'price':
        return priceMatrixGridColumnFilters.priceMin.trim() !== '' || priceMatrixGridColumnFilters.priceMax.trim() !== '';
      case 'psf':
        return priceMatrixGridColumnFilters.psfMin.trim() !== '' || priceMatrixGridColumnFilters.psfMax.trim() !== '';
      default:
        return false;
    }
  }, [priceMatrixGridColumnFilters]);
  const setPriceMatrixGridTextFilter = React.useCallback((column: 'unitNumber' | 'status', value: string) => {
    setPriceMatrixGridColumnFilters((prev) => ({
      ...prev,
      [column]: column === 'status' ? value.toUpperCase() : value,
    }));
    setPriceMatrixGridPage(0);
  }, []);
  const setPriceMatrixGridRangeFilter = React.useCallback((column: 'floor' | 'stack' | 'bedroom' | 'areaSqft' | 'price' | 'psf', bound: 'min' | 'max', value: string) => {
    const sanitizedValue = (column === 'price' || column === 'psf')
      ? value.replace(/,/g, '').replace(/[^\d.]/g, '')
      : value.replace(/[^\d.-]/g, '');
    const key = `${column}${bound === 'min' ? 'Min' : 'Max'}` as keyof typeof priceMatrixGridColumnFilters;
    setPriceMatrixGridColumnFilters((prev) => ({
      ...prev,
      [key]: sanitizedValue,
    }));
    setPriceMatrixGridPage(0);
  }, []);
  const priceMatrixGridRows = React.useMemo(() => {
    if (!priceMatrixSimulationSession) {
      return [];
    }
    const matrixUnitIds = new Set(priceMatrixSimulationSession.units.map((unit) => unit.unitId));
    return towerViewUnits
      .filter((unit) => matrixUnitIds.has(unit.id))
      .map((unit) => {
        const parsed = parseUnitNumber(unit.unitNumber, unit.floorNumber);
        return {
          id: unit.id,
          unitNumber: unit.unitNumber,
          floor: parsed.floor,
          stack: parsed.stack,
          bedroom: normalizeBedroomValue(unit.bedroom),
          areaSqft: unit.areaSqft ?? null,
          price: unit.price ?? null,
          psf: unit.psf ?? null,
          status: (unit.unitStatus ?? '').toString().trim().toUpperCase() || '',
        };
      })
      .sort((a, b) => {
        const floorDiff = (b.floor ?? -1) - (a.floor ?? -1);
        if (floorDiff !== 0) return floorDiff;
        const aStack = a.stack == null ? '' : String(a.stack).padStart(3, '0');
        const bStack = b.stack == null ? '' : String(b.stack).padStart(3, '0');
        return aStack.localeCompare(bStack);
      });
  }, [normalizeBedroomValue, parseUnitNumber, priceMatrixSimulationSession, towerViewUnits]);
  const filteredPriceMatrixGridRows = React.useMemo(() => {
    const query = priceMatrixGridQuery.trim().toLowerCase();
    const filteredRows = priceMatrixGridRows.filter((row) => {
      const searchText = [
        row.unitNumber,
        row.floor != null ? String(row.floor) : '',
        row.stack != null ? String(row.stack) : '',
        row.bedroom != null ? `${row.bedroom}br` : '',
        row.status,
      ].join(' ').toLowerCase();
      if (query && !searchText.includes(query)) {
        return false;
      }
      if (priceMatrixGridColumnFilters.unitNumber.trim() && !row.unitNumber.toLowerCase().includes(priceMatrixGridColumnFilters.unitNumber.trim().toLowerCase())) {
        return false;
      }
      if (priceMatrixGridColumnFilters.status.trim() && !row.status.toLowerCase().includes(priceMatrixGridColumnFilters.status.trim().toLowerCase())) {
        return false;
      }
      const rangeFilters: Array<{ value: number | null; min: string; max: string }> = [
        { value: row.floor, min: priceMatrixGridColumnFilters.floorMin, max: priceMatrixGridColumnFilters.floorMax },
        { value: row.stack, min: priceMatrixGridColumnFilters.stackMin, max: priceMatrixGridColumnFilters.stackMax },
        { value: row.bedroom, min: priceMatrixGridColumnFilters.bedroomMin, max: priceMatrixGridColumnFilters.bedroomMax },
        { value: row.areaSqft, min: priceMatrixGridColumnFilters.areaSqftMin, max: priceMatrixGridColumnFilters.areaSqftMax },
        { value: row.price, min: priceMatrixGridColumnFilters.priceMin, max: priceMatrixGridColumnFilters.priceMax },
        { value: row.psf, min: priceMatrixGridColumnFilters.psfMin, max: priceMatrixGridColumnFilters.psfMax },
      ];
      return rangeFilters.every(({ value, min, max }) => {
        const minNumber = min.trim() === '' ? null : Number(min);
        const maxNumber = max.trim() === '' ? null : Number(max);
        if ((minNumber != null || maxNumber != null) && (value == null || !Number.isFinite(value))) {
          return false;
        }
        if (minNumber != null && Number.isFinite(minNumber) && value != null && value < minNumber) {
          return false;
        }
        if (maxNumber != null && Number.isFinite(maxNumber) && value != null && value > maxNumber) {
          return false;
        }
        return true;
      });
    });
    const sortColumn = (Object.entries(priceMatrixGridSortConfig).find(([, direction]) => direction && direction !== 'default')?.[0] ?? null) as PriceMatrixGridColumnKey | null;
    if (!sortColumn) {
      return filteredRows;
    }
    const direction = getPriceMatrixGridSortDirection(sortColumn);
    const sortedRows = [...filteredRows];
    sortedRows.sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      const result = String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' });
      return direction === 'asc' ? result : -result;
    });
    return sortedRows;
  }, [getPriceMatrixGridSortDirection, priceMatrixGridColumnFilters, priceMatrixGridQuery, priceMatrixGridRows, priceMatrixGridSortConfig]);
  const paginatedPriceMatrixGridRows = React.useMemo(() => {
    const start = priceMatrixGridPage * priceMatrixGridRowsPerPage;
    return filteredPriceMatrixGridRows.slice(start, start + priceMatrixGridRowsPerPage);
  }, [filteredPriceMatrixGridRows, priceMatrixGridPage, priceMatrixGridRowsPerPage]);
  const selectedPriceMatrixStatusUnitIds = React.useMemo(() => {
    if (!priceMatrixStatusSelectionRange) {
      return new Set<number>();
    }
    const start = Math.max(0, Math.min(priceMatrixStatusSelectionRange.start, priceMatrixStatusSelectionRange.end));
    const end = Math.min(
      filteredPriceMatrixGridRows.length - 1,
      Math.max(priceMatrixStatusSelectionRange.start, priceMatrixStatusSelectionRange.end)
    );
    const ids = new Set<number>();
    for (let index = start; index <= end; index += 1) {
      const row = filteredPriceMatrixGridRows[index];
      if (row) ids.add(row.id);
    }
    return ids;
  }, [filteredPriceMatrixGridRows, priceMatrixStatusSelectionRange]);
  React.useEffect(() => {
    setPriceMatrixStatusDrafts((prev) => {
      const next: Record<string, string> = {};
      priceMatrixGridRows.forEach((row) => {
        const key = String(row.id);
        next[key] = Object.prototype.hasOwnProperty.call(prev, key) ? prev[key] : row.status;
      });
      return next;
    });
  }, [priceMatrixGridRows]);
  React.useEffect(() => {
    if (priceMatrixStatusSelectionAnchorIndex == null || filteredPriceMatrixGridRows.length === 0) {
      return;
    }
    if (priceMatrixStatusSelectionAnchorIndex >= filteredPriceMatrixGridRows.length) {
      setPriceMatrixStatusSelectionAnchorIndex(null);
      setPriceMatrixStatusSelectionRange(null);
    }
  }, [filteredPriceMatrixGridRows.length, priceMatrixStatusSelectionAnchorIndex]);
  const normalizePriceMatrixStatusInput = React.useCallback((value: string): string | null => {
    const normalized = value.trim().toUpperCase();
    if (!normalized) return null;
    if (['SOLD', 'S', 'SOLD OUT'].includes(normalized)) return 'SOLD';
    if (['AVAILABLE', 'AVAIL', 'A', 'AVL', 'UNSOLD'].includes(normalized)) return 'AVAILABLE';
    return null;
  }, []);
  const selectPriceMatrixStatusCell = React.useCallback((absoluteRowIndex: number, extendSelection: boolean = false) => {
    if (extendSelection && priceMatrixStatusSelectionAnchorIndex != null) {
      setPriceMatrixStatusSelectionRange({
        start: priceMatrixStatusSelectionAnchorIndex,
        end: absoluteRowIndex,
      });
      return;
    }
    setPriceMatrixStatusSelectionAnchorIndex(absoluteRowIndex);
    setPriceMatrixStatusSelectionRange({
      start: absoluteRowIndex,
      end: absoluteRowIndex,
    });
  }, [priceMatrixStatusSelectionAnchorIndex]);
  const commitPriceMatrixStatusDraft = React.useCallback((unitId: number, rawValue: string) => {
    const normalized = normalizePriceMatrixStatusInput(rawValue);
    if (!currentProjectId || !onSetPriceMatrixSimulationStatus) {
      return;
    }
    void onSetPriceMatrixSimulationStatus(currentProjectId, unitId, normalized);
    setPriceMatrixStatusDrafts((prev) => ({
      ...prev,
      [String(unitId)]: normalized ?? '',
    }));
  }, [currentProjectId, normalizePriceMatrixStatusInput, onSetPriceMatrixSimulationStatus]);
  const applyPriceMatrixStatuses = React.useCallback((updates: Array<{ unitId: number; status: string | null }>) => {
    if (!currentProjectId || !onApplyPriceMatrixSimulationStatuses || updates.length === 0) {
      return;
    }
    void onApplyPriceMatrixSimulationStatuses(currentProjectId, updates);
    setPriceMatrixStatusDrafts((prev) => {
      const next = { ...prev };
      updates.forEach(({ unitId, status }) => {
        next[String(unitId)] = status ?? '';
      });
      return next;
    });
  }, [currentProjectId, onApplyPriceMatrixSimulationStatuses]);
  const handleResetPriceMatrixSimulation = React.useCallback(() => {
    if (!priceMatrixSimulationSession?.units?.length) {
      return;
    }
    applyPriceMatrixStatuses(
      priceMatrixSimulationSession.units.map((unit) => ({
        unitId: unit.unitId,
        status: 'AVAILABLE',
      }))
    );
  }, [applyPriceMatrixStatuses, priceMatrixSimulationSession]);
  const handlePastePriceMatrixStatuses = React.useCallback((
    startRowIndex: number,
    pastedText: string
  ) => {
    const values = pastedText
      .replace(/\r\n/g, '\n')
      .split(/\n|\t/)
      .map((value) => value.trim())
      .filter((value) => value.length > 0);
    if (values.length === 0) {
      return;
    }

    const updates: Array<{ unitId: number; status: string | null }> = [];
    values.forEach((rawValue, offset) => {
      const row = filteredPriceMatrixGridRows[startRowIndex + offset];
      if (!row) return;
      const normalized = normalizePriceMatrixStatusInput(rawValue);
      if (!normalized) return;
      updates.push({ unitId: row.id, status: normalized });
    });
    if (updates.length === 0) {
      return;
    }
    applyPriceMatrixStatuses(updates);
  }, [applyPriceMatrixStatuses, filteredPriceMatrixGridRows, normalizePriceMatrixStatusInput]);
  const canLoadPriceMatrix =
    Boolean(projectDetails?.priceMatrixSheetId)
    && Boolean(currentProjectId)
    && (!hasUnitPrices || Boolean(priceMatrixSimulationSession));
  const canManagePriceMatrix = isAdminUser && canLoadPriceMatrix;
  const shouldShowPriceReferenceTools = !hasUnitPrices || Boolean(priceMatrixSimulationSession) || canLoadPriceMatrix;
  const isExecutiveCondoProject = (
    projectDetails?.projectCategoryId
    ?? projectDetailsMap.get(projectDetails?.name ?? selectedProject)?.projectCategoryId
  ) === 'C1803';
  const topViewingBestPsfByUnitTypeData = React.useMemo(
    () => buildBestByUnitTypeFromTowerUnits(towerViewUnits, isExecutiveCondoProject, true, 'psf'),
    [towerViewUnits, isExecutiveCondoProject],
  );
  const isExecutiveCondoByProjectName = React.useCallback((projectName?: string | null) => {
    if (!projectName) return false;
    return projectDetailsMap.get(projectName)?.projectCategoryId === 'C1803';
  }, [projectDetailsMap]);
  const isExecutiveCondoUnit = React.useCallback((
    unit?: Pick<TowerViewUnit | ComparableUnit | TopViewingReferenceUnit, 'projectName' | 'priceDps' | 'psfDps'> | null,
  ) => {
    if (!unit) return false;
    return isExecutiveCondoByProjectName(unit.projectName) || Number(unit.priceDps ?? 0) > 0 || Number(unit.psfDps ?? 0) > 0;
  }, [isExecutiveCondoByProjectName]);
  const isReferenceExecutiveCondo = topViewingReferenceUnit
    ? isExecutiveCondoUnit(topViewingReferenceUnit)
    : false;
  const isIndicativeReference = topViewingReferenceUnit?.referenceSource === 'indicative';
  const isGuesstimationReference = topViewingReferenceUnit?.referenceSource === 'guesstimation';
  const isComparisonLocked = priceMatrixSimulationSession
    ? false
    : isTopViewingIndicativeBeforeLaunch || isGuesstimationReference;
  const indicativeRangeLabel = topViewingReferenceUnit?.indicativeSizeRange
    ? `${formatNumber(topViewingReferenceUnit.indicativeSizeRange.min)}-${formatNumber(topViewingReferenceUnit.indicativeSizeRange.max)} sqft`
    : null;
  const formatThousandsInput = (value: string): string => {
    const cleaned = value.replace(/[^\d.]/g, '');
    if (!cleaned) return '';
    const [intPart, decPart] = cleaned.split('.');
    const intFormatted = intPart ? Number(intPart).toLocaleString('en-US') : '';
    return decPart !== undefined ? `${intFormatted}.${decPart}` : intFormatted;
  };

  const budgetProjectDistanceByName = React.useMemo(() => {
    const projectNames = new Set<string>();
    topViewingBudgetSortedUnits.forEach((unit) => {
      if (unit.projectName) projectNames.add(unit.projectName);
    });
    topViewingBudgetProjectsForView.forEach((projectName) => {
      if (projectName) projectNames.add(projectName);
    });
    const distanceMap = new Map<string, number | null>();
    projectNames.forEach((projectName) => {
      distanceMap.set(projectName, getTopViewingDistanceFromReference(projectName));
    });
    return distanceMap;
  }, [getTopViewingDistanceFromReference, topViewingBudgetProjectsForView, topViewingBudgetSortedUnits]);

  // Nearby Deal uses the currently-loaded project as the distance origin, so deals surface as
  // soon as a project is opened. Fall back to reference project only when no project is loaded.
  const nearbyDealOriginProjectName = selectedTopViewingSitePlan ?? selectedProject ?? projectDetails?.name ?? topViewingReferenceUnit?.projectName ?? null;
  const nearbyDealRadiusKm = topViewingBudgetNearbyRadiusKm;
  const nearbyDealOriginProjectNameNormalized = React.useMemo(
    () => (nearbyDealOriginProjectName ?? '').trim().toLowerCase(),
    [nearbyDealOriginProjectName],
  );

  React.useEffect(() => {
    setNearbyDealVisible(false);
    setNearbyDealSectionOpen(false);
  }, [nearbyDealOriginProjectName]);

  const getNearbyDealDistance = React.useCallback((projectName: string): number | null => {
    if (!nearbyDealOriginProjectName) return null;
    const origin = projectDetailsMap.get(nearbyDealOriginProjectName)
      ?? ((projectDetails && projectDetails.name === nearbyDealOriginProjectName) ? projectDetails : null);
    const target = projectDetailsMap.get(projectName);
    if (origin?.mapLatitude == null || origin?.mapLongitude == null ||
        target?.mapLatitude == null || target?.mapLongitude == null) {
      return null;
    }
    const R = 6371; // Earth radius in km
    const dLat = (target.mapLatitude - origin.mapLatitude) * Math.PI / 180;
    const dLng = (target.mapLongitude - origin.mapLongitude) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(origin.mapLatitude * Math.PI / 180) * Math.cos(target.mapLatitude * Math.PI / 180) *
      Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }, [nearbyDealOriginProjectName, projectDetails, projectDetailsMap]);

  // Nearby Deal: thumbsup project groups within the selected radius, with distance, nearest first.
  const nearbyDealThumbsupGroups = React.useMemo(() => {
    if (!canAccessNearbyDeal) return [];
    return nearbyThumbsupGroups
      .filter((group) => group.projectName.trim().toLowerCase() !== nearbyDealOriginProjectNameNormalized)
      .map((group) => ({ ...group, distance: getNearbyDealDistance(group.projectName) }))
      .filter((group) => group.distance !== null && group.distance <= nearbyDealRadiusKm)
      .sort((a, b) => (a.distance as number) - (b.distance as number));
  }, [canAccessNearbyDeal, nearbyThumbsupGroups, nearbyDealOriginProjectNameNormalized, getNearbyDealDistance, nearbyDealRadiusKm]);

  // Nearby Deal: price promo project groups within the selected radius, with distance, nearest first.
  const nearbyDealPricePromoGroups = React.useMemo(() => {
    if (!canAccessNearbyDeal) return [];
    return nearbyPricePromoGroups
      .filter((group) => group.projectName.trim().toLowerCase() !== nearbyDealOriginProjectNameNormalized)
      .map((group) => ({ ...group, distance: getNearbyDealDistance(group.projectName) }))
      .filter((group) => group.distance !== null && group.distance <= nearbyDealRadiusKm)
      .sort((a, b) => (a.distance as number) - (b.distance as number));
  }, [canAccessNearbyDeal, nearbyPricePromoGroups, nearbyDealOriginProjectNameNormalized, getNearbyDealDistance, nearbyDealRadiusKm]);

  // Distance filtering needs coordinates in projectDetailsMap for the origin (loaded) project
  // AND every thumbsup / price promo project. The map is otherwise only populated lazily for
  // reference/comparable/budget projects, which is why deals previously appeared only after
  // setting a reference unit. Bulk-fetch any missing coordinates so it works on project load.
  const nearbyDealCoordsFetchedRef = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    if (!canAccessNearbyDeal) return;
    const idByName = new Map<string, string>();
    if (nearbyDealOriginProjectName && currentProjectId) {
      idByName.set(nearbyDealOriginProjectName, String(currentProjectId));
    }
    for (const g of nearbyThumbsupGroups) {
      if (g.projectName && g.projectId) idByName.set(g.projectName, String(g.projectId));
    }
    for (const g of nearbyPricePromoGroups) {
      if (g.projectName && g.projectId) idByName.set(g.projectName, String(g.projectId));
    }

    const toFetch: string[] = [];
    idByName.forEach((id, name) => {
      const details = projectDetailsMap.get(name);
      const hasCoords = details?.mapLatitude != null && details?.mapLongitude != null;
      const fetchedKey = `${id}:${name}`;
      if (!hasCoords && !nearbyDealCoordsFetchedRef.current.has(fetchedKey)) {
        toFetch.push(id);
      }
    });
    if (toFetch.length === 0) return;
    const uniqueProjectIds = Array.from(new Set(toFetch));
    idByName.forEach((id, name) => {
      if (uniqueProjectIds.includes(id)) {
        nearbyDealCoordsFetchedRef.current.add(`${id}:${name}`);
      }
    });

    let cancelled = false;
    void (async () => {
      try {
        const list = await api.getBulkProjectDetails(uniqueProjectIds);
        if (cancelled || list.length === 0) return;
        setProjectDetailsMap((prev) => {
          const merged = new Map(prev);
          list.forEach((d) => { if (d.name) merged.set(d.name, d); });
          return merged;
        });
      } catch (err) {
        console.error('Failed to load Nearby Deal project coordinates:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [canAccessNearbyDeal, nearbyDealOriginProjectName, currentProjectId, nearbyThumbsupGroups, nearbyPricePromoGroups, projectDetailsMap, setProjectDetailsMap]);

  // Shared predicate: does a project pass the PROJECT VIEW column filters (text / numeric range /
  // exclusion)? Kept in one place so PROJECT VIEW rows and UNIT VIEW apply identical criteria — a
  // project the user filters away in PROJECT VIEW should not reappear (via its units) in UNIT VIEW.
  const projectPassesBudgetProjectViewFilters = React.useCallback((projectName: string): boolean => {
    const toNum = (value: string): number | null => {
      const trimmed = value.trim();
      if (!trimmed) return null;
      const parsed = Number(trimmed);
      return Number.isFinite(parsed) ? parsed : null;
    };
    const getRawValue = (name: string, key: string): string => {
      const value = getBudgetProjectCellValue(name, key);
      if (key === 'distance') return value ? value.replace(/,/g, '') : '';
      if (key === 'walkingTimeToMrt') return value ? value.replace(/[^0-9.]/g, '') : '';
      if (/(price|psf)$/i.test(key)) return value ? value.replace(/[^0-9.]/g, '') : '';
      return value ?? '';
    };
    const exclusionKeys = ['projectName', 'projectCategory', 'tenure', 'region', 'nearestMrt', 'district'];
    return PROJECT_VIEW_COLUMN_KEYS.every((key) => {
      const raw = getRawValue(projectName, key);
      if (exclusionKeys.includes(key)) {
        const excluded = (topViewingBudgetExclusionFilters as Record<string, string[]>)[key] ?? [];
        return excluded.length === 0 || !excluded.includes(raw);
      }
      const textFilter = (budgetProjectTextFilters[key] ?? '').trim().toLowerCase();
      if (textFilter) {
        return raw.toLowerCase().includes(textFilter);
      }
      if (projectViewNumericColumns.has(key)) {
        const range = budgetProjectRangeFilters[key] ?? { min: '', max: '' };
        const min = toNum(range.min);
        const max = toNum(range.max);
        if (min == null && max == null) return true;
        const num = toNum(raw);
        if (num == null) return false;
        if (min != null && num < min) return false;
        if (max != null && num > max) return false;
      }
      return true;
    });
  }, [
    PROJECT_VIEW_COLUMN_KEYS,
    budgetProjectRangeFilters,
    budgetProjectTextFilters,
    getBudgetProjectCellValue,
    projectViewNumericColumns,
    topViewingBudgetExclusionFilters,
  ]);

  // Only the PROJECT VIEW-local text/range filters need to be propagated to UNIT VIEW here;
  // exclusion filters are shared with the parent and already applied to the unit list upstream.
  const hasActiveBudgetProjectViewFilter = React.useMemo(() => {
    const hasText = Object.values(budgetProjectTextFilters).some((v) => (v ?? '').trim() !== '');
    const hasRange = Object.values(budgetProjectRangeFilters).some(
      (r) => (r?.min ?? '').trim() !== '' || (r?.max ?? '').trim() !== '',
    );
    return hasText || hasRange;
  }, [budgetProjectTextFilters, budgetProjectRangeFilters]);

  // Set of project names that survive the PROJECT VIEW filters. `null` when no PROJECT VIEW-local
  // filter is active, signalling UNIT VIEW to skip the extra project-level filtering entirely.
  const budgetProjectViewSurvivingNames = React.useMemo(() => {
    if (!hasActiveBudgetProjectViewFilter) return null;
    const set = new Set<string>();
    topViewingBudgetProjectsForView.forEach((projectName) => {
      if (projectPassesBudgetProjectViewFilters(projectName)) set.add(projectName);
    });
    return set;
  }, [hasActiveBudgetProjectViewFilter, projectPassesBudgetProjectViewFilters, topViewingBudgetProjectsForView]);

  const topViewingBudgetUnitsForDistance = React.useMemo(() => {
    const unitsAfterRegionFilter = topViewingBudgetRegionTab !== 'NEARBY'
      ? topViewingBudgetSortedUnits
      : topViewingBudgetSortedUnits.filter((unit) => {
      const distance = budgetProjectDistanceByName.get(unit.projectName) ?? null;
      return distance !== null && distance <= topViewingBudgetNearbyRadiusKm;
    });

    // Hide units belonging to projects that were filtered away in PROJECT VIEW, so switching
    // back to UNIT VIEW stays consistent with what the user last narrowed the project list to.
    const unitsAfterProjectFilter = budgetProjectViewSurvivingNames
      ? unitsAfterRegionFilter.filter((unit) => budgetProjectViewSurvivingNames.has(unit.projectName))
      : unitsAfterRegionFilter;

    const hasSuperMatchFilter = topViewingBudgetSuperMatch.bedroom || topViewingBudgetSuperMatch.size || topViewingBudgetSuperMatch.floor;
    if (!hasSuperMatchFilter) return unitsAfterProjectFilter;

    const referenceBedroom = normalizeBedroomValue(topViewingReferenceUnit?.bedroom);
    const parsedReferenceAreaSqft = Number(topViewingReferenceUnit?.areaSqft);
    const referenceAreaSqft = Number.isFinite(parsedReferenceAreaSqft) && parsedReferenceAreaSqft > 0
      ? parsedReferenceAreaSqft
      : null;
    const referenceFloor = parseUnitNumber(topViewingReferenceUnit?.unitNumber ?? '').floor;

    return unitsAfterProjectFilter.filter((unit) => {
      if (topViewingBudgetSuperMatch.bedroom) {
        const candidateBedroom = normalizeBedroomValue(unit.bedroom);
        if (referenceBedroom == null || candidateBedroom == null || candidateBedroom < referenceBedroom) {
          return false;
        }
      }

      if (topViewingBudgetSuperMatch.size) {
        const candidateAreaSqft = Number(unit.areaSqft);
        if (referenceAreaSqft == null || !Number.isFinite(candidateAreaSqft) || candidateAreaSqft < referenceAreaSqft) {
          return false;
        }
      }

      if (topViewingBudgetSuperMatch.floor) {
        const candidateFloor = Number(unit.floor);
        if (referenceFloor == null || !Number.isFinite(candidateFloor) || candidateFloor < referenceFloor) {
          return false;
        }
      }

      return true;
    });
  }, [
    budgetProjectDistanceByName,
    budgetProjectViewSurvivingNames,
    normalizeBedroomValue,
    topViewingBudgetNearbyRadiusKm,
    topViewingBudgetRegionTab,
    topViewingBudgetSortedUnits,
    topViewingBudgetSuperMatch.bedroom,
    topViewingBudgetSuperMatch.floor,
    topViewingBudgetSuperMatch.size,
    topViewingReferenceUnit?.areaSqft,
    topViewingReferenceUnit?.bedroom,
    topViewingReferenceUnit?.unitNumber,
    parseUnitNumber,
  ]);

  const handleTopViewingBudgetClearAllFilters = React.useCallback(() => {
    handleTopViewingBudgetClearFilters();
    setTopViewingBudgetSuperMatch({
      bedroom: false,
      size: false,
      floor: false,
    });
    setTopViewingBudgetShowReferenceProjectUnits(false);
    // Project-view text/range filters also narrow the unit list, so clear them too.
    setBudgetProjectTextFilters({});
    setBudgetProjectRangeFilters({});
  }, [handleTopViewingBudgetClearFilters, setTopViewingBudgetShowReferenceProjectUnits]);

  // Auto-find: expand budget to 30%, wait for fetch, then find the minimum % for the cheapest matching unit.
  // Uses a ref to track whether we've seen the loading state cycle (loading=true → loading=false).
  const budgetAutoFindSeenLoadingRef = React.useRef(false);

  React.useEffect(() => {
    if (!budgetAutoFindPending || !topViewingBudgetRange) return;

    // Step 1: wait for range to reflect 30%
    if (topViewingBudgetRange.percent < 30) return;

    // Step 2: track the loading cycle
    if (topViewingBudgetUnitsLoading) {
      budgetAutoFindSeenLoadingRef.current = true;
      return;
    }

    // Step 3: loading is false. Either we saw it go true→false (fetch completed),
    // or it was never true (data was cached). Either way, data is ready.
    // But if we haven't seen loading AND the fetch range hasn't settled yet, skip.
    // Use a small timeout to allow one more render cycle for the data to settle.
    const timerId = window.setTimeout(() => {
      setBudgetAutoFindPending(false);
      budgetAutoFindSeenLoadingRef.current = false;

      if (topViewingBudgetUnitsForDistance.length > 0 && topViewingBudgetRange) {
        const cheapestPrice = Math.min(...topViewingBudgetUnitsForDistance.map((u) => u.priceNps ?? u.price ?? Infinity));
        if (Number.isFinite(cheapestPrice) && topViewingBudgetRange.base > 0) {
          const neededPercent = Math.max(0, Math.ceil(((cheapestPrice / topViewingBudgetRange.base) - 1) * 100));
          const clamped = Math.min(30, Math.max(0, neededPercent));
          setTopViewingBudgetSliderValue(clamped);
          setTopViewingBudgetTolerance(clamped);
        }
      }
      // If no results even at 30%, leave at 30%
    }, 100);

    return () => window.clearTimeout(timerId);
  }, [budgetAutoFindPending, topViewingBudgetUnitsLoading, topViewingBudgetUnitsForDistance, topViewingBudgetRange, setTopViewingBudgetSliderValue, setTopViewingBudgetTolerance]);

  const handleBudgetAutoFind = React.useCallback(() => {
    budgetAutoFindSeenLoadingRef.current = false;
    setBudgetAutoFindPending(true);
    // Set budget to max range, triggering API fetch
    setTopViewingBudgetSliderValue(30);
    setTopViewingBudgetTolerance(30);
  }, [setTopViewingBudgetSliderValue, setTopViewingBudgetTolerance]);

  const handleDistanceAutoFind = React.useCallback(() => {
    // From sorted units (already filtered by all filters except distance), apply super match and find min distance
    let candidates = topViewingBudgetSortedUnits;

    // Apply super match filters (same as topViewingBudgetUnitsForDistance logic)
    const hasSuperMatchFilter = topViewingBudgetSuperMatch.bedroom || topViewingBudgetSuperMatch.size || topViewingBudgetSuperMatch.floor;
    if (hasSuperMatchFilter) {
      const referenceBedroom = normalizeBedroomValue(topViewingReferenceUnit?.bedroom);
      const parsedRefArea = Number(topViewingReferenceUnit?.areaSqft);
      const referenceAreaSqft = Number.isFinite(parsedRefArea) && parsedRefArea > 0 ? parsedRefArea : null;
      const referenceFloor = parseUnitNumber(topViewingReferenceUnit?.unitNumber ?? '').floor;

      candidates = candidates.filter((unit) => {
        if (topViewingBudgetSuperMatch.bedroom) {
          const cb = normalizeBedroomValue(unit.bedroom);
          if (referenceBedroom == null || cb == null || cb < referenceBedroom) return false;
        }
        if (topViewingBudgetSuperMatch.size) {
          const ca = Number(unit.areaSqft);
          if (referenceAreaSqft == null || !Number.isFinite(ca) || ca < referenceAreaSqft) return false;
        }
        if (topViewingBudgetSuperMatch.floor) {
          const cf = Number(unit.floor);
          if (referenceFloor == null || !Number.isFinite(cf) || cf < referenceFloor) return false;
        }
        return true;
      });
    }

    // Find the minimum distance among candidates
    let minDist = Infinity;
    candidates.forEach((unit) => {
      const dist = budgetProjectDistanceByName.get(unit.projectName) ?? null;
      if (dist !== null && dist > 0 && dist < minDist) {
        minDist = dist;
      }
    });

    if (Number.isFinite(minDist)) {
      // Round up to nearest 0.1 km
      const rounded = Math.ceil(minDist * 10) / 10;
      const clamped = Math.min(10, Math.max(0.5, rounded));
      setTopViewingBudgetNearbyRadiusKm(clamped);
    }
  }, [
    topViewingBudgetSortedUnits,
    topViewingBudgetSuperMatch,
    normalizeBedroomValue,
    topViewingReferenceUnit,
    parseUnitNumber,
    budgetProjectDistanceByName,
    setTopViewingBudgetNearbyRadiusKm,
  ]);

  const hasBudgetExecutiveCondo = React.useMemo(
    () => topViewingBudgetUnitsForDistance.some((unit) => isExecutiveCondoByProjectName(unit.projectName)),
    [isExecutiveCondoByProjectName, topViewingBudgetUnitsForDistance]
  );
  const showBudgetDualPricing = hasBudgetExecutiveCondo;
  const renderDpsValue = React.useCallback((dpsValue: number | string | null | undefined, isExecutiveCondo: boolean) => {
    if (!isExecutiveCondo) {
      return 'N.A';
    }

    const numericValue = typeof dpsValue === 'number' ? dpsValue : Number(dpsValue);
    return Number.isFinite(numericValue) && numericValue > 0 ? formatCurrency(numericValue) : '-';
  }, [formatCurrency]);

  const resolvedTopViewingFloorPlanUnit = React.useMemo(() => {
    if (topViewingFloorPlanUnit) return topViewingFloorPlanUnit;
    const anchored = topViewingUnitMenuAnchor?.unit;
    if (anchored && anchored.floorplan && anchored.floorplan === topViewingFloorPlanUrl) return anchored;
    if (topViewingFloorPlanUrl) {
      const fromTower = towerViewUnits.find((u) => u.floorplan === topViewingFloorPlanUrl);
      if (fromTower) return fromTower;
      const fromAvailable = topViewingFilteredAvailableUnits.find((u) => u.floorplan === topViewingFloorPlanUrl);
      if (fromAvailable) return fromAvailable;
      const fromComparable = topViewingComparableUnits.find((u) => u.floorplan === topViewingFloorPlanUrl);
      if (fromComparable) return fromComparable;
    }
    return null;
  }, [
    topViewingFloorPlanUnit,
    topViewingUnitMenuAnchor,
    topViewingFloorPlanUrl,
    towerViewUnits,
    topViewingFilteredAvailableUnits,
    topViewingComparableUnits,
  ]);

  React.useEffect(() => {
    if (canViewProjectMembers) return;
    if (projectDetailsTab === 'members') {
      setProjectDetailsTab('details');
    }
  }, [canViewProjectMembers, projectDetailsTab, setProjectDetailsTab]);

  // Super Ratio is its own project-details tab; fall back to Details if the
  // grant is missing (or goes away mid-session) so the tab can't be stranded.
  React.useEffect(() => {
    if (!canAccessSuperRatio && projectDetailsTab === 'superRatio') {
      setProjectDetailsTab('details');
    }
  }, [canAccessSuperRatio, projectDetailsTab, setProjectDetailsTab]);

  // Super Ratio quick-look dialog, opened from the project header.
  const [superRatioDialogOpen, setSuperRatioDialogOpen] = React.useState(false);
  const superRatioMappable = Boolean(
    projectDetails?.name && projectDetails?.mapLatitude && projectDetails?.mapLongitude,
  );
  const canOpenSuperRatioDialog = canAccessSuperRatio && superRatioMappable;

  React.useEffect(() => {
    if (!canOpenSuperRatioDialog) setSuperRatioDialogOpen(false);
  }, [canOpenSuperRatioDialog]);

  // Load (and reload on a radius change) while the dialog is open. Skipped when
  // the Super Ratio tab is already showing, since its own effect owns the fetch.
  // Keyed on the project name rather than the object so a re-render of the
  // parent can't retrigger the request.
  const superRatioProjectName = projectDetails?.name;
  React.useEffect(() => {
    if (!superRatioDialogOpen || !canOpenSuperRatioDialog || !superRatioProjectName) return;
    if (projectDetailsTab === 'superRatio') return;
    fetchDemandSupply(
      superRatioProjectName,
      demandSupplyRadius,
      demandSupplyMinRadius,
      demandSupplyNationwide,
    );
  }, [
    superRatioDialogOpen,
    canOpenSuperRatioDialog,
    projectDetailsTab,
    superRatioProjectName,
    demandSupplyRadius,
    demandSupplyMinRadius,
    demandSupplyNationwide,
    fetchDemandSupply,
  ]);

  const getUnitPriceSelectionDisabledReason = React.useCallback((
    unit: (TowerViewUnit | ComparableUnit) | null | undefined,
    actionLabel: string,
    unavailableCopy: string,
  ): {
    main: string;
    sub?: string;
  } | null => {
    if (!unit) return null;
    if (disableSetReferenceAction) {
      return { main: actionLabel, sub: '*Not available for external users' };
    }
    const status = (unit.unitStatus || '').toString().toLowerCase();
    if (status === 'sold') {
      return { main: actionLabel, sub: unavailableCopy };
    }
    if (isUnitStatusNotReleased(status)) {
      return { main: actionLabel, sub: '*Not released units cannot be selected' };
    }
    const primaryPrice = Number(unit.priceNps ?? unit.price ?? 0);
    const alternatePrice = Number(unit.priceDps ?? 0);
    const hasPrice =
      (Number.isFinite(primaryPrice) && primaryPrice > 0) ||
      (Number.isFinite(alternatePrice) && alternatePrice > 0);
    if (!hasPrice) {
      return { main: actionLabel, sub: '*Price required to set selection' };
    }
    return null;
  }, [disableSetReferenceAction]);
  const getReferenceUnitDisabledReason = React.useCallback(
    (unit: (TowerViewUnit | ComparableUnit) | null | undefined) => {
      const reason = getUnitPriceSelectionDisabledReason(unit, 'Set as Reference Unit', '*Sold units cannot set as reference');
      if (!reason) return null;
      if (reason.sub === '*Price required to set selection') {
        return { main: 'Set as Reference Unit', sub: '*Price required to set as reference' };
      }
      return reason;
    },
    [getUnitPriceSelectionDisabledReason],
  );
  const getBlinkerChoiceDisabledReason = React.useCallback(
    (unit: (TowerViewUnit | ComparableUnit) | null | undefined) => {
      const reason = getUnitPriceSelectionDisabledReason(unit, 'Set as Anticipate Blinker Unit', '*Sold units cannot set as anticipate blinker unit');
      if (!reason) return null;
      if (reason.sub === '*Price required to set selection') {
        return { main: 'Set as Anticipate Blinker Unit', sub: '*Price is not available' };
      }
      return reason;
    },
    [getUnitPriceSelectionDisabledReason],
  );
  const topViewingUnitMenuSetReferenceDisabledReason = React.useMemo(
    () => getReferenceUnitDisabledReason(topViewingUnitMenuAnchor?.unit),
    [getReferenceUnitDisabledReason, topViewingUnitMenuAnchor],
  );
  const topViewingUnitMenuSetBlinkerDisabledReason = React.useMemo(
    () => getBlinkerChoiceDisabledReason(topViewingUnitMenuAnchor?.unit),
    [getBlinkerChoiceDisabledReason, topViewingUnitMenuAnchor],
  );
  const activeTopViewingProjectName = React.useMemo(
    () => selectedTopViewingSitePlan || sitePlanProjectName || selectedProject || topViewingReferenceUnit?.projectName || '',
    [selectedProject, selectedTopViewingSitePlan, sitePlanProjectName, topViewingReferenceUnit?.projectName],
  );
  const sectionUnitMenuCanSetReference = React.useMemo(
    () => Boolean(
      sectionUnitMenuAnchor &&
      sectionUnitMenuAnchor.mode === 'comparable' &&
      sectionUnitMenuAnchor.unit.projectName === activeTopViewingProjectName,
    ),
    [activeTopViewingProjectName, sectionUnitMenuAnchor],
  );
  const sectionUnitMenuSetReferenceDisabledReason = React.useMemo(
    () => (sectionUnitMenuCanSetReference ? getReferenceUnitDisabledReason(sectionUnitMenuAnchor?.unit) : null),
    [getReferenceUnitDisabledReason, sectionUnitMenuAnchor, sectionUnitMenuCanSetReference],
  );
  const sectionUnitMenuSetBlinkerDisabledReason = React.useMemo(
    () => getBlinkerChoiceDisabledReason(sectionUnitMenuAnchor?.unit),
    [getBlinkerChoiceDisabledReason, sectionUnitMenuAnchor],
  );
  const getUnitComparisonPrice = React.useCallback((unit: TowerViewUnit | ComparableUnit | TopViewingReferenceUnit | null | undefined): number | null => {
    if (!unit) return null;
    const price = Number(unit.priceNps ?? unit.price ?? 0);
    return Number.isFinite(price) && price > 0 ? price : null;
  }, []);
  const getUnitTypeLabel = React.useCallback((unit: TowerViewUnit | ComparableUnit | TopViewingReferenceUnit | null | undefined): string => {
    if (!unit) return '';
    const typeDescription = (unit.unitTypeDescription ?? '').trim();
    if (typeDescription) return typeDescription.replace(/Bedroom/gi, 'BR');
    if ('unitType' in unit) {
      return (unit.unitType ?? '').trim().replace(/Bedroom/gi, 'BR');
    }
    return '';
  }, []);
  const isUnitWithinBlinkerRange = React.useCallback((unit: TowerViewUnit | ComparableUnit | null | undefined): boolean => {
    if (!topViewingBlinkerRange || !unit) return false;
    const price = getUnitComparisonPrice(unit);
    if (price == null) return false;
    const priceMatch = price >= topViewingBlinkerRange.min && price <= topViewingBlinkerRange.max;
    if (!priceMatch) return false;
    if (topViewingBlinkerSuperUpgrade && topViewingBlinkerChoiceUnit) {
      const isMatch = isSuperUpgradeBedroomMatch({
        blinkerBedroom: normalizeBedroomValue(topViewingBlinkerChoiceUnit.bedroom),
        blinkerUnitType: topViewingBlinkerChoiceUnit.unitTypeDescription,
        candidateBedroom: normalizeBedroomValue(unit.bedroom),
        candidateUnitType: unit.unitTypeDescription,
      });
      if (!isMatch) return false;
    }
    return true;
  }, [getUnitComparisonPrice, normalizeBedroomValue, topViewingBlinkerRange, topViewingBlinkerSuperUpgrade, topViewingBlinkerChoiceUnit]);

  /**
   * Storey cells for every stack drawn on the 3D site plan, keyed by
   * `${blockDigits}-${normalizedStack}` with a bare-stack fallback for site
   * plans whose regions carry no block number — the same key shape as
   * `sitePlanStackFloorCounts` and the sold-percentage lookups.
   *
   * Ground floor first, padded to the block's floors: a floor where this stack
   * has no unit (but another stack in the block does) is a `null` slot, so a
   * stack that starts at level 3 stands as tall as its neighbours.
   * Deliberately free of the heat-map and availability colours: `solarTowers`
   * resolves those, which keeps this map stable while the viewer toggles them.
   */
  const sitePlanStoreyCells = React.useMemo(() => {
    const result = new Map<string, Array<SitePlanStoreyCell | null>>();
    const splitFloorProject = !!draftFloorGroups(sitePlanProjectName || selectedTopViewingSitePlan);
    const splitFloorStacks = new Map<string, Array<Array<SitePlanStoreyCell | null>>>();
    if (!sitePlanIs3d) return result;
    towerViewData.blocks.forEach((blockData, blockKey) => {
      const blockDigits = String(blockKey).replace(/\D/g, '');
      const floorsAsc = [...blockData.floors].sort((a, b) => a - b);
      blockData.stacks.forEach((stackNum) => {
        const cells: SitePlanStoreyCell[] = [];
        floorsAsc.forEach((floor) => {
          const unit = blockData.grid.get(`${floor}-${stackNum}`) as TowerViewUnit | ComparableUnit | undefined;
          if (!unit) return;
          const status = (unit.unitStatus || '').toString().toLowerCase();
          const sold = status === 'sold';
          const notReleased = isUnitStatusNotReleased(status);
          const bedroom = isPenthouseUnit(unit) ? PENTHOUSE_FILTER_VALUE : normalizeBedroomValue(unit.bedroom);
          cells.push({
            unit,
            floor,
            sold,
            notReleased,
            dimmed: !sitePlanUnitMatchesFilters(unit),
            highlighted: !sold && isUnitWithinBlinkerRange(unit),
            bedroomColor: getBedroomColor(bedroom),
          });
        });
        if (cells.length === 0) return;
        const slots = padStoreys(cells, (cell) => cell.floor, floorsAsc);
        const normStack = padSitePlanStackKey(stackNum);
        if (blockDigits) result.set(`${blockDigits}-${normStack}`, slots);
        if (!result.has(normStack)) result.set(normStack, slots);
        if (splitFloorProject) {
          const sections = splitFloorStacks.get(normStack) ?? [];
          sections.push(slots);
          splitFloorStacks.set(normStack, sections);
        }
      });
    });
    // Serra and Promenade can expose the tiers of one physical building as
    // separate inventory blocks, including named blocks with no numeric key.
    // Their bare stack lookup must therefore contain every tier.
    splitFloorStacks.forEach((sections, normStack) => {
      result.set(normStack, mergeFloorGroupStackUnits(sections, cell => cell.floor));
    });
    return result;
  }, [
    PENTHOUSE_FILTER_VALUE,
    getBedroomColor,
    isPenthouseUnit,
    isUnitWithinBlinkerRange,
    normalizeBedroomValue,
    sitePlanIs3d,
    selectedTopViewingSitePlan,
    sitePlanProjectName,
    sitePlanUnitMatchesFilters,
    towerViewData,
  ]);

  /**
   * Tower massing for the 3D / 3DS scene. Lives below `sitePlanStoreyCells`
   * because it resolves each storey's final colour here — the heat map and the
   * sold / not-released tints belong to the view, not to the unit data.
   */
  const solarTowers = React.useMemo<SolarTower[]>(() => {
    const seen = new Set<string>();
    const sectioned = !!draftFloorGroups(sitePlanProjectName || selectedTopViewingSitePlan);
    return (sitePlanData?.blocks || []).filter(stack => {
      if (!stack.stack) return false;
      const key = `${stack.blockNumber || ''}:${stack.stack}${sectioned ? `:${stack.x}:${stack.y}` : ''}`;
      if (seen.has(key)) return false; seen.add(key); return true;
    }).map(stack => {
      const norm = normalizeStackKey(stack.stack);
      const blockNum = (stack.blockNumber || '').replace(/\D/g, '');
      const bounds = sitePlanData?.bounds;
      const sold = sitePlanStackSoldPercentages.get(norm) ?? 0;
      const stackColor = sitePlanShowHeatMap ? getHeatMapColor(sold) : '#55c7b0';
      const cells = sectioned
        ? sitePlanStoreyCells.get(norm) ?? []
        : (blockNum ? sitePlanStoreyCells.get(`${blockNum}-${norm}`) : undefined)
          ?? sitePlanStoreyCells.get(norm)
          ?? [];
      const fallbackFloors = sitePlanStackFloorCounts.counts.get(`${blockNum}-${norm}`) ?? sitePlanStackFloorCounts.counts.get(norm) ?? 0;
      return {
        x: bounds ? bounds.x + Number(stack.x) / 100 * bounds.width : Number(stack.x),
        y: bounds ? bounds.y + Number(stack.y) / 100 * bounds.height : Number(stack.y),
        width: bounds ? Number(stack.width) / 100 * bounds.width : Number(stack.width),
        depth: bounds ? Number(stack.height) / 100 * bounds.height : Number(stack.height),
        floors: cells.length > 0 ? cells.length : fallbackFloors,
        blockNumber: stack.blockNumber || sitePlanStackBlocks.get(norm) || '', topFloor: fallbackFloors,
        label: stack.stack || '', color: stackColor,
        visible: sitePlanShowPoints && !shouldHideNonMatchingSitePlanStack(stack.stack, stack.blockNumber),
        blinking: sitePlanBlinkingStacks.has(norm) || sitePlanBlinkingStacks.has(`${blockNum}-${stack.stack}`),
        // Ground floor first, matching the texture the scene paints on the sides.
        // Sold stays red even under the heat map — it is what the heat map counts.
        units: cells.map((cell) => (cell == null
          ? { color: EMPTY_STOREY_COLOR, emphasis: false, empty: true }
          : {
            floor: cell.floor,
            color: cell.dimmed ? '#6a7b8d'
              : cell.sold ? '#d0453f'
              : cell.notReleased ? '#8a97a6'
              : sitePlanShowHeatMap ? stackColor : cell.bedroomColor,
            emphasis: cell.highlighted,
            unitNumber: cell.unit.unitNumber,
            stackNumber: padSitePlanStackKey(stack.stack),
          })),
        onClick: () => { if (stack.stack && sitePlanProjectName) handleOpenStackAnalysis(sitePlanProjectName, stack.stack, stack.blockNumber); },
        onUnitClick: (index: number, element: HTMLElement, clientX: number, clientY: number) => {
          const cell = cells[index];
          if (cell === null) return; // No unit on this floor.
          if (cell && !cell.dimmed) {
            setTopViewingUnitMenuAnchor({ element, unit: cell.unit, mouseX: clientX, mouseY: clientY });
            return;
          }
          if (stack.stack && sitePlanProjectName) handleOpenStackAnalysis(sitePlanProjectName, stack.stack, stack.blockNumber);
        },
      };
    });
  }, [sitePlanData, normalizeStackKey, sitePlanStackSoldPercentages, sitePlanStackFloorCounts, sitePlanShowHeatMap,
    getHeatMapColor, sitePlanShowPoints, shouldHideNonMatchingSitePlanStack, sitePlanBlinkingStacks, sitePlanProjectName,
    handleOpenStackAnalysis, sitePlanStoreyCells, setTopViewingUnitMenuAnchor, sitePlanStackBlocks, selectedTopViewingSitePlan]);


  const superUpgradeMatchCount = React.useMemo(() => {
    if (!topViewingBlinkerRange || !topViewingBlinkerChoiceUnit) return 0;
    const blinkerBedroom = normalizeBedroomValue(topViewingBlinkerChoiceUnit.bedroom);
    const blinkerUnitType = topViewingBlinkerChoiceUnit.unitTypeDescription;
    return towerViewUnits.filter((unit) => {
      const status = (unit.unitStatus || '').toString().toLowerCase();
      if (status === 'sold') return false;
      const price = getUnitComparisonPrice(unit);
      if (price == null) return false;
      if (price < topViewingBlinkerRange.min || price > topViewingBlinkerRange.max) return false;
      return isSuperUpgradeBedroomMatch({
        blinkerBedroom,
        blinkerUnitType,
        candidateBedroom: normalizeBedroomValue(unit.bedroom),
        candidateUnitType: unit.unitTypeDescription,
      });
    }).length;
  }, [towerViewUnits, topViewingBlinkerRange, topViewingBlinkerChoiceUnit, getUnitComparisonPrice, normalizeBedroomValue]);

  const topViewingBudgetProjectsForDistance = React.useMemo(() => {
    if (topViewingBudgetRegionTab !== 'NEARBY') return topViewingBudgetProjectsForView;
    return topViewingBudgetProjectsForView.filter((projectName) => {
      const distance = budgetProjectDistanceByName.get(projectName) ?? null;
      return distance !== null && distance <= topViewingBudgetNearbyRadiusKm;
    });
  }, [
    budgetProjectDistanceByName,
    topViewingBudgetNearbyRadiusKm,
    topViewingBudgetProjectsForView,
    topViewingBudgetRegionTab,
  ]);
  const budgetDistanceLabel = 'Distance from anchor project';
  const topViewingBudgetMatchedCountByProject = React.useMemo(() => {
    const map = new Map<string, { total: number; br1: number; br2: number; br3: number; br4: number; br5: number; ph: number }>();
    topViewingBudgetUnitsForDistance.forEach((unit) => {
      const key = unit.projectName;
      if (!key) return;
      const curr = map.get(key) ?? { total: 0, br1: 0, br2: 0, br3: 0, br4: 0, br5: 0, ph: 0 };
      curr.total += 1;
      const br = normalizeBedroomValue(unit.bedroom);
      const unitType = String(unit.unitTypeDescription || unit.unitType || '').toLowerCase();
      const isPh = unitType.includes('penthouse') || unitType.includes('ph');
      if (isPh) curr.ph += 1;
      else if (br === 1) curr.br1 += 1;
      else if (br === 2) curr.br2 += 1;
      else if (br === 3) curr.br3 += 1;
      else if (br === 4) curr.br4 += 1;
      else if (br === 5) curr.br5 += 1;
      map.set(key, curr);
    });
    return map;
  }, [topViewingBudgetUnitsForDistance, normalizeBedroomValue]);
  const budgetMatchedVisibleBrColumns = React.useMemo(() => {
    const brKeys = ['br1', 'br2', 'br3', 'br4', 'br5', 'ph'] as const;
    const result: Record<string, boolean> = {};
    for (const br of brKeys) {
      result[br] = false;
      topViewingBudgetMatchedCountByProject.forEach((counts) => {
        if (counts[br] > 0) { result[br] = true; }
      });
    }
    return result;
  }, [topViewingBudgetMatchedCountByProject]);
  const unitMatchesBudgetBedroomBucket = React.useCallback(
    (unit: (typeof topViewingBudgetUnitsForDistance)[number], bucket: BudgetProjectMatchedBrBucket) => {
      const unitType = String(unit.unitTypeDescription || unit.unitType || '').toLowerCase();
      const isPh = unitType.includes('penthouse') || unitType.includes('ph');
      if (bucket === 'ph') return isPh;
      if (isPh) return false;
      const br = normalizeBedroomValue(unit.bedroom);
      const want = { br1: 1, br2: 2, br3: 3, br4: 4, br5: 5 }[bucket];
      return br === want;
    },
    [normalizeBedroomValue],
  );
  const handleBudgetProjectMatchedBrClick = React.useCallback(
    (e: React.MouseEvent, projectName: string, bucket: BudgetProjectMatchedBrBucket, count: number) => {
      e.stopPropagation();
      if (count <= 0) return;
      setBudgetProjectMatchedBrExpand((prev) =>
        (prev?.projectName === projectName && prev?.bucket === bucket ? null : { projectName, bucket }),
      );
    },
    [],
  );
  const budgetProjectExpandedMatchedUnits = React.useMemo(() => {
    if (!budgetProjectMatchedBrExpand) return [];
    return topViewingBudgetUnitsForDistance.filter(
      (u) =>
        u.projectName === budgetProjectMatchedBrExpand.projectName &&
        unitMatchesBudgetBedroomBucket(u, budgetProjectMatchedBrExpand.bucket),
    );
  }, [budgetProjectMatchedBrExpand, topViewingBudgetUnitsForDistance, unitMatchesBudgetBedroomBucket]);
  const isBudgetReferenceProjectUnit = React.useCallback((unit: Pick<TowerViewUnit, 'projectName'>): boolean => {
    if (!topViewingBudgetShowReferenceProjectUnits || !topViewingReferenceUnit?.projectName) return false;
    return unit.projectName.trim().toLowerCase() === topViewingReferenceUnit.projectName.trim().toLowerCase();
  }, [topViewingBudgetShowReferenceProjectUnits, topViewingReferenceUnit?.projectName]);
  const getBudgetUnitRowSx = React.useCallback((unit: Pick<TowerViewUnit, 'projectName'>) => ({
    cursor: 'pointer',
    ...(budgetReferenceProjectRowsBlinking && isBudgetReferenceProjectUnit(unit)
      ? {
          '@keyframes budgetReferenceProjectFlash': {
            '0%, 100%': { boxShadow: 'inset 0 0 0 9999px rgba(var(--color-accent-rgb), 0)' },
            '15%, 45%, 75%': { boxShadow: 'inset 0 0 0 9999px rgba(var(--color-accent-rgb), 0.5)' },
            '30%, 60%': { boxShadow: 'inset 0 0 0 9999px rgba(var(--color-accent-rgb), 0.12)' },
          },
          '&& > .MuiTableCell-root': {
            animation: 'budgetReferenceProjectFlash 2s ease-in-out',
          },
        }
      : {}),
  }), [budgetReferenceProjectRowsBlinking, isBudgetReferenceProjectUnit]);
  const getTopViewingAvailableIconButtonSx = (column: TopViewingColumnKey) => ({
    p: 0.5,
    color: hasTopViewingActiveFilter(column) ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)',
    '&:hover': {
      backgroundColor: 'var(--color-surface-ghost-strong)',
      color: 'var(--color-accent)',
    },
  });
  const getBudgetProjectIconButtonSx = (column: string) => {
    const base = { p: 0.5, color: 'var(--color-text-strong)', '&:hover': { bgcolor: 'rgba(var(--color-text-rgb), 0.1)', color: 'var(--color-accent)' } };
    const exclusionCols = ['projectName', 'projectCategory', 'tenure', 'region', 'nearestMrt', 'district'];
    if (exclusionCols.includes(column) && hasTopViewingBudgetActiveFilter(column as any)) {
      return { ...base, color: 'var(--color-accent)', bgcolor: 'rgba(255, 152, 0, 0.15)' };
    }
    const textFilter = (budgetProjectTextFilters[column] ?? '').trim();
    if (textFilter) return { ...base, color: 'var(--color-accent)', bgcolor: 'rgba(255, 152, 0, 0.15)' };
    const range = budgetProjectRangeFilters[column] ?? { min: '', max: '' };
    if ((range.min ?? '').trim() || (range.max ?? '').trim()) return { ...base, color: 'var(--color-accent)', bgcolor: 'rgba(255, 152, 0, 0.15)' };
    return base;
  };
  const getSortIndicatorIcon = (direction: SortDirection | 'asc' | 'desc' | null | undefined) => {
    if (direction !== 'asc' && direction !== 'desc') {
      return <MenuIcon fontSize="small" />;
    }
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
        {direction === 'asc'
          ? <ArrowUpwardIcon sx={{ fontSize: '0.85rem' }} />
          : <ArrowDownwardIcon sx={{ fontSize: '0.85rem' }} />}
        <MenuIcon fontSize="small" />
      </Box>
    );
  };
  const getTopViewingCompSortDirection = React.useCallback((column: TopViewingCompColumnKey): SortDirection => {
    if (topViewingCompSortConfig.column !== column || topViewingCompSortConfig.direction == null) {
      return 'default';
    }
    return topViewingCompSortConfig.direction;
  }, [topViewingCompSortConfig]);
  const getBudgetProjectSortDirection = React.useCallback((column: string): SortDirection => {
    if (budgetProjectSortConfig.column !== column) {
      return 'default';
    }
    return budgetProjectSortConfig.direction;
  }, [budgetProjectSortConfig]);
  const getProjectViewSortDirection = React.useCallback((column: string): SortDirection => {
    if (projectViewSortConfig.column !== column) {
      return 'default';
    }
    return projectViewSortConfig.direction;
  }, [projectViewSortConfig]);
  const budgetProjectRowsForDisplay = React.useMemo(() => {
    const getRawValue = (projectName: string, key: string): string => {
      const value = getBudgetProjectCellValue(projectName, key);
      if (key === 'distance') {
        return value ? value.replace(/,/g, '') : '';
      }
      if (key === 'walkingTimeToMrt') {
        return value ? value.replace(/[^0-9.]/g, '') : '';
      }
      if (/(price|psf)$/i.test(key)) {
        return value ? value.replace(/[^0-9.]/g, '') : '';
      }
      return value ?? '';
    };

    const filtered = topViewingBudgetProjectsForDistance.filter((projectName) => {
      const matchedCount = topViewingBudgetMatchedCountByProject.get(projectName);
      if (!matchedCount || matchedCount.total <= 0) return false;
      return projectPassesBudgetProjectViewFilters(projectName);
    });

    if (!budgetProjectSortConfig.column || budgetProjectSortConfig.direction === 'default') {
      return filtered;
    }
    const col = budgetProjectSortConfig.column;
    const sorted = [...filtered].sort((a, b) => {
      const aVal = getRawValue(a, col);
      const bVal = getRawValue(b, col);
      if (projectViewNumericColumns.has(col)) {
        const aNum = Number(aVal);
        const bNum = Number(bVal);
        if (!Number.isFinite(aNum) && !Number.isFinite(bNum)) return 0;
        if (!Number.isFinite(aNum)) return 1;
        if (!Number.isFinite(bNum)) return -1;
        return aNum - bNum;
      }
      return aVal.localeCompare(bVal);
    });
    return budgetProjectSortConfig.direction === 'asc' ? sorted : sorted.reverse();
  }, [
    budgetProjectSortConfig,
    getBudgetProjectCellValue,
    projectPassesBudgetProjectViewFilters,
    projectViewNumericColumns,
    topViewingBudgetMatchedCountByProject,
    topViewingBudgetProjectsForDistance,
  ]);
  const topViewingBudgetUnitsPageRows = React.useMemo(() => {
    const start = topViewingBudgetUnitsPage * TOP_VIEWING_PAGE_SIZE;
    return topViewingBudgetUnitsForDistance.slice(start, start + TOP_VIEWING_PAGE_SIZE);
  }, [topViewingBudgetUnitsForDistance, topViewingBudgetUnitsPage, TOP_VIEWING_PAGE_SIZE]);
  const topViewingBudgetProjectsPageRows = React.useMemo(() => {
    const start = topViewingBudgetProjectsPage * TOP_VIEWING_PAGE_SIZE;
    return budgetProjectRowsForDisplay.slice(start, start + TOP_VIEWING_PAGE_SIZE);
  }, [budgetProjectRowsForDisplay, topViewingBudgetProjectsPage, TOP_VIEWING_PAGE_SIZE]);
  React.useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(topViewingBudgetUnitsForDistance.length / TOP_VIEWING_PAGE_SIZE) - 1);
    setTopViewingBudgetUnitsPage((prev) => (prev > maxPage ? maxPage : prev));
  }, [topViewingBudgetUnitsForDistance.length, TOP_VIEWING_PAGE_SIZE]);
  React.useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(budgetProjectRowsForDisplay.length / TOP_VIEWING_PAGE_SIZE) - 1);
    setTopViewingBudgetProjectsPage((prev) => (prev > maxPage ? maxPage : prev));
  }, [budgetProjectRowsForDisplay.length, TOP_VIEWING_PAGE_SIZE]);
  React.useEffect(() => {
    setBudgetProjectMatchedBrExpand(null);
  }, [topViewingBudgetProjectsPage]);
  React.useEffect(() => {
    if (topViewingBudgetViewMode !== 'project') {
      setBudgetProjectMatchedBrExpand(null);
    }
  }, [topViewingBudgetViewMode]);
  const budgetTableContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [budgetTableViewport, setBudgetTableViewport] = React.useState<{ scrollTop: number; height: number }>({
    scrollTop: 0,
    height: 0,
  });
  const updateBudgetTableViewport = React.useCallback((forcedScrollTop?: number) => {
    const container = budgetTableContainerRef.current;
    if (!container) return;
    const nextScrollTop = forcedScrollTop ?? container.scrollTop;
    const nextHeight = container.clientHeight;
    setBudgetTableViewport((prev) => {
      if (prev.scrollTop === nextScrollTop && prev.height === nextHeight) {
        return prev;
      }
      return {
        scrollTop: nextScrollTop,
        height: nextHeight,
      };
    });
  }, []);
  const throttledBudgetTableScrollUpdate = useThrottle((scrollTop: number) => {
    updateBudgetTableViewport(scrollTop);
  }, 16);
  const handleBudgetTableScroll = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      throttledBudgetTableScrollUpdate(event.currentTarget.scrollTop);
    },
    [throttledBudgetTableScrollUpdate]
  );
  React.useEffect(() => {
    updateBudgetTableViewport();
  }, [
    topViewingBudgetProjectsPageRows.length,
    topViewingBudgetRegionTab,
    topViewingBudgetUnitsPageRows.length,
    topViewingBudgetViewMode,
    updateBudgetTableViewport,
  ]);
  React.useEffect(() => {
    const handleResize = () => updateBudgetTableViewport();
    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [updateBudgetTableViewport]);
  const budgetUnitColSpan = TOP_VIEWING_BUDGET_UNIT_COLUMN_KEYS.filter(
    (column) => (showBudgetDualPricing || column !== 'dps') && budgetUnitColumnVisibility[column] !== false,
  ).length + (topViewingBudgetRegionTab === 'SUPER' ? 1 : 0);
  const budgetProjectColSpan = PROJECT_VIEW_COLUMN_KEYS.length + 1 + Object.values(budgetMatchedVisibleBrColumns).filter(Boolean).length;
  const shouldVirtualizeBudgetUnitRows = topViewingBudgetUnitsPageRows.length > 40;
  const shouldVirtualizeBudgetProjectRows = topViewingBudgetProjectsPageRows.length > 40 && !budgetProjectMatchedBrExpand;
  const budgetUnitVirtualRows = React.useMemo(() => {
    if (!shouldVirtualizeBudgetUnitRows) {
      return {
        rows: topViewingBudgetUnitsPageRows,
        topSpacer: 0,
        bottomSpacer: 0,
      };
    }
    const rowHeight = isMobile ? 96 : 88;
    const overscan = 10;
    const total = topViewingBudgetUnitsPageRows.length;
    const viewportHeight = Math.max(1, budgetTableViewport.height || 680);
    const visibleCount = Math.ceil(viewportHeight / rowHeight);
    const maxStart = Math.max(0, total - visibleCount);
    const startIndex = Math.min(
      maxStart,
      Math.max(0, Math.floor(budgetTableViewport.scrollTop / rowHeight) - overscan)
    );
    const endIndex = Math.min(total, startIndex + visibleCount + overscan * 2);
    return {
      rows: topViewingBudgetUnitsPageRows.slice(startIndex, endIndex),
      topSpacer: startIndex * rowHeight,
      bottomSpacer: Math.max(0, (total - endIndex) * rowHeight),
    };
  }, [budgetTableViewport.height, budgetTableViewport.scrollTop, isMobile, shouldVirtualizeBudgetUnitRows, topViewingBudgetUnitsPageRows]);
  const budgetProjectVirtualRows = React.useMemo(() => {
    if (!shouldVirtualizeBudgetProjectRows) {
      return {
        rows: topViewingBudgetProjectsPageRows,
        topSpacer: 0,
        bottomSpacer: 0,
      };
    }
    const rowHeight = isMobile ? 64 : 56;
    const overscan = 10;
    const total = topViewingBudgetProjectsPageRows.length;
    const viewportHeight = Math.max(1, budgetTableViewport.height || 680);
    const visibleCount = Math.ceil(viewportHeight / rowHeight);
    const maxStart = Math.max(0, total - visibleCount);
    const startIndex = Math.min(
      maxStart,
      Math.max(0, Math.floor(budgetTableViewport.scrollTop / rowHeight) - overscan)
    );
    const endIndex = Math.min(total, startIndex + visibleCount + overscan * 2);
    return {
      rows: topViewingBudgetProjectsPageRows.slice(startIndex, endIndex),
      topSpacer: startIndex * rowHeight,
      bottomSpacer: Math.max(0, (total - endIndex) * rowHeight),
    };
  }, [budgetProjectMatchedBrExpand, budgetTableViewport.height, budgetTableViewport.scrollTop, isMobile, shouldVirtualizeBudgetProjectRows, topViewingBudgetProjectsPageRows]);
  const projectViewCategoryOptions = React.useMemo(() => {
    const projectNameSet = new Set<string>();
    const projectCategorySet = new Set<string>();
    const districtSet = new Set<string>();
    const regionSet = new Set<string>();
    const nearestMrtSet = new Set<string>();
    const tenureSet = new Set<string>();

    topViewingProjectsForProjectView.forEach((projectName) => {
      const normalizedProjectName = (projectName ?? '').trim();
      if (normalizedProjectName) projectNameSet.add(normalizedProjectName);
      const details = projectDetailsMap.get(projectName);
      const projectCategory = (details?.projectCategory ?? '').trim();
      const district = (details?.district ?? '').trim();
      const region = (details?.area ?? '').trim();
      const nearestMrt = (details?.nearestMrt ?? '').trim();
      const tenure = (details?.tenure ?? '').trim();
      if (projectCategory) projectCategorySet.add(projectCategory);
      if (district) districtSet.add(district);
      if (region) regionSet.add(region);
      if (nearestMrt) nearestMrtSet.add(nearestMrt);
      if (tenure) tenureSet.add(tenure);
    });

    return {
      projectName: Array.from(projectNameSet).sort((a, b) => a.localeCompare(b)),
      projectCategory: Array.from(projectCategorySet).sort((a, b) => a.localeCompare(b)),
      district: Array.from(districtSet).sort((a, b) => a.localeCompare(b)),
      region: Array.from(regionSet).sort((a, b) => a.localeCompare(b)),
      nearestMrt: Array.from(nearestMrtSet).sort((a, b) => a.localeCompare(b)),
      tenure: Array.from(tenureSet).sort((a, b) => a.localeCompare(b)),
    };
  }, [projectDetailsMap, topViewingProjectsForProjectView]);
  const topViewingFilteredProjectsForDisplay = React.useMemo(() => {
    const getProjectValue = (
      details: ProjectDetails | undefined,
      projectName: string,
      key: keyof typeof projectViewCategoryFilters
    ): string => {
      if (key === 'projectName') return (projectName ?? '').trim();
      if (!details) return '';
      if (key === 'projectCategory') return (details.projectCategory ?? '').trim();
      if (key === 'district') return (details.district ?? '').trim();
      if (key === 'region') return (details.area ?? '').trim();
      if (key === 'nearestMrt') return (details.nearestMrt ?? '').trim();
      return (details.tenure ?? '').trim();
    };
    const getDistrictLocationValue = (details: ProjectDetails | undefined): string => {
      if (!details) return '';
      if (details.districtLocation) return details.districtLocation;
      const parts = [details.area, details.district].filter(Boolean);
      return parts.length ? parts.join(' / ') : '';
    };
    const getNumericSortValue = (
      details: ProjectDetails | undefined,
      projectName: string,
      key: string
    ): number | null => {
      switch (key) {
        case 'distance':
          return getTopViewingDistanceFromReference(projectName);
        case 'totalUnits':
          return details?.totalUnits != null ? Number(details.totalUnits) : null;
        case 'soldUnits':
          return details?.soldUnits ?? null;
        case 'walkingTimeToMrt':
          return details?.walkingTimeToMrt ?? null;
        default: {
          const brMatch = key.match(/^(br[1-5]|ph)(avail|price|psf)$/);
          if (!brMatch) return null;
          const brKey = brMatch[1] as 'br1' | 'br2' | 'br3' | 'br4' | 'br5' | 'ph';
          const sub = brMatch[2];
          const brData = details?.[brKey];
          if (!brData) return null;
          if (sub === 'avail') return brData.availableUnits ?? null;
          if (sub === 'price') return brData.fromPrice ?? null;
          if (sub === 'psf') return brData.fromPsf ?? null;
          return null;
        }
      }
    };
    const getTextSortValue = (
      details: ProjectDetails | undefined,
      projectName: string,
      key: string
    ): string => {
      switch (key) {
        case 'projectName':
          return projectName ?? '';
        case 'projectCategory':
          return details?.projectCategory ?? '';
        case 'developer':
          return details?.developer ?? '';
        case 'districtLocation':
          return getDistrictLocationValue(details);
        case 'district':
          return details?.district ?? '';
        case 'region':
          return details?.area ?? '';
        case 'nearestMrt':
          return details?.nearestMrt ?? '';
        case 'tenure':
          return details?.tenure ?? '';
        case 'completedDate':
          return details?.completedDate ? new Date(details.completedDate).toISOString() : '';
        case 'distance': {
          const distance = getTopViewingDistanceFromReference(projectName);
          return distance != null ? String(distance) : '';
        }
        default:
          return '';
      }
    };

    const filtered = topViewingFilteredProjectsForView.filter((projectName) => {
      const details = projectDetailsMap.get(projectName);
      return (Object.keys(projectViewCategoryFilters) as Array<keyof typeof projectViewCategoryFilters>).every((key) => {
        const selected = projectViewCategoryFilters[key];
        if (selected.length === 0) return true;
        const value = getProjectValue(details, projectName, key);
        return value !== '' && selected.includes(value);
      });
    });

    if (!projectViewSortConfig.column || projectViewSortConfig.direction === 'default') {
      return filtered;
    }

    const sortColumn = projectViewSortConfig.column;
    const sorted = [...filtered].sort((a, b) => {
      const detailsA = projectDetailsMap.get(a);
      const detailsB = projectDetailsMap.get(b);
      let comparison = 0;

      if (projectViewNumericColumns.has(sortColumn)) {
        const aValue = getNumericSortValue(detailsA, a, sortColumn);
        const bValue = getNumericSortValue(detailsB, b, sortColumn);
        if (aValue == null && bValue == null) comparison = 0;
        else if (aValue == null) comparison = 1;
        else if (bValue == null) comparison = -1;
        else comparison = aValue - bValue;
      } else if (sortColumn === 'completedDate') {
        const aTime = detailsA?.completedDate ? new Date(detailsA.completedDate).getTime() : null;
        const bTime = detailsB?.completedDate ? new Date(detailsB.completedDate).getTime() : null;
        if (aTime == null && bTime == null) comparison = 0;
        else if (aTime == null) comparison = 1;
        else if (bTime == null) comparison = -1;
        else comparison = aTime - bTime;
      } else {
        const aValue = getTextSortValue(detailsA, a, sortColumn).toLowerCase();
        const bValue = getTextSortValue(detailsB, b, sortColumn).toLowerCase();
        comparison = aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' });
      }

      if (comparison === 0) {
        comparison = a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
      }

      return projectViewSortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [
    getTopViewingDistanceFromReference,
    projectDetailsMap,
    projectViewCategoryFilters,
    projectViewNumericColumns,
    projectViewSortConfig,
    topViewingFilteredProjectsForView,
  ]);
  const topViewingFilteredProjectNameSetForDisplay = React.useMemo(
    () => new Set(topViewingFilteredProjectsForDisplay),
    [topViewingFilteredProjectsForDisplay]
  );
  const topViewingComparableUnitsFromApi = React.useMemo(
    () =>
      topViewingFilteredComparableUnits.filter((unit) =>
        topViewingFilteredProjectNameSetForDisplay.has(unit.projectName)
      ),
    [topViewingFilteredComparableUnits, topViewingFilteredProjectNameSetForDisplay]
  );
  /** Price-matrix rows are merged into `topViewingFilteredComparableUnits` in SuperPickerApp so filters/sort apply here too. */
  const topViewingComparableUnitsForDisplay = topViewingComparableUnitsFromApi;
  const hasComparableExecutiveCondo = React.useMemo(
    () => topViewingComparableUnitsForDisplay.some((unit) => isExecutiveCondoByProjectName(unit.projectName)),
    [isExecutiveCondoByProjectName, topViewingComparableUnitsForDisplay]
  );
  const showComparableDualPricing = hasComparableExecutiveCondo;
  const availableUnitColumnsForMenu = React.useMemo(
    () => TOP_VIEWING_AVAILABLE_UNIT_COLUMN_KEYS.filter((column) => isExecutiveCondoProject || column !== 'dps'),
    [isExecutiveCondoProject],
  );
  const comparableUnitColumnsForMenu = React.useMemo(
    () => TOP_VIEWING_COMPARABLE_UNIT_COLUMN_KEYS.filter((column) => showComparableDualPricing || column !== 'dps'),
    [showComparableDualPricing],
  );
  const budgetUnitColumnsForMenu = React.useMemo(
    () => TOP_VIEWING_BUDGET_UNIT_COLUMN_KEYS.filter((column) => showBudgetDualPricing || column !== 'dps'),
    [showBudgetDualPricing],
  );
  const isAvailableUnitColumnVisible = React.useCallback(
    (column: (typeof TOP_VIEWING_AVAILABLE_UNIT_COLUMN_KEYS)[number]) => availableUnitColumnVisibility[column] !== false,
    [availableUnitColumnVisibility],
  );
  const isComparableUnitColumnVisible = React.useCallback(
    (column: (typeof TOP_VIEWING_COMPARABLE_UNIT_COLUMN_KEYS)[number]) => comparableUnitColumnVisibility[column] !== false,
    [comparableUnitColumnVisibility],
  );
  const isBudgetUnitColumnVisible = React.useCallback(
    (column: (typeof TOP_VIEWING_BUDGET_UNIT_COLUMN_KEYS)[number]) => budgetUnitColumnVisibility[column] !== false,
    [budgetUnitColumnVisibility],
  );
  const renderBudgetUnitViewTableCells = React.useCallback(
    (unit: (typeof topViewingBudgetUnitsForDistance)[number]) => {
      const isUnitShortlisted = shortlistByUnitId.has(unit.id);
      const distance = unit.distance ?? budgetProjectDistanceByName.get(unit.projectName) ?? null;
      const npsPrice = unit.priceNps ?? unit.price;
      const dpsPrice = unit.priceDps ?? null;
      const npsPsf = unit.psfNps ?? unit.psf;
      const isBudgetExecutiveCondo = isExecutiveCondoByProjectName(unit.projectName);
      return (
        <>
          {isBudgetUnitColumnVisible('projectName') && (
            <TableCell
              sx={{
                position: 'sticky',
                left: 0,
                zIndex: 2,
                minWidth: 190,
                backgroundColor: 'var(--color-bg)',
                borderRight: '1px solid rgba(var(--color-text-rgb), 0.08)',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
                  <Typography variant="body2">{unit.projectName}</Typography>
                  {topViewingReferenceUnit && (topViewingReferenceUnit.id === unit.id || (topViewingReferenceUnit.projectName === unit.projectName && topViewingReferenceUnit.unitNumber === unit.unitNumber)) && (
                    <Tooltip title="Reference unit" arrow placement="top">
                      <BookmarkIcon sx={{ fontSize: '0.9rem !important', color: '#1976d2', flexShrink: 0 }} />
                    </Tooltip>
                  )}
                  {topViewingFloorPlansToCompare.some((fp) => fp.unit.id === unit.id) && (
                    <Tooltip title="In compare basket" arrow placement="top">
                      <ShoppingCartIcon sx={{ fontSize: '0.9rem !important', color: '#FFEB3B', flexShrink: 0 }} />
                    </Tooltip>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleOpenTowerView(unit as unknown as ComparableUnit);
                    }}
                    sx={{ color: 'rgba(var(--color-text-rgb), 0.5)', '&:hover': { bgcolor: 'rgba(var(--color-text-rgb), 0.1)' } }}
                    title="View Tower View"
                  >
                    <ViewModuleIcon fontSize="small" />
                  </IconButton>
                  {unit.floorplan && (
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleOpenFloorPlan(unit as unknown as ComparableUnit);
                      }}
                      sx={{
                        color: 'primary.main',
                        '&:hover': { bgcolor: 'rgba(var(--color-text-rgb), 0.1)' },
                      }}
                      title="View Floor Plan"
                    >
                      <ImageIcon fontSize="small" />
                    </IconButton>
                  )}
                  {!hideShortlist && (
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleShortlistAction({
                          unitId: unit.id,
                          label: `${unit.projectName} ${unit.unitNumber}`,
                          floorplan: unit.floorplan || null,
                        });
                      }}
                      disabled={shortlistUpdatingUnitId === unit.id}
                      sx={{
                        color: isUnitShortlisted ? 'var(--color-warning-strong)' : 'var(--color-text-strong)',
                        '&:hover': { bgcolor: 'rgba(var(--color-text-rgb), 0.1)' },
                      }}
                      title={isUnitShortlisted ? 'Remove from shortlist' : 'Add to shortlist'}
                    >
                      {isUnitShortlisted ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                    </IconButton>
                  )}
                </Box>
              </Box>
            </TableCell>
          )}
          {isBudgetUnitColumnVisible('projectCategory') && <TableCell>{getBudgetProjectCellValue(unit.projectName, 'projectCategory')}</TableCell>}
          {isBudgetUnitColumnVisible('blockNumber') && <TableCell>{formatBlockNumber(unit.blockNumber)}</TableCell>}
          {isBudgetUnitColumnVisible('unitNumber') && <TableCell>{unit.unitNumber}</TableCell>}
          {isBudgetUnitColumnVisible('floor') && <TableCell align="center">{unit.floor != null && unit.floor !== 0 ? unit.floor : '-'}</TableCell>}
          {isBudgetUnitColumnVisible('stack') && <TableCell align="center">{unit.stack != null && unit.stack !== 0 ? unit.stack : '-'}</TableCell>}
          {isBudgetUnitColumnVisible('bedroom') && <TableCell align="center">{unit.bedroom}</TableCell>}
          {isBudgetUnitColumnVisible('bathroom') && <TableCell align="center">{unit.bathroom}</TableCell>}
          {isBudgetUnitColumnVisible('areaSqft') && <TableCell align="right">{formatNumber(unit.areaSqft)}</TableCell>}
          {isBudgetUnitColumnVisible('price') && <TableCell align="right">{npsPrice > 0 ? formatCurrency(npsPrice) : '-'}</TableCell>}
          {isBudgetUnitColumnVisible('psf') && <TableCell align="right">{npsPsf > 0 ? formatCurrency(npsPsf) : '-'}</TableCell>}
          {showBudgetDualPricing && isBudgetUnitColumnVisible('dps') && (
            <TableCell align="right">{renderDpsValue(dpsPrice, isBudgetExecutiveCondo)}</TableCell>
          )}
          {isBudgetUnitColumnVisible('outlay') && (
            <TableCell
              align="right"
              onClick={(event) => {
                if (unit.outlay <= 0) return;
                openTopViewingFormulaPopover(event, getOutlayFormulaTooltip(unit.price, unit.outlay));
              }}
              sx={{ cursor: unit.outlay > 0 ? 'pointer' : 'default' }}
            >
              {unit.outlay > 0 ? (
                <Typography component="span" variant="body2" sx={{ cursor: 'pointer', textDecoration: 'underline dotted rgba(var(--color-text-rgb), 0.45)', textUnderlineOffset: 3 }}>
                  {formatCurrency(unit.outlay)}
                </Typography>
              ) : '-'}
            </TableCell>
          )}
          {isBudgetUnitColumnVisible('installment') && (
            <TableCell
              align="right"
              onClick={(event) => {
                if (unit.installment <= 0) return;
                openTopViewingFormulaPopover(event, getInstallmentFormulaTooltip(unit.price, unit.installment));
              }}
              sx={{ cursor: unit.installment > 0 ? 'pointer' : 'default' }}
            >
              {unit.installment > 0 ? (
                <Typography component="span" variant="body2" sx={{ cursor: 'pointer', textDecoration: 'underline dotted rgba(var(--color-text-rgb), 0.45)', textUnderlineOffset: 3 }}>
                  {formatCurrency(unit.installment)}
                </Typography>
              ) : '-'}
            </TableCell>
          )}
          {isBudgetUnitColumnVisible('distance') && (
            <TableCell align="right">
              {distance != null ? `${distance.toFixed(2)} km` : '-'}
            </TableCell>
          )}
          {isBudgetUnitColumnVisible('district') && <TableCell>{getBudgetProjectCellValue(unit.projectName, 'district')}</TableCell>}
          {isBudgetUnitColumnVisible('region') && <TableCell>{getBudgetProjectCellValue(unit.projectName, 'region')}</TableCell>}
          {isBudgetUnitColumnVisible('tenure') && <TableCell>{getBudgetProjectCellValue(unit.projectName, 'tenure')}</TableCell>}
          {isBudgetUnitColumnVisible('completedDate') && <TableCell>{getBudgetProjectCellValue(unit.projectName, 'completedDate')}</TableCell>}
          {isBudgetUnitColumnVisible('nearestMrt') && <TableCell>{getBudgetProjectCellValue(unit.projectName, 'nearestMrt')}</TableCell>}
          {isBudgetUnitColumnVisible('walkingTimeToMrt') && <TableCell>{getBudgetProjectCellValue(unit.projectName, 'walkingTimeToMrt')}</TableCell>}
          {topViewingBudgetRegionTab === 'SUPER' && (
            <TableCell>
              {unit.superReasons && unit.superReasons.length > 0
                ? unit.superReasons.join(', ')
                : '-'}
            </TableCell>
          )}
        </>
      );
    },
    [
      budgetProjectDistanceByName,
      formatBlockNumber,
      formatCurrency,
      formatNumber,
      getBudgetProjectCellValue,
      getInstallmentFormulaTooltip,
      getOutlayFormulaTooltip,
      handleOpenFloorPlan,
      handleOpenTowerView,
      handleShortlistAction,
      hideShortlist,
      isBudgetUnitColumnVisible,
      isExecutiveCondoByProjectName,
      openTopViewingFormulaPopover,
      renderDpsValue,
      shortlistByUnitId,
      shortlistUpdatingUnitId,
      showBudgetDualPricing,
      topViewingBudgetRegionTab,
      topViewingFloorPlansToCompare,
      topViewingReferenceUnit,
    ],
  );
  const comparableUnitVisibleColumnCount = React.useMemo(
    () => comparableUnitColumnsForMenu.filter((column) => comparableUnitColumnVisibility[column] !== false).length,
    [comparableUnitColumnVisibility, comparableUnitColumnsForMenu],
  );
  const toggleAvailableUnitColumn = React.useCallback((column: string) => {
    setAvailableUnitColumnVisibility((prev) => ({ ...prev, [column]: prev[column] === false }));
  }, []);
  const toggleComparableUnitColumn = React.useCallback((column: string) => {
    setComparableUnitColumnVisibility((prev) => ({ ...prev, [column]: prev[column] === false }));
  }, []);
  const toggleBudgetUnitColumn = React.useCallback((column: string) => {
    setBudgetUnitColumnVisibility((prev) => ({ ...prev, [column]: prev[column] === false }));
  }, []);
  const resetAvailableUnitColumns = React.useCallback(() => {
    setAvailableUnitColumnVisibility(createInitialColumnVisibility(TOP_VIEWING_AVAILABLE_UNIT_COLUMN_KEYS, { showPsfColumnByDefault, hiddenColumns: ['distance'] }));
  }, [showPsfColumnByDefault]);
  const resetComparableUnitColumns = React.useCallback(() => {
    setComparableUnitColumnVisibility(createInitialColumnVisibility(TOP_VIEWING_COMPARABLE_UNIT_COLUMN_KEYS, { showPsfColumnByDefault }));
  }, [showPsfColumnByDefault]);
  const resetBudgetUnitColumns = React.useCallback(() => {
    setBudgetUnitColumnVisibility(createInitialColumnVisibility(TOP_VIEWING_BUDGET_UNIT_COLUMN_KEYS));
  }, []);
  const topViewingAvailableUnitsPageRows = React.useMemo(() => {
    const start = topViewingAvailableUnitsPage * TOP_VIEWING_PAGE_SIZE;
    return topViewingSortedUnits.slice(start, start + TOP_VIEWING_PAGE_SIZE);
  }, [topViewingAvailableUnitsPage, topViewingSortedUnits, TOP_VIEWING_PAGE_SIZE]);
  const topViewingDistinctStacks = React.useMemo(() => {
    const set = new Set<string>();
    topViewingFilteredAvailableUnits.forEach((u) => {
      const parsed = parseUnitNumber(u.unitNumber, (u as TowerViewUnit).floorNumber);
      if (parsed.stack != null && parsed.stack > 0) set.add(String(parsed.stack));
    });
    return Array.from(set).sort((a, b) => Number(a) - Number(b));
  }, [topViewingFilteredAvailableUnits, parseUnitNumber]);
  const topViewingDistinctUnitTypeDescriptions = React.useMemo(() => {
    const set = new Set<string>();
    topViewingFilteredAvailableUnits.forEach((u) => {
      if (u.unitTypeDescription) set.add(u.unitTypeDescription);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [topViewingFilteredAvailableUnits]);
  const topViewingComparableUnitsPageRows = React.useMemo(() => {
    const start = topViewingComparableUnitsPage * TOP_VIEWING_PAGE_SIZE;
    return topViewingComparableUnitsForDisplay.slice(start, start + TOP_VIEWING_PAGE_SIZE);
  }, [topViewingComparableUnitsForDisplay, topViewingComparableUnitsPage, TOP_VIEWING_PAGE_SIZE]);
  const topViewingComparableProjectsPageRows = React.useMemo(() => {
    const start = topViewingComparableProjectsPage * TOP_VIEWING_PAGE_SIZE;
    return topViewingFilteredProjectsForDisplay.slice(start, start + TOP_VIEWING_PAGE_SIZE);
  }, [topViewingComparableProjectsPage, topViewingFilteredProjectsForDisplay, TOP_VIEWING_PAGE_SIZE]);
  React.useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(topViewingSortedUnits.length / TOP_VIEWING_PAGE_SIZE) - 1);
    setTopViewingAvailableUnitsPage((prev) => (prev > maxPage ? maxPage : prev));
  }, [topViewingSortedUnits.length, TOP_VIEWING_PAGE_SIZE]);
  React.useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(topViewingComparableUnitsForDisplay.length / TOP_VIEWING_PAGE_SIZE) - 1);
    setTopViewingComparableUnitsPage((prev) => (prev > maxPage ? maxPage : prev));
  }, [topViewingComparableUnitsForDisplay.length, TOP_VIEWING_PAGE_SIZE]);
  React.useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(topViewingFilteredProjectsForDisplay.length / TOP_VIEWING_PAGE_SIZE) - 1);
    setTopViewingComparableProjectsPage((prev) => (prev > maxPage ? maxPage : prev));
  }, [topViewingFilteredProjectsForDisplay.length, TOP_VIEWING_PAGE_SIZE]);
  const topViewingMatchedCount = topViewingComparableUnitsForDisplay.length;
  const topViewingMatchedBedroomCount = React.useMemo(() => {
    const referenceBedroom = normalizeBedroomValue(topViewingReferenceUnit?.bedroom);
    if (referenceBedroom == null) return 0;
    return topViewingComparableUnitsForDisplay.filter(
      (unit) => normalizeBedroomValue(unit.bedroom) === referenceBedroom
    ).length;
  }, [normalizeBedroomValue, topViewingComparableUnitsForDisplay, topViewingReferenceUnit?.bedroom]);
  const topViewingComparableFilterSummaryParts = React.useMemo(() => {
    const parts: string[] = [];
    if (topViewingCompFilters.projectName.length > 0) {
      parts.push(`Project: ${topViewingCompFilters.projectName.join(', ')}`);
    }
    if (topViewingCompFilters.projectCategory.length > 0) {
      parts.push(`Category: ${topViewingCompFilters.projectCategory.join(', ')}`);
    }
    if (topViewingCompFilters.blockNumber.trim()) {
      parts.push(`Block: ${topViewingCompFilters.blockNumber.trim()}`);
    }
    const compRangeLabels: Record<string, string> = {
      floor: 'Floor',
      stack: 'Stack',
      bedroom: 'Bedroom',
      bathroom: 'Bath',
      areaSqft: 'Area',
      price: 'Price',
      psf: 'PSF',
      dps: 'DPS',
      outlay: 'Outlay',
      installment: 'Installment',
      distance: 'Distance',
    };
    Object.entries(compRangeLabels).forEach(([key, label]) => {
      const range = topViewingCompRangeFiltersApplied[key];
      if (!range) return;
      const min = range.min?.trim();
      const max = range.max?.trim();
      if (min && max) parts.push(`${label}: ${min}-${max}`);
      else if (min) parts.push(`${label}: ≥${min}`);
      else if (max) parts.push(`${label}: ≤${max}`);
    });
    if (topViewingThumbsUpFilterActive) {
      parts.push('Thumbs up only');
    }
    (Object.keys(projectViewCategoryFilters) as Array<keyof typeof projectViewCategoryFilters>).forEach((key) => {
      const values = projectViewCategoryFilters[key];
      if (values.length > 0) {
        const label = key === 'nearestMrt'
          ? 'MRT'
          : key === 'projectCategory'
          ? 'Project Category'
          : key.charAt(0).toUpperCase() + key.slice(1);
        parts.push(`${label}: ${values.join(', ')}`);
      }
    });
    Object.entries(projectViewFilters).forEach(([key, value]) => {
      const trimmed = (value ?? '').trim();
      if (!trimmed) return;
      const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
      parts.push(`${label}: ${trimmed}`);
    });
    Object.entries(projectViewRangeFilters).forEach(([key, range]) => {
      const min = range?.min?.trim();
      const max = range?.max?.trim();
      if (!min && !max) return;
      const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
      if (min && max) parts.push(`${label}: ${min}-${max}`);
      else if (min) parts.push(`${label}: ≥${min}`);
      else if (max) parts.push(`${label}: ≤${max}`);
    });
    return parts;
  }, [
    projectViewCategoryFilters,
    projectViewFilters,
    projectViewRangeFilters,
    topViewingCompFilters.blockNumber,
    topViewingCompFilters.projectCategory,
    topViewingCompFilters.projectName,
    topViewingCompRangeFiltersApplied,
    topViewingThumbsUpFilterActive,
  ]);
  const getProjectViewIconButtonSx = React.useCallback((column: string) => {
    const base = {
      p: 0.5,
      color: 'var(--color-text-strong)',
      '&:hover': {
        bgcolor: 'rgba(var(--color-text-rgb), 0.1)',
        color: 'var(--color-accent)',
      },
    };

    const categoryColumns: Array<keyof typeof projectViewCategoryFilters> = ['projectName', 'projectCategory', 'district', 'region', 'nearestMrt', 'tenure'];
    if (categoryColumns.includes(column as keyof typeof projectViewCategoryFilters)) {
      const selected = projectViewCategoryFilters[column as keyof typeof projectViewCategoryFilters] ?? [];
      if (selected.length > 0) {
        return { ...base, color: 'var(--color-accent)', bgcolor: 'rgba(255, 152, 0, 0.15)' };
      }
    }

    const range = projectViewRangeFilters[column] ?? { min: '', max: '' };
    if ((range.min ?? '').trim() !== '' || (range.max ?? '').trim() !== '') {
      return { ...base, color: 'var(--color-accent)', bgcolor: 'rgba(255, 152, 0, 0.15)' };
    }

    const textFilter = (projectViewFilters[column] ?? '').trim();
    if (textFilter !== '') {
      return { ...base, color: 'var(--color-accent)', bgcolor: 'rgba(255, 152, 0, 0.15)' };
    }

    if (column === 'districtLocation' && projectViewDistrictFilter.length > 0) {
      return { ...base, color: 'var(--color-accent)', bgcolor: 'rgba(255, 152, 0, 0.15)' };
    }

    if (projectViewSortConfig.column === column && projectViewSortConfig.direction !== 'default') {
      return { ...base, color: 'var(--color-info)', bgcolor: 'rgba(var(--color-info-rgb), 0.15)' };
    }

    return base;
  }, [projectViewCategoryFilters, projectViewDistrictFilter, projectViewFilters, projectViewRangeFilters, projectViewSortConfig]);
  const handleClearAllComparableFilters = React.useCallback(() => {
    setTopViewingCompFilters({
      projectName: topViewingReferenceUnit?.projectName ? [topViewingReferenceUnit.projectName] : [],
      projectCategory: [],
      blockNumber: '',
      unitTypeDescription: [],
    });
    setTopViewingCompRangeFilters({
      floor: { min: '', max: '' },
      stack: { min: '', max: '' },
      bedroom: { min: '', max: '' },
      bathroom: { min: '', max: '' },
      areaSqft: { min: '', max: '' },
      price: { min: '', max: '' },
      psf: { min: '', max: '' },
      outlay: { min: '', max: '' },
      installment: { min: '', max: '' },
      distance: { min: '', max: '' },
    });
    setTopViewingThumbsUpFilterActive(false);
    setProjectViewFilters({});
    setProjectViewRangeFilters({});
    setProjectViewCategoryFilters({
      projectName: topViewingReferenceUnit?.projectName ? [topViewingReferenceUnit.projectName] : [],
      projectCategory: [],
      district: [],
      region: [],
      nearestMrt: [],
      tenure: [],
    });
    setProjectViewDistrictFilter([]);
    setProjectViewSortConfig({ column: null, direction: 'default' });
    handleTopViewingCompClosePopover();
    setProjectViewPopoverAnchor(null);
  }, [
    handleTopViewingCompClosePopover,
    setProjectViewDistrictFilter,
    setProjectViewFilters,
    setProjectViewPopoverAnchor,
    setProjectViewRangeFilters,
    setProjectViewSortConfig,
    setTopViewingCompFilters,
    setTopViewingCompRangeFilters,
    setTopViewingThumbsUpFilterActive,
    topViewingReferenceUnit?.projectName,
  ]);

  const renderTopViewingBestBedroomContent = () => {
    const isBestPsfBedroomView = topViewingAvailableUnitsViewMode === 'bestPsfBedroom';
    const bestByUnitTypeData = isBestPsfBedroomView
      ? topViewingBestPsfByUnitTypeData
      : topViewingBestByUnitTypeData;
    const bestByUnitTypeLoading = isBestPsfBedroomView ? false : topViewingBestByUnitTypeLoading;
    const showSecondaryPsf = !isBestPsfBedroomView && bestByUnitTypeData?.showPsfInTowerView !== false;
    const emptyMessage = isBestPsfBedroomView
      ? 'Select a project to view BEST PSF BEDROOM TYPE.'
      : 'Select a project to view BEST PX BEDROOM TYPE.';
    const lockedMessage = isBestPsfBedroomView
      ? 'BEST PSF BEDROOM TYPE will be unlocked after official pricing is released.'
      : 'BEST PX BEDROOM TYPE will be unlocked after official pricing is released.';
    const summaryHeading = isBestPsfBedroomView ? 'Best PSF unit by type' : 'Cheapest unit by type';
    const primaryColumnLabel = isBestPsfBedroomView ? 'PSF' : 'Price ($)';
    const ecPrimaryDpsLabel = isBestPsfBedroomView ? 'DPS PSF' : 'DPS Price ($)';
    const ecPrimaryNpsLabel = isBestPsfBedroomView ? 'NPS PSF' : 'NPS Price ($)';

    if (!topViewingBestPxProjectName) {
      return (
        <Box
          sx={{
            width: '100%',
            border: '1px dashed rgba(var(--color-text-rgb), 0.2)',
            borderRadius: 1.5,
            p: 4,
            textAlign: 'center',
            bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
            color: 'rgba(var(--color-text-rgb), 0.65)',
          }}
        >
          <Typography variant="body2">
            {emptyMessage}
          </Typography>
        </Box>
      );
    }

    if (isTopViewingIndicativeBeforeLaunch && !priceMatrixSimulationSession) {
      const hasAnyBestPxUnits = bestByUnitTypeData?.unitTypes?.some(
        (ut) => (ut.units?.length ?? 0) > 0,
      );
      if (!bestByUnitTypeLoading && !hasAnyBestPxUnits) {
        return (
          <Box
            sx={{
              width: '100%',
              border: '1px dashed rgba(var(--color-text-rgb), 0.2)',
              borderRadius: 1.5,
              p: 4,
              textAlign: 'center',
              bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
              color: 'rgba(var(--color-text-rgb), 0.65)',
              filter: 'grayscale(1)',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Not Available Before Price Release
            </Typography>
            <Typography variant="body2">
              {lockedMessage}
            </Typography>
          </Box>
        );
      }
    }

    return (
      <Box sx={{ width: '100%' }}>
        {!bestByUnitTypeLoading && bestByUnitTypeData && (bestByUnitTypeData.unitTypes?.length ?? 0) > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: 'var(--color-heading)', fontWeight: 600, mb: 1 }}>
              {summaryHeading}
            </Typography>
            <TableContainer
              sx={{
                border: '1px solid rgba(var(--color-text-rgb), 0.1)',
                borderRadius: 1,
                mb: 2,
                overflowX: 'auto',
              }}
            >
              <Table
                size="small"
                sx={{
                  minWidth: 620,
                  '& .MuiTableCell-root': {
                    py: 0.75,
                    px: 1.5,
                    color: 'var(--color-text-strong)',
                    borderColor: 'rgba(var(--color-text-rgb), 0.1)',
                    fontSize: '0.8rem',
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    bgcolor: 'var(--color-surface-elevated)',
                    color: 'var(--color-heading)',
                    fontWeight: 600,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Unit type</TableCell>
                    <TableCell>Unit no</TableCell>
                    <TableCell align="right">Area (sqft)</TableCell>
                    {bestByUnitTypeData.isExecutiveCondo ? (
                      <>
                        <TableCell align="right">{ecPrimaryDpsLabel}</TableCell>
                        {showSecondaryPsf && <TableCell align="right">DPS PSF</TableCell>}
                        <TableCell align="right">{ecPrimaryNpsLabel}</TableCell>
                        {showSecondaryPsf && <TableCell align="right">NPS PSF</TableCell>}
                      </>
                    ) : (
                      <>
                        <TableCell align="right">{primaryColumnLabel}</TableCell>
                        {showSecondaryPsf && <TableCell align="right">PSF</TableCell>}
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(bestByUnitTypeData.unitTypes ?? [])
                    .filter((row) => row.units.length > 0)
                    .map((row) => {
                      const cheapest = row.units[0];
                      return (
                        <TableRow
                          key={row.id}
                          hover
                          onClick={(event) => handleTopViewingBestPxUnitClick(event, cheapest.unitNumber, cheapest.blockNumber)}
                          sx={{
                            cursor: (
                              topViewingBestPxUnitsByKey.has(`${topViewingBestPxProjectName.toLowerCase()}::${(cheapest.blockNumber || '').toLowerCase()}::${cheapest.unitNumber.toLowerCase()}`) ||
                              topViewingBestPxUnitsByKey.has(`${topViewingBestPxProjectName.toLowerCase()}::::${cheapest.unitNumber.toLowerCase()}`)
                            ) ? 'pointer' : 'default',
                          }}
                        >
                          <TableCell sx={{ fontWeight: 500 }}>{((row.description ?? row.unitType) || 'Unit type').replace(/Bedroom/gi, 'BR')}</TableCell>
                          <TableCell>{cheapest.unitNumber}</TableCell>
                          <TableCell align="right">{cheapest.totalAreaSqft != null ? formatNumber(cheapest.totalAreaSqft) : '-'}</TableCell>
                          {bestByUnitTypeData.isExecutiveCondo ? (
                            <>
                              <TableCell align="right">
                                {isBestPsfBedroomView
                                  ? (cheapest.psfDps != null ? `${formatCurrency(cheapest.psfDps)} PSF` : cheapest.psf != null ? `${formatCurrency(cheapest.psf)} PSF` : '-')
                                  : (cheapest.sellingPriceDps != null ? formatCurrency(cheapest.sellingPriceDps) : cheapest.sellingPrice != null ? formatCurrency(cheapest.sellingPrice) : '-')}
                              </TableCell>
                              {showSecondaryPsf && (
                                <TableCell align="right">{cheapest.psfDps != null ? `${formatCurrency(cheapest.psfDps)} PSF` : cheapest.psf != null ? `${formatCurrency(cheapest.psf)} PSF` : '-'}</TableCell>
                              )}
                              <TableCell align="right">
                                {isBestPsfBedroomView
                                  ? (cheapest.psfNps != null ? `${formatCurrency(cheapest.psfNps)} PSF` : '-')
                                  : (cheapest.sellingPriceNps != null ? formatCurrency(cheapest.sellingPriceNps) : '-')}
                              </TableCell>
                              {showSecondaryPsf && (
                                <TableCell align="right">{cheapest.psfNps != null ? `${formatCurrency(cheapest.psfNps)} PSF` : '-'}</TableCell>
                              )}
                            </>
                          ) : (
                            <>
                              <TableCell align="right">
                                {isBestPsfBedroomView
                                  ? (cheapest.psf != null ? `${formatCurrency(cheapest.psf)} PSF` : '-')
                                  : (cheapest.sellingPrice != null ? formatCurrency(cheapest.sellingPrice) : '-')}
                              </TableCell>
                              {showSecondaryPsf && (
                                <TableCell align="right">{cheapest.psf != null ? `${formatCurrency(cheapest.psf)} PSF` : '-'}</TableCell>
                              )}
                            </>
                          )}
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              flex: { md: '1 1 65%' },
              width: '100%',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2,
              minWidth: 0,
            }}
          >
            {bestByUnitTypeLoading ? (
              <Box sx={{ gridColumn: '1 / -1', py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: 'var(--color-accent)' }} />
              </Box>
            ) : bestByUnitTypeData?.unitTypes?.length ? (
              bestByUnitTypeData.unitTypes.map((row) => (
                <Box key={row.id} sx={{ border: '1px solid rgba(var(--color-text-rgb), 0.1)', borderRadius: 1, overflow: 'hidden' }}>
                  <Typography variant="subtitle2" sx={{ px: 1.5, py: 1, bgcolor: 'var(--color-surface-elevated)', color: 'var(--color-heading)', fontWeight: 600 }}>
                    {((row.description ?? row.unitType) || 'Unit type').replace(/Bedroom/gi, 'BR')}
                  </Typography>
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table
                      size="small"
                      sx={{
                        minWidth: 640,
                        '& .MuiTableCell-root': {
                          py: 0.75,
                          px: 1,
                          color: 'var(--color-text-strong)',
                          borderColor: 'rgba(var(--color-text-rgb), 0.1)',
                          fontSize: '0.8rem',
                        },
                        '& .MuiTableHead-root .MuiTableCell-root': {
                          bgcolor: 'var(--color-surface-elevated)',
                          color: 'var(--color-heading)',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>UNIT NO</TableCell>
                          <TableCell>BEDRM TYPE</TableCell>
                          <TableCell>TYPE</TableCell>
                          <TableCell align="right">AREA</TableCell>
                          {bestByUnitTypeData?.isExecutiveCondo ? (
                            <>
                              <TableCell align="right">{isBestPsfBedroomView ? 'DPS PSF' : 'DPS PRICE ($)'}</TableCell>
                              {showSecondaryPsf && <TableCell align="right">DPS PSF</TableCell>}
                              <TableCell align="right">{isBestPsfBedroomView ? 'NPS PSF' : 'NPS PRICE ($)'}</TableCell>
                              {showSecondaryPsf && <TableCell align="right">NPS PSF</TableCell>}
                            </>
                          ) : (
                            <>
                              <TableCell align="right">{isBestPsfBedroomView ? 'PSF' : 'PRICE ($)'}</TableCell>
                              {showSecondaryPsf && <TableCell align="right">PSF</TableCell>}
                            </>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {row.units.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={
                                bestByUnitTypeData?.isExecutiveCondo
                                  ? (showSecondaryPsf ? 8 : 6)
                                  : (showSecondaryPsf ? 6 : 5)
                              }
                              align="center"
                              sx={{ py: 2 }}
                            >
                              No units
                            </TableCell>
                          </TableRow>
                        ) : (
                          row.units.map((u, idx) => (
                            <TableRow
                              key={`${row.id}-${u.unitNumber}-${idx}`}
                              hover
                              onClick={(event) => handleTopViewingBestPxUnitClick(event, u.unitNumber, u.blockNumber)}
                              sx={{
                                cursor: (
                                  topViewingBestPxUnitsByKey.has(`${topViewingBestPxProjectName.toLowerCase()}::${(u.blockNumber || '').toLowerCase()}::${u.unitNumber.toLowerCase()}`) ||
                                  topViewingBestPxUnitsByKey.has(`${topViewingBestPxProjectName.toLowerCase()}::::${u.unitNumber.toLowerCase()}`)
                                ) ? 'pointer' : 'default',
                              }}
                            >
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{u.unitNumber}</TableCell>
                              <TableCell>{((row.description ?? row.unitType) || '').replace(/Bedroom/gi, 'BR')}</TableCell>
                              <TableCell>{u.type ?? '-'}</TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{u.totalAreaSqft != null ? formatNumber(u.totalAreaSqft) : '-'}</TableCell>
                              {bestByUnitTypeData?.isExecutiveCondo ? (
                                <>
                                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    {isBestPsfBedroomView
                                      ? (u.psfDps != null ? `${formatCurrency(u.psfDps)} PSF` : u.psf != null ? `${formatCurrency(u.psf)} PSF` : '-')
                                      : (u.sellingPriceDps != null ? formatCurrency(u.sellingPriceDps) : u.sellingPrice != null ? formatCurrency(u.sellingPrice) : '-')}
                                  </TableCell>
                                  {showSecondaryPsf && (
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{u.psfDps != null ? `${formatCurrency(u.psfDps)} PSF` : u.psf != null ? `${formatCurrency(u.psf)} PSF` : '-'}</TableCell>
                                  )}
                                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    {isBestPsfBedroomView
                                      ? (u.psfNps != null ? `${formatCurrency(u.psfNps)} PSF` : '-')
                                      : (u.sellingPriceNps != null ? formatCurrency(u.sellingPriceNps) : '-')}
                                  </TableCell>
                                  {showSecondaryPsf && (
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{u.psfNps != null ? `${formatCurrency(u.psfNps)} PSF` : '-'}</TableCell>
                                  )}
                                </>
                              ) : (
                                <>
                                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    {isBestPsfBedroomView
                                      ? (u.psf != null ? `${formatCurrency(u.psf)} PSF` : '-')
                                      : (u.sellingPrice != null ? formatCurrency(u.sellingPrice) : '-')}
                                  </TableCell>
                                  {showSecondaryPsf && (
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{u.psf != null ? `${formatCurrency(u.psf)} PSF` : '-'}</TableCell>
                                  )}
                                </>
                              )}
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))
            ) : (
              <Box sx={{ gridColumn: '1 / -1', py: 4, color: 'var(--color-text-muted)', textAlign: 'center' }}>
                <Typography variant="body2">No unit types for this project.</Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              flex: { md: '0 0 33%' },
              width: '100%',
              minWidth: 0,
              position: { md: 'sticky' },
              top: { md: 16 },
            }}
          >
            <Typography variant="subtitle2" sx={{ color: 'var(--color-heading)', mb: 1 }}>
              Site map
            </Typography>
            {topViewingBestPxBedroomSitePlan?.imageUrl ? (
              <Box
                component="img"
                src={topViewingBestPxBedroomSitePlan.imageUrl}
                alt="Site plan"
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: { md: 400 },
                  objectFit: 'contain',
                  borderRadius: 1,
                  border: '1px solid rgba(var(--color-text-rgb), 0.1)',
                }}
              />
            ) : (
              <Button
                variant="outlined"
                size="small"
                onClick={() => topViewingBestPxProjectName && handleOpenSitePlanForProject(topViewingBestPxProjectName)}
                sx={{ color: 'var(--color-accent)', borderColor: 'rgba(var(--color-accent-rgb), 0.5)' }}
              >
                View site map
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <>
      {/* Back to List / Project Essentials + Refresh */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            if (fromProjectEssential && onBackToProjectEssential) {
              onBackToProjectEssential();
              return;
            }
            setSelectedTopViewingSitePlan(null);
          }}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {fromProjectEssential ? '← Back to Project Essentials' : '← Back to List'}
        </Button>
        <Box sx={{ flex: 1 }} />
        <IconButton
          size="small"
          disabled={refreshing || priceMatrixSimulationRefreshing}
          onClick={async () => {
            if (selectedTopViewingSitePlan) {
              setRefreshing(true);
              try {
                await handleSelectTopViewingSitePlan(selectedTopViewingSitePlan, { forceRefresh: true });
                if (priceMatrixSimulationSession && currentProjectId && onRefreshPriceMatrixSimulation) {
                  await onRefreshPriceMatrixSimulation(currentProjectId);
                }
              } finally {
                setRefreshing(false);
              }
            }
          }}
          sx={{
            border: '1px solid',
            borderColor: 'rgba(var(--color-text-rgb), 0.23)',
            borderRadius: 1,
            flexShrink: 0,
          }}
          title="Refresh"
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Project Details Section */}
      {(projectDetails || referenceUnit) && (
          <Card
            data-tutorial="project-information"
            sx={{
              ...mobileOptimizedStyles,
              mb: 4,
              bgcolor: 'var(--color-surface)',
              borderLeft: '4px solid var(--color-accent)',
              boxShadow: '0 2px 8px rgba(var(--color-shadow-rgb), 0.15)',
              overflow: 'visible',
              // GPU translateZ from mobileOptimizedStyles turns overflow:visible
              // into a scrollport and clips the Super Ratio pill on collapse.
              transform: 'none',
              willChange: 'auto',
              maxWidth: '100%',
              transition: 'box-shadow 150ms ease-in-out',
              '&:hover': {
                boxShadow: '0 4px 16px rgba(var(--color-shadow-rgb), 0.25)',
              },
            }}
          >
            <Accordion 
              expanded={projectSectionExpanded} 
              onChange={(_, expanded) => handleProjectSectionToggle(expanded)}
              TransitionProps={{ timeout: 0 }}
              sx={{
                bgcolor: 'var(--color-surface)',
                overflow: 'visible',
                maxWidth: '100%',
                '&:before': {
                  display: 'none',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: 'var(--color-accent)' }} />}
                sx={{
                  px: { xs: 1.5, sm: 3 },
                  py: { xs: 1.5, sm: 2 },
                  bgcolor: 'var(--color-surface)',
                  cursor: 'pointer',
                  overflow: 'visible',
                  alignItems: 'center',
                  minWidth: 0,
                  '& .MuiAccordionSummary-content': {
                    overflow: 'visible',
                    my: 0,
                    mr: 1,
                    minWidth: 0,
                    flex: '1 1 auto',
                    alignItems: 'center',
                  },
                  '& .MuiAccordionSummary-expandIconWrapper': {
                    alignSelf: 'center',
                    flexShrink: 0,
                  },
                  '&:hover': {
                    bgcolor: 'rgba(var(--color-text-rgb), 0.04)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1.5, width: '100%', minWidth: 0, overflow: 'visible' }}>
                  {/* Top Row: Name + metadata + Super Ratio CTA (aligned with Expand/Collapse) */}
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'stretch', md: 'center' }, gap: { xs: 1, md: 2 }, width: '100%', minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, maxWidth: '100%', flex: { xs: '1 1 auto', md: '0 1 auto' } }}>
                      <LocationOnIcon sx={{ color: 'var(--color-accent)', flexShrink: 0 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--color-text-strong)', fontSize: { xs: '1rem', sm: '1.25rem' }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                        {projectDetails?.name || referenceUnit?.projectName || selectedProject || 'Project Information'}
                      </Typography>
                      {projectDetailsLoading && (
                        <CircularProgress size={20} sx={{ ml: 'auto', color: 'var(--color-accent)', flexShrink: 0 }} />
                      )}
                    </Box>

                    {/* Metadata Row (only shown when collapsed) */}
                    {!projectSectionExpanded && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, flexWrap: 'wrap', minWidth: 0, maxWidth: '100%', ml: { xs: 4, md: 0 } }}>
                        {/* Vertical Divider on Desktop */}
                        <Box sx={{ width: '1px', height: 16, bgcolor: 'rgba(var(--color-text-rgb), 0.15)', display: { xs: 'none', md: 'block' }, mx: 0.5 }} />
                        
                        {(projectDetails?.topDate || currentSitePlan?.topDate) && (
                          <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', whiteSpace: 'nowrap' }}>
                            TOP: <strong>{projectDetails?.topDate || currentSitePlan?.topDate}</strong>
                          </Typography>
                        )}
                        {projectDetails?.district && (
                          <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', whiteSpace: 'nowrap' }}>
                            District: <strong>{projectDetails.district}</strong>
                          </Typography>
                        )}
                        {currentSitePlan?.area && (
                          <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', whiteSpace: 'nowrap' }}>
                            Region: <strong>{currentSitePlan.area}</strong>
                          </Typography>
                        )}
                        {projectDetails?.totalUnits != null && (
                          <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', whiteSpace: 'nowrap' }}>
                            Units: <strong>{projectDetails.totalUnits}</strong>
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Super Ratio CTA + Expand/Collapse label share one right-side
                        cluster so they stay on the same baseline; chevron stays in expandIcon. */}
                    <Box
                      sx={{
                        ml: { xs: 4, md: 'auto' },
                        mr: { xs: 0.5, md: 0 },
                        pt: { xs: 1, md: 0.5 },
                        flexShrink: 0,
                        maxWidth: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        overflow: 'visible',
                      }}
                    >
                      {canOpenSuperRatioDialog && (
                        <Box
                          sx={{
                            position: 'relative',
                            display: 'inline-flex',
                            alignItems: 'center',
                            flexShrink: 0,
                            maxWidth: '100%',
                            // Glow lives on the wrapper and pulses via `opacity` so the
                            // shadow itself never re-rasterises mid-animation.
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              inset: 0,
                              borderRadius: 999,
                              boxShadow:
                                '0 0 0 1px rgba(255, 216, 155, 0.55), 0 8px 26px rgba(237, 137, 54, 0.68)',
                              opacity: 0,
                              animation: 'superRatioGlow 3.2s ease-in-out infinite',
                              animationPlayState: headerLoopPlayState,
                              pointerEvents: 'none',
                              '@keyframes superRatioGlow': {
                                '0%, 100%': { opacity: 0 },
                                '50%': { opacity: 1 },
                              },
                              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                            },
                          }}
                        >
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSuperRatioDialogOpen(true);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onFocus={(e) => e.stopPropagation()}
                            startIcon={
                              <DonutLargeIcon
                                sx={{
                                  fontSize: '1.05rem !important',
                                  animation: 'superRatioSpin 9s linear infinite',
                                  animationPlayState: headerLoopPlayState,
                                  '@keyframes superRatioSpin': {
                                    from: { transform: 'rotate(0deg)' },
                                    to: { transform: 'rotate(360deg)' },
                                  },
                                }}
                              />
                            }
                            sx={{
                              position: 'relative',
                              overflow: 'hidden',
                              px: { xs: 1.5, sm: 2.25 },
                              py: { xs: 0.7, sm: 0.85 },
                              minWidth: 0,
                              borderRadius: 999,
                              color: '#1a1005',
                              fontWeight: 800,
                              fontSize: { xs: '0.68rem', sm: '0.74rem' },
                              lineHeight: 1,
                              letterSpacing: { xs: '0.06em', sm: '0.13em' },
                              textTransform: 'uppercase',
                              whiteSpace: 'nowrap',
                              border: '1px solid rgba(255, 226, 168, 0.75)',
                              background:
                                'linear-gradient(100deg, #f6ad55 0%, #ffd89b 18%, #ed8936 42%, #c05621 58%, #ed8936 78%, #ffd89b 92%, #f6ad55 100%)',
                              backgroundSize: '320% 100%',
                              backgroundPosition: '0% 50%',
                              animation: 'superRatioFlow 7s ease-in-out infinite',
                              animationPlayState: headerLoopPlayState,
                              boxShadow:
                                '0 0 0 1px rgba(237, 137, 54, 0.35), 0 6px 20px rgba(237, 137, 54, 0.45)',
                              transition: 'transform 160ms ease, box-shadow 220ms ease',
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                bottom: 0,
                                left: 0,
                                width: '45%',
                                background:
                                  'linear-gradient(100deg, transparent 0%, rgba(255,255,255,0.65) 50%, transparent 100%)',
                                transform: 'translate3d(-140%, 0, 0) skewX(-22deg)',
                                willChange: 'transform',
                                animation: 'superRatioSheen 3.6s ease-in-out infinite',
                                animationPlayState: headerLoopPlayState,
                                pointerEvents: 'none',
                              },
                              '&:hover': {
                                transform: 'translateY(-1px) scale(1.02)',
                                boxShadow:
                                  '0 0 0 1px rgba(255, 216, 155, 0.6), 0 10px 28px rgba(237, 137, 54, 0.62)',
                              },
                              '&:active': { transform: 'translateY(0) scale(0.99)' },
                              '@keyframes superRatioFlow': {
                                '0%': { backgroundPosition: '0% 50%' },
                                '50%': { backgroundPosition: '100% 50%' },
                                '100%': { backgroundPosition: '0% 50%' },
                              },
                              '@keyframes superRatioSheen': {
                                '0%': { transform: 'translate3d(-140%, 0, 0) skewX(-22deg)' },
                                '55%, 100%': { transform: 'translate3d(320%, 0, 0) skewX(-22deg)' },
                              },
                              '@media (prefers-reduced-motion: reduce)': {
                                animation: 'none',
                                '&::after': { animation: 'none', opacity: 0 },
                                '& .MuiButton-startIcon svg': { animation: 'none' },
                              },
                            }}
                          >
                            Super Ratio
                          </Button>
                        </Box>
                      )}
                      {canCreateShareLink && projectDetails?.id && (
                        <ProjectShareLinkButton
                          projectId={projectDetails.id}
                          projectName={projectDetails.name || ''}
                          playState={headerLoopPlayState}
                        />
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'var(--color-accent)',
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          fontSize: '0.72rem',
                          display: { xs: 'none', sm: 'inline-block' },
                          opacity: 0.85,
                          whiteSpace: 'nowrap',
                          lineHeight: 1,
                        }}
                      >
                        {projectSectionExpanded ? 'Collapse' : 'Expand Info'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Bottom Row: Tab Shortcuts (only shown when collapsed) */}
                  {!projectSectionExpanded && (
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', maxWidth: '100%', minWidth: 0, mt: 0.5, pt: 0.5, overflow: 'visible' }}>
                      <Tabs
                        value={projectDetailsTab}
                        onChange={(e, newValue) => handleTabShortcutClick(e, newValue)}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{ 
                          minHeight: 'unset',
                          minWidth: 0,
                          maxWidth: '100%',
                          flex: 1,
                          overflow: 'visible',
                          '& .MuiTabs-scroller': { overflow: 'hidden !important' },
                          '& .MuiTabs-flexContainer': { overflow: 'visible' },
                          '& .MuiTab-root': {
                            color: 'rgba(var(--color-text-rgb), 0.7)',
                            minHeight: 'unset',
                            py: 0.75,
                            px: { xs: 1, sm: 2 },
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            display: 'inline-flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 0.75,
                            overflow: 'visible',
                            '&.Mui-selected': {
                              color: 'var(--color-accent)',
                            },
                          },
                          '& .MuiTabs-indicator': {
                            bgcolor: 'var(--color-accent)',
                          },
                        }}
                      >
                        <Tab icon={<InfoIcon sx={{ fontSize: '0.9rem !important', m: '0 !important' }} />} label="Details" value="details" onClick={(e) => e.stopPropagation()} />
                        <Tab icon={<MapIcon sx={{ fontSize: '0.9rem !important', m: '0 !important' }} />} label="Map" value="amenities" onClick={(e) => e.stopPropagation()} />
                        {canAccessSuperRatio && (
                          <Tab
                            icon={<TrendingUpIcon sx={{ fontSize: '0.9rem !important', m: '0 !important' }} />}
                            value="superRatio"
                            onClick={(e) => e.stopPropagation()}
                            sx={{ position: 'relative', overflow: 'visible' }}
                            label={
                              <>
                                Super Ratio
                                <NewBadge sx={TAB_NEW_BADGE_SX} />
                              </>
                            }
                          />
                        )}
                        <Tab icon={<PermMediaIcon sx={{ fontSize: '0.9rem !important', m: '0 !important' }} />} label="Media" value="media" onClick={(e) => e.stopPropagation()} />
                        <Tab icon={<FolderIcon sx={{ fontSize: '0.9rem !important', m: '0 !important' }} />} label="Materials" value="files" onClick={(e) => e.stopPropagation()} />
                        {canViewProjectMembers && <Tab icon={<PeopleIcon sx={{ fontSize: '0.9rem !important', m: '0 !important' }} />} label="Members" value="members" onClick={(e) => e.stopPropagation()} />}
                      </Tabs>
                    </Box>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0, bgcolor: 'var(--color-surface)' }}>
                <Box sx={{ p: 3, bgcolor: 'var(--color-surface)' }}>
                  {projectDetailsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : projectDetails ? (
                    <Grid container spacing={3}>
                      {/* Left Side - Info with Tabs */}
                      <Grid item xs={12} md={projectDetailsTab === 'members' && canViewProjectMembers ? 12 : (projectDetailsTab === 'files' || projectDetailsTab === 'media' ? 12 : 6)}>
                        <Tabs
                          value={projectDetailsTab}
                          onChange={(_, newValue) => {
                            setProjectDetailsTab(newValue);
                            // Fetch amenities when switching to the Amenities tab (Super Ratio
                            // loads via the auto-fetch effect keyed on the tab)
                            if (newValue === 'amenities' && projectDetails.name && projectDetails.mapLatitude && projectDetails.mapLongitude) {
                              fetchAmenities(projectDetails.name, amenitiesRadius);
                            }
                            // Fetch project members when switching to members tab
                            if (newValue === 'members' && projectDetails.id && canViewProjectMembers) {
                              fetchProjectMembers(projectDetails.id);
                            }
                            // Fetch every Files-tab data source in parallel
                            // when the tab is opened. Factsheet + brochure come
                            // from ProjectMedia (legacy single-row endpoints).
                            // EDMs and eBooks come from the project-essential
                            // join tables (EDMProject / ProjectLauncherProject)
                            // and are returned as flat arrays per project.
                            // Each promise is `.catch()`-guarded so a single
                            // failing source doesn't blank the whole tab.
                            if (newValue === 'files' && projectDetails.id) {
                              setFilesLoading(true);
                              setFilesFactsheetData(null);
                              setFilesBrochureData(null);
                              setFilesEdms([]);
                              setFilesEbooks([]);
                              Promise.all([
                                api.getProjectFactsheet(projectDetails.id).then((d) => d).catch(() => null),
                                api.getProjectBrochure(projectDetails.id).then((d) => d).catch(() => null),
                                api.getProjectEdms(projectDetails.id).then((d) => d.items).catch(() => [] as ProjectEdmFile[]),
                                api.getProjectEbooks(projectDetails.id).then((d) => d.items).catch(() => [] as ProjectEbookFile[]),
                              ]).then(([factsheet, brochure, edms, ebooks]) => {
                                setFilesFactsheetData(factsheet);
                                setFilesBrochureData(brochure);
                                setFilesEdms(edms);
                                setFilesEbooks(ebooks);
                                setFilesLoading(false);
                              }).catch(() => setFilesLoading(false));

                              // Grouped Files (per-category) sub-tab data
                              setFilesGroupedLoading(true);
                              setFilesGrouped([]);
                              api.getProjectFilesGrouped(projectDetails.id)
                                .then((res) => setFilesGrouped(filterPdfFileGroups(res.groups)))
                                .catch(() => setFilesGrouped([]))
                                .finally(() => setFilesGroupedLoading(false));
                            }
                            if (newValue === 'media' && projectDetails.id) {
                              setProjectMediaLoading(true);
                              setProjectMediaError(null);
                              setProjectMediaItems([]);
                              api.getProjectMedia(projectDetails.id)
                                .then((res) => setProjectMediaItems(res.items || []))
                                .catch((err) => setProjectMediaError(err?.message || 'Failed to load media'))
                                .finally(() => setProjectMediaLoading(false));
                            }
                          }}
                          variant="scrollable"
                          scrollButtons="auto"
                          allowScrollButtonsMobile
                          sx={{ 
                            mb: 2,
                            width: '100%',
                            overflow: 'visible',
                            '& .MuiTabs-flexContainer': { overflow: 'visible' },
                            '& .MuiTab-root': {
                              color: 'rgba(var(--color-text-rgb), 0.7)',
                              overflow: 'visible',
                              '&.Mui-selected': {
                                color: 'var(--color-accent)',
                              },
                            },
                            '& .MuiTabs-indicator': {
                              bgcolor: 'var(--color-accent)',
                            },
                          }}
                        >
                          <Tab label="Details" value="details" />
                          <Tab label="Map" value="amenities" />
                          {canAccessSuperRatio && (
                            <Tab
                              value="superRatio"
                              /* Anchors the ribbon to the tab's top-left padding, above the
                                 label rather than across it. */
                              sx={{ position: 'relative', overflow: 'visible' }}
                              label={
                                <>
                                  Super Ratio
                                  <NewBadge sx={TAB_NEW_BADGE_SX} />
                                </>
                              }
                            />
                          )}
                          <Tab label="Media" value="media" />
                          <Tab label="Materials" value="files" />
                          {canViewProjectMembers && <Tab label="Project Members" value="members" />}
                        </Tabs>
                        
                        <Box
                          sx={{
                            ...scrollableStyles,
                            maxHeight: 'calc(100vh - 400px)',
                            overflowY: 'auto',
                            pr: 1,
                            '& .MuiTypography-root': {
                              color: 'var(--color-text-strong)',
                            },
                            '& .MuiTypography-caption': {
                              color: 'rgba(var(--color-text-rgb), 0.7)',
                            },
                          }}
                        >
                        {projectDetailsTab === 'details' ? (
                          <Grid container spacing={2}>
                            {projectDetails.liveStatusEnabled && (
                              <Grid item xs={12}>
                                <Box
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    bgcolor: 'rgba(var(--color-accent-rgb), 0.1)',
                                    border: '1px solid rgba(var(--color-accent-rgb), 0.3)',
                                  }}
                                >
                                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                                    Live status
                                  </Typography>
                                  <Typography variant="body2" fontWeight={600} sx={{ color: 'var(--color-text-strong)' }}>
                                    {projectDetails.liveStatusUpdatedAt
                                      ? `Last updated ${new Date(projectDetails.liveStatusUpdatedAt).toLocaleString()}`
                                      : 'No updates yet'}
                                  </Typography>
                                </Box>
                              </Grid>
                            )}
                            {projectDetails.name && (
                              <Grid item xs={12}>
                                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                                  Name
                                </Typography>
                                <Typography variant="body1" fontWeight="medium" sx={{ color: 'var(--color-text-strong)' }}>
                                  {projectDetails.name}
                                </Typography>
                              </Grid>
                            )}
                            {projectDetails.developer && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                  Developer
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                  {projectDetails.developer}
                                </Typography>
                              </Grid>
                            )}
                            {projectDetails.tenure && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                  Tenure
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                  {projectDetails.tenure}
                                </Typography>
                              </Grid>
                            )}
                            {projectDetails.totalUnits && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                  Total Units
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                  {projectDetails.totalUnits}
                                </Typography>
                              </Grid>
                            )}
                            {projectDetails.completedDate && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                  Completed Date
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                  {new Date(projectDetails.completedDate).toLocaleDateString()}
                                </Typography>
                              </Grid>
                            )}
                            {projectDetails.area && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                  Area
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                  {projectDetails.area}
                                </Typography>
                              </Grid>
                            )}
                            {projectDetails.district && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                  District
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                  {projectDetails.district}
                                </Typography>
                              </Grid>
                            )}
                            {projectDetails.address && (
                              <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                  Address
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                  {projectDetails.address}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        ) : canAccessSuperRatio && projectDetailsTab === 'superRatio' ? (
                          <DemandSupplyPanel
                            data={demandSupplyData}
                            loading={demandSupplyLoading}
                            radius={demandSupplyRadius}
                            minRadius={demandSupplyMinRadius}
                            onRadiusChange={onDemandSupplyRadiusChange}
                            nationwide={demandSupplyNationwide}
                            onNationwideChange={onDemandSupplyNationwideChange}
                            selectedTypes={selectedDemandSupplyTypes}
                            setSelectedTypes={setSelectedDemandSupplyTypes}
                          />
                        ) : projectDetailsTab === 'amenities' ? (
                          <Box>
                            {/* Radius Selector */}
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Search Radius
                              </Typography>
                              <ToggleButtonGroup
                                value={amenitiesRadius}
                                exclusive
                                onChange={(_, newRadius) => {
                                  if (newRadius !== null && projectDetails.name && projectDetails.mapLatitude && projectDetails.mapLongitude) {
                                    setAmenitiesRadius(newRadius);
                                    fetchAmenities(projectDetails.name, newRadius);
                                  }
                                }}
                                size="small"
                              >
                                <ToggleButton value={500}>500m</ToggleButton>
                                <ToggleButton value={1000}>1km</ToggleButton>
                                <ToggleButton value={2000}>2km</ToggleButton>
                              </ToggleButtonGroup>
                            </Box>

                            {/* Amenity Type Filters */}
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Show Amenities
                              </Typography>
                              <FormGroup row>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={selectedAmenityTypes.mrtLrt}
                                      onChange={(e) =>
                                        setSelectedAmenityTypes((prev) => ({
                                          ...prev,
                                          mrtLrt: e.target.checked,
                                        }))
                                      }
                                      size="small"
                                    />
                                  }
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Box
                                        sx={{
                                          width: 12,
                                          height: 12,
                                          backgroundColor: 'var(--color-danger)',
                                          borderRadius: '2px',
                                          border: '1px solid rgba(var(--color-shadow-rgb), 0.1)',
                                        }}
                                      />
                                      <Typography variant="body2">MRT/LRT</Typography>
                                    </Box>
                                  }
                                />
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={selectedAmenityTypes.schools}
                                      onChange={(e) =>
                                        setSelectedAmenityTypes((prev) => ({
                                          ...prev,
                                          schools: e.target.checked,
                                        }))
                                      }
                                      size="small"
                                    />
                                  }
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Box
                                        sx={{
                                          width: 12,
                                          height: 12,
                                          backgroundColor: 'var(--color-success)',
                                          borderRadius: '2px',
                                          border: '1px solid rgba(var(--color-shadow-rgb), 0.1)',
                                        }}
                                      />
                                      <Typography variant="body2">Schools</Typography>
                                    </Box>
                                  }
                                />
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={selectedAmenityTypes.supermarkets}
                                      onChange={(e) =>
                                        setSelectedAmenityTypes((prev) => ({
                                          ...prev,
                                          supermarkets: e.target.checked,
                                        }))
                                      }
                                      size="small"
                                    />
                                  }
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Box
                                        sx={{
                                          width: 12,
                                          height: 12,
                                          backgroundColor: 'var(--color-warning)',
                                          borderRadius: '2px',
                                          border: '1px solid rgba(var(--color-shadow-rgb), 0.1)',
                                        }}
                                      />
                                      <Typography variant="body2">Supermarkets</Typography>
                                    </Box>
                                  }
                                />
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={selectedAmenityTypes.preschools}
                                      onChange={(e) =>
                                        setSelectedAmenityTypes((prev) => ({
                                          ...prev,
                                          preschools: e.target.checked,
                                        }))
                                      }
                                      size="small"
                                    />
                                  }
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Box
                                        sx={{
                                          width: 12,
                                          height: 12,
                                          backgroundColor: 'var(--color-secondary)',
                                          borderRadius: '2px',
                                          border: '1px solid rgba(var(--color-shadow-rgb), 0.1)',
                                        }}
                                      />
                                      <Typography variant="body2">Preschools</Typography>
                                    </Box>
                                  }
                                />
                              </FormGroup>
                            </Box>
                            
                            {amenitiesLoading ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                              </Box>
                            ) : amenitiesData ? (
                              <Box>
                                {/* MRT/LRT */}
                                {selectedAmenityTypes.mrtLrt && (
                                  <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 2 }}>
                                      <Box
                                        sx={{
                                          width: 16,
                                          height: 16,
                                          backgroundColor: 'var(--color-danger)',
                                          borderRadius: '2px',
                                          border: '1px solid rgba(var(--color-shadow-rgb), 0.1)',
                                        }}
                                      />
                                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        MRT/LRT ({amenitiesData.amenities.mrtLrt.filter((a) => a.name && a.name.trim() !== '' && a.name.trim() !== '-').length})
                                      </Typography>
                                    </Box>
                                    {amenitiesData.amenities.mrtLrt.filter((a) => a.name && a.name.trim() !== '' && a.name.trim() !== '-').length > 0 ? (
                                  <Box sx={{ mb: 3 }}>
                                    {amenitiesData.amenities.mrtLrt.filter((a) => a.name && a.name.trim() !== '' && a.name.trim() !== '-').map((station) => (
                                      <Card key={station.id} sx={{ mb: 1 }}>
                                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                          <Typography variant="body2" fontWeight="medium">
                                            {station.name}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {station.mrtLine ? `${station.mrtLine} • ${Math.round(station.distance)}m away` : `${Math.round(station.distance)}m away`}
                                          </Typography>
                                        </CardContent>
                                      </Card>
                                    ))}
                                  </Box>
                                ) : (
                                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        No MRT/LRT stations found within {amenitiesRadius}m
                                      </Typography>
                                    )}
                                  </>
                                )}
                                
                                {/* Schools */}
                                {selectedAmenityTypes.schools && (
                                  <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                      <Box
                                        sx={{
                                          width: 16,
                                          height: 16,
                                          backgroundColor: 'var(--color-success)',
                                          borderRadius: '2px',
                                          border: '1px solid rgba(var(--color-shadow-rgb), 0.1)',
                                        }}
                                      />
                                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        Schools ({amenitiesData.amenities.schools.filter((a) => a.name && a.name.trim() !== '' && a.name.trim() !== '-').length})
                                      </Typography>
                                    </Box>
                                    {amenitiesData.amenities.schools.filter((a) => a.name && a.name.trim() !== '' && a.name.trim() !== '-').length > 0 ? (
                                  <Box sx={{ mb: 3 }}>
                                    {amenitiesData.amenities.schools.filter((a) => a.name && a.name.trim() !== '' && a.name.trim() !== '-').map((school) => (
                                      <Card key={school.id} sx={{ mb: 1 }}>
                                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                          <Typography variant="body2" fontWeight="medium">
                                            {school.name}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {school.type ? `${school.type}${school.category ? ` - ${school.category}` : ''} • ${Math.round(school.distance)}m away` : `${Math.round(school.distance)}m away`}
                                          </Typography>
                                        </CardContent>
                                      </Card>
                                    ))}
                                  </Box>
                                ) : (
                                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        No schools found within {amenitiesRadius}m
                                      </Typography>
                                    )}
                                  </>
                                )}
                                
                                {/* Preschools */}
                                {selectedAmenityTypes.preschools && (
                                  <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                      <Box
                                        sx={{
                                          width: 16,
                                          height: 16,
                                          backgroundColor: 'var(--color-secondary)',
                                          borderRadius: '2px',
                                          border: '1px solid rgba(var(--color-shadow-rgb), 0.1)',
                                        }}
                                      />
                                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        Preschools ({amenitiesData.amenities.preschools.filter((a) => a.name && a.name.trim() !== '' && a.name.trim() !== '-').length})
                                      </Typography>
                                    </Box>
                                    {amenitiesData.amenities.preschools.filter((a) => a.name && a.name.trim() !== '' && a.name.trim() !== '-').length > 0 ? (
                                  <Box sx={{ mb: 3 }}>
                                    {amenitiesData.amenities.preschools.filter((a) => a.name && a.name.trim() !== '' && a.name.trim() !== '-').map((preschool) => (
                                      <Card key={preschool.id} sx={{ mb: 1 }}>
                                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                          <Typography variant="body2" fontWeight="medium">
                                            {preschool.name}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {preschool.type ? `${preschool.type}${preschool.category ? ` - ${preschool.category}` : ''} • ${Math.round(preschool.distance)}m away` : `${Math.round(preschool.distance)}m away`}
                                          </Typography>
                                        </CardContent>
                                      </Card>
                                    ))}
                                  </Box>
                                ) : (
                                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        No preschools found within {amenitiesRadius}m
                                      </Typography>
                                    )}
                                  </>
                                )}
                                
                                {/* Supermarkets */}
                                {selectedAmenityTypes.supermarkets && (
                                  <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                      <Box
                                        sx={{
                                          width: 16,
                                          height: 16,
                                          backgroundColor: 'var(--color-warning)',
                                          borderRadius: '2px',
                                          border: '1px solid rgba(var(--color-shadow-rgb), 0.1)',
                                        }}
                                      />
                                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        Supermarkets ({amenitiesData.amenities.supermarkets.filter((a) => a.name && a.name.trim() !== '' && a.name.trim() !== '-').length})
                                      </Typography>
                                    </Box>
                                    {amenitiesData.amenities.supermarkets.filter((a) => a.name && a.name.trim() !== '' && a.name.trim() !== '-').length > 0 ? (
                                  <Box sx={{ mb: 3 }}>
                                    {amenitiesData.amenities.supermarkets.filter((a) => a.name && a.name.trim() !== '' && a.name.trim() !== '-').map((amenity) => (
                                      <Card key={amenity.id} sx={{ mb: 1 }}>
                                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                          <Typography variant="body2" fontWeight="medium">
                                            {amenity.name}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {amenity.address ? `${amenity.address} • ${Math.round(amenity.distance)}m away` : `${Math.round(amenity.distance)}m away`}
                                          </Typography>
                                        </CardContent>
                                      </Card>
                                    ))}
                                  </Box>
                                ) : (
                                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        No supermarkets found within {amenitiesRadius}m
                                      </Typography>
                                    )}
                                  </>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Click on a radius to load amenities
                              </Typography>
                            )}
                          </Box>
                        ) : projectDetailsTab === 'media' ? (
                          <Box>
                            {projectMediaLoading ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                              </Box>
                            ) : projectMediaError ? (
                              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                {projectMediaError}
                              </Typography>
                            ) : (() => {
                              const galleryItems = projectMediaItems.filter((i) => normalizeMediaType(i.type) === 'image');
                              const videoItems = projectMediaItems.filter((i) => normalizeMediaType(i.type) === 'video');
                              const ivtItems = projectMediaItems.filter((i) => normalizeMediaType(i.type) === 'ivt');
                              const totalCount = galleryItems.length + videoItems.length + ivtItems.length;
                              const subTabCounts: Record<MediaSubTabId, number> = {
                                gallery: galleryItems.length,
                                videos: videoItems.length,
                                virtualTour: ivtItems.length,
                              };
                              const mediaSubTabs: Array<{ id: MediaSubTabId; label: string; icon: React.ReactNode }> = [
                                { id: 'gallery', label: 'Gallery', icon: <CollectionsIcon fontSize="small" /> },
                                { id: 'videos', label: 'Videos', icon: <VideocamIcon fontSize="small" /> },
                                { id: 'virtualTour', label: 'Virtual tour', icon: <ThreeDRotationIcon fontSize="small" /> },
                              ];
                              const visibleSubTabs = mediaSubTabs.filter((t) => subTabCounts[t.id] > 0);
                              if (totalCount === 0) {
                                return (
                                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                    No media for this project.
                                  </Typography>
                                );
                              }
                              const subTabItems: Record<MediaSubTabId, ProjectMediaItem[]> = {
                                gallery: galleryItems,
                                videos: videoItems,
                                virtualTour: ivtItems,
                              };
                              const emptyLabels: Record<MediaSubTabId, string> = {
                                gallery: 'No gallery images for this project.',
                                videos: 'No videos for this project.',
                                virtualTour: 'No virtual tours for this project.',
                              };
                              const activeSubTab =
                                visibleSubTabs.some((t) => t.id === mediaSubTab)
                                  ? mediaSubTab
                                  : visibleSubTabs[0].id;
                              const activeItems = subTabItems[activeSubTab].map((item) =>
                                mapProjectMediaToListItem(item, {
                                  onOpenVirtualTour: (tour) => setVirtualTourPreview(tour),
                                }),
                              );
                              return (
                                <Box>
                                  {visibleSubTabs.length > 1 && (
                                    <Tabs
                                      value={activeSubTab}
                                      onChange={(_, v: MediaSubTabId) => setMediaSubTab(v)}
                                      variant="scrollable"
                                      scrollButtons="auto"
                                      allowScrollButtonsMobile
                                      sx={{
                                        minHeight: 36,
                                        mb: 2,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        '& .MuiTab-root': {
                                          textTransform: 'none',
                                          fontWeight: 600,
                                          minHeight: 36,
                                          fontSize: '0.8rem',
                                          px: 1.5,
                                          gap: 0.5,
                                        },
                                      }}
                                    >
                                      {visibleSubTabs.map((t) => (
                                        <Tab
                                          key={t.id}
                                          value={t.id}
                                          icon={t.icon as React.ReactElement}
                                          iconPosition="start"
                                          label={`${t.label} (${subTabCounts[t.id]})`}
                                        />
                                      ))}
                                    </Tabs>
                                  )}
                                  {activeItems.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                      {emptyLabels[activeSubTab]}
                                    </Typography>
                                  ) : (
                                    <Box
                                      sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                                        gap: { xs: 1, sm: 1.25 },
                                      }}
                                    >
                                      {activeItems.map(({ id, ...rowProps }) => (
                                        <DownloadFileRow key={id} {...rowProps} />
                                      ))}
                                    </Box>
                                  )}
                                  <VirtualTourDialog
                                    open={Boolean(virtualTourPreview)}
                                    onClose={() => setVirtualTourPreview(null)}
                                    title={virtualTourPreview?.title || 'Virtual tour'}
                                    url={virtualTourPreview?.url || null}
                                  />
                                </Box>
                              );
                            })()}
                          </Box>
                        ) : projectDetailsTab === 'files' ? (
                          <Box>
                            {filesLoading ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                              </Box>
                            ) : (
                              (() => {
                                // ── Files tab body ──
                                // Builds three `DownloadFileRow` groups
                                // (Project Documents / EDMs / eBooks) from the
                                // four data sources fetched on tab open. Each
                                // row is rendered via the shared
                                // `DownloadFileRow` visual, which handles
                                // preview-on-click for PDFs/images and a
                                // Download / Share affordance per row.
                                const slugifyForFile = (raw: string, fallback: string) =>
                                  raw.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || fallback;

                                // ── Factsheet / Brochure → ProjectMedia legacy rows ──
                                const buildMediaRow = (
                                  data: ProjectFactsheet | ProjectBrochure | null,
                                  displayName: 'Factsheet' | 'Brochure',
                                  baseFallback: 'factsheet' | 'brochure',
                                ): (DownloadFileRowProps & { id: string }) | null => {
                                  if (!data) return null;
                                  const mediaUrl = data.url?.trim() ?? '';
                                  const imageUrl = data.imageLarge || data.imageMedium || data.imageSmall || null;
                                  const type = (data.type ?? '').toLowerCase();
                                  const isPdf = mediaUrl.toLowerCase().endsWith('.pdf') || type === 'pdf';
                                  const downloadUrl = isPdf ? mediaUrl : (imageUrl || mediaUrl);
                                  if (!downloadUrl) return null;
                                  const ext = downloadUrl.toLowerCase().endsWith('.pdf') ? 'pdf' : 'jpg';
                                  const rawName = currentSitePlan?.projectName || projectDetails?.name || baseFallback;
                                  const safeBaseName = slugifyForFile(rawName, baseFallback);
                                  const handleCustomClick = () => {
                                    if (baseFallback === 'factsheet') {
                                      setFactsheetData(data as ProjectFactsheet);
                                      setPdfNumPages(0);
                                      setPdfPageNumber(1);
                                      setFactsheetModalOpen(true);
                                    } else {
                                      setBrochureData(data as ProjectBrochure);
                                      setBrochurePdfNumPages(0);
                                      setBrochurePdfPageNumber(1);
                                      setBrochureModalOpen(true);
                                    }
                                  };
                                  return {
                                    id: baseFallback,
                                    filename: `${safeBaseName}-${baseFallback}.${ext}`,
                                    displayName,
                                    subtitle: data.description || `Project ${baseFallback}`,
                                    url: downloadUrl,
                                    previewImage: imageUrl,
                                    uploadedAt: data.updatedAt,
                                    onClick: handleCustomClick,
                                  };
                                };

                                const documentRows: Array<DownloadFileRowProps & { id: string }> = [];
                                const factsheetRow = buildMediaRow(filesFactsheetData, 'Factsheet', 'factsheet');
                                const brochureRow = buildMediaRow(filesBrochureData, 'Brochure', 'brochure');
                                if (factsheetRow) documentRows.push(factsheetRow);
                                if (brochureRow) documentRows.push(brochureRow);

                                // ── EDMs (EDMProject join table) ──
                                const edmRows: Array<DownloadFileRowProps & { id: string }> = filesEdms.map((edm) => {
                                  const projId = projectDetails?.id || currentSitePlan?.projectId;
                                  return {
                                    id: `edm:${edm.id}`,
                                    filename: edm.filename || `${slugifyForFile(edm.title, 'edm')}.pdf`,
                                    displayName: edm.title,
                                    subtitle: edm.category ? `EDM · ${edm.category}` : 'EDM',
                                    url: edm.url && edm.url !== '#' ? edm.url : null,
                                    urlPublic: edm.urlPublic,
                                    previewImage: edm.cover,
                                    uploadedAt: edm.createdAt,
                                    onPreview: () => logProjectDetailsPreviewed('edm', projId, edm.id, analyticsCea),
                                    onDownloadClick: () => logProjectDetailsDownloaded('edm', projId, edm.id, analyticsCea),
                                    shareAsset: isAdminUser
                                      ? { id: edm.id, kind: 'edm' as const, title: edm.title || edm.id, projectId: projId }
                                      : null,
                                  };
                                });

                                // ── eBooks (ProjectLauncherProject join table) ──
                                const ebookRows: Array<DownloadFileRowProps & { id: string }> = filesEbooks.map((ebook) => {
                                  const projId = projectDetails?.id || currentSitePlan?.projectId;
                                  const filename = `${ebook.title || ebook.id}.pdf`;
                                  return {
                                    id: `ebook:${ebook.id}`,
                                    filename,
                                    displayName: ebook.title,
                                    subtitle: ebook.type ? `eBook · ${ebook.type}` : 'eBook',
                                    url: ebook.url,
                                    previewImage: ebook.cover,
                                    uploadedAt: ebook.createdAt,
                                    forceBlobDownload: true,
                                    onPreview: () => logProjectDetailsPreviewed('ebook', projId, ebook.id, analyticsCea),
                                    onDownloadClick: () => logProjectDetailsDownloaded('ebook', projId, ebook.id, analyticsCea),
                                    shareAsset: isAdminUser
                                      ? { id: ebook.id, kind: 'ebook' as const, title: ebook.title || ebook.id, projectId: projId }
                                      : null,
                                  };
                                });

                                const renderRows = (
                                  rows: Array<DownloadFileRowProps & { id: string }>,
                                ) => {
                                  if (rows.length === 0) {
                                    return (
                                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                        No files in this section.
                                      </Typography>
                                    );
                                  }
                                  return (
                                    <Box
                                      sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                                        gap: { xs: 1, sm: 1.25 },
                                      }}
                                    >
                                      {rows.map(({ id, ...rowProps }) => (
                                        <DownloadFileRow key={id} {...rowProps} />
                                      ))}
                                    </Box>
                                  );
                                };

                                // Friendly labels for the raw Category codes stored
                                // on ProjectFile rows. Unknown codes fall back to a
                                // title-cased version of the raw code so new codes
                                // surface without needing a FE change.
                                const CATEGORY_LABELS: Record<string, string> = {
                                  FACTSHEET: 'Factsheets',
                                  BROCHURE: 'Brochures',
                                  FLOORPLAN: 'Floor Plans',
                                  SITEPLAN: 'Site Plans',
                                  PRICELIST: 'Price Lists',
                                  EBOOK: 'eBooks',
                                  VIDEO: 'Videos',
                                  EDM: 'EDMs',
                                  DPI: 'Developer Project Info',
                                  PF: 'Project Forms',
                                  SMC: 'Sales & Marketing Collaterals',
                                  STS: 'Saleskit / Training Slides',
                                  PU: 'Pricing Update',
                                  OTH: 'Others',
                                  OTHER: 'Others',
                                };
                                const prettyCategory = (code: string): string => {
                                  const upper = (code || '').toUpperCase();
                                  if (CATEGORY_LABELS[upper]) return CATEGORY_LABELS[upper];
                                  return upper
                                    .toLowerCase()
                                    .split(/[_\s]+/)
                                    .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : ''))
                                    .join(' ');
                                };

                                const filesRowsTotal = filesGrouped.reduce((sum, g) => sum + g.items.length, 0);

                                const subTabs: Array<{ id: 'documents' | 'edms' | 'ebooks' | 'files' | 'agentAccess'; label: string; count: number }> = [
                                  { id: 'documents', label: 'Project Documents', count: documentRows.length },
                                  { id: 'edms', label: 'EDMs', count: edmRows.length },
                                  { id: 'ebooks', label: 'eBooks', count: ebookRows.length },
                                  { id: 'files', label: 'Files', count: filesRowsTotal },
                                  { id: 'agentAccess', label: 'Agent Access Folder', count: projectResourceLinks.length },
                                ];
                                const activeSubId = subTabs.find((t) => t.id === filesSubTab)?.id ?? 'documents';

                                const buildProjectDownloadUrl = (projectId: string, fileName: string): string => {
                                  const params = new URLSearchParams({
                                    module: 'Download',
                                    id: projectId,
                                    fileName,
                                  });
                                  return `https://portal.huttonsgroup.com/Project/DownloadFile?${params.toString()}`;
                                };

                                const renderFilesGrouped = () => {
                                  if (filesGroupedLoading) {
                                    return (
                                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                        <CircularProgress size={24} />
                                      </Box>
                                    );
                                  }
                                  if (filesGrouped.length === 0) {
                                    return (
                                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                        No files in this section.
                                      </Typography>
                                    );
                                  }
                                  return (
                                    <Stack spacing={2}>
                                      {filesGrouped.map((group) => (
                                        <Accordion
                                          key={group.category}
                                          disableGutters
                                          elevation={0}
                                          square
                                          sx={{
                                            border: '1px solid var(--color-border)',
                                            borderRadius: '8px !important',
                                            overflow: 'hidden',
                                            bgcolor: 'var(--color-surface)',
                                            color: 'var(--color-text)',
                                            '&:before': { display: 'none' },
                                          }}
                                        >
                                          <AccordionSummary
                                            expandIcon={<ExpandMoreIcon sx={{ color: 'var(--color-text-muted)' }} />}
                                            sx={{
                                              bgcolor: 'var(--color-surface-elevated)',
                                              minHeight: 48,
                                              px: 1.5,
                                              py: 0.5,
                                              '&.Mui-expanded': {
                                                borderBottom: '1px solid var(--color-border)',
                                              },
                                              '& .MuiAccordionSummary-content': {
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                m: '0 !important',
                                              },
                                            }}
                                          >
                                            <Typography
                                              variant="subtitle2"
                                              sx={{
                                                fontWeight: 700,
                                                color: 'var(--color-text-strong)',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.06em',
                                                fontSize: '0.78rem',
                                                flex: 1,
                                              }}
                                            >
                                              {prettyCategory(group.category)}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', mr: 1 }}>
                                              {group.items.length} file{group.items.length === 1 ? '' : 's'}
                                            </Typography>
                                          </AccordionSummary>
                                          <AccordionDetails
                                            sx={{
                                              p: 1.5,
                                              bgcolor: 'var(--color-surface)',
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                display: 'grid',
                                                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                                                gap: { xs: 1, sm: 1.25 },
                                              }}
                                            >
                                              {group.items.map((file) => {
                                                const projId = projectDetails?.id || currentSitePlan?.projectId;
                                                const filename = file.filename || file.displayName;
                                                const downloadUrl = filename && projId ? buildProjectDownloadUrl(projId, filename) : null;
                                                // Prefer the canonical DownloadFile endpoint for previews — same bytes
                                                // the Download button serves, reliably proxied for inline preview.
                                                const previewUrl = downloadUrl ?? file.url;

                                                const handleRowClick = () => {
                                                  setLocalPreviewFile({
                                                    title: file.displayName || filename,
                                                    url: previewUrl,
                                                    filename: filename,
                                                  });
                                                  setLocalPreviewOpen(true);
                                                };

                                                return (
                                                  <DownloadFileRow
                                                    key={file.id}
                                                    filename={filename}
                                                    displayName={file.displayName}
                                                    subtitle={prettyCategory(group.category)}
                                                    url={previewUrl}
                                                    uploadedAt={file.updatedAt || file.createdAt}
                                                    onClick={handleRowClick}
                                                  />
                                                );
                                              })}
                                            </Box>
                                          </AccordionDetails>
                                        </Accordion>
                                      ))}

                                      {localPreviewFile && (
                                        <FilePreviewDialog
                                          open={localPreviewOpen}
                                          onClose={() => setLocalPreviewOpen(false)}
                                          title={localPreviewFile.title}
                                          url={localPreviewFile.url}
                                          filename={localPreviewFile.title || localPreviewFile.filename}
                                        />
                                      )}
                                    </Stack>
                                  );
                                };

                                // Format an ISO timestamp as "08 May 2026" — mirrors the date
                                // format used elsewhere on this surface.
                                const formatResourceLinkDate = (iso: string | null): string => {
                                  if (!iso) return '';
                                  const d = new Date(iso);
                                  if (Number.isNaN(d.getTime())) return '';
                                  return d.toLocaleDateString('en-GB', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                  });
                                };

                                const renderAgentAccessLinks = () => {
                                  if (projectResourceLinksLoading) {
                                    return (
                                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                        <CircularProgress />
                                      </Box>
                                    );
                                  }
                                  if (projectResourceLinks.length === 0) {
                                    return (
                                      <Box sx={{ py: 4, textAlign: 'center' }}>
                                        <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', mb: isCurrentUserProjectIc || isAdminUser || isProjectDeptUser ? 1.5 : 0 }}>
                                          No links yet.
                                        </Typography>
                                        {(isCurrentUserProjectIc || isAdminUser || isProjectDeptUser) && (
                                          <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<AddIcon fontSize="small" />}
                                            onClick={openAgentAccessAddLinkDialog}
                                            sx={{ textTransform: 'none', fontWeight: 600 }}
                                          >
                                            Add link
                                          </Button>
                                        )}
                                      </Box>
                                    );
                                  }
                                  return (
                                    <Box
                                      sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                                        gap: { xs: 1, sm: 1.25 },
                                        p: '2px', // Prevent parent clipping of hover border shadows
                                      }}
                                    >
                                      {projectResourceLinks.map((link) => (
                                        <Box
                                          key={link.id}
                                          component="a"
                                          href={link.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: 1.25, sm: 2 },
                                            p: { xs: 1.25, sm: 1.5 },
                                            borderRadius: 2,
                                            border: '1px solid var(--color-border-strong, rgba(var(--color-text-rgb), 0.12))',
                                            bgcolor: 'var(--color-surface)',
                                            color: 'inherit',
                                            textDecoration: 'none',
                                            cursor: 'pointer',
                                            transition: 'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
                                            '&:hover': {
                                              borderColor: 'var(--color-accent, #818cf8)',
                                              boxShadow: '0 0 0 1px var(--color-accent, #818cf8)',
                                            },
                                          }}
                                        >
                                          <LinkIcon sx={{ color: 'var(--color-accent, #818cf8)' }} />
                                          <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontWeight: 600,
                                                color: 'var(--color-text-strong)',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                              }}
                                              title={link.label || link.url}
                                            >
                                              {link.label || link.url}
                                            </Typography>
                                            {formatResourceLinkDate(link.createdAt) && (
                                              <Typography
                                                variant="caption"
                                                sx={{ color: 'var(--color-text-muted)' }}
                                              >
                                                Added {formatResourceLinkDate(link.createdAt)}
                                              </Typography>
                                            )}
                                          </Box>
                                          <OpenInNewIcon fontSize="small" sx={{ color: 'var(--color-text-muted)' }} />
                                        </Box>
                                      ))}
                                    </Box>
                                  );
                                };

                                return (
                                  <Box>
                                    <Tabs
                                      value={activeSubId}
                                      onChange={(_, v: 'documents' | 'edms' | 'ebooks' | 'files' | 'agentAccess') => setFilesSubTab(v)}
                                      variant="scrollable"
                                      scrollButtons="auto"
                                      allowScrollButtonsMobile
                                      sx={{
                                        minHeight: 36,
                                        mb: 2,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        '& .MuiTab-root': {
                                          textTransform: 'none',
                                          fontWeight: 600,
                                          minHeight: 36,
                                          fontSize: '0.8rem',
                                          px: 1.5,
                                        },
                                      }}
                                    >
                                      {subTabs.map((t) => (
                                        <Tab key={t.id} value={t.id} label={`${t.label} (${t.count})`} />
                                      ))}
                                    </Tabs>
                                    {activeSubId === 'documents' && renderRows(documentRows)}
                                    {activeSubId === 'edms' && renderRows(edmRows)}
                                    {activeSubId === 'ebooks' && renderRows(ebookRows)}
                                    {activeSubId === 'files' && renderFilesGrouped()}
                                    {activeSubId === 'agentAccess' && renderAgentAccessLinks()}
                                  </Box>
                                );
                              })()
                            )}
                          </Box>
                        ) : (
                          canViewProjectMembers && projectDetailsTab === 'members' ? (
                            <Box>
                              {/* Search bar */}
                              <TextField
                                fullWidth
                                size="small"
                                placeholder="Search by name, CEA or mobile"
                                value={projectMembersSearch}
                                onChange={(e) => setProjectMembersSearch(e.target.value)}
                                sx={{
                                  mb: 2,
                                  '& .MuiOutlinedInput-root': {
                                    color: 'var(--color-text)',
                                    '& fieldset': { borderColor: 'rgba(var(--color-text-rgb), 0.2)' },
                                    '&:hover fieldset': { borderColor: 'var(--color-accent)' },
                                    '&.Mui-focused fieldset': { borderColor: 'var(--color-accent)' },
                                  },
                                }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <SearchIcon sx={{ color: 'rgba(var(--color-text-rgb), 0.5)' }} />
                                    </InputAdornment>
                                  ),
                                  endAdornment: projectMembersSearch ? (
                                    <InputAdornment position="end">
                                      <IconButton size="small" onClick={() => setProjectMembersSearch('')}>
                                        <CloseIcon fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  ) : undefined,
                                }}
                              />

                              {projectMembersLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                  <CircularProgress />
                                </Box>
                              ) : projectMembersSearch.trim() ? (
                                //  Search results: list view ─
                                filteredProjectMembers.length === 0 ? (
                                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                    No matching members found
                                  </Typography>
                                ) : (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {filteredProjectMembers.map(({ role, member }, idx) => (
                                      <Card
                                        key={idx}
                                        onClick={() => onMemberTap?.(member)}
                                        sx={{
                                          bgcolor: 'var(--color-surface)',
                                          border: '1px solid rgba(var(--color-text-rgb), 0.12)',
                                          boxShadow: 'var(--shadow-card)',
                                          cursor: 'pointer',
                                          transition: 'transform 0.15s, box-shadow 0.15s',
                                          '&:hover': {
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 4px 16px rgba(var(--color-accent-rgb), 0.15)',
                                          },
                                        }}
                                      >
                                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar
                                              src={getPhotoUrl(member.photo)}
                                              sx={{ width: 44, height: 44, bgcolor: 'primary.main', fontSize: '0.85rem', flexShrink: 0 }}
                                            >
                                              {(member.name || 'N/A')
                                                .split(' ')
                                                .map((part: string) => part[0])
                                                .slice(0, 2)
                                                .join('')
                                                .toUpperCase()}
                                            </Avatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                              <Typography variant="body1" fontWeight="bold" sx={{ color: 'var(--color-text-strong)', lineHeight: 1.3 }}>
                                                {member.name || 'N/A'}
                                              </Typography>
                                              <Typography variant="caption" sx={{ color: 'var(--color-accent)', fontWeight: 600, display: 'block', lineHeight: 1.4 }}>
                                                {role}
                                              </Typography>
                                              <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', display: 'block', lineHeight: 1.3 }}>
                                                CEA: {member.ceaNumber || 'N/A'}
                                              </Typography>
                                              {member.mobile && (
                                                <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', display: 'block', lineHeight: 1.3 }}>
                                                  {member.mobile}
                                                </Typography>
                                              )}
                                            </Box>
                                            <Box sx={{ flexShrink: 0 }}>
                                              <Avatar sx={{ width: 32, height: 32, bgcolor: 'transparent', border: '1px solid rgba(var(--color-text-rgb), 0.15)' }}>
                                                <ArrowForwardIcon sx={{ fontSize: 16, color: 'var(--color-accent)' }} />
                                              </Avatar>
                                            </Box>
                                          </Box>
                                        </CardContent>
                                      </Card>
                                    ))}
                                  </Box>
                                )
                              ) : projectMembers && projectMembers.members.length > 0 ? (
                                // ── Default: grouped table view ─
                                <Box>
                                  {projectMembers.members.map((roleGroup, roleIndex) => {
                                    const showTaggers = roleGroup.members.some((member) => member.taggers && member.taggers.length > 0);
                                    const showPcs = roleGroup.members.some((member) => member.pcs && member.pcs.length > 0);

                                    const renderAssignees = (people: ProjectMemberInfo[] | undefined) => {
                                      if (!people || people.length === 0) {
                                        return (
                                          <Typography variant="caption" color="text.secondary">
                                            -
                                          </Typography>
                                        );
                                      }
                                      return (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                          {people.map((person, personIndex) => (
                                            <Box key={`${person.userId}-${personIndex}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <Avatar
                                                src={getPhotoUrl(person.photo)}
                                                sx={{ width: 28, height: 28, bgcolor: 'primary.light', fontSize: '0.75rem' }}
                                              >
                                                {(person.name || 'N/A')
                                                  .split(' ')
                                                  .map((part) => part[0])
                                                  .slice(0, 2)
                                                  .join('')
                                                  .toUpperCase()}
                                              </Avatar>
                                              <Box>
                                                <Typography variant="caption" fontWeight="medium" display="block">
                                                  {person.name || 'N/A'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                  CEA: {person.ceaNumber || 'N/A'}
                                                </Typography>
                                              </Box>
                                            </Box>
                                          ))}
                                        </Box>
                                      );
                                    };

                                    return (
                                      <Box key={roleIndex} sx={{ mb: 3 }}>
                                        <Typography
                                          variant="subtitle1"
                                          sx={{
                                            fontWeight: 600,
                                            mb: 1.5,
                                            pb: 1,
                                            borderBottom: '1px solid',
                                            borderColor: 'divider',
                                          }}
                                        >
                                          {roleGroup.role}
                                        </Typography>
                                        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Agent</TableCell>
                                                <TableCell sx={{ fontWeight: 600, width: 140 }}>CEA</TableCell>
                                                <TableCell sx={{ fontWeight: 600, width: 160 }}>Mobile</TableCell>
                                                {showPcs && (
                                                  <TableCell sx={{ fontWeight: 600 }}>PCs</TableCell>
                                                )}
                                                {showTaggers && (
                                                  <TableCell sx={{ fontWeight: 600 }}>Taggers</TableCell>
                                                )}
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {roleGroup.members.map((member, memberIndex) => (
                                                <TableRow
                                                  key={memberIndex}
                                                  hover
                                                  onClick={() => onMemberTap?.(member)}
                                                  sx={{ cursor: 'pointer' }}
                                                >
                                                  <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                      <Avatar
                                                        src={getPhotoUrl(member.photo)}
                                                        sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontSize: '0.85rem' }}
                                                      >
                                                        {(member.name || 'N/A')
                                                          .split(' ')
                                                          .map((part) => part[0])
                                                          .slice(0, 2)
                                                          .join('')
                                                          .toUpperCase()}
                                                      </Avatar>
                                                      <Box>
                                                        <Typography variant="body2" fontWeight="medium">
                                                          {member.name || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                          {roleGroup.role}
                                                        </Typography>
                                                      </Box>
                                                    </Box>
                                                  </TableCell>
                                                  <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                      {member.ceaNumber || 'N/A'}
                                                    </Typography>
                                                  </TableCell>
                                                  <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                      {member.mobile || 'N/A'}
                                                    </Typography>
                                                  </TableCell>
                                                  {showPcs && (
                                                    <TableCell>{renderAssignees(member.pcs)}</TableCell>
                                                  )}
                                                  {showTaggers && (
                                                    <TableCell>{renderAssignees(member.taggers)}</TableCell>
                                                  )}
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No project members found
                                </Typography>
                              )}
                            </Box>
                          ) : null
                        )}
                        </Box>
                      </Grid>
                      {/* Right Side - Map */}
                      {(projectDetailsTab !== 'members' || !canViewProjectMembers) && projectDetailsTab !== 'files' && projectDetailsTab !== 'media' && (
                        <Grid item xs={12} md={6}>
                          {projectDetails.mapLatitude && projectDetails.mapLongitude ? (
                            <Box
                              data-pdf-capture="amenities-map"
                              sx={{
                                width: '100%',
                                height: '100%',
                                minHeight: 400,
                                borderRadius: 1,
                                overflow: 'hidden',
                                border: '1px solid',
                                borderColor: 'divider',
                                position: 'relative',
                              }}
                            >
                              {canAccessSuperRatio && projectDetailsTab === 'superRatio' ? (
                                <DemandSupplyMap
                                  latitude={projectDetails.mapLatitude}
                                  longitude={projectDetails.mapLongitude}
                                  data={demandSupplyData}
                                  radius={demandSupplyRadius}
                                  minRadius={demandSupplyMinRadius}
                                  nationwide={demandSupplyNationwide}
                                  selectedTypes={selectedDemandSupplyTypes}
                                />
                              ) : (
                                <ProjectMap
                                  latitude={projectDetails.mapLatitude}
                                  longitude={projectDetails.mapLongitude}
                                  tab={projectDetailsTab}
                                  amenitiesData={amenitiesData}
                                  radius={amenitiesRadius}
                                  selectedAmenityTypes={selectedAmenityTypes}
                                />
                              )}
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                width: '100%',
                                height: '100%',
                                minHeight: 400,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'grey.100',
                              }}
                            >
                              <Typography variant="body2" color="text.secondary">
                                Map location not available
                              </Typography>
                            </Box>
                          )}
                        </Grid>
                      )}
                    </Grid>
                  ) : (
                    <Box sx={{ py: 2 }}>
                      <Typography variant="body2" color="text.secondary" align="center">
                        Loading project details...
                      </Typography>
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Card>
      )}

      {/* Super Ratio quick-look — near-fullscreen so the chart and the map both
          get real room. Mounted outside the accordion so collapsing the header
          never unmounts an open dialog. */}
      <Dialog
        open={superRatioDialogOpen && canOpenSuperRatioDialog}
        onClose={() => setSuperRatioDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '90vw',
            height: '90vh',
            maxWidth: '90vw',
            maxHeight: '90vh',
            m: 0,
            bgcolor: 'var(--color-surface)',
            color: 'rgba(var(--color-text-rgb), 0.9)',
            backgroundImage:
              'radial-gradient(circle at 0% 0%, rgba(237, 137, 54, 0.12), transparent 42%)',
            border: '1px solid rgba(237, 137, 54, 0.25)',
            borderRadius: 2,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            py: 1.5,
            borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <DonutLargeIcon sx={{ color: 'var(--color-accent)' }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  fontSize: '0.95rem',
                  color: 'var(--color-text-strong)',
                  lineHeight: 1.2,
                }}
              >
                Super Ratio
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(var(--color-text-rgb), 0.65)',
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {projectDetails?.name} · Potential HDB upgrader demand vs competitive supply
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setSuperRatioDialogOpen(false)}
            sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', flexShrink: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        {/* Desktop fills the fixed height with two columns; stacked mobile
            scrolls the dialog itself instead of being clipped. */}
        <DialogContent sx={{ p: 0, overflowY: { xs: 'auto', md: 'hidden' }, overflowX: 'hidden' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 5fr) minmax(0, 6fr)' },
              gridTemplateRows: { xs: 'auto auto', md: '1fr' },
              height: { xs: 'auto', md: '100%' },
              minHeight: 0,
            }}
          >
            <Box sx={{ minWidth: 0, minHeight: 0, overflowY: { xs: 'visible', md: 'auto' }, p: 2 }}>
              <DemandSupplyPanel
                data={demandSupplyData}
                loading={demandSupplyLoading}
                radius={demandSupplyRadius}
                minRadius={demandSupplyMinRadius}
                onRadiusChange={onDemandSupplyRadiusChange}
                nationwide={demandSupplyNationwide}
                onNationwideChange={onDemandSupplyNationwideChange}
                selectedTypes={selectedDemandSupplyTypes}
                setSelectedTypes={setSelectedDemandSupplyTypes}
              />
            </Box>
            <Box
              sx={{
                minWidth: 0,
                minHeight: { xs: 420, md: 0 },
                borderLeft: { xs: 'none', md: '1px solid rgba(var(--color-text-rgb), 0.08)' },
                borderTop: { xs: '1px solid rgba(var(--color-text-rgb), 0.08)', md: 'none' },
              }}
            >
              {projectDetails?.mapLatitude && projectDetails?.mapLongitude && (
                <DemandSupplyMap
                  latitude={projectDetails.mapLatitude}
                  longitude={projectDetails.mapLongitude}
                  data={demandSupplyData}
                  radius={demandSupplyRadius}
                  minRadius={demandSupplyMinRadius}
                  nationwide={demandSupplyNationwide}
                  selectedTypes={selectedDemandSupplyTypes}
                />
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Detail View */}
              <Grid container spacing={2} sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                <Grid item xs={12} sx={{ minWidth: 0, flex: 1 }}>
                {projectDetails?.liveStatusEnabled && (
                  <Box
                    sx={{
                      mb: 2,
                      px: 2,
                      py: 1.25,
                      borderRadius: 2,
                      border: '1px solid var(--color-danger)',
                      bgcolor: 'rgba(var(--color-danger-rgb), 0.08)',
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 0.75,
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: 'var(--color-danger)',
                        animation: 'pulseBlink 1s ease-in-out infinite',
                        '@keyframes pulseBlink': {
                          '0%': { opacity: 0.2, transform: 'scale(0.8)' },
                          '50%': { opacity: 1, transform: 'scale(1.2)' },
                          '100%': { opacity: 0.2, transform: 'scale(0.8)' },
                        },
                      }}
                    />
                    <Typography sx={{ color: 'var(--color-danger)', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      Live Update ←
                    </Typography>
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: 'var(--color-danger)',
                        animation: 'pulseBlink 1s ease-in-out infinite',
                      }}
                    />
                    <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', whiteSpace: 'nowrap' }}>
                      Last updated at {formatLiveStatusTimestamp(projectDetails.liveStatusUpdatedAt)}.
                    </Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: { xs: 1, sm: 1.25 },
                    mb: 2,
                  }}
                >
                  {Boolean(
                    filesFactsheetData && (
                      filesFactsheetData.url?.trim() ||
                      filesFactsheetData.imageLarge ||
                      filesFactsheetData.imageMedium ||
                      filesFactsheetData.imageSmall
                    )
                  ) && (
                    <QuickActionTile
                      label="Factsheet"
                      icon={<DescriptionIcon fontSize="small" />}
                      onClick={handleOpenFactsheet}
                      disabled={!currentSitePlan?.projectId}
                    />
                  )}
                  {Boolean(
                    filesBrochureData && (
                      filesBrochureData.url?.trim() ||
                      filesBrochureData.imageLarge ||
                      filesBrochureData.imageMedium ||
                      filesBrochureData.imageSmall
                    )
                  ) && (
                    <QuickActionTile
                      label="Brochure"
                      icon={<MenuBookIcon fontSize="small" />}
                      onClick={handleOpenBrochure}
                      disabled={!currentSitePlan?.projectId && !projectDetails?.id}
                    />
                  )}
                  {filesEdms.length > 0 && (
                    <QuickActionTile
                      label="EDMs"
                      icon={<CampaignIcon fontSize="small" />}
                      count={filesEdms.length}
                      onClick={() => {
                        setFileListDialogType('edm');
                        setFileListDialogOpen(true);
                      }}
                    />
                  )}
                  {filesEbooks.length > 0 && (
                    <QuickActionTile
                      label="eBooks"
                      icon={<MenuBookIcon fontSize="small" />}
                      count={filesEbooks.length}
                      onClick={() => {
                        setFileListDialogType('ebook');
                        setFileListDialogOpen(true);
                      }}
                    />
                  )}
                  {(projectDetails?.id || currentSitePlan?.projectId) && (
                    <QuickActionTile
                      label="Agent Access Folder"
                      icon={<FolderSharedIcon fontSize="small" />}
                      count={projectResourceLinks.length}
                      onClick={() => setAgentAccessDialogOpen(true)}
                    />
                  )}
                  {isAdminUser && projectPodcastVideos.length > 0 && (
                    <QuickActionTile
                      label="Podcast Videos"
                      icon={<PodcastsIcon fontSize="small" />}
                      count={projectPodcastVideos.length}
                      onClick={() => setPodcastVideosDialogOpen(true)}
                    />
                  )}
                  {isAdminUser && (currentSitePlan?.projectId || projectDetails?.id) && (
                    <QuickActionTile
                      label="Training Videos"
                      icon={<OndemandVideoIcon fontSize="small" />}
                      count={projectTrainingVideos.length}
                      onClick={() => setTrainingVideosDialogOpen(true)}
                    />
                  )}
                  {hasProjectTrainingEvents && (
                    <QuickActionTile
                      label="Training"
                      icon={<SchoolIcon fontSize="small" />}
                      onClick={() => {
                        logProjectTrainingClick(currentProjectId, ceaNumber);
                        setProjectTrainingRegistrationOpen(true);
                      }}
                    />
                  )}
                  {onExportProjectReport && (
                    <QuickActionTile
                      label="Export Report"
                      icon={<PictureAsPdfIcon fontSize="small" />}
                      onClick={() => void onExportProjectReport()}
                    />
                  )}
                </Box>
                <Card data-tutorial="site-plan" sx={{ ...mobileOptimizedStyles, mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Site Plan
                        </Typography>
                        {sitePlanOriginalUrl && sitePlanViewMode === '2d' && (
                          <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={sitePlanShowOriginal ? 'original' : 'edited'}
                            onChange={(_, value) => value && setSitePlanShowOriginal(value === 'original')}
                            aria-label="Site plan version"
                            sx={{
                              '& .MuiToggleButton-root': {
                                px: 1.25,
                                py: 0.125,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                lineHeight: 1.6,
                                textTransform: 'none',
                              },
                            }}
                          >
                            <ToggleButton value="edited">Edited</ToggleButton>
                            <ToggleButton value="original">Original</ToggleButton>
                          </ToggleButtonGroup>
                        )}
                      </Box>
                      {(((canAccessSitePlan3d || canAccessSitePlan3dSun) && sitePlanData?.imageUrl) || sitePlanDroneView || sitePlanViewSlides.length > 0) && (
                        <ToggleButtonGroup
                          size="small"
                          exclusive
                          value={sitePlanViewMode}
                          onChange={(_, value) => value && setSitePlanViewMode(value)}
                          sx={{
                            '& .MuiToggleButton-root': {
                              px: 1.5,
                              py: 0.25,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              lineHeight: 1.6,
                            },
                          }}
                        >
                          <ToggleButton value="2d">2D</ToggleButton>
                          {canAccessSitePlan3d && sitePlanData?.imageUrl && <ToggleButton value="3d">3D</ToggleButton>}
                          {canAccessSitePlan3dSun && sitePlanData?.imageUrl && <ToggleButton value="3ds">3DS</ToggleButton>}
                          {sitePlanDroneView && <ToggleButton value="drone">Drone</ToggleButton>}
                          {sitePlanViewSlides.length > 0 && <ToggleButton value="views">Views</ToggleButton>}
                        </ToggleButtonGroup>
                      )}
                    </Box>
                    {/* Defence in depth: `sitePlanViewMode` already resolves to 2D
                        without the matching grant, while this explicit gate prevents
                        the WebGL feature from mounting if that logic is changed later. */}
                    {sitePlanViewMode === 'drone' && sitePlanDroneView ? (
                      <SitePlanDroneView droneView={sitePlanDroneView} />
                    ) : sitePlanViewMode === 'views' && sitePlanViewSlides.length > 0 ? (
                      <SitePlanViewsSlider slides={sitePlanViewSlides} />
                    ) : ((sitePlanViewMode === '3d' && canAccessSitePlan3d) || (sitePlanViewMode === '3ds' && canAccessSitePlan3dSun)) && sitePlanData?.imageUrl ? (
                      <React.Suspense fallback={<Box sx={{ p: 6, textAlign: 'center' }}>Loading 3D site plan…</Box>}>
                        <SolarSitePlan key={`${currentSitePlan?.projectId}-${sitePlanData.imageUrl}`}
                          site={sitePlanData} projectId={currentSitePlan?.projectId || currentProjectId || ''}
                          latitude={currentSitePlan?.mapLatitude ?? (projectDetailsForLandLotMap?.name === selectedTopViewingSitePlan ? projectDetailsForLandLotMap?.mapLatitude : null)}
                          longitude={currentSitePlan?.mapLongitude ?? (projectDetailsForLandLotMap?.name === selectedTopViewingSitePlan ? projectDetailsForLandLotMap?.mapLongitude : null)}
                          towers={solarTowers} onFallback={() => setSitePlanViewMode('2d')}
                          imageProxyUrl={buildSitePlanImageProxyUrl(currentSitePlan?.projectId || currentProjectId)}
                          sunlight={sitePlanViewMode === '3ds'} canCalibrateNorth={isAdminUser} canViewNearbyMap={isAdminUser}
                          projectName={sitePlanProjectName || selectedTopViewingSitePlan} />
                      </React.Suspense>
                    ) : sitePlanData?.imageUrl ? (
                      <Box
                        data-pdf-capture="site-map"
                        onPointerDown={handleSitePlan3dPointerDown}
                        onPointerMove={handleSitePlan3dPointerMove}
                        onPointerUp={handleSitePlan3dPointerUp}
                        onPointerCancel={handleSitePlan3dPointerUp}
                        onClickCapture={handleSitePlan3dClickCapture}
                        sx={{
                          ...gpuAccelerationStyles,
                          position: 'relative',
                          border: '1px solid rgba(var(--color-text-rgb), 0.12)',
                          borderRadius: 1,
                          overflow: 'hidden',
                          height: 'min(600px, 65vh)',
                          touchAction: sitePlanIs3d ? 'none' : undefined,
                          cursor: sitePlanIs3d ? 'grab' : undefined,
                          '&:active': sitePlanIs3d ? { cursor: 'grabbing' } : undefined,
                        }}
                      >
                        <TransformWrapper
                          key={sitePlanShowOriginal ? 'original' : 'edited'}
                          initialScale={1}
                          minScale={SITE_PLAN_MIN_SCALE}
                          maxScale={4}
                          centerOnInit={true}
                          limitToBounds={true}
                          centerZoomedOut={true}
                          disablePadding={true}
                          wheel={{
                            step: 0.05,
                          }}
                          pinch={{
                            step: 5,
                          }}
                          doubleClick={{
                            disabled: false,
                            step: 0.7,
                          }}
                          panning={{
                            // In 3D, single-pointer drag orbits the map instead of panning.
                            disabled: sitePlanIs3d,
                            velocityDisabled: false,
                          }}
                        >
                          {({ zoomIn, zoomOut, resetTransform, setTransform, instance }) => {
                            const currentScale = instance?.transformState?.scale ?? 1;
                            const isAtMinScale = currentScale <= SITE_PLAN_MIN_SCALE + 0.001;
                            const handleZoomOut = () => {
                              if (isAtMinScale) {
                                if (instance && currentScale < SITE_PLAN_MIN_SCALE) {
                                  const { positionX, positionY } = instance.transformState;
                                  setTransform(positionX, positionY, SITE_PLAN_MIN_SCALE);
                                }
                                return;
                              }
                              zoomOut();
                            };

                            return (
                              <>
                              <TransformComponent
                                wrapperStyle={{ width: '100%', height: 'min(600px, 65vh)', overflow: 'hidden' }}
                                contentStyle={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', perspective: sitePlanIs3d ? '1400px' : undefined }}
                              >
                                <Box
                                  ref={sitePlanPlaneRef}
                                  sx={{
                                    position: 'relative',
                                    display: 'inline-block',
                                    transform: sitePlanIs3d ? `rotateX(${SITE_PLAN_3D_DEFAULT_TILT}deg)` : 'none',
                                    transformStyle: sitePlanIs3d ? 'preserve-3d' : 'flat',
                                    transition: 'transform 0.4s ease',
                                  }}
                                >
                                  <Box
                                    component="img"
                                    data-magnify-image="site-plan"
                                    src={sitePlanShowOriginal ? sitePlanOriginalUrl! : sitePlanData.imageUrl}
                                    alt={sitePlanShowOriginal ? 'Original site plan' : 'Site Plan'}
                                    sx={{
                                      maxWidth: '100%',
                                      maxHeight: 'min(600px, 65vh)',
                                      width: 'auto',
                                      height: 'auto',
                                      display: 'block',
                                      objectFit: 'contain',
                                    }}
                                  />
                                  {sitePlanShowPoints && !sitePlanShowOriginal && sitePlanData.blocks.filter((block) => block.stack && block.facing).map((stack, idx) => {
                          const bounds = sitePlanData.bounds;
                          const left = bounds ? bounds.x + (stack.x / 100) * bounds.width : stack.x;
                          const top = bounds ? bounds.y + (stack.y / 100) * bounds.height : stack.y;
                          const width = bounds ? (stack.width / 100) * bounds.width : stack.width;
                          const height = bounds ? (stack.height / 100) * bounds.height : stack.height;

                          // Normalize stack number for comparison
                          const normalizedStackNum = normalizeStackKey(stack.stack);
                          if (shouldHideNonMatchingSitePlanStack(stack.stack, stack.blockNumber)) {
                            return null;
                          }
                          const blockNum = (stack.blockNumber || '').replace(/\D/g, '');
                          const compositeKey = blockNum && stack.stack ? `${blockNum}-${String(stack.stack).trim()}` : null;
                          const isBlinking = (normalizedStackNum && sitePlanBlinkingStacks.has(normalizedStackNum)) ||
                            (compositeKey != null && sitePlanBlinkingStacks.has(compositeKey));
                          const soldPercentage = stack.stack
                            ? (sitePlanStackSoldPercentages.get(normalizedStackNum)
                              ?? sitePlanStackSoldPercentages.get(String(stack.stack))
                              ?? 0)
                            : 0;
                          const dotColor = sitePlanShowHeatMap ? getHeatMapColor(soldPercentage) : 'var(--color-success)';
                          const labelColor = soldPercentage === 100 ? 'var(--color-black)' : 'var(--color-white)';
                          const borderColor = soldPercentage === 100 ? '2px solid var(--color-black)' : sitePlanDotBorder;

                          if (sitePlanIs3d) {
                            const floors = (blockNum ? sitePlanStackFloorCounts.counts.get(`${blockNum}-${normalizedStackNum}`) : undefined)
                              ?? sitePlanStackFloorCounts.counts.get(normalizedStackNum)
                              ?? 0;
                            const maxFloors = sitePlanStackFloorCounts.maxFloors;
                            const pxPerFloor = maxFloors > 0 ? Math.min(6, Math.max(2.5, 110 / maxFloors)) : 0;
                            const extrusion = floors > 0
                              ? Math.round(floors * pxPerFloor)
                              : (maxFloors > 0 ? Math.max(10, Math.round(maxFloors * pxPerFloor * 0.4)) : 24);
                            const faceSx = {
                              position: 'absolute',
                              // Translucent "glass" fill with a crisp full-strength edge.
                              backgroundColor: `color-mix(in srgb, ${dotColor} 40%, transparent)`,
                              border: `1px solid ${dotColor}`,
                            } as const;
                            const sideShade = 'linear-gradient(rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0.12))';
                            const sideShadeDark = 'linear-gradient(rgba(0, 0, 0, 0.22), rgba(0, 0, 0, 0.22))';
                            return (
                              <Box
                                key={`top-stack-${stack.stack}-${stack.facing}-${idx}`}
                                onClick={() => stack.stack && sitePlanProjectName && handleOpenStackAnalysis(sitePlanProjectName, stack.stack, stack.blockNumber)}
                                title={`${stack.blockNumber ? `Blk ${stack.blockNumber} ` : ''}Stack ${stack.stack}${floors > 0 ? ` · ${floors} storeys` : ''}`}
                                sx={{
                                  position: 'absolute',
                                  left: `${left}%`,
                                  top: `${top}%`,
                                  width: `${width}%`,
                                  height: `${height}%`,
                                  cursor: 'pointer',
                                  transformStyle: 'preserve-3d',
                                }}
                              >
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    // Inset shrinks the tower footprint to ~64% of the admin-drawn
                                    // region; the full region (parent) stays the click target.
                                    inset: '18%',
                                    transformStyle: 'preserve-3d',
                                    // Rise from the ground once the map tilt (0.4s) settles; scaleZ
                                    // squashes the whole cuboid onto the plane and grows it upward.
                                    '@keyframes sitePlan3dRise': {
                                      from: { transform: 'scaleZ(0.001)' },
                                      to: { transform: 'scaleZ(1)' },
                                    },
                                    animation: `sitePlan3dRise 0.7s ease-out ${(0.45 + Math.min(idx * 0.03, 0.6)).toFixed(2)}s both`,
                                    '@keyframes sitePlan3dBlink': {
                                      '0%, 100%': { opacity: 1 },
                                      '50%': { opacity: 0.3 },
                                    },
                                    // Blink each face, not a 3D ancestor: opacity < 1 on an element
                                    // with preserve-3d flattens it and collapses the cuboid.
                                    '& > *': {
                                      animation: isBlinking ? 'sitePlan3dBlink 0.5s ease-in-out infinite' : 'none',
                                    },
                                  }}
                                >
                                <Box
                                  sx={{
                                    ...faceSx,
                                    inset: 0,
                                    backgroundColor: `color-mix(in srgb, ${dotColor} 62%, transparent)`,
                                    border: borderColor,
                                    transform: `translateZ(${extrusion}px)`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: sitePlanDotFontSize,
                                    fontWeight: 'bold',
                                    color: labelColor,
                                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  <Box component="span" sx={{ display: 'inline-block', transform: 'rotate(calc(-1 * var(--site-plan-3d-rz, 0deg)))' }}>
                                    {stack.stack}
                                  </Box>
                                </Box>
                                <Box sx={{ ...faceSx, left: 0, top: '100%', width: '100%', height: `${extrusion}px`, transformOrigin: 'center top', transform: 'rotateX(90deg)', backgroundImage: sideShade }} />
                                <Box sx={{ ...faceSx, left: 0, bottom: '100%', width: '100%', height: `${extrusion}px`, transformOrigin: 'center bottom', transform: 'rotateX(-90deg)', backgroundImage: sideShade }} />
                                <Box sx={{ ...faceSx, top: 0, right: '100%', width: `${extrusion}px`, height: '100%', transformOrigin: 'center right', transform: 'rotateY(90deg)', backgroundImage: sideShadeDark }} />
                                <Box sx={{ ...faceSx, top: 0, left: '100%', width: `${extrusion}px`, height: '100%', transformOrigin: 'center left', transform: 'rotateY(-90deg)', backgroundImage: sideShadeDark }} />
                                </Box>
                              </Box>
                            );
                          }

                          return (
                            <Box
                              key={`top-stack-${stack.stack}-${stack.facing}-${idx}`}
                              onClick={() => stack.stack && sitePlanProjectName && handleOpenStackAnalysis(sitePlanProjectName, stack.stack, stack.blockNumber)}
                              sx={{
                                position: 'absolute',
                                left: `${left}%`,
                                top: `${top}%`,
                                width: `${width}%`,
                                height: `${height}%`,
                                cursor: 'pointer',
                                '@keyframes sitePlanBlink': {
                                  '0%, 100%': {
                                    opacity: 1,
                                    transform: 'translate(-50%, -50%) scale(1)',
                                  },
                                  '50%': {
                                    opacity: 0.3,
                                    transform: 'translate(-50%, -50%) scale(1.3)',
                                  },
                                },
                                '&::before': {
                                  content: `"${stack.stack}"`,
                                  position: 'absolute',
                                  left: '50%',
                                  top: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  width: `${sitePlanDotSize}px`,
                                  height: `${sitePlanDotSize}px`,
                                  borderRadius: '50%',
                                  backgroundColor: dotColor,
                                  border: borderColor,
                                  boxShadow: sitePlanDotShadow,
                                  zIndex: 10,
                                  animation: isBlinking ? 'sitePlanBlink 0.5s ease-in-out infinite' : 'none',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: sitePlanDotFontSize,
                                  fontWeight: 'bold',
                                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
                                  color: labelColor,
                                },
                              }}
                            />
                          );
                        })}
                                </Box>
                              </TransformComponent>

                              {/* Zoom Controls */}
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: 16,
                                  right: 16,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 1,
                                  zIndex: 100,
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => zoomIn()}
                                  sx={{
                                    bgcolor: 'var(--color-surface)',
                                    border: '1px solid rgba(var(--color-text-rgb), 0.2)',
                                    '&:hover': { bgcolor: 'var(--color-surface-hover)' },
                                  }}
                                >
                                  <ZoomInIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={handleZoomOut}
                                  disabled={isAtMinScale}
                                  sx={{
                                    bgcolor: 'var(--color-surface)',
                                    border: '1px solid rgba(var(--color-text-rgb), 0.2)',
                                    '&:hover': { bgcolor: 'var(--color-surface-hover)' },
                                  }}
                                >
                                  <ZoomOutIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    resetTransform();
                                    if (sitePlanIs3d) resetSitePlan3dView();
                                  }}
                                  sx={{
                                    bgcolor: 'var(--color-surface)',
                                    border: '1px solid rgba(var(--color-text-rgb), 0.2)',
                                    '&:hover': { bgcolor: 'var(--color-surface-hover)' },
                                  }}
                                >
                                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{sitePlanIs3d ? 'Reset View' : 'Reset Zoom'}</Typography>
                                </IconButton>
                              </Box>
                              {sitePlanIs3d && (
                                <Typography
                                  sx={{
                                    position: 'absolute',
                                    top: 8,
                                    left: 8,
                                    zIndex: 100,
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    bgcolor: 'rgba(0, 0, 0, 0.45)',
                                    color: '#fff',
                                    pointerEvents: 'none',
                                  }}
                                >
                                  Drag to rotate · Pinch or scroll to zoom
                                </Typography>
                              )}
                              </>
                            );
                          }}
                        </TransformWrapper>
                      </Box>
                    ) : projectDetailsForLandLotMap?.mapLatitude != null
                      && projectDetailsForLandLotMap?.mapLongitude != null
                      && Number.isFinite(Number(projectDetailsForLandLotMap.mapLatitude))
                      && Number.isFinite(Number(projectDetailsForLandLotMap.mapLongitude)) ? (
                      <Box>
                        <Box
                          sx={{
                            mb: 1.5,
                            py: 1.25,
                            px: 1.5,
                            borderRadius: 1.5,
                            border: '2px solid rgba(var(--color-accent-rgb), 0.55)',
                            bgcolor: 'rgba(var(--color-accent-rgb), 0.14)',
                            boxShadow: '0 2px 8px rgba(var(--color-shadow-rgb), 0.12)',
                            textAlign: 'center',
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 800,
                              letterSpacing: '0.04em',
                              textTransform: 'uppercase',
                              color: 'var(--color-accent)',
                            }}
                          >
                            Site Plan Not Available Yet
                          </Typography>
                        </Box>
                        <Box data-pdf-capture="site-map" sx={{ ...gpuAccelerationStyles, position: 'relative', border: '1px solid rgba(var(--color-text-rgb), 0.12)', borderRadius: 1, overflow: 'hidden' }}>
                          <ProjectLandLotMap
                            latitude={Number(projectDetailsForLandLotMap.mapLatitude)}
                            longitude={Number(projectDetailsForLandLotMap.mapLongitude)}
                            projectName={
                              projectDetailsForLandLotMap?.name
                              ?? selectedTopViewingSitePlan
                              ?? ''
                            }
                          />
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                        Select a site plan to load map details.
                      </Typography>
                    )}
                    {sitePlanShowOriginal && (
                      <Typography variant="body2" sx={{ mt: 1.5, color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                        Showing the developer's original site plan. Switch to Edited for unit points and filters.
                      </Typography>
                    )}
                    <Box sx={{ mt: 2, display: sitePlanShowOriginal ? 'none' : 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                      <FormControlLabel control={<Switch checked={sitePlanShowPoints} onChange={(e) => setSitePlanShowPoints(e.target.checked)} />} label="Show points" />
                      <FormControlLabel control={<Switch checked={sitePlanShowHeatMap} onChange={(e) => setSitePlanShowHeatMap(e.target.checked)} />} label="Sold heat map" />
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id="top-siteplan-bedroom-filter">Bedroom</InputLabel>
                        <Select
                          labelId="top-siteplan-bedroom-filter"
                          multiple
                          value={sitePlanBedroomFilter.length === 0 ? [ANY_FILTER_VALUE] : sitePlanBedroomFilter}
                          onChange={(e) => {
                            const values = e.target.value as Array<string | number>;
                            if (values.includes(ANY_FILTER_VALUE)) {
                              const withoutAny = values.filter((v) => v !== ANY_FILTER_VALUE);
                              if (sitePlanBedroomFilter.length > 0) {
                                setSitePlanBedroomFilter([]);
                              } else {
                                setSitePlanBedroomFilter(withoutAny.map((v) => Number(v)).filter((v) => Number.isFinite(v)));
                              }
                            } else {
                              setSitePlanBedroomFilter(values.map((v) => Number(v)).filter((v) => Number.isFinite(v)));
                            }
                          }}
                          label="Bedroom"
                          renderValue={(selected) => {
                            const filtered = (selected as Array<string | number>).filter((v) => v !== ANY_FILTER_VALUE);
                            return filtered.length === 0 ? 'Any' : filtered.map((value) => value === PENTHOUSE_FILTER_VALUE ? 'PH' : `${value}BR`).join(', ');
                          }}
                        >
                          <MenuItem key="any" value={ANY_FILTER_VALUE}>Any</MenuItem>
                          {sitePlanBedroomFilterOptions.map((bedroom) => (
                            <MenuItem key={bedroom} value={bedroom}>{bedroom === PENTHOUSE_FILTER_VALUE ? 'Penthouse' : `${bedroom} Bedroom`}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id="top-siteplan-unittype-filter">Unit Type</InputLabel>
                        <Select
                          labelId="top-siteplan-unittype-filter"
                          multiple
                          value={sitePlanUnitTypeFilter.length === 0 ? [ANY_FILTER_VALUE] : sitePlanUnitTypeFilter}
                          onChange={(e) => {
                            const values = e.target.value as Array<string | number>;
                            if (values.includes(ANY_FILTER_VALUE)) {
                              const withoutAny = values.filter((v) => v !== ANY_FILTER_VALUE);
                              if (sitePlanUnitTypeFilter.length > 0) {
                                setSitePlanUnitTypeFilter([]);
                              } else {
                                setSitePlanUnitTypeFilter(withoutAny.map((v) => Number(v)).filter((v) => Number.isFinite(v)));
                              }
                            } else {
                              setSitePlanUnitTypeFilter(values.map((v) => Number(v)).filter((v) => Number.isFinite(v)));
                            }
                          }}
                          label="Unit Type"
                          renderValue={(selected) => {
                            const filtered = (selected as Array<string | number>).filter((v) => v !== ANY_FILTER_VALUE);
                            if (filtered.length === 0) return 'Any';
                            return filtered.map(id => sitePlanUnitTypes.find(ut => ut.id === id)?.description || sitePlanUnitTypes.find(ut => ut.id === id)?.unitType || id).join(', ');
                          }}
                        >
                          <MenuItem key="any" value={ANY_FILTER_VALUE}>Any</MenuItem>
                          {sitePlanUnitTypes.map((ut) => (
                            <MenuItem key={ut.id} value={ut.id}>{ut.description || ut.unitType}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {Array.from(sitePlanStackSoldPercentages.values()).some((value) => value === 100) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 'auto' }}>
                        <Box
                          sx={{
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            backgroundColor: '#ff4d4f',
                            border: '2px solid var(--color-black)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.65rem',
                            fontWeight: 'bold',
                            color: 'var(--color-black)',
                          }}
                        >
                          #
                        </Box>
                        <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                          Stack is fully sold(black label & border)
                        </Typography>
                      </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>

                {towerViewUnits.length > 0 && (
                <Box ref={topViewingTowerViewSectionRef}>
                <Card
                  data-tutorial="tower-view"
                  sx={{ ...mobileOptimizedStyles, mb: 2, position: 'relative', overflow: 'hidden' }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        All Blocks Tower View
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setTowerViewSectionExpanded((prev) => !prev)}
                        sx={{ ml: 'auto' }}
                      >
                        <ExpandMoreIcon sx={{ transform: towerViewSectionExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s ease' }} />
                      </IconButton>
                    </Box>
                    <Collapse in={towerViewSectionExpanded}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={topViewingTowerBedroomColor}
                              onChange={(e) => setTopViewingTowerBedroomColor(e.target.checked)}
                            />
                          }
                          label={<Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>Bedroom color</Typography>}
                          sx={{ ml: 0 }}
                        />
                        {topViewingTowerBedroomColor && sitePlanBedroomFilterOptions.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', mr: 0.5 }}>Bedroom:</Typography>
                            {sitePlanBedroomFilterOptions.map((br) => (
                              <Box key={br} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: getBedroomColor(br), border: '1px solid rgba(255,255,255,0.5)' }} />
                                <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                                  {br === PENTHOUSE_FILTER_VALUE ? 'PH' : `${br}BR`}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto', flexWrap: 'wrap' }}>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                          <InputLabel id="top-tower-stack-filter">Stack</InputLabel>
                          <Select
                            labelId="top-tower-stack-filter"
                            multiple
                            value={sitePlanStackFilter.length === 0 ? [ANY_FILTER_VALUE] : sitePlanStackFilter}
                            onChange={(e) => {
                              const values = e.target.value as Array<string>;
                              if (values.includes(ANY_FILTER_VALUE)) {
                                const withoutAny = values.filter((v) => v !== ANY_FILTER_VALUE);
                                if (sitePlanStackFilter.length > 0) {
                                  setSitePlanStackFilter([]);
                                } else {
                                  setSitePlanStackFilter(withoutAny);
                                }
                              } else {
                                setSitePlanStackFilter(values);
                              }
                            }}
                            label="Stack"
                            renderValue={(selected) => {
                              const filtered = (selected as Array<string>).filter((v) => v !== ANY_FILTER_VALUE);
                              return filtered.length === 0 ? 'Any' : filtered.map(getStackFilterLabel).join(', ');
                            }}
                          >
                            <MenuItem key="any" value={ANY_FILTER_VALUE}>Any</MenuItem>
                            {sitePlanStacksOptions.map((stack) => (
                              <MenuItem key={stack} value={stack}>{getStackFilterLabel(stack)}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Box sx={{ minWidth: 90 }}>
                          <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', display: 'block', mb: 0.25 }}>
                            Show all attributes
                          </Typography>
                          <Switch
                            checked={topViewingTowerDisplayOptions.length === TOP_VIEWING_TOWER_DISPLAY_OPTIONS.length}
                            onChange={(e) => {
                              setTopViewingTowerDisplayOptions(e.target.checked ? [...TOP_VIEWING_TOWER_DISPLAY_OPTIONS] : []);
                            }}
                          />
                        </Box>
                        <FormControl size="small" sx={{ minWidth: 240 }}>
                          <InputLabel id="top-tower-display-options-label">Show</InputLabel>
                          <Select
                            labelId="top-tower-display-options-label"
                            multiple
                            value={topViewingTowerDisplayOptions}
                            onChange={(e) => setTopViewingTowerDisplayOptions(e.target.value as Array<'bedroom' | 'price' | 'psf' | 'size'>)}
                            label="Show"
                            renderValue={(selected) => selected.length === 0 ? 'Unit no. only' : selected.join(', ')}
                          >
                            <MenuItem value="bedroom">
                              <Checkbox size="small" checked={topViewingTowerDisplayOptions.indexOf('bedroom') > -1} sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', p: 0.5, mr: 1 }} />
                              Show bedroom type
                            </MenuItem>
                            <MenuItem value="price">
                              <Checkbox size="small" checked={topViewingTowerDisplayOptions.indexOf('price') > -1} sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', p: 0.5, mr: 1 }} />
                              Show price
                            </MenuItem>
                            <MenuItem value="psf">
                              <Checkbox size="small" checked={topViewingTowerDisplayOptions.indexOf('psf') > -1} sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', p: 0.5, mr: 1 }} />
                              Show PSF
                            </MenuItem>
                            <MenuItem value="size">
                              <Checkbox size="small" checked={topViewingTowerDisplayOptions.indexOf('size') > -1} sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', p: 0.5, mr: 1 }} />
                              Show size
                            </MenuItem>
                          </Select>
                        </FormControl>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', border: '1px solid rgba(var(--color-text-rgb), 0.2)', borderRadius: 1 }}>
                          <IconButton size="small" onClick={() => setTopViewingTowerZoom((prev) => Math.max(0.5, prev - 0.1))} disabled={topViewingTowerZoom <= 0.5}>
                            <ZoomOutIcon fontSize="small" />
                          </IconButton>
                          <Typography variant="caption" sx={{ minWidth: 44, textAlign: 'center' }}>
                            {Math.round(topViewingTowerZoom * 100)}%
                          </Typography>
                          <IconButton size="small" onClick={() => setTopViewingTowerZoom((prev) => Math.min(2, prev + 0.1))} disabled={topViewingTowerZoom >= 2}>
                            <ZoomInIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ ...scrollableStyles, maxHeight: { xs: 520, md: isTabletLandscape ? 'calc(100vh - 220px)' : 'calc(100vh - 300px)' }, overflow: 'auto' }}>
                      <Box
                        data-pdf-capture="tower-view"
                        sx={{
                          ...gpuAccelerationStyles,
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 2,
                          width: 'fit-content',
                          minWidth: '100%',
                          zoom: topViewingTowerZoom,
                          transformOrigin: 'top left',
                          ...(isPdfCapturing ? { bgcolor: '#ffffff', p: 2 } : {}),
                        }}
                      >
                        {towerViewData.blockNumbers.map((block) => {
                        const blockData = towerViewData.blocks.get(block);
                        if (!blockData || blockData.floors.length === 0 || blockData.stacks.length === 0) return null;

                        // Filter logic
                        const hasBedroomFilter = sitePlanBedroomFilter.length > 0;
                        const hasUnitTypeFilter = sitePlanUnitTypeFilter.length > 0;
                        const hasStackFilter = sitePlanStackFilter.length > 0;
                        const hasAnyFilter = hasBedroomFilter || hasUnitTypeFilter || hasStackFilter;

                        const unitMatchesFilter = sitePlanUnitMatchesFilters;

                        // Only show stacks that have at least one unit matching the filter
                        const visibleStacks = hasAnyFilter
                          ? blockData.stacks.filter((stack) =>
                              blockData.floors.some((floor) => unitMatchesFilter(blockData.grid.get(`${floor}-${stack}`) as TowerViewUnit | ComparableUnit | undefined))
                            )
                          : blockData.stacks;

                        // Skip block if no stacks match filter
                        if (visibleStacks.length === 0) return null;

                        const showTowerPrice = topViewingTowerDisplayOptions.includes('price');
                        const showTowerPsf = topViewingTowerDisplayOptions.includes('psf');
                        const showTowerBedroom = topViewingTowerDisplayOptions.includes('bedroom');
                        const showTowerSize = topViewingTowerDisplayOptions.includes('size');
                        const isCompactTowerView = topViewingTowerDisplayOptions.length === 0;
                        const hasDenseTowerContent = showTowerPrice || showTowerPsf || showTowerSize;
                        const cellWidth = isCompactTowerView ? 40 : hasDenseTowerContent ? 100 : 88;

                          return (
                          <Box key={`top-block-${block}`} sx={{ flex: '0 0 auto' }}>
                            <Box sx={{ p: 1, textAlign: 'center', fontWeight: 700, borderRadius: '6px 6px 0 0', ...(isPdfCapturing ? { bgcolor: '#ed8936', color: '#fff' } : { bgcolor: 'var(--color-accent)', color: 'var(--color-text-inverse)' }) }}>
                              {formatBlockNumber(block)}
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: `40px repeat(${visibleStacks.length}, ${cellWidth}px)`, gridAutoRows: 'auto', gap: '1px', width: 'fit-content', p: '1px', borderRadius: '0 0 6px 6px', ...(isPdfCapturing ? { bgcolor: '#bdbdbd' } : { bgcolor: 'rgba(var(--color-text-rgb), 0.16)' }) }}>
                              <Box sx={isPdfCapturing ? { bgcolor: '#9e9e9e' } : { bgcolor: 'rgba(var(--color-shadow-rgb), 0.35)' }} />
                              {visibleStacks.map((stack) => {
                                // Collect all distinct unit types in this stack
                                const stackUnits = blockData.floors
                                  .map((floor) => blockData.grid.get(`${floor}-${stack}`) as TowerViewUnit | ComparableUnit | undefined)
                                  .filter((u): u is TowerViewUnit | ComparableUnit => !!u);
                                const seenUtCodes = new Set<string>();
                                const allEntries: Array<{ desc: string; unitTypes: string[]; size: number | null }> = [];
                                for (const unit of stackUnits) {
                                  const utId = 'projectUnitTypeId' in unit ? (unit as TowerViewUnit).projectUnitTypeId : null;
                                  const utCode = 'unitType' in unit ? (unit as TowerViewUnit).unitType : null;
                                  const codeKey = utCode ?? (utId != null ? `id:${utId}` : '');
                                  if (!codeKey || seenUtCodes.has(codeKey)) continue;
                                  seenUtCodes.add(codeKey);
                                  const utInfo = utId != null ? sitePlanUnitTypes.find((ut) => ut.id === utId) : null;
                                  const rawDesc = utInfo?.description ?? ('unitTypeDescription' in unit ? (unit as TowerViewUnit).unitTypeDescription : null) ?? '';
                                  const desc = rawDesc.replace(/Bedroom/gi, 'BR');
                                  const utTypes = (utInfo as unknown as { unitTypes?: string[] })?.unitTypes;
                                  const unitTypesArr = Array.isArray(utTypes) && utTypes.length > 0
                                    ? utTypes
                                    : (utCode ?? utInfo?.unitType ? [utCode ?? utInfo?.unitType ?? ''] : []);
                                  const size = utInfo?.size ?? ('areaSqft' in unit ? (unit as TowerViewUnit).areaSqft : null);
                                  allEntries.push({ desc, unitTypes: unitTypesArr, size });
                                }
                                // Group by desc + size
                                const grouped: Array<{ desc: string; unitTypes: string[]; size: number | null }> = [];
                                for (const entry of allEntries) {
                                  const existing = grouped.find((g) => g.desc === entry.desc && g.size === entry.size);
                                  if (existing) {
                                    entry.unitTypes.forEach((t) => { if (t && !existing.unitTypes.includes(t)) existing.unitTypes.push(t); });
                                  } else {
                                    grouped.push({ desc: entry.desc, unitTypes: [...entry.unitTypes], size: entry.size });
                                  }
                                }
                                if (grouped.length === 0) {
                                  grouped.push({ desc: '', unitTypes: [], size: null });
                                }
                                const stackAnalysisStackNumber = String(stack).padStart(2, '0');
                                const canOpenStackAnalysis = !isPdfCapturing && Boolean(activeTopViewingProjectName);
                                const openStackAnalysis = () => {
                                  if (!canOpenStackAnalysis) return;
                                  handleOpenStackAnalysis(activeTopViewingProjectName, stackAnalysisStackNumber);
                                };
                                return (
                                  <Box
                                    key={`top-stack-header-${block}-${stack}`}
                                    onClick={openStackAnalysis}
                                    role={canOpenStackAnalysis ? 'button' : undefined}
                                    tabIndex={canOpenStackAnalysis ? 0 : undefined}
                                    onKeyDown={(event) => {
                                      if (!canOpenStackAnalysis) return;
                                      if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        openStackAnalysis();
                                      }
                                    }}
                                    sx={{
                                      minHeight: 48,
                                      textAlign: 'center',
                                      fontSize: '0.72rem',
                                      fontWeight: 700,
                                      py: 0.5,
                                      px: 0.25,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      cursor: canOpenStackAnalysis ? 'pointer' : 'default',
                                      userSelect: canOpenStackAnalysis ? 'none' : 'auto',
                                      '&:hover': canOpenStackAnalysis ? { bgcolor: 'rgba(var(--color-accent-rgb), 0.28)' } : undefined,
                                      '&:focus-visible': canOpenStackAnalysis ? { outline: '2px solid var(--color-accent)', outlineOffset: '-2px' } : undefined,
                                      ...(isPdfCapturing
                                        ? { bgcolor: '#9e9e9e', color: '#111' }
                                        : { bgcolor: 'rgba(var(--color-shadow-rgb), 0.35)', color: 'var(--color-text-strong)' }),
                                    }}
                                  >
                                    <Typography component="span" sx={{ fontSize: '0.72rem', fontWeight: 700, lineHeight: 1.2 }}>{String(stack).padStart(2, '0')}</Typography>
                                    {grouped.map((g, i) => {
                                      const sizeStr = g.size != null && Number.isFinite(g.size) ? `${g.size} sqft` : '';
                                      const codesStr = g.unitTypes.join(' / ');
                                      const line3 = codesStr && sizeStr ? `${codesStr} / ${sizeStr}` : (sizeStr || codesStr || '-');
                                      return (
                                        <React.Fragment key={i}>
                                          {g.desc ? <Typography component="span" sx={{ fontSize: '0.6rem', fontWeight: 600, lineHeight: 1.2, opacity: 0.95 }}>{g.desc}</Typography> : null}
                                          <Typography component="span" sx={{ fontSize: '0.58rem', lineHeight: 1.2, opacity: 0.9 }}>{line3}</Typography>
                                        </React.Fragment>
                                      );
                                    })}
                                  </Box>
                                );
                              })}
                              {blockData.floors.map((floor) => (
                                <React.Fragment key={`top-floor-${block}-${floor}`}>
                                  <Box sx={{ textAlign: 'center', fontSize: '0.72rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 0.25, ...(isPdfCapturing ? { bgcolor: '#9e9e9e', color: '#111' } : { bgcolor: 'rgba(var(--color-shadow-rgb), 0.35)', color: 'var(--color-text-strong)' }) }}>
                                    {formatTowerFloorLabel(floor)}
                                  </Box>
                                  {visibleStacks.map((stack) => {
                                    const unit = blockData.grid.get(`${floor}-${stack}`) as TowerViewUnit | ComparableUnit | undefined;
                                    const unitStatus = (unit?.unitStatus || '').toString().toLowerCase();
                                    const isSold = unitStatus === 'sold';
                                    const isNotReleased = isUnitStatusNotReleased(unitStatus);
                                    const unitDisplay = unit ? formatTowerUnitDisplay(unit, floor, stack) : '-';
                                    const unitBedroom = unit ? ('bedroom' in unit ? normalizeBedroomValue(unit.bedroom) : null) : null;
                                    const unitUnitTypeStr = (() => {
                                      if (!unit) return '';
                                      const utId = 'projectUnitTypeId' in unit ? (unit as TowerViewUnit).projectUnitTypeId : null;
                                      const utCode = 'unitType' in unit ? (unit as TowerViewUnit).unitType : null;
                                      const utInfo = utId != null ? sitePlanUnitTypes.find((ut) => ut.id === utId) : null;
                                      const utTypesArr = (utInfo as unknown as { unitTypes?: string[] })?.unitTypes;
                                      const utTypes = Array.isArray(utTypesArr) && utTypesArr.length > 0
                                        ? utTypesArr.join(' / ')
                                        : (utCode ?? utInfo?.unitType ?? utInfo?.description ?? ('unitTypeDescription' in unit ? (unit as TowerViewUnit).unitTypeDescription : null) ?? '');
                                      return String(utTypes).replace(/Bedroom/gi, 'BR');
                                    })();
                                    const bedroomForColor = unit && isPenthouseUnit(unit) ? PENTHOUSE_FILTER_VALUE : unitBedroom;
                                    const unitNpsPrice = unit && 'price' in unit ? (unit.priceNps ?? unit.price ?? 0) : 0;
                                    const unitDpsPrice = unit && 'price' in unit ? (unit.priceDps ?? 0) : 0;
                                    const unitNpsPsf = unit && 'psf' in unit ? (unit.psfNps ?? unit.psf ?? 0) : 0;
                                    const unitSize = unit && 'areaSqft' in unit ? (unit as TowerViewUnit).areaSqft : null;
                                    const filteredOut = Boolean(unit && hasAnyFilter && !unitMatchesFilter(unit));
                                    const showAsEmpty = !unit || filteredOut;
                                    const isInCompareBasket = !showAsEmpty && unit && topViewingFloorPlansToCompare.some((fp) => fp.unit.id === unit.id);
                                    const isReferenceUnit = !showAsEmpty && unit && topViewingReferenceUnit && topViewingReferenceUnit.id > 0 && topViewingReferenceUnit.id === unit.id;
                                    const isBlinkerChoiceUnit = !showAsEmpty && unit && topViewingBlinkerChoiceUnit && topViewingBlinkerChoiceUnit.id > 0 && topViewingBlinkerChoiceUnit.id === unit.id;
                                    const isBlinkerMatchedUnit = !showAsEmpty && !isSold && unit && isUnitWithinBlinkerRange(unit);

                                    const pdfStyle = isPdfCapturing
                                      ? {
                                          bgcolor: showAsEmpty ? '#e0e0e0' : (isSold ? '#c62828' : '#2e7d32'),
                                          color: showAsEmpty ? '#333333' : '#ffffff',
                                          borderColor: showAsEmpty ? '#999999' : 'rgba(255,255,255,0.5)',
                                        }
                                      : {};
                                    const showPriceOrStatus = isPdfCapturing || topViewingTowerDisplayOptions.includes('price');
                                    const showBedroom = isPdfCapturing || topViewingTowerDisplayOptions.includes('bedroom');
                                    const showPsf = !isPdfCapturing && topViewingTowerDisplayOptions.includes('psf') && !isSold;
                                    const showSize = topViewingTowerDisplayOptions.includes('size');

                                    return (
                                      <Box
                                        key={`top-cell-${block}-${floor}-${stack}`}
                                        onClick={(e) => {
                                          if (unit && !filteredOut && !isPdfCapturing) {
                                            setTopViewingUnitMenuAnchor({ element: e.currentTarget as HTMLElement, unit, mouseX: e.clientX, mouseY: e.clientY });
                                          }
                                        }}
                                        sx={{
                                          position: 'relative',
                                          width: cellWidth,
                                          px: 0.75,
                                          py: 0.6,
                                          bgcolor: pdfStyle.bgcolor ?? (showAsEmpty
                                            ? 'rgba(var(--color-shadow-rgb), 0.18)'
                                            : (isSold ? 'var(--color-danger)' : (topViewingTowerBedroomColor ? getBedroomColor(bedroomForColor) : 'rgba(var(--color-danger-rgb), 0.95)'))),
                                          color: pdfStyle.color ?? (showAsEmpty
                                            ? 'rgba(var(--color-text-rgb), 0.9)'
                                            : (isLightTheme && topViewingTowerBedroomColor && !isSold
                                              ? 'rgba(15, 23, 42, 0.95)'
                                              : 'var(--color-text-inverse)')),
                                          border: '1px solid',
                                          borderColor: pdfStyle.borderColor ?? (isLightTheme
                                            ? '#000'
                                            : (showAsEmpty
                                              ? 'rgba(var(--color-text-rgb), 0.45)'
                                              : (isSold ? 'rgba(255,255,255,0.65)' : (topViewingTowerBedroomColor ? 'rgba(255,255,255,0.5)' : 'rgba(var(--color-text-rgb), 0.35)')))),
                                          ...(isBlinkerMatchedUnit ? {
                                            borderWidth: '2px',
                                            borderColor: blinkerAccentColor,
                                            boxShadow: `0 0 0 1px rgba(${blinkerAccentRgb}, 0.45), 0 0 16px rgba(${blinkerAccentRgb}, 0.55)`,
                                            animation: `${blinkerKeyframeName} 1s ease-in-out infinite`,
                                            [`@keyframes ${blinkerKeyframeName}`]: {
                                              '0%, 100%': {
                                                borderColor: blinkerAccentColor,
                                                boxShadow: `0 0 0 1px rgba(${blinkerAccentRgb}, 0.45), 0 0 16px rgba(${blinkerAccentRgb}, 0.55)`,
                                              },
                                              '50%': {
                                                borderColor: `rgba(${blinkerAccentRgb}, 0.35)`,
                                                boxShadow: `0 0 0 1px rgba(${blinkerAccentRgb}, 0.18), 0 0 6px rgba(${blinkerAccentRgb}, 0.2)`,
                                              },
                                            },
                                          } : {}),
                                          borderRadius: 0.5,
                                          boxSizing: 'border-box',
                                          display: 'flex',
                                          flexDirection: 'column',
                                          alignItems: 'center',
                                          justifyContent: 'flex-start',
                                          lineHeight: 1.02,
                                          gap: 0.06,
                                          opacity: showAsEmpty ? 1 : 1,
                                          cursor: unit && !filteredOut && !isPdfCapturing ? 'pointer' : 'default',
                                          '&:hover': !isPdfCapturing && unit && !filteredOut ? {
                                            opacity: 0.85,
                                            transform: 'scale(1.05)',
                                          } : {},
                                          transition: 'all 0.2s ease',
                                        }}
                                      >
                                        <Typography sx={{ fontSize: showAsEmpty ? '0.9rem' : '0.66rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                          {showAsEmpty ? '-' : unitDisplay}
                                        </Typography>
                                        {!showAsEmpty && unit && showBedroom && (
                                          <Typography sx={{ fontSize: '0.58rem', opacity: 0.95, whiteSpace: 'nowrap' }}>
                                            {unitBedroom != null
                                              ? `${unitBedroom}BR${unitUnitTypeStr ? ` / ${unitUnitTypeStr}` : ''}`
                                              : '-'}
                                          </Typography>
                                        )}
                                        {!showAsEmpty && unit && showPriceOrStatus && (
                                          isSold ? (
                                            <Typography sx={{ fontSize: '0.56rem', opacity: 0.95, whiteSpace: 'nowrap' }}>
                                              SOLD
                                            </Typography>
                                          ) : isNotReleased ? (
                                            <Typography sx={{ fontSize: '0.56rem', opacity: 0.95, whiteSpace: 'nowrap' }}>
                                              -
                                            </Typography>
                                          ) : (
                                            <>
                                              {isPdfCapturing && (
                                                <Typography sx={{ fontSize: '0.56rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                  Available
                                                </Typography>
                                              )}
                                              <Typography sx={{ fontSize: '0.56rem', opacity: 0.95, whiteSpace: 'nowrap' }}>
                                                {unitNpsPrice > 0 ? `${formatCurrency(unitNpsPrice)}${isExecutiveCondoProject ? ' (NPS)' : ''}` : '-'}
                                              </Typography>
                                              {isExecutiveCondoProject && (
                                                <Typography sx={{ fontSize: '0.54rem', opacity: 0.9, whiteSpace: 'nowrap' }}>
                                                  {unitDpsPrice > 0 ? `${formatCurrency(unitDpsPrice)} (DPS)` : '-'}
                                                </Typography>
                                              )}
                                            </>
                                          )
                                        )}
                                        {!showAsEmpty && unit && showPsf && (
                                          <Typography sx={{ fontSize: '0.56rem', opacity: 0.95, whiteSpace: 'nowrap' }}>
                                            {isNotReleased ? '-' : (unitNpsPsf > 0 ? `${formatCurrency(unitNpsPsf)} psf` : '-')}
                                          </Typography>
                                        )}
                                        {!showAsEmpty && unit && showSize && (
                                          <Typography sx={{ fontSize: '0.56rem', opacity: 0.95, whiteSpace: 'nowrap' }}>
                                            {unitSize != null && Number.isFinite(unitSize) && unitSize > 0 ? `${unitSize} sqft` : '-'}
                                          </Typography>
                                        )}
                                        {isReferenceUnit && (
                                          <BookmarkIcon
                                            sx={{
                                              position: 'absolute',
                                              top: 2,
                                              left: 2,
                                              fontSize: '0.75rem !important',
                                              opacity: 0.95,
                                              color: '#1976d2',
                                            }}
                                          />
                                        )}
                                        {isBlinkerChoiceUnit && (
                                          <VisibilityIcon
                                            sx={{
                                              position: 'absolute',
                                              top: 2,
                                              right: 2,
                                              fontSize: '0.72rem !important',
                                              opacity: 0.95,
                                              color: blinkerAccentColor,
                                            }}
                                          />
                                        )}
                                        {isInCompareBasket && (
                                          <ShoppingCartIcon
                                            sx={{
                                              position: 'absolute',
                                              top: 2,
                                              right: 2,
                                              fontSize: '0.75rem !important',
                                              opacity: 0.95,
                                              color: '#FFEB3B',
                                            }}
                                          />
                                        )}
                                      </Box>
                                    );
                                  })}
                                </React.Fragment>
                              ))}
                            </Box>
                          </Box>
                        );
                        })}
                      </Box>
                    </Box>
                    </Collapse>
                  </CardContent>
                  {priceMatrixSimulationSession && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 9999,
                        pointerEvents: 'none',
                        backgroundImage: PRICE_SIMULATION_WATERMARK,
                        backgroundRepeat: 'repeat',
                      }}
                    />
                  )}
                </Card>
                </Box>
                )}

                {shouldShowPriceReferenceTools && (
                  <Card sx={{ ...mobileOptimizedStyles, mb: 2 }}>
                    <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                      <Box sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
                            {isIndicativeReference
                              ? 'Price Reference (Indicative Price)'
                              : isGuesstimationReference
                                ? 'Price Reference (Guess-timation Price)'
                                : 'Price Reference'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                            {priceMatrixSimulationSession
                              ? 'Prices come from the price matrix and SOLD simulation is managed in-app.'
                              : indicativePriceRows.length > 0
                                ? 'Use indicative pricing to set a temporary reference.'
                                : 'Use Guess-timation price to set a temporary reference.'}
                          </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => setPriceReferenceSectionExpanded((prev) => !prev)}
                          >
                            <ExpandMoreIcon sx={{ transform: priceReferenceSectionExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s ease' }} />
                          </IconButton>
                        </Box>
                      </Box>
                      {priceReferenceSectionExpanded && (
                      <>
                      {priceMatrixSimulationError && (
                        <Alert severity="error" sx={{ mb: 1 }}>
                          {priceMatrixSimulationError}
                        </Alert>
                      )}
                      {indicativePriceRows.length > 0 ? (
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                              xs: '1fr',
                              sm: 'repeat(2, minmax(0, 1fr))',
                              md: 'repeat(auto-fit, minmax(220px, 1fr))',
                            },
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          {indicativePriceRows.map((row) => {
                            const range = row.range ?? null;
                            const low = range?.low ?? null;
                            const high = range?.high ?? null;
                            const sizeRange = indicativeSizeRanges.get(row.bedroom) ?? null;
                            const sizeRangeLabel = sizeRange
                              ? `Size range: ${formatNumber(sizeRange.min)}-${formatNumber(sizeRange.max)} sqft`
                              : 'Size range unavailable';
                            const priceRows: Array<{ key: string; label: string; value: number }> = [];
                            if (low != null && low > 0) {
                              priceRows.push({ key: 'from', label: 'From', value: low });
                            }
                            if (high != null && high > 0) {
                              priceRows.push({ key: 'to', label: 'To', value: high });
                            }
                            const handleIndicativeClick = (price: number, label: string) => {
                              if (!sizeRange || !selectedReferenceProjectName) return;
                              openIndicativeSizeDialog({
                                bedroom: row.bedroom,
                                price,
                                priceLabel: label,
                              });
                            };

                            return (
                              <Box
                                key={row.key}
                                sx={{
                                  p: 1.1,
                                  borderRadius: 1.25,
                                  border: '1px solid rgba(var(--color-text-rgb), 0.16)',
                                  bgcolor: 'rgba(var(--color-text-rgb), 0.04)',
                                  boxShadow: 'inset 0 1px 0 rgba(var(--color-shadow-rgb), 0.05)',
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.65 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'var(--color-text-strong)', letterSpacing: '-0.01em' }}>
                                    {row.label}
                                  </Typography>
                                  <Chip
                                    label={sizeRangeLabel}
                                    size="small"
                                    sx={{
                                      bgcolor: 'rgba(var(--color-info-rgb), 0.12)',
                                      color: 'var(--color-info)',
                                      fontWeight: 700,
                                      borderRadius: 1.25,
                                      height: 20,
                                      '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' },
                                    }}
                                  />
                                </Box>
                                <Stack spacing={0.65}>
                                  {priceRows.length === 0 ? (
                                    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                                      Price not available
                                    </Typography>
                                  ) : (
                                    priceRows.map((priceRow, index) => (
                                      <Button
                                        key={`${row.key}-${priceRow.key}-${index}`}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        disabled={!sizeRange || !selectedReferenceProjectName}
                                        onClick={() => handleIndicativeClick(priceRow.value, priceRow.label)}
                                        sx={{
                                          justifyContent: 'space-between',
                                          textTransform: 'none',
                                          px: 1.2,
                                          py: 0.75,
                                          borderRadius: 1.1,
                                          border: '1px solid rgba(var(--color-accent-rgb), 0.35)',
                                          bgcolor: 'rgba(var(--color-accent-rgb), 0.14)',
                                          boxShadow: '0 4px 10px rgba(var(--color-shadow-rgb), 0.08)',
                                          '&:hover': {
                                            bgcolor: 'rgba(var(--color-accent-rgb), 0.2)',
                                            borderColor: 'rgba(var(--color-accent-rgb), 0.6)',
                                          },
                                          '&.Mui-disabled': {
                                            borderColor: 'rgba(var(--color-text-rgb), 0.08)',
                                            color: 'rgba(var(--color-text-rgb), 0.4)',
                                          },
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                                          <Typography variant="body2" sx={{ color: 'var(--color-text-strong)', fontWeight: 700 }}>
                                            {priceRow.label}
                                          </Typography>
                                          <Typography component="span" sx={{ fontWeight: 800, color: 'var(--color-text-strong)', letterSpacing: '-0.01em' }}>
                                            {formatCurrency(priceRow.value)}
                                          </Typography>
                                        </Box>
                                      </Button>
                                    ))
                                  )}
                                </Stack>
                              </Box>
                            );
                          })}
                        </Box>
                      ) : (
                        <>
                          <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                            Indicative pricing is not available for this project.
                          </Typography>
                          {guesstimationBedroomOptions.length === 0 && (
                            <Box sx={{ mt: 0.5 }}>
                              {!guesstimationNoUnitsFormOpen ? (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={openGuesstimationNoUnitsForm}
                                  disabled={!selectedReferenceProjectName}
                                  sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    borderRadius: 1.5,
                                    borderColor: 'rgba(var(--color-accent-rgb), 0.45)',
                                    color: 'var(--color-accent)',
                                    '&:hover': {
                                      borderColor: 'rgba(var(--color-accent-rgb), 0.7)',
                                      bgcolor: 'rgba(var(--color-accent-rgb), 0.08)',
                                    },
                                  }}
                                >
                                  Guess-timation Price
                                </Button>
                              ) : (
                                <Box
                                  sx={{
                                    p: 1.1,
                                    borderRadius: 1.25,
                                    border: '1px solid rgba(var(--color-text-rgb), 0.16)',
                                    bgcolor: 'rgba(var(--color-text-rgb), 0.04)',
                                  }}
                                >
                                  <Stack spacing={1}>
                                    <FormControl fullWidth size="small">
                                      <InputLabel id="guesstimation-no-units-br-label">Bedroom</InputLabel>
                                      <Select
                                        labelId="guesstimation-no-units-br-label"
                                        label="Bedroom"
                                        value={guesstimationNoUnitsBedroom}
                                        onChange={(e) => setGuesstimationNoUnitsBedroom(Number(e.target.value))}
                                      >
                                        {[1, 2, 3, 4, 5].map((br) => (
                                          <MenuItem key={br} value={br}>
                                            {br} BR
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                      <TextField
                                        size="small"
                                        fullWidth
                                        label="Size from (sqft)"
                                        autoComplete="off"
                                        value={formatThousandsInput(guesstimationNoUnitsSizeFrom)}
                                        onChange={(e) => setGuesstimationNoUnitsSizeFrom(e.target.value.replace(/,/g, '').replace(/[^\d.]/g, ''))}
                                        inputProps={{ autoComplete: 'off' }}
                                      />
                                      <TextField
                                        size="small"
                                        fullWidth
                                        label="Size to (sqft)"
                                        autoComplete="off"
                                        value={formatThousandsInput(guesstimationNoUnitsSizeTo)}
                                        onChange={(e) => setGuesstimationNoUnitsSizeTo(e.target.value.replace(/,/g, '').replace(/[^\d.]/g, ''))}
                                        inputProps={{ autoComplete: 'off' }}
                                      />
                                    </Box>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      label="Price"
                                      autoComplete="off"
                                      value={formatThousandsInput(guesstimationNoUnitsPrice)}
                                      onChange={(e) => setGuesstimationNoUnitsPrice(e.target.value.replace(/,/g, '').replace(/[^\d.]/g, ''))}
                                      InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                      }}
                                      inputProps={{ autoComplete: 'off' }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                      <Button
                                        size="small"
                                        onClick={() => setGuesstimationNoUnitsFormOpen(false)}
                                        sx={{ textTransform: 'none' }}
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        variant="contained"
                                        size="small"
                                        onClick={confirmGuesstimationNoUnitsPrice}
                                        disabled={
                                          !selectedReferenceProjectName
                                          || !(Number(guesstimationNoUnitsPrice.replace(/,/g, '')) > 0)
                                          || !(Number(guesstimationNoUnitsSizeFrom.replace(/,/g, '')) > 0)
                                          || !(Number(guesstimationNoUnitsSizeTo.replace(/,/g, '')) > 0)
                                        }
                                        sx={{
                                          textTransform: 'none',
                                          fontWeight: 700,
                                          bgcolor: 'var(--color-accent)',
                                          '&:hover': { bgcolor: 'var(--color-accent-strong)' },
                                        }}
                                      >
                                        Done
                                      </Button>
                                    </Box>
                                  </Stack>
                                </Box>
                              )}
                            </Box>
                          )}
                        </>
                      )}
                      {indicativePriceRows.length === 0 && guesstimationBedroomOptions.length > 0 && (
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                              xs: '1fr',
                              sm: 'repeat(2, minmax(0, 1fr))',
                              md: 'repeat(auto-fit, minmax(220px, 1fr))',
                            },
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          {guesstimationBedroomOptions.map((opt) => {
                            const sizeRange = indicativeSizeRanges.get(opt.bedroom) ?? null;
                            const sizeRangeLabel = sizeRange
                              ? `Size range: ${formatNumber(sizeRange.min)}-${formatNumber(sizeRange.max)} sqft`
                              : 'Size range unavailable';
                            const priceEstimate = projectDetails?.priceEstimates?.[String(opt.bedroom)] ?? null;
                            return (
                              <Box
                                key={`guesstimation-br-${opt.bedroom}`}
                                sx={{
                                  p: 1.1,
                                  borderRadius: 1.25,
                                  border: '1px solid rgba(var(--color-text-rgb), 0.16)',
                                  bgcolor: 'rgba(var(--color-text-rgb), 0.04)',
                                  boxShadow: 'inset 0 1px 0 rgba(var(--color-shadow-rgb), 0.05)',
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.65 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'var(--color-text-strong)', letterSpacing: '-0.01em' }}>
                                    {opt.label}
                                  </Typography>
                                  <Chip
                                    label={sizeRangeLabel}
                                    size="small"
                                    sx={{
                                      bgcolor: 'rgba(var(--color-info-rgb), 0.12)',
                                      color: 'var(--color-info)',
                                      fontWeight: 700,
                                      borderRadius: 1.25,
                                      height: 20,
                                      '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' },
                                    }}
                                  />
                                </Box>
                                {priceEstimate && (
                                  <Box
                                    sx={{
                                      mb: 0.65,
                                      px: 0.85,
                                      py: 0.4,
                                      borderRadius: 1,
                                      display: 'flex',
                                      alignItems: 'baseline',
                                      flexWrap: 'wrap',
                                      columnGap: 0.65,
                                      bgcolor: 'rgba(var(--color-accent-rgb), 0.1)',
                                      border: '1px solid rgba(var(--color-accent-rgb), 0.25)',
                                    }}
                                  >
                                    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', whiteSpace: 'nowrap' }}>
                                      Est. Price Starting From
                                    </Typography>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'var(--color-accent)', letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
                                      {priceEstimate}
                                    </Typography>
                                  </Box>
                                )}
                                <Button
                                  variant="contained"
                                  size="small"
                                  fullWidth
                                  onClick={() => openGuesstimationDialog(opt.bedroom)}
                                  disabled={!selectedReferenceProjectName}
                                  sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    borderRadius: 1.1,
                                    px: 1.2,
                                    py: 0.75,
                                    bgcolor: 'var(--color-accent)',
                                    '&:hover': {
                                      bgcolor: 'var(--color-accent-strong)',
                                    },
                                  }}
                                >
                                  Guess-timation Price
                                </Button>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                      {canLoadPriceMatrix && (
                        <Box sx={{ mt: 1.1, display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                          <Button
                            variant="contained"
                            size="small"
                            disabled={!currentProjectId || priceMatrixSimulationLoading}
                            onClick={() => {
                              if (!currentProjectId || !onLoadPriceMatrixSimulation) return;
                              void onLoadPriceMatrixSimulation(currentProjectId);
                            }}
                          >
                            {priceMatrixSimulationLoading ? 'Loading...' : (priceMatrixSimulationSession ? 'Reload Guess-timated Price List' : 'Guess-timated Price List')}
                          </Button>
                          {priceMatrixSimulationSession && canManagePriceMatrix && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => setPriceMatrixGridOpen(true)}
                            >
                              Open Simulation Grid
                            </Button>
                          )}
                          {priceMatrixSimulationSession && !isAdminUser && (
                            <Button
                              variant={priceMatrixSimulationSession.syncEnabled ? 'outlined' : 'contained'}
                              size="small"
                              color={priceMatrixSimulationSession.syncEnabled ? 'warning' : 'info'}
                              disabled={!currentProjectId || priceMatrixSimulationRefreshing}
                              onClick={() => {
                                if (!currentProjectId || !onTogglePriceMatrixSimulationSync) return;
                                void onTogglePriceMatrixSimulationSync(
                                  currentProjectId,
                                  !Boolean(priceMatrixSimulationSession.syncEnabled)
                                );
                              }}
                            >
                              {priceMatrixSimulationRefreshing
                                ? 'Syncing...'
                                : priceMatrixSimulationSession.syncEnabled
                                  ? 'Stop Sync'
                                  : 'Sync Simulation'}
                            </Button>
                          )}
                          {priceMatrixSimulationSession && (
                            <Button
                              variant="outlined"
                              size="small"
                              color="warning"
                              onClick={() => {
                                if (!currentProjectId || !onCancelPriceMatrixSimulation) return;
                                setPriceMatrixGridOpen(false);
                                void onCancelPriceMatrixSimulation(currentProjectId);
                              }}
                            >
                              Cancel Simulation
                            </Button>
                          )}
                        </Box>
                      )}
                      {priceMatrixSimulationSession && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'rgba(var(--color-text-rgb), 0.65)' }}>
                          Matrix synced {formatLiveStatusTimestamp(priceMatrixSimulationSession.updatedAt)}. Matched {priceMatrixSimulationSession.matchedUnits} units.
                          {!isAdminUser && priceMatrixSimulationSession.syncEnabled ? ' Live sync is on.' : ''}
                        </Typography>
                      )}
                      </>
                      )}
                    </CardContent>
                  </Card>
                )}

                <Card data-tutorial="available-units" sx={{ ...mobileOptimizedStyles, position: 'relative' }}>
                  {priceMatrixSimulationSession && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 9999,
                        pointerEvents: 'none',
                        backgroundImage: PRICE_SIMULATION_WATERMARK,
                        backgroundRepeat: 'repeat',
                      }}
                    />
                  )}
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1.5, flexWrap: 'wrap' }}>
                      <Tabs
                        value={topViewingAvailableUnitsViewMode}
                        onChange={(_, newValue) => {
                          if (newValue) {
                            setTopViewingAvailableUnitsViewMode(newValue);
                          }
                        }}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{
                          minHeight: 0,
                          width: 'fit-content',
                          p: 0.375,
                          border: '1px solid rgba(var(--color-text-rgb), 0.12)',
                          borderRadius: 1.5,
                          bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
                          '& .MuiTabs-indicator': {
                            display: 'none',
                          },
                          '& .MuiTabs-flexContainer': {
                            gap: 0.5,
                          },
                          '& .MuiTab-root': {
                            minHeight: 38,
                            minWidth: 'unset',
                            px: 1.5,
                            py: 0.75,
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 1,
                            border: '1px solid transparent',
                            color: 'rgba(var(--color-text-rgb), 0.72)',
                            '&.Mui-selected': {
                              color: 'var(--color-text-strong)',
                              bgcolor: 'rgba(var(--color-accent-rgb), 0.14)',
                              borderColor: 'rgba(var(--color-accent-rgb), 0.32)',
                              boxShadow: 'inset 0 -2px 0 var(--color-accent)',
                            },
                            '&.Mui-disabled': {
                              color: 'rgba(var(--color-text-rgb), 0.35)',
                            },
                            '&:not(.Mui-disabled):hover': {
                              bgcolor: 'rgba(var(--color-text-rgb), 0.06)',
                            },
                          },
                        }}
                      >
                        <Tab value="availableUnits" label={`Available Units (${topViewingSortedUnits.length})`} />
                        <Tab
                          value="bestPxBedroom"
                          label="BEST PX BEDROOM TYPE"
                          disabled={!topViewingBestPxProjectName}
                        />
                        <Tab
                          value="bestPsfBedroom"
                          label="BEST PSF BEDROOM TYPE"
                          disabled={!topViewingBestPxProjectName}
                        />
                      </Tabs>
                      {topViewingAvailableUnitsViewMode === 'availableUnits' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <ColumnVisibilityMenu
                            anchorEl={availableUnitColumnsAnchor}
                            columns={availableUnitColumnsForMenu}
                            getLabel={formatColumnVisibilityLabel}
                            onClose={() => setAvailableUnitColumnsAnchor(null)}
                            onOpen={(event) => setAvailableUnitColumnsAnchor(event.currentTarget)}
                            onReset={resetAvailableUnitColumns}
                            onToggle={toggleAvailableUnitColumn}
                            visibility={availableUnitColumnVisibility}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setTopViewingAvailableUnitFilters({
                              projectName: '',
                              blockNumber: '',
                              floorMin: '',
                              floorMax: '',
                              stackMin: '',
                              stackMax: '',
                              stackSelected: [],
                              unitTypeDescriptionSelected: [],
                              bedroomMin: '',
                              bedroomMax: '',
                              bathroomMin: '',
                              bathroomMax: '',
                              areaMin: '',
                              areaMax: '',
                              priceMin: '',
                              priceMax: '',
                              dpsMin: '',
                              dpsMax: '',
                              psfMin: '',
                              psfMax: '',
                              outlayMin: '',
                              outlayMax: '',
                              installmentMin: '',
                              installmentMax: '',
                            })}
                          >
                            Clear Filters
                          </Button>
                        </Box>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (topViewingAvailableUnitsViewMode === 'availableUnits') {
                            setAvailableUnitsSectionExpanded((prev) => !prev);
                          } else {
                            setBestPxBedroomSectionExpanded((prev) => !prev);
                          }
                        }}
                      >
                        <ExpandMoreIcon
                          sx={{
                            transform: (
                              topViewingAvailableUnitsViewMode === 'availableUnits'
                                ? availableUnitsSectionExpanded
                                : bestPxBedroomSectionExpanded
                            ) ? 'rotate(0deg)' : 'rotate(-90deg)',
                            transition: 'transform 0.2s ease',
                          }}
                        />
                      </IconButton>
                    </Box>
                    {(
                      topViewingAvailableUnitsViewMode === 'availableUnits'
                        ? availableUnitsSectionExpanded
                        : bestPxBedroomSectionExpanded
                    ) && (
                    (
                      topViewingAvailableUnitsViewMode === 'availableUnits' ? (
                      <>
                        <TableContainer sx={{ ...scrollableStyles, maxHeight: { xs: 420, md: isTabletLandscape ? 'calc(100vh - 260px)' : 'calc(100vh - 350px)' } }}>
                          <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            {isAvailableUnitColumnVisible('projectName') && (
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  Project Name
                                  <IconButton size="small" onClick={(e) => handleTopViewingOpenPopover(e, 'projectName')} sx={getTopViewingAvailableIconButtonSx('projectName')}>
                                    {getSortIndicatorIcon(getTopViewingSortDirection('projectName'))}
                                  </IconButton>
                                </Box>
                              </TableCell>
                            )}
                            {isAvailableUnitColumnVisible('unitNumber') && <TableCell>Unit</TableCell>}
                            {isAvailableUnitColumnVisible('blockNumber') && (
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  Block
                                  <IconButton size="small" onClick={(e) => handleTopViewingOpenPopover(e, 'blockNumber')} sx={getTopViewingAvailableIconButtonSx('blockNumber')}>
                                    {getSortIndicatorIcon(getTopViewingSortDirection('blockNumber'))}
                                  </IconButton>
                                </Box>
                              </TableCell>
                            )}
                            {(['floor', 'stack', 'unitTypeDescription', 'bedroom', 'bathroom', 'areaSqft', 'price', 'psf'] as const).map((column) => (
                              isAvailableUnitColumnVisible(column) && (
                                <TableCell
                                  key={column}
                                  align={column === 'price' || column === 'psf' || column === 'areaSqft' ? 'right' : column === 'unitTypeDescription' ? 'left' : 'center'}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: column === 'price' || column === 'psf' || column === 'areaSqft'
                                        ? 'flex-end'
                                        : column === 'unitTypeDescription' ? 'flex-start' : 'center',
                                      gap: 0.5,
                                    }}
                                  >
                                    {column === 'price' && isExecutiveCondoProject ? 'Price / NPS' : formatColumnVisibilityLabel(column)}
                                    <IconButton size="small" onClick={(e) => handleTopViewingOpenPopover(e, column)} sx={getTopViewingAvailableIconButtonSx(column)}>
                                      {getSortIndicatorIcon(getTopViewingSortDirection(column))}
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              )
                            ))}
                            {isExecutiveCondoProject && isAvailableUnitColumnVisible('dps') && (
                              <TableCell align="right">
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                  DPS
                                  <IconButton size="small" onClick={(e) => handleTopViewingOpenPopover(e, 'dps')} sx={getTopViewingAvailableIconButtonSx('dps')}>
                                    {getSortIndicatorIcon(getTopViewingSortDirection('dps'))}
                                  </IconButton>
                                </Box>
                              </TableCell>
                            )}
                            {(['outlay', 'installment', 'distance'] as const).map((column) => (
                              isAvailableUnitColumnVisible(column) && (
                                <TableCell
                                  key={column}
                                  align="right"
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'flex-end',
                                      gap: 0.5,
                                    }}
                                  >
                                    {formatColumnVisibilityLabel(column)}
                                    {column === 'outlay' && (
                                      <Tooltip title="How outlay is calculated">
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setTopViewingOutlayInfoAnchor(e.currentTarget);
                                          }}
                                          sx={{ ...getIconButtonSx(), color: 'var(--color-info)', '&:hover': { bgcolor: 'rgba(var(--color-info-rgb), 0.1)' } }}
                                        >
                                          <HelpOutlineIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {column === 'installment' && (
                                      <Tooltip title="How installment is calculated">
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setTopViewingInstallmentInfoAnchor(e.currentTarget);
                                          }}
                                          sx={{ ...getIconButtonSx(), color: 'var(--color-info)', '&:hover': { bgcolor: 'rgba(var(--color-info-rgb), 0.1)' } }}
                                        >
                                          <HelpOutlineIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <IconButton size="small" onClick={(e) => handleTopViewingOpenPopover(e, column)} sx={getTopViewingAvailableIconButtonSx(column)}>
                                      {getSortIndicatorIcon(getTopViewingSortDirection(column))}
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              )
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {topViewingAvailableUnitsPageRows.map((unit, unitRowIndex) => {
                            const isUnitShortlisted = shortlistByUnitId.has(unit.id);
                            const npsPrice = unit.priceNps ?? unit.price;
                            const dpsPrice = unit.priceDps ?? null;
                            const npsPsf = unit.psfNps ?? unit.psf;
                            return (
                              <TableRow
                                key={unit.id}
                                hover
                                {...(unitRowIndex === 0 ? { 'data-tutorial': 'unit-row-first' } : {})}
                                onClick={(e) => setTopViewingUnitMenuAnchor({ element: e.currentTarget, unit, mouseX: e.clientX, mouseY: e.clientY })}
                                sx={{ cursor: 'pointer' }}
                              >
                                {isAvailableUnitColumnVisible('projectName') && (
                                  <TableCell>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
                                        <Typography variant="body2">{unit.projectName}</Typography>
                                        {topViewingReferenceUnit && (topViewingReferenceUnit.id === unit.id || (topViewingReferenceUnit.projectName === unit.projectName && topViewingReferenceUnit.unitNumber === unit.unitNumber)) && (
                                          <Tooltip title="Reference unit" arrow placement="top">
                                            <BookmarkIcon sx={{ fontSize: '0.9rem !important', color: '#1976d2', flexShrink: 0 }} />
                                          </Tooltip>
                                        )}
                                        {topViewingFloorPlansToCompare.some((fp) => fp.unit.id === unit.id) && (
                                          <Tooltip title="In compare basket" arrow placement="top">
                                            <ShoppingCartIcon sx={{ fontSize: '0.9rem !important', color: '#FFEB3B', flexShrink: 0 }} />
                                          </Tooltip>
                                        )}
                                      </Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenTowerView(unit as unknown as ComparableUnit);
                                          }}
                                          sx={{ color: 'rgba(var(--color-text-rgb), 0.5)', '&:hover': { bgcolor: 'rgba(var(--color-text-rgb), 0.1)' } }}
                                          title="View Tower View"
                                        >
                                          <ViewModuleIcon fontSize="small" />
                                        </IconButton>
                                        {!hideShortlist && (
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleShortlistAction({
                                              unitId: unit.id,
                                              label: `${unit.projectName} ${unit.unitNumber}`,
                                              floorplan: unit.floorplan || null,
                                            });
                                          }}
                                          disabled={shortlistUpdatingUnitId === unit.id}
                                          sx={{
                                            color: isUnitShortlisted ? 'var(--color-warning-strong)' : 'var(--color-text-strong)',
                                            '&:hover': { bgcolor: 'rgba(var(--color-text-rgb), 0.1)' },
                                          }}
                                          title={isUnitShortlisted ? 'Remove from shortlist' : 'Add to shortlist'}
                                        >
                                          {isUnitShortlisted ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                                        </IconButton>
                                        )}
                                      </Box>
                                    </Box>
                                  </TableCell>
                                )}
                                {isAvailableUnitColumnVisible('unitNumber') && <TableCell sx={{ whiteSpace: 'nowrap' }}>{unit.unitNumber}</TableCell>}
                                {isAvailableUnitColumnVisible('blockNumber') && <TableCell>{formatBlockNumber(unit.blockNumber)}</TableCell>}
                                {isAvailableUnitColumnVisible('floor') && <TableCell align="center">{unit.floorLabel ?? (unit.floor != null && unit.floor !== 0 ? unit.floor : '-')}</TableCell>}
                                {isAvailableUnitColumnVisible('stack') && <TableCell align="center">{unit.stack != null && unit.stack !== 0 ? unit.stack : '-'}</TableCell>}
                                {isAvailableUnitColumnVisible('unitTypeDescription') && <TableCell>{unit.unitTypeDescription ? unit.unitTypeDescription.replace(/Bedroom/gi, 'BR') : '-'}</TableCell>}
                                {isAvailableUnitColumnVisible('bedroom') && <TableCell align="center">{unit.bedroom}</TableCell>}
                                {isAvailableUnitColumnVisible('bathroom') && <TableCell align="center">{unit.bathroom}</TableCell>}
                                {isAvailableUnitColumnVisible('areaSqft') && <TableCell align="right">{formatNumber(unit.areaSqft)}</TableCell>}
                                {isAvailableUnitColumnVisible('price') && <TableCell align="right">{npsPrice > 0 ? formatCurrency(npsPrice) : '-'}</TableCell>}
                                {isAvailableUnitColumnVisible('psf') && <TableCell align="right">{npsPsf > 0 ? formatCurrency(npsPsf) : '-'}</TableCell>}
                                {isExecutiveCondoProject && isAvailableUnitColumnVisible('dps') && (
                                  <TableCell align="right">{renderDpsValue(dpsPrice, true)}</TableCell>
                                )}
                                {isAvailableUnitColumnVisible('outlay') && (
                                  <TableCell
                                    align="right"
                                    onClick={(event) => {
                                      if (unit.outlay <= 0) return;
                                      openTopViewingFormulaPopover(event, getOutlayFormulaTooltip(unit.price, unit.outlay));
                                    }}
                                    sx={{ cursor: unit.outlay > 0 ? 'pointer' : 'default' }}
                                  >
                                    {unit.outlay > 0 ? (
                                      <Typography component="span" variant="body2" sx={{ cursor: 'pointer', textDecoration: 'underline dotted rgba(var(--color-text-rgb), 0.45)', textUnderlineOffset: 3 }}>
                                        {formatCurrency(unit.outlay)}
                                      </Typography>
                                    ) : '-'}
                                  </TableCell>
                                )}
                                {isAvailableUnitColumnVisible('installment') && (
                                  <TableCell
                                    align="right"
                                    onClick={(event) => {
                                      if (unit.installment <= 0) return;
                                      openTopViewingFormulaPopover(event, getInstallmentFormulaTooltip(unit.price, unit.installment));
                                    }}
                                    sx={{ cursor: unit.installment > 0 ? 'pointer' : 'default' }}
                                  >
                                    {unit.installment > 0 ? (
                                      <Typography component="span" variant="body2" sx={{ cursor: 'pointer', textDecoration: 'underline dotted rgba(var(--color-text-rgb), 0.45)', textUnderlineOffset: 3 }}>
                                        {formatCurrency(unit.installment)}
                                      </Typography>
                                    ) : '-'}
                                  </TableCell>
                                )}
                                {isAvailableUnitColumnVisible('distance') && <TableCell align="right">{unit.distance != null ? unit.distance.toFixed(2) + ' km' : '-'}</TableCell>}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                          </Table>
                        </TableContainer>
                        <TablePagination
                          component="div"
                          count={topViewingSortedUnits.length}
                          page={topViewingAvailableUnitsPage}
                          onPageChange={(_, nextPage) => setTopViewingAvailableUnitsPage(nextPage)}
                          rowsPerPage={TOP_VIEWING_PAGE_SIZE}
                          onRowsPerPageChange={() => {}}
                          rowsPerPageOptions={[TOP_VIEWING_PAGE_SIZE]}
                          sx={{
                            borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)',
                            '& .MuiTablePagination-toolbar': {
                              minHeight: 40,
                            },
                          }}
                        />
                      </>
                      ) : (
                        renderTopViewingBestBedroomContent()
                      )
                    )
                    )}
                    <Popover
                      open={Boolean(topViewingPopoverAnchor)}
                      anchorEl={topViewingPopoverAnchor?.element}
                      onClose={handleTopViewingClosePopover}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    >
                      {topViewingPopoverAnchor && (
                        <Box sx={{ p: 2, minWidth: 260 }}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1, color: 'var(--color-info)', fontWeight: 500 }}>
                              Sort
                            </Typography>
                            <ToggleButtonGroup
                              value={getTopViewingSortDirection(topViewingPopoverAnchor.column)}
                              exclusive
                              onChange={(_, value) => {
                                if (value !== null) {
                                  handleTopViewingSortChange(topViewingPopoverAnchor.column, value);
                                }
                              }}
                              fullWidth
                              sx={{
                                '& .MuiToggleButtonGroup-grouped': {
                                  border: '1px solid rgba(var(--color-shadow-rgb), 0.12)',
                                  '&:not(:first-of-type)': {
                                    borderLeft: '1px solid rgba(var(--color-shadow-rgb), 0.12)',
                                    marginLeft: 0,
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: 'var(--color-info)',
                                    color: 'var(--color-text-strong)',
                                  },
                                },
                              }}
                            >
                              <ToggleButton value="default" sx={{ flex: 1, py: 0.75 }}>Default</ToggleButton>
                              <ToggleButton value="asc" sx={{ flex: 1, py: 0.75 }}>Asc</ToggleButton>
                              <ToggleButton value="desc" sx={{ flex: 1, py: 0.75 }}>Desc</ToggleButton>
                            </ToggleButtonGroup>
                          </Box>

                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Typography variant="body2" sx={{ color: 'var(--color-info)', fontWeight: 500, mr: 1 }}>
                                Filter
                              </Typography>
                              <FilterIcon sx={{ fontSize: 18, color: 'var(--color-info)' }} />
                            </Box>
                            {topViewingPopoverAnchor.column === 'projectName' || topViewingPopoverAnchor.column === 'blockNumber' ? (
                              <Box>
                                <TextField
                                  fullWidth
                                  size="small"
                                  placeholder={`Filter ${topViewingPopoverAnchor.column === 'projectName' ? 'Project Name' : 'Block'}`}
                                  value={topViewingPopoverAnchor.column === 'projectName' ? topViewingAvailableUnitFilters.projectName : topViewingAvailableUnitFilters.blockNumber}
                                  onChange={(e) => handleTopViewingFilterChange(topViewingPopoverAnchor.column, e.target.value)}
                                  InputProps={{
                                    endAdornment: hasTopViewingActiveFilter(topViewingPopoverAnchor.column) && (
                                      <InputAdornment position="end">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleTopViewingFilterChange(topViewingPopoverAnchor.column, '')}
                                          sx={{ p: 0.5 }}
                                        >
                                          <Typography variant="caption" sx={{ fontSize: 12 }}>✕</Typography>
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                                <Button
                                  size="small"
                                  onClick={() => handleTopViewingFilterChange(topViewingPopoverAnchor.column, '')}
                                  sx={{ mt: 1 }}
                                  fullWidth
                                  disabled={!hasTopViewingActiveFilter(topViewingPopoverAnchor.column)}
                                >
                                  Clear Filter
                                </Button>
                              </Box>
                            ) : topViewingPopoverAnchor.column === 'stack' ? (
                              <Box>
                                <FormControl fullWidth size="small">
                                  <Select
                                    multiple
                                    displayEmpty
                                    value={topViewingAvailableUnitFilters.stackSelected}
                                    onChange={(event) => {
                                      const rawValue = event.target.value;
                                      const value = typeof rawValue === 'string' ? rawValue.split(',') : rawValue;
                                      if (value.includes('__SELECT_ALL__')) {
                                        if (topViewingAvailableUnitFilters.stackSelected.length === topViewingDistinctStacks.length) {
                                          setTopViewingAvailableUnitFilters((prev) => ({ ...prev, stackSelected: [], stackMin: '', stackMax: '' }));
                                        } else {
                                          setTopViewingAvailableUnitFilters((prev) => ({ ...prev, stackSelected: [...topViewingDistinctStacks], stackMin: '', stackMax: '' }));
                                        }
                                        return;
                                      }
                                      setTopViewingAvailableUnitFilters((prev) => ({ ...prev, stackSelected: value, stackMin: '', stackMax: '' }));
                                    }}
                                    renderValue={(selected) => {
                                      const selectedValues = selected as string[];
                                      if (selectedValues.length === 0) return 'All Stacks';
                                      if (selectedValues.length === topViewingDistinctStacks.length) return 'All Selected';
                                      return `${selectedValues.length} selected`;
                                    }}
                                  >
                                    <MenuItem value="__SELECT_ALL__">
                                      <Checkbox
                                        checked={topViewingAvailableUnitFilters.stackSelected.length === topViewingDistinctStacks.length && topViewingDistinctStacks.length > 0}
                                        indeterminate={topViewingAvailableUnitFilters.stackSelected.length > 0 && topViewingAvailableUnitFilters.stackSelected.length < topViewingDistinctStacks.length}
                                      />
                                      <Typography component="span" sx={{ fontWeight: 600 }}>Select All</Typography>
                                    </MenuItem>
                                    {topViewingDistinctStacks.map((option) => {
                                      const isSelected = topViewingAvailableUnitFilters.stackSelected.includes(option);
                                      return (
                                        <MenuItem key={option} value={option}>
                                          <Checkbox checked={isSelected} />
                                          <Typography component="span">{option}</Typography>
                                        </MenuItem>
                                      );
                                    })}
                                  </Select>
                                </FormControl>
                                <Button
                                  size="small"
                                  onClick={() => setTopViewingAvailableUnitFilters((prev) => ({ ...prev, stackSelected: [], stackMin: '', stackMax: '' }))}
                                  sx={{ mt: 1 }}
                                  fullWidth
                                  disabled={!hasTopViewingActiveFilter('stack')}
                                >
                                  Clear Filter (Show All)
                                </Button>
                              </Box>
                            ) : topViewingPopoverAnchor.column === 'unitTypeDescription' ? (
                              <Box>
                                <FormControl fullWidth size="small">
                                  <Select
                                    multiple
                                    displayEmpty
                                    value={topViewingAvailableUnitFilters.unitTypeDescriptionSelected}
                                    onChange={(event) => {
                                      const rawValue = event.target.value;
                                      const value = typeof rawValue === 'string' ? rawValue.split(',') : rawValue;
                                      if (value.includes('__SELECT_ALL__')) {
                                        if (topViewingAvailableUnitFilters.unitTypeDescriptionSelected.length === topViewingDistinctUnitTypeDescriptions.length) {
                                          setTopViewingAvailableUnitFilters((prev) => ({ ...prev, unitTypeDescriptionSelected: [] }));
                                        } else {
                                          setTopViewingAvailableUnitFilters((prev) => ({ ...prev, unitTypeDescriptionSelected: [...topViewingDistinctUnitTypeDescriptions] }));
                                        }
                                        return;
                                      }
                                      setTopViewingAvailableUnitFilters((prev) => ({ ...prev, unitTypeDescriptionSelected: value }));
                                    }}
                                    renderValue={(selected) => {
                                      const selectedValues = selected as string[];
                                      if (selectedValues.length === 0) return 'All Types';
                                      if (selectedValues.length === topViewingDistinctUnitTypeDescriptions.length) return 'All Selected';
                                      return `${selectedValues.length} selected`;
                                    }}
                                  >
                                    <MenuItem value="__SELECT_ALL__">
                                      <Checkbox
                                        checked={topViewingAvailableUnitFilters.unitTypeDescriptionSelected.length === topViewingDistinctUnitTypeDescriptions.length && topViewingDistinctUnitTypeDescriptions.length > 0}
                                        indeterminate={topViewingAvailableUnitFilters.unitTypeDescriptionSelected.length > 0 && topViewingAvailableUnitFilters.unitTypeDescriptionSelected.length < topViewingDistinctUnitTypeDescriptions.length}
                                      />
                                      <Typography component="span" sx={{ fontWeight: 600 }}>Select All</Typography>
                                    </MenuItem>
                                    {topViewingDistinctUnitTypeDescriptions.map((option) => {
                                      const isSelected = topViewingAvailableUnitFilters.unitTypeDescriptionSelected.includes(option);
                                      return (
                                        <MenuItem key={option} value={option}>
                                          <Checkbox checked={isSelected} />
                                          <Typography component="span">{option}</Typography>
                                        </MenuItem>
                                      );
                                    })}
                                  </Select>
                                </FormControl>
                                <Button
                                  size="small"
                                  onClick={() => setTopViewingAvailableUnitFilters((prev) => ({ ...prev, unitTypeDescriptionSelected: [] }))}
                                  sx={{ mt: 1 }}
                                  fullWidth
                                  disabled={!hasTopViewingActiveFilter('unitTypeDescription')}
                                >
                                  Clear Filter (Show All)
                                </Button>
                              </Box>
                            ) : (
                              topViewingPopoverAnchor.column !== 'distance' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  {(() => {
                                    const shouldFormatThousands = topViewingPopoverAnchor.column === 'price' || topViewingPopoverAnchor.column === 'psf';
                                    const minRaw = ({
                                      floor: topViewingAvailableUnitFilters.floorMin,
                                      bedroom: topViewingAvailableUnitFilters.bedroomMin,
                                      bathroom: topViewingAvailableUnitFilters.bathroomMin,
                                      areaSqft: topViewingAvailableUnitFilters.areaMin,
                                      price: topViewingAvailableUnitFilters.priceMin,
                                      dps: topViewingAvailableUnitFilters.dpsMin,
                                      psf: topViewingAvailableUnitFilters.psfMin,
                                      outlay: topViewingAvailableUnitFilters.outlayMin,
                                      installment: topViewingAvailableUnitFilters.installmentMin,
                                    } as Record<string, string>)[topViewingPopoverAnchor.column];
                                    const maxRaw = ({
                                      floor: topViewingAvailableUnitFilters.floorMax,
                                      bedroom: topViewingAvailableUnitFilters.bedroomMax,
                                      bathroom: topViewingAvailableUnitFilters.bathroomMax,
                                      areaSqft: topViewingAvailableUnitFilters.areaMax,
                                      price: topViewingAvailableUnitFilters.priceMax,
                                      dps: topViewingAvailableUnitFilters.dpsMax,
                                      psf: topViewingAvailableUnitFilters.psfMax,
                                      outlay: topViewingAvailableUnitFilters.outlayMax,
                                      installment: topViewingAvailableUnitFilters.installmentMax,
                                    } as Record<string, string>)[topViewingPopoverAnchor.column];
                                    return (
                                      <>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type={shouldFormatThousands ? 'text' : 'number'}
                                    inputProps={shouldFormatThousands ? { inputMode: 'decimal' } : undefined}
                                    label="Min"
                                    value={shouldFormatThousands ? formatThousandsInput(minRaw) : minRaw}
                                    onChange={(e) => handleTopViewingRangeFilterChange(topViewingPopoverAnchor.column as any, 'min', shouldFormatThousands ? e.target.value.replace(/,/g, '').replace(/[^\d.]/g, '') : e.target.value)}
                                  />
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type={shouldFormatThousands ? 'text' : 'number'}
                                    inputProps={shouldFormatThousands ? { inputMode: 'decimal' } : undefined}
                                    label="Max"
                                    value={shouldFormatThousands ? formatThousandsInput(maxRaw) : maxRaw}
                                    onChange={(e) => handleTopViewingRangeFilterChange(topViewingPopoverAnchor.column as any, 'max', shouldFormatThousands ? e.target.value.replace(/,/g, '').replace(/[^\d.]/g, '') : e.target.value)}
                                  />
                                  <Button
                                    size="small"
                                    onClick={() => {
                                      handleTopViewingRangeFilterChange(topViewingPopoverAnchor.column as any, 'min', '');
                                      handleTopViewingRangeFilterChange(topViewingPopoverAnchor.column as any, 'max', '');
                                    }}
                                    fullWidth
                                    disabled={!hasTopViewingActiveFilter(topViewingPopoverAnchor.column)}
                                  >
                                    Clear Filter
                                  </Button>
                                      </>
                                    );
                                  })()}
                                </Box>
                              )
                            )}
                          </Box>
                        </Box>
                      )}
                    </Popover>
                  </CardContent>
                </Card>

              {/* Comparable Units Table - shown when reference unit is set */}
              {topViewingReferenceUnit && (
                <Box data-tutorial="comparable-units" ref={topViewingComparableSectionRef} sx={{ mt: 2 }}>
                  <Card
                    sx={{
                      position: 'relative',
                      overflow: 'hidden',
                      border: '1px solid rgba(var(--color-text-rgb), 0.08)',
                    }}
                  >
                    {priceMatrixSimulationSession && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          zIndex: 9999,
                          pointerEvents: 'none',
                          backgroundImage: PRICE_SIMULATION_WATERMARK,
                          backgroundRepeat: 'repeat',
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        p: 3,
                        bgcolor: 'var(--color-surface)',
                        ...(isComparisonLocked
                          ? { filter: 'grayscale(1)', opacity: 0.75, pointerEvents: 'none' }
                          : {}),
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: topViewingCompViewMode === 'unit' && topViewingAreaTolerance > 0 ? 1 : 2, flexWrap: 'wrap', gap: 1 }}>
                        {topViewingCompViewMode === 'unit' ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 0.5 }}>
                              <Typography variant="h6" sx={{ color: 'var(--color-text-strong)', fontWeight: 600, letterSpacing: '-0.02em' }}>
                                {isGuesstimationReference
                                  ? (topViewingReferenceUnit.bedroom > 0 ? `Best-matched ${topViewingReferenceUnit.bedroom}-Bedroom units` : 'Best-matched units')
                                  : `Best-matched ${topViewingReferenceUnit.bedroom}-Bedroom units`}
                              </Typography>
                              {isIndicativeReference ? (
                                <Typography
                                  component="span"
                                  sx={{
                                    color: 'rgba(var(--color-text-rgb), 0.7)',
                                    fontWeight: 600,
                                  }}
                                >
                                  ({indicativeRangeLabel ?? 'Size range unavailable'})
                                </Typography>
                              ) : isGuesstimationReference ? (
                                <Typography
                                  component="span"
                                  sx={{
                                    color: 'rgba(var(--color-text-rgb), 0.7)',
                                    fontWeight: 600,
                                  }}
                                >
                                  ({indicativeRangeLabel ?? 'Guess-timation price reference'})
                                </Typography>
                              ) : (
                                <Typography
                                  component="span"
                                  onClick={() => {
                                    setTopViewingToleranceSliderValue(topViewingAreaTolerance);
                                    setTopViewingToleranceModalOpen(true);
                                  }}
                                  sx={{
                                    color: 'var(--color-accent)',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    textUnderlineOffset: 3,
                                    '&:hover': { color: 'var(--color-accent-strong)' },
                                  }}
                                >
                                  ({topViewingReferenceUnit.areaSqft != null ? Math.round(topViewingReferenceUnit.areaSqft).toLocaleString() : '—'} sqft{topViewingAreaTolerance > 0 ? ` ±${topViewingAreaTolerance}%` : ''}{topViewingAreaTolerance > 0 && topViewingReferenceUnit.areaSqft != null ? ` ${getAreaRangeLabel(topViewingReferenceUnit.areaSqft, topViewingAreaTolerance) ?? '—'}` : ''})
                                </Typography>
                              )}
                              <Typography variant="h6" sx={{ color: 'var(--color-text-strong)', fontWeight: 600, letterSpacing: '-0.02em' }}>
                                {' '}you may consider
                              </Typography>
                              <Tooltip
                                title={
                                  <Box sx={{ p: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                      Thumbs up criteria:
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                                      The best matched unit receives a thumbs up icon based on:
                                    </Typography>
                                    <Typography variant="caption" component="div">
                                      • Closest match to reference unit size
                                    </Typography>
                                    <Typography variant="caption" component="div">
                                      • Best price and PSF combination
                                    </Typography>
                                  </Box>
                                }
                                arrow
                              >
                                <IconButton size="small" sx={{ ml: 0.5 }}>
                                  <HelpOutlineIcon fontSize="small" sx={{ color: 'var(--color-text-muted)' }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            {isGuesstimationReference ? (
                              <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                                Budget matched units use the Guess-timation price. Comparable unit matching stays locked.
                              </Typography>
                            ) : (
                              <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                                Matched count: {topViewingMatchedCount} | Matched {topViewingReferenceUnit.bedroom}-BR: {topViewingMatchedBedroomCount}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="h6" sx={{ color: 'var(--color-text-strong)' }}>
                            Project Comparison View
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          <ToggleButtonGroup
                            value={topViewingCompViewMode}
                            exclusive
                            onChange={(_, newMode) => { if (newMode) setTopViewingCompViewMode(newMode); }}
                            size="small"
                            disabled={isComparisonLocked}
                            sx={{
                              '& .MuiToggleButton-root': {
                                color: 'var(--color-text-strong)',
                                borderColor: 'rgba(var(--color-text-rgb), 0.3)',
                                '&.Mui-selected': {
                                  bgcolor: 'var(--color-accent)',
                                  color: 'var(--color-text-inverse)',
                                  '&:hover': { bgcolor: 'var(--color-accent-strong)' },
                                },
                                '&:hover': { bgcolor: 'rgba(var(--color-text-rgb), 0.1)' },
                              },
                            }}
                          >
                            <ToggleButton value="unit">Unit View ({topViewingComparableUnitsForDisplay.length})</ToggleButton>
                            <ToggleButton value="project">Project View ({topViewingFilteredProjectsForDisplay.length})</ToggleButton>
                          </ToggleButtonGroup>
                          {topViewingComparableFilterSummaryParts.length > 0 && (
                            <Button
                              size="small"
                              onClick={handleClearAllComparableFilters}
                              disabled={isComparisonLocked}
                              sx={{ color: 'var(--color-text-muted)', '&:hover': { color: 'var(--color-accent)' } }}
                            >
                              Clear all filters
                            </Button>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => setTopViewingComparableSectionOpen((prev) => !prev)}
                            sx={{ border: '1px solid rgba(var(--color-text-rgb), 0.2)' }}
                            title={topViewingComparableSectionOpen ? 'Collapse' : 'Expand'}
                            disabled={isComparisonLocked}
                          >
                            <ExpandMoreIcon
                              sx={{
                                transform: topViewingComparableSectionOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease',
                              }}
                            />
                          </IconButton>
                        </Box>
                      </Box>

                      <Collapse in={topViewingComparableSectionOpen || isComparisonLocked} timeout="auto" unmountOnExit>
                      {isComparisonLocked ? (
                        <Box
                          sx={{
                            width: '100%',
                            border: '1px dashed rgba(var(--color-text-rgb), 0.2)',
                            borderRadius: 1.5,
                            p: 4,
                            textAlign: 'center',
                            bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
                            color: 'rgba(var(--color-text-rgb), 0.65)',
                            filter: 'grayscale(1)',
                          }}
                        >
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                            {isGuesstimationReference ? 'Comparable Analysis Locked' : 'Not Available Before Price Release'}
                          </Typography>
                          <Typography variant="body2">
                            {isGuesstimationReference
                              ? 'Guess-timation price references can drive budget matched results, but comparable unit analysis stays locked.'
                              : 'Comparable unit analysis will be unlocked after official pricing is released.'}
                          </Typography>
                        </Box>
                      ) : topViewingComparableUnitsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                          <CircularProgress />
                        </Box>
                      ) : topViewingCompViewMode === 'project' ? (
                        <Box
                          sx={{
                            width: '100%',
                            overflowX: 'auto',
                            '&::-webkit-scrollbar': {
                              height: 8,
                            },
                            '&::-webkit-scrollbar-track': {
                              bgcolor: 'rgba(var(--color-text-rgb), 0.05)',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              bgcolor: 'rgba(var(--color-text-rgb), 0.2)',
                              borderRadius: 1,
                              '&:hover': {
                                bgcolor: 'rgba(var(--color-text-rgb), 0.3)',
                              },
                            },
                          }}
                        >
                        <TableContainer
                          sx={{
                            bgcolor: 'transparent',
                            maxHeight: { xs: 420, md: 560 },
                            overflowY: 'auto',
                          }}
                        >
                        <Table
                          stickyHeader
                          sx={{
                            tableLayout: 'fixed',
                            width: 3790,
                            minWidth: 3790,
                            '& .MuiTableCell-root': {
                              color: 'var(--color-text-strong)',
                              borderColor: 'rgba(var(--color-text-rgb), 0.1)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              boxSizing: 'border-box',
                            },
                            '& .MuiTableCell-root[data-show-full-value]': {
                              overflow: 'visible',
                              textOverflow: 'clip',
                            },
                            '& .MuiTableHead-root .MuiTableCell-root': {
                              bgcolor: 'var(--color-surface-thead)',
                              color: 'var(--color-text-strong)',
                              fontWeight: 600,
                            },
                            '& .MuiTableBody-root .MuiTableRow-root': {
                              bgcolor: 'var(--color-surface)',
                              '&:hover': {
                                bgcolor: 'rgba(var(--color-text-rgb), 0.05)',
                                '& .sticky-cell': {
                                  bgcolor: 'var(--color-surface-row-hover)',
                                },
                              },
                              '&:nth-of-type(even)': {
                                bgcolor: 'rgba(var(--color-text-rgb), 0.02)',
                                '& .sticky-cell': {
                                  bgcolor: 'var(--color-surface-row-alt)',
                                },
                              },
                              '&:nth-of-type(even):hover': {
                                '& .sticky-cell': {
                                  bgcolor: 'var(--color-surface-row-hover)',
                                },
                              },
                            },
                          }}
                        >
                          <colgroup>
                            <col style={{ width: 200, minWidth: 200 }} />
                            <col style={{ width: 180, minWidth: 180 }} />
                            <col style={{ width: 100, minWidth: 100 }} />
                            <col style={{ width: 140 }} />
                            <col style={{ width: 100 }} />
                            <col style={{ width: 100 }} />
                            <col style={{ width: 180 }} />
                            <col style={{ width: 120 }} />
                            <col style={{ width: 120 }} />
                            <col style={{ width: 160 }} />
                            <col style={{ width: 110 }} />
                            <col style={{ width: 130 }} />
                            <col style={{ width: 120 }} />
                            <col style={{ width: 130 }} />
                            {[...Array(18)].map((_, i) => <col key={i} style={{ width: i % 3 === 1 ? 150 : 90 }} />)}
                            <col style={{ width: 120 }} />
                          </colgroup>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, color: 'var(--color-text-strong)', position: 'sticky', left: 0, zIndex: 2, minWidth: 200, width: 200, bgcolor: 'var(--color-surface-thead)', borderRight: '1px solid rgba(var(--color-text-rgb), 0.1)' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  Project Name
                                  <IconButton size="small" onClick={(e) => setProjectViewPopoverAnchor({ element: e.currentTarget, column: 'projectName' })} sx={getProjectViewIconButtonSx('projectName')}><MenuIcon fontSize="small" /></IconButton>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600, color: 'var(--color-text-strong)', minWidth: 180 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  Project Category
                                  <IconButton size="small" onClick={(e) => setProjectViewPopoverAnchor({ element: e.currentTarget, column: 'projectCategory' })} sx={getProjectViewIconButtonSx('projectCategory')}><MenuIcon fontSize="small" /></IconButton>
                                </Box>
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, color: 'var(--color-text-strong)', minWidth: 100 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                  Distance
                                  <IconButton size="small" onClick={(e) => setProjectViewPopoverAnchor({ element: e.currentTarget, column: 'distance' })} sx={getProjectViewIconButtonSx('distance')}><MenuIcon fontSize="small" /></IconButton>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600, color: 'var(--color-text-strong)', minWidth: 120 }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>Developer<IconButton size="small" onClick={(e) => setProjectViewPopoverAnchor({ element: e.currentTarget, column: 'developer' })} sx={getProjectViewIconButtonSx('developer')}><MenuIcon fontSize="small" /></IconButton></Box></TableCell>
                              <TableCell sx={{ fontWeight: 600, color: 'var(--color-text-strong)', minWidth: 100 }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>Total Units<IconButton size="small" onClick={(e) => setProjectViewPopoverAnchor({ element: e.currentTarget, column: 'totalUnits' })} sx={getProjectViewIconButtonSx('totalUnits')}><MenuIcon fontSize="small" /></IconButton></Box></TableCell>
                              <TableCell sx={{ fontWeight: 600, color: 'var(--color-text-strong)', minWidth: 100 }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>Sold Units<IconButton size="small" onClick={(e) => setProjectViewPopoverAnchor({ element: e.currentTarget, column: 'soldUnits' })} sx={getProjectViewIconButtonSx('soldUnits')}><MenuIcon fontSize="small" /></IconButton></Box></TableCell>
                              <TableCell sx={{ fontWeight: 600, color: 'var(--color-text-strong)', minWidth: 180 }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>District/Location<IconButton size="small" onClick={(e) => setProjectViewPopoverAnchor({ element: e.currentTarget, column: 'districtLocation' })} sx={getProjectViewIconButtonSx('districtLocation')}><MenuIcon fontSize="small" /></IconButton></Box></TableCell>
                              <TableCell sx={{ fontWeight: 600, color: 'var(--color-text-strong)', minWidth: 120 }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>District<IconButton size="small" onClick={(e) => setProjectViewPopoverAnchor({ element: e.currentTarget, column: 'district' })} sx={getProjectViewIconButtonSx('district')}><MenuIcon fontSize="small" /></IconButton></Box></TableCell>
                              <TableCell sx={{ fontWeight: 600, color: 'var(--color-text-strong)', minWidth: 120 }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>Region<IconButton size="small" onClick={(e) => setProjectViewPopoverAnchor({ element: e.currentTarget, column: 'region' })} sx={getProjectViewIconButtonSx('region')}><MenuIcon fontSize="small" /></IconButton></Box></TableCell>
                              <TableCell sx={{ fontWeight: 600, color: 'var(--color-text-strong)', minWidth: 140 }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>Nearest Mrt<IconButton size="small" onClick={(e) => setProjectViewPopoverAnchor({ element: e.currentTarget, column: 'nearestMrt' })} sx={getProjectViewIconButtonSx('nearestMrt')}><MenuIcon fontSize="small" /></IconButton></Box></TableCell>
                              <TableCell sx={{ fontWeight: 600, color: 'var(--color-text-strong)', minWidth: 120 }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>Walking Time<IconButton size="small" onClick={(e) => setProjectViewPopoverAnchor({ element: e.currentTarget, column: 'walkingTimeToMrt' })} sx={getProjectViewIconButtonSx('walkingTimeToMrt')}><MenuIcon fontSize="small" /></IconButton></Box></TableCell>
                              <TableCell sx={{ fontWeight: 600, color: 'var(--color-text-strong)', minWidth: 120 }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>Tenure<IconButton size="small" onClick={(e) => setProjectViewPopoverAnchor({ element: e.currentTarget, column: 'tenure' })} sx={getProjectViewIconButtonSx('tenure')}><MenuIcon fontSize="small" /></IconButton></Box></TableCell>
                              <TableCell sx={{ fontWeight: 600, minWidth: 120 }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>Completed Date<IconButton size="small" onClick={(e) => setProjectViewPopoverAnchor({ element: e.currentTarget, column: 'completedDate' })} sx={getProjectViewIconButtonSx('completedDate')}><MenuIcon fontSize="small" /></IconButton></Box></TableCell>
                              {[1, 2, 3, 4, 5, 'PH'].map((br) => (
                                <Fragment key={br}>
                                  <TableCell align="right" sx={{ fontWeight: 600, color: 'var(--color-text-strong)', minWidth: 90 }}><Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>{br === 'PH' ? 'PH' : `${br}BR`} Avail<IconButton size="small" onClick={(e) => setProjectViewPopoverAnchor({ element: e.currentTarget, column: br === 'PH' ? 'phavail' : `br${br}avail` })} sx={getProjectViewIconButtonSx(br === 'PH' ? 'phavail' : `br${br}avail`)}><MenuIcon fontSize="small" /></IconButton></Box></TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600, color: 'var(--color-text-strong)', minWidth: 100 }}><Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>{br === 'PH' ? 'PH' : `${br}BR`} Price<IconButton size="small" onClick={(e) => setProjectViewPopoverAnchor({ element: e.currentTarget, column: br === 'PH' ? 'phprice' : `br${br}price` })} sx={getProjectViewIconButtonSx(br === 'PH' ? 'phprice' : `br${br}price`)}><MenuIcon fontSize="small" /></IconButton></Box></TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600, color: 'var(--color-text-strong)', minWidth: 90 }}><Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>{br === 'PH' ? 'PH' : `${br}BR`} Psf<IconButton size="small" onClick={(e) => setProjectViewPopoverAnchor({ element: e.currentTarget, column: br === 'PH' ? 'phpsf' : `br${br}psf` })} sx={getProjectViewIconButtonSx(br === 'PH' ? 'phpsf' : `br${br}psf`)}><MenuIcon fontSize="small" /></IconButton></Box></TableCell>
                                </Fragment>
                              ))}
                              <TableCell sx={{ fontWeight: 600, color: 'var(--color-text-strong)', minWidth: 120 }}>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {topViewingProjectsForProjectView.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={33} align="center" sx={{ py: 4 }}>
                                  <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                                    No projects available. Please search for comparable units first.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : topViewingFilteredProjectsForDisplay.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={33} align="center" sx={{ py: 4 }}>
                                  <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                                    No projects match the current filters.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              topViewingComparableProjectsPageRows.map((projectName) => {
                                const details = projectDetailsMap.get(projectName);
                                const stickyCellSx = { bgcolor: 'var(--color-surface)' };
                                const brKeys = ['br1', 'br2', 'br3', 'br4', 'br5', 'ph'] as const;
                                return (
                                  <TableRow key={projectName} hover>
                                    <TableCell className="sticky-cell" sx={{ fontWeight: 500, color: 'var(--color-text-strong)', position: 'sticky', left: 0, zIndex: 1, minWidth: 200, width: 200, borderRight: '1px solid rgba(var(--color-text-rgb), 0.1)', ...stickyCellSx }}>{projectName}</TableCell>
                                    <TableCell sx={{ color: 'var(--color-text-strong)', minWidth: 180 }}>
                                      {details?.projectCategory ?? '-'}
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: 'var(--color-text-strong)', minWidth: 100 }}>
                                      {(() => {
                                        const distance = getTopViewingDistanceFromReference(projectName);
                                        return distance !== null ? (
                                          <Typography variant="body2" sx={{ color: 'var(--color-text-strong)' }}>
                                            {formatNumber(distance, 2)} km
                                          </Typography>
                                        ) : (
                                          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                                            -
                                          </Typography>
                                        );
                                      })()}
                                    </TableCell>
                                    <TableCell sx={{ color: 'var(--color-text-strong)' }}>{details?.developer ?? '-'}</TableCell>
                                    <TableCell sx={{ color: 'var(--color-text-strong)' }}>{details?.totalUnits ?? '-'}</TableCell>
                                    <TableCell sx={{ color: 'var(--color-text-strong)' }}>{details?.soldUnits ?? '-'}</TableCell>
                                    <TableCell sx={{ color: 'var(--color-text-strong)' }}>{details?.districtLocation || (details?.area || details?.district ? [details.area, details.district].filter(Boolean).join(' / ') : '') || '-'}</TableCell>
                                    <TableCell sx={{ color: 'var(--color-text-strong)' }}>{details?.district ?? '-'}</TableCell>
                                    <TableCell sx={{ color: 'var(--color-text-strong)' }}>{details?.area ?? '-'}</TableCell>
                                    <TableCell sx={{ color: 'var(--color-text-strong)' }}>{details?.nearestMrt ?? '-'}</TableCell>
                                    <TableCell sx={{ color: 'var(--color-text-strong)' }}>
                                      {details?.walkingTimeToMrt != null ? `${details.walkingTimeToMrt} min` : '-'}
                                    </TableCell>
                                    <TableCell sx={{ color: 'var(--color-text-strong)' }}>{details?.tenure ?? '-'}</TableCell>
                                    <TableCell sx={{ color: 'var(--color-text-strong)' }}>
                                      {details?.completedDate
                                        ? new Date(details.completedDate).toLocaleDateString('en-SG', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                          })
                                        : '-'}
                                    </TableCell>
                                    {brKeys.map((key) => {
                                      const br = details?.[key];
                                      return (
                                        <Fragment key={key}>
                                          <TableCell align="right" sx={{ color: 'var(--color-text-strong)' }}>
                                            {br?.availableUnits ?? '-'}
                                          </TableCell>
                                          <TableCell align="right" data-show-full-value sx={{ color: 'var(--color-text-strong)', whiteSpace: 'nowrap', minWidth: 140 }}>
                                            {br?.fromPrice != null ? formatCurrency(br.fromPrice) : '-'}
                                          </TableCell>
                                          <TableCell align="right" data-show-full-value sx={{ color: 'var(--color-text-strong)', whiteSpace: 'nowrap', minWidth: 100 }}>
                                            {br?.fromPsf != null ? formatCurrency(br.fromPsf) : '-'}
                                          </TableCell>
                                        </Fragment>
                                      );
                                    })}
                                    <TableCell>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => void handleOpenSitePlanForProject(projectName)}
                                      >
                                        View Site Plan
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                        </TableContainer>
                        <TablePagination
                          component="div"
                          count={topViewingFilteredProjectsForDisplay.length}
                          page={topViewingComparableProjectsPage}
                          onPageChange={(_, nextPage) => setTopViewingComparableProjectsPage(nextPage)}
                          rowsPerPage={TOP_VIEWING_PAGE_SIZE}
                          onRowsPerPageChange={() => {}}
                          rowsPerPageOptions={[TOP_VIEWING_PAGE_SIZE]}
                          sx={{
                            borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)',
                            '& .MuiTablePagination-toolbar': {
                              minHeight: 40,
                            },
                          }}
                        />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            overflowX: 'auto',
                            '&::-webkit-scrollbar': {
                              height: 8,
                            },
                            '&::-webkit-scrollbar-track': {
                              bgcolor: 'rgba(var(--color-text-rgb), 0.05)',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              bgcolor: 'rgba(var(--color-text-rgb), 0.2)',
                              borderRadius: 1,
                              '&:hover': {
                                bgcolor: 'rgba(var(--color-text-rgb), 0.3)',
                              },
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                            <ColumnVisibilityMenu
                              anchorEl={comparableUnitColumnsAnchor}
                              columns={comparableUnitColumnsForMenu}
                              getLabel={formatColumnVisibilityLabel}
                              onClose={() => setComparableUnitColumnsAnchor(null)}
                              onOpen={(event) => setComparableUnitColumnsAnchor(event.currentTarget)}
                              onReset={resetComparableUnitColumns}
                              onToggle={toggleComparableUnitColumn}
                              visibility={comparableUnitColumnVisibility}
                            />
                          </Box>
                          <TableContainer
                            sx={{
                              bgcolor: 'transparent',
                              maxHeight: { xs: 420, md: 560 },
                              overflowY: 'auto',
                            }}
                          >
                            <Table
                              size="small"
                              stickyHeader
                              sx={{
                                '& .MuiTableCell-root': {
                                  py: 1,
                                  color: 'var(--color-table-text)',
                                  borderColor: 'rgba(var(--color-text-rgb), 0.1)',
                                },
                                '& .MuiTableHead-root .MuiTableCell-root': {
                                  bgcolor: 'var(--color-surface-elevated)',
                                  color: 'var(--color-heading)',
                                  fontWeight: 600,
                                },
                                '& .MuiTableBody-root .MuiTableRow-root': {
                                  bgcolor: 'var(--color-surface)',
                                  '&:hover': {
                                    bgcolor: 'rgba(var(--color-text-rgb), 0.05)',
                                  },
                                  '&:nth-of-type(even)': {
                                    bgcolor: 'rgba(var(--color-text-rgb), 0.02)',
                                  },
                                },
                              }}
                            >
                              <TableHead>
                                <TableRow>
                                {isComparableUnitColumnVisible('projectName') && (
                                <TableCell sx={{ pr: 1, cursor: 'pointer' }} onClick={() => handleTopViewingCompSort('projectName')}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {getTopViewingCompColumnLabel('projectName')}
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleTopViewingCompOpenPopover(e, 'projectName');
                                      }}
                                      sx={{ color: hasTopViewingCompActiveFilter('projectName') ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)' }}
                                    >
                                      {getSortIndicatorIcon(getTopViewingCompSortDirection('projectName'))}
                                    </IconButton>
                                  </Box>
                                </TableCell>
                                )}
                                {isComparableUnitColumnVisible('projectCategory') && (
                                <TableCell sx={{ minWidth: 160, cursor: 'pointer' }} onClick={() => handleTopViewingCompSort('projectCategory')}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {getTopViewingCompColumnLabel('projectCategory')}
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleTopViewingCompOpenPopover(e, 'projectCategory');
                                      }}
                                      sx={{ color: hasTopViewingCompActiveFilter('projectCategory') ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)' }}
                                    >
                                      {getSortIndicatorIcon(getTopViewingCompSortDirection('projectCategory'))}
                                    </IconButton>
                                  </Box>
                                </TableCell>
                                )}
                                {isComparableUnitColumnVisible('blockNumber') && (
                                <TableCell align="center" sx={{ width: 80, minWidth: 80, maxWidth: 80, px: 0.5, pr: 1, cursor: 'pointer' }} onClick={() => handleTopViewingCompSort('blockNumber')}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                    {getTopViewingCompColumnLabel('blockNumber')}
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleTopViewingCompOpenPopover(e, 'blockNumber');
                                      }}
                                      sx={{ color: hasTopViewingCompActiveFilter('blockNumber') ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)' }}
                                    >
                                      {getSortIndicatorIcon(getTopViewingCompSortDirection('blockNumber'))}
                                    </IconButton>
                                  </Box>
                                </TableCell>
                                )}
                                {isComparableUnitColumnVisible('unitNumber') && (
                                <TableCell align="center" sx={{ width: 100, minWidth: 100, maxWidth: 100, px: 0.5, pr: 1 }}>
                                  Unit #
                                </TableCell>
                                )}
                                {isComparableUnitColumnVisible('floor') && (
                                <TableCell align="center" sx={{ width: 70, minWidth: 70, maxWidth: 70, px: 0.5, pr: 1, cursor: 'pointer' }} onClick={() => handleTopViewingCompSort('floor')}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                    {getTopViewingCompColumnLabel('floor')}
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleTopViewingCompOpenPopover(e, 'floor');
                                      }}
                                      sx={{ color: hasTopViewingCompActiveFilter('floor') ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)' }}
                                    >
                                      {getSortIndicatorIcon(getTopViewingCompSortDirection('floor'))}
                                    </IconButton>
                                  </Box>
                                </TableCell>
                                )}
                                {isComparableUnitColumnVisible('stack') && (
                                <TableCell align="center" sx={{ width: 70, minWidth: 70, maxWidth: 70, px: 0.5, pr: 1, cursor: 'pointer' }} onClick={() => handleTopViewingCompSort('stack')}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                    {getTopViewingCompColumnLabel('stack')}
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleTopViewingCompOpenPopover(e, 'stack');
                                      }}
                                      sx={{ color: hasTopViewingCompActiveFilter('stack') ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)' }}
                                    >
                                      {getSortIndicatorIcon(getTopViewingCompSortDirection('stack'))}
                                    </IconButton>
                                  </Box>
                                </TableCell>
                                )}
                                {isComparableUnitColumnVisible('unitTypeDescription') && (
                                <TableCell align="left" sx={{ minWidth: 120, px: 0.5, pr: 1, cursor: 'pointer' }} onClick={() => handleTopViewingCompSort('unitTypeDescription')}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {getTopViewingCompColumnLabel('unitTypeDescription')}
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleTopViewingCompOpenPopover(e, 'unitTypeDescription');
                                      }}
                                      sx={{ color: hasTopViewingCompActiveFilter('unitTypeDescription') ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)' }}
                                    >
                                      {getSortIndicatorIcon(getTopViewingCompSortDirection('unitTypeDescription'))}
                                    </IconButton>
                                  </Box>
                                </TableCell>
                                )}
                                {isComparableUnitColumnVisible('bedroom') && (
                                <TableCell align="center" sx={{ width: 70, minWidth: 70, maxWidth: 70, px: 0.5, pr: 1, cursor: 'pointer' }} onClick={() => handleTopViewingCompSort('bedroom')}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                    {getTopViewingCompColumnLabel('bedroom')}
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleTopViewingCompOpenPopover(e, 'bedroom');
                                      }}
                                      sx={{ color: hasTopViewingCompActiveFilter('bedroom') ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)' }}
                                    >
                                      {getSortIndicatorIcon(getTopViewingCompSortDirection('bedroom'))}
                                    </IconButton>
                                  </Box>
                                </TableCell>
                                )}
                                {isComparableUnitColumnVisible('bathroom') && (
                                <TableCell align="center" sx={{ width: 60, minWidth: 60, maxWidth: 60, px: 0.5, pr: 1, cursor: 'pointer' }} onClick={() => handleTopViewingCompSort('bathroom')}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                    {getTopViewingCompColumnLabel('bathroom')}
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleTopViewingCompOpenPopover(e, 'bathroom');
                                      }}
                                      sx={{ color: hasTopViewingCompActiveFilter('bathroom') ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)' }}
                                    >
                                      {getSortIndicatorIcon(getTopViewingCompSortDirection('bathroom'))}
                                    </IconButton>
                                  </Box>
                                </TableCell>
                                )}
                                {isComparableUnitColumnVisible('areaSqft') && (
                                <TableCell align="center" sx={{ width: 80, minWidth: 80, maxWidth: 80, px: 0.5, pr: 1, cursor: 'pointer' }} onClick={() => handleTopViewingCompSort('areaSqft')}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                    {getTopViewingCompColumnLabel('areaSqft')}
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleTopViewingCompOpenPopover(e, 'areaSqft');
                                      }}
                                      sx={{ color: hasTopViewingCompActiveFilter('areaSqft') ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)' }}
                                    >
                                      {getSortIndicatorIcon(getTopViewingCompSortDirection('areaSqft'))}
                                    </IconButton>
                                  </Box>
                                </TableCell>
                                )}
                                {isComparableUnitColumnVisible('price') && (
                                <TableCell align="right" sx={{ width: 160, minWidth: 160, maxWidth: 160, pr: 1, cursor: 'pointer' }} onClick={() => handleTopViewingCompSort('price')}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                    {showComparableDualPricing ? 'Price / NPS' : getTopViewingCompColumnLabel('price')}
                                    <Tooltip title="Click to see thumbs up criteria">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setTopViewingThumbsUpLegendAnchor(e.currentTarget);
                                        }}
                                        sx={{
                                          color: 'var(--color-info)',
                                          '&:hover': {
                                            bgcolor: 'rgba(var(--color-info-rgb), 0.1)',
                                          },
                                        }}
                                      >
                                        <HelpOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title={topViewingThumbsUpFilterActive ? "Show all units" : "Show only thumbs up units"}>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setTopViewingThumbsUpFilterActive(!topViewingThumbsUpFilterActive);
                                        }}
                                        sx={{
                                          color: topViewingThumbsUpFilterActive ? 'var(--color-success)' : 'rgba(var(--color-text-rgb), 0.5)',
                                          ...(topViewingThumbsUpFilterActive && {
                                            bgcolor: 'rgba(var(--color-success-rgb), 0.1)',
                                          }),
                                        }}
                                      >
                                        <ThumbUpIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleTopViewingCompOpenPopover(e, 'price');
                                      }}
                                      sx={{ color: hasTopViewingCompActiveFilter('price') ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)' }}
                                    >
                                      {getSortIndicatorIcon(getTopViewingCompSortDirection('price'))}
                                    </IconButton>
                                  </Box>
                                </TableCell>
                                )}
                                {isComparableUnitColumnVisible('psf') && (
                                <TableCell align="right" sx={{ pr: 1.5, cursor: 'pointer' }} onClick={() => handleTopViewingCompSort('psf')}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                    {getTopViewingCompColumnLabel('psf')}
                                    <Tooltip title="Click to see thumbs up criteria">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setTopViewingThumbsUpLegendAnchor(e.currentTarget);
                                        }}
                                        sx={{
                                          color: 'var(--color-info)',
                                          '&:hover': {
                                            bgcolor: 'rgba(var(--color-info-rgb), 0.1)',
                                          },
                                        }}
                                      >
                                        <HelpOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title={topViewingThumbsUpFilterActive ? "Show all units" : "Show only thumbs up units"}>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setTopViewingThumbsUpFilterActive(!topViewingThumbsUpFilterActive);
                                        }}
                                        sx={{
                                          color: topViewingThumbsUpFilterActive ? 'var(--color-success)' : 'rgba(var(--color-text-rgb), 0.5)',
                                          ...(topViewingThumbsUpFilterActive && {
                                            bgcolor: 'rgba(var(--color-success-rgb), 0.1)',
                                          }),
                                        }}
                                      >
                                        <ThumbUpIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleTopViewingCompOpenPopover(e, 'psf');
                                      }}
                                      sx={{ color: hasTopViewingCompActiveFilter('psf') ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)' }}
                                    >
                                      {getSortIndicatorIcon(getTopViewingCompSortDirection('psf'))}
                                    </IconButton>
                                  </Box>
                                </TableCell>
                                )}
                                {showComparableDualPricing && isComparableUnitColumnVisible('dps') && (
                                  <TableCell align="right" sx={{ width: 140, minWidth: 140, maxWidth: 140, pr: 1, cursor: 'pointer' }} onClick={() => handleTopViewingCompSort('dps')}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                      DPS
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleTopViewingCompOpenPopover(e, 'dps');
                                        }}
                                        sx={{ color: hasTopViewingCompActiveFilter('dps') ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)' }}
                                      >
                                        {getSortIndicatorIcon(getTopViewingCompSortDirection('dps'))}
                                      </IconButton>
                                    </Box>
                                  </TableCell>
                                )}
                                {isComparableUnitColumnVisible('outlay') && (
                                <TableCell align="right" sx={{ width: 120, minWidth: 120, maxWidth: 120, pr: 1, cursor: 'pointer' }} onClick={() => handleTopViewingCompSort('outlay')}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                    {getTopViewingCompColumnLabel('outlay')}
                                    <Tooltip title="How outlay is calculated">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setTopViewingOutlayInfoAnchor(e.currentTarget);
                                        }}
                                        sx={{
                                          color: 'var(--color-info)',
                                          '&:hover': {
                                            bgcolor: 'rgba(var(--color-info-rgb), 0.1)',
                                          },
                                        }}
                                      >
                                        <HelpOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleTopViewingCompOpenPopover(e, 'outlay');
                                      }}
                                      sx={{ color: hasTopViewingCompActiveFilter('outlay') ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)' }}
                                    >
                                      {getSortIndicatorIcon(getTopViewingCompSortDirection('outlay'))}
                                    </IconButton>
                                  </Box>
                                </TableCell>
                                )}
                                {isComparableUnitColumnVisible('installment') && (
                                <TableCell align="right" sx={{ width: 100, minWidth: 100, maxWidth: 100, pr: 1, cursor: 'pointer' }} onClick={() => handleTopViewingCompSort('installment')}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                    {getTopViewingCompColumnLabel('installment')}
                                    <Tooltip title="How installment is calculated">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setTopViewingInstallmentInfoAnchor(e.currentTarget);
                                        }}
                                        sx={{
                                          ...getIconButtonSx(),
                                          color: 'var(--color-info)',
                                          '&:hover': { bgcolor: 'rgba(var(--color-info-rgb), 0.1)' },
                                        }}
                                      >
                                        <HelpOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleTopViewingCompOpenPopover(e, 'installment');
                                      }}
                                      sx={{ color: hasTopViewingCompActiveFilter('installment') ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)' }}
                                    >
                                      {getSortIndicatorIcon(getTopViewingCompSortDirection('installment'))}
                                    </IconButton>
                                  </Box>
                                </TableCell>
                                )}
                                {isComparableUnitColumnVisible('distance') && (
                                <TableCell align="right" sx={{ cursor: 'pointer' }} onClick={() => handleTopViewingCompSort('distance')}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                    {getTopViewingCompColumnLabel('distance')}
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleTopViewingCompOpenPopover(e, 'distance');
                                      }}
                                      sx={{ color: hasTopViewingCompActiveFilter('distance') ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)' }}
                                    >
                                      {getSortIndicatorIcon(getTopViewingCompSortDirection('distance'))}
                                    </IconButton>
                                  </Box>
                                </TableCell>
                                )}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {topViewingComparableUnitsForDisplay.length === 0 ? (
                                <TableRow>
                                <TableCell colSpan={comparableUnitVisibleColumnCount} align="center" sx={{ py: 4, color: 'var(--color-text-strong)' }}>
                                  <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                                    {topViewingComparableUnits.length > 0
                                      ? 'No comparable units found for the current filters. Try adjusting filters.'
                                      : 'No comparable units found.'}
                                  </Typography>
                                </TableCell>
                                </TableRow>
                              ) : (
                                topViewingComparableUnitsPageRows.map((unit) => {
                                  const outlay = unit.outlay > 0 ? unit.outlay : 0;
                                  const installment = unit.installment > 0 ? unit.installment : 0;
                                  const parsed = parseUnitNumber(unit.unitNumber);
                                  const hasHighestDrop = unit.hasHighestPriceDrop;
                                  const isShortlisted = shortlistByUnitId.has(unit.id);
                                  const distance = getTopViewingDistanceFromReference(unit.projectName);
                                  const qualifiesThumbsUp = qualifiesForThumbsUp(unit);
                                  const npsPrice = unit.priceNps ?? unit.price;
                                  const dpsPrice = unit.priceDps ?? null;
                                  const npsPsf = unit.psfNps ?? unit.psf;
                                  const isComparableExecutiveCondo = isExecutiveCondoByProjectName(unit.projectName);

                                  return (
                                    <TableRow
                                      key={unit.id}
                                      hover
                                      onClick={(event) => {
                                        if (suppressNextSectionUnitMenuRef.current) {
                                          return;
                                        }
                                        if (isTopViewingFormulaCellTarget(event.target)) {
                                          return;
                                        }
                                        setTopViewingUnitMenuAnchor(null);
                                        setSectionUnitMenuAnchor({
                                          unit,
                                          mouseX: event.clientX - 2,
                                          mouseY: event.clientY - 4,
                                          mode: 'comparable',
                                        });
                                      }}
                                      sx={{
                                        cursor: 'pointer',
                                        ...(hasHighestDrop && {
                                          border: '2px solid',
                                          borderColor: 'error.main',
                                          animation: 'blinkBorder 1s ease-in-out infinite',
                                          '@keyframes blinkBorder': {
                                            '0%, 100%': { borderColor: 'error.main' },
                                            '50%': { borderColor: 'transparent' },
                                          },
                                        }),
                                      }}
                                    >
                                      {isComparableUnitColumnVisible('projectName') && (
                                      <TableCell sx={{ color: 'var(--color-text-strong)' }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
                                            <Typography variant="body2" sx={{ color: 'var(--color-accent)' }}>
                                              {unit.projectName}
                                            </Typography>
                                            {topViewingReferenceUnit && (topViewingReferenceUnit.id === unit.id || (topViewingReferenceUnit.projectName === unit.projectName && topViewingReferenceUnit.unitNumber === unit.unitNumber)) && (
                                              <Tooltip title="Reference unit" arrow placement="top">
                                                <BookmarkIcon sx={{ fontSize: '0.9rem !important', color: '#1976d2', flexShrink: 0 }} />
                                              </Tooltip>
                                            )}
                                            {topViewingFloorPlansToCompare.some((fp) => fp.unit.id === unit.id) && (
                                              <Tooltip title="In compare basket" arrow placement="top">
                                                <ShoppingCartIcon sx={{ fontSize: '0.9rem !important', color: '#FFEB3B', flexShrink: 0 }} />
                                              </Tooltip>
                                            )}
                                          </Box>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                                            {/* <Box
                                              sx={{
                                                display: 'flex',
                                                gap: 0.4,
                                                alignItems: 'center',
                                              }}
                                            >
                                              {[0, 1, 2, 3].map((barIndex) => (
                                                <Box
                                                  key={barIndex}
                                                  sx={{
                                                    width: 10,
                                                    height: 8,
                                                    borderRadius: 999,
                                                    bgcolor: barIndex < heatBarCount ? 'var(--color-danger)' : 'var(--color-neutral)',
                                                  }}
                                                />
                                              ))}
                                            </Box> */}
                                            <IconButton
                                              size="small"
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                handleOpenTowerView(unit);
                                              }}
                                              sx={{ color: 'rgba(var(--color-text-rgb), 0.5)', '&:hover': { bgcolor: 'rgba(var(--color-text-rgb), 0.1)' } }}
                                              title="View Tower View"
                                            >
                                              <ViewModuleIcon fontSize="small" />
                                            </IconButton>
                                            {unit.floorplan && (
                                              <IconButton
                                                size="small"
                                                onClick={(event) => {
                                                  event.stopPropagation();
                                                  handleOpenFloorPlan(unit);
                                                }}
                                                sx={{
                                                  color: 'primary.main',
                                                  '&:hover': { bgcolor: 'rgba(var(--color-text-rgb), 0.1)' },
                                                }}
                                                title="View Floor Plan"
                                              >
                                                <ImageIcon fontSize="small" />
                                              </IconButton>
                                            )}
                                            {!hideShortlist && (
                                            <IconButton
                                              size="small"
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                handleShortlistAction({
                                                  unitId: unit.id,
                                                  label: `${unit.projectName} ${unit.unitNumber}`,
                                                  floorplan: unit.floorplan || null,
                                                });
                                              }}
                                              disabled={shortlistUpdatingUnitId === unit.id}
                                              sx={{
                                                color: isShortlisted ? 'var(--color-warning-strong)' : 'var(--color-text-strong)',
                                                '&:hover': { bgcolor: 'rgba(var(--color-text-rgb), 0.1)' },
                                              }}
                                              title={isShortlisted ? 'Remove from shortlist' : 'Add to shortlist'}
                                            >
                                              {isShortlisted ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                                            </IconButton>
                                            )}
                                          </Box>
                                        </Box>
                                      </TableCell>
                                      )}
                                      {isComparableUnitColumnVisible('projectCategory') && (
                                      <TableCell sx={{ color: 'var(--color-text-strong)' }}>
                                        {(projectDetailsMap.get(unit.projectName)?.projectCategory ?? '').trim() || '-'}
                                      </TableCell>
                                      )}
                                      {isComparableUnitColumnVisible('blockNumber') && (
                                      <TableCell align="center" sx={{ width: 80, minWidth: 80, maxWidth: 80, px: 0.5, pr: 1, color: 'var(--color-text-strong)' }}>
                                        {formatBlockNumber(unit.blockNumber || null)}
                                      </TableCell>
                                      )}
                                      {isComparableUnitColumnVisible('unitNumber') && (
                                      <TableCell align="center" sx={{ width: 100, minWidth: 100, maxWidth: 100, px: 0.5, pr: 1, color: 'var(--color-text-strong)' }}>
                                        {unit.unitNumber}
                                      </TableCell>
                                      )}
                                      {isComparableUnitColumnVisible('floor') && (
                                      <TableCell align="center" sx={{ width: 70, minWidth: 70, maxWidth: 70, px: 0.5, pr: 1, color: 'var(--color-text-strong)' }}>
                                        {parsed.floor !== null ? parsed.floor : '-'}
                                      </TableCell>
                                      )}
                                      {isComparableUnitColumnVisible('stack') && (
                                      <TableCell align="center" sx={{ width: 70, minWidth: 70, maxWidth: 70, px: 0.5, pr: 1, color: 'var(--color-text-strong)' }}>
                                        {parsed.stack !== null ? parsed.stack : '-'}
                                      </TableCell>
                                      )}
                                      {isComparableUnitColumnVisible('unitTypeDescription') && (
                                      <TableCell sx={{ minWidth: 120, px: 0.5, pr: 1, color: 'var(--color-text-strong)' }}>
                                        {unit.unitTypeDescription ? unit.unitTypeDescription.replace(/Bedroom/gi, 'BR') : '-'}
                                      </TableCell>
                                      )}
                                      {isComparableUnitColumnVisible('bedroom') && (
                                      <TableCell align="center" sx={{ width: 70, minWidth: 70, maxWidth: 70, px: 0.5, pr: 1, color: 'var(--color-text-strong)' }}>
                                        {unit.bedroom}
                                      </TableCell>
                                      )}
                                      {isComparableUnitColumnVisible('bathroom') && (
                                      <TableCell align="center" sx={{ width: 60, minWidth: 60, maxWidth: 60, px: 0.5, color: 'var(--color-text-strong)' }}>
                                        {unit.bathroom}
                                      </TableCell>
                                      )}
                                      {isComparableUnitColumnVisible('areaSqft') && (
                                      <TableCell align="center" sx={{ width: 80, minWidth: 80, maxWidth: 80, px: 0.5, color: 'var(--color-text-strong)' }}>
                                        {formatNumber(unit.areaSqft)}
                                      </TableCell>
                                      )}
                                      {isComparableUnitColumnVisible('price') && (
                                      <TableCell align="right" sx={{ width: 160, minWidth: 160, maxWidth: 160, pr: 1, color: 'var(--color-text-strong)' }}>
                                        <Box>
                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap' }}>
                                            <Typography
                                              variant="body2"
                                              component={hasHighestDrop ? 'span' : 'div'}
                                              onClick={hasHighestDrop ? (event: React.MouseEvent<HTMLElement>) => {
                                                event.stopPropagation();
                                                handleOpenPriceChart(unit.id, 'price', true, 'top_viewing_price_drop');
                                              } : undefined}
                                              sx={{
                                                color: 'var(--color-text-strong)',
                                                ...(hasHighestDrop && {
                                                  cursor: 'pointer',
                                                  textDecoration: 'underline',
                                                  '&:hover': {
                                                    opacity: 0.7,
                                                  },
                                                }),
                                              }}
                                            >
                                              {formatCurrency(npsPrice)}
                                            </Typography>
                                            {hasHighestDrop && (
                                              <Chip
                                                label="PRICE PROMO"
                                                size="small"
                                                onClick={(event) => {
                                                  event.stopPropagation();
                                                  setPricePromoInfoOpen(true);
                                                }}
                                                sx={{
                                                  height: 20,
                                                  fontSize: '0.65rem',
                                                  fontWeight: 'bold',
                                                  bgcolor: 'var(--color-success)',
                                                  color: '#0f2a1d',
                                                  cursor: 'pointer',
                                                }}
                                              />
                                            )}
                                            {qualifiesThumbsUp && (
                                              <ThumbUpIcon sx={{ fontSize: 16, color: 'var(--color-success)' }} />
                                            )}
                                          </Box>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: getPriceDiffColor(unit.priceDiff),
                                              fontWeight: 'medium',
                                            }}
                                          >
                                            {unit.priceDiff >= 0 ? '+' : ''}
                                            {formatCurrency(unit.priceDiff)} ({unit.priceDiffPercent >= 0 ? '+' : ''}
                                            {formatNumber(unit.priceDiffPercent, 1)}%)
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      )}
                                      {isComparableUnitColumnVisible('psf') && (
                                      <TableCell align="right" sx={{ pr: 1.5, color: 'var(--color-text-strong)' }}>
                                        <Box>
                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                            <Typography variant="body2" sx={{ color: 'var(--color-text-strong)' }}>
                                              {formatCurrency(npsPsf)}
                                            </Typography>
                                            {qualifiesThumbsUp && (
                                              <ThumbUpIcon sx={{ fontSize: 16, color: 'var(--color-success)' }} />
                                            )}
                                          </Box>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: getPriceDiffColor(unit.psfDiff),
                                              fontWeight: 'medium',
                                            }}
                                          >
                                            {unit.psfDiff >= 0 ? '+' : ''}
                                            {formatCurrency(unit.psfDiff)} psf ({unit.psfDiffPercent >= 0 ? '+' : ''}
                                            {formatNumber(unit.psfDiffPercent, 1)}%)
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      )}
                                      {showComparableDualPricing && isComparableUnitColumnVisible('dps') && (
                                        <TableCell align="right" sx={{ width: 140, minWidth: 140, maxWidth: 140, pr: 1, color: 'var(--color-text-strong)' }}>
                                          {renderDpsValue(dpsPrice, isComparableExecutiveCondo)}
                                        </TableCell>
                                      )}
                                      {isComparableUnitColumnVisible('outlay') && (
                                      <TableCell
                                        align="right"
                                        data-top-viewing-formula-cell="true"
                                        sx={{ width: 120, minWidth: 120, maxWidth: 120, pr: 1, color: 'var(--color-text-strong)', cursor: outlay > 0 ? 'pointer' : 'default' }}
                                        onClick={(event) => {
                                          if (outlay <= 0) return;
                                          openTopViewingFormulaPopover(event, getOutlayFormulaTooltip(unit.price, outlay));
                                        }}
                                      >
                                        <Box>
                                          {outlay > 0 ? (
                                            <Typography component="span" variant="body2" sx={{ color: 'var(--color-text-strong)', cursor: 'pointer', textDecoration: 'underline dotted rgba(var(--color-text-rgb), 0.45)', textUnderlineOffset: 3 }}>
                                              {formatCurrency(outlay)}
                                            </Typography>
                                          ) : '-'}
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              display: 'block',
                                              mt: 0.25,
                                              color: getPriceDiffColor(unit.outlayDiff),
                                              fontWeight: 'medium',
                                            }}
                                          >
                                            {unit.outlayDiff >= 0 ? '+' : ''}{formatCurrency(unit.outlayDiff)}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      )}
                                      {isComparableUnitColumnVisible('installment') && (
                                      <TableCell
                                        align="right"
                                        data-top-viewing-formula-cell="true"
                                        sx={{ width: 100, minWidth: 100, maxWidth: 100, pr: 1, color: 'var(--color-text-strong)', cursor: installment > 0 ? 'pointer' : 'default' }}
                                        onClick={(event) => {
                                          if (installment <= 0) return;
                                          openTopViewingFormulaPopover(event, getInstallmentFormulaTooltip(unit.price, installment));
                                        }}
                                      >
                                        <Box>
                                          {installment > 0 ? (
                                            <Typography component="span" variant="body2" sx={{ color: 'var(--color-text-strong)', cursor: 'pointer', textDecoration: 'underline dotted rgba(var(--color-text-rgb), 0.45)', textUnderlineOffset: 3 }}>
                                              {formatCurrency(installment)}
                                            </Typography>
                                          ) : '-'}
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              display: 'block',
                                              mt: 0.25,
                                              color: getPriceDiffColor(unit.installmentDiff),
                                              fontWeight: 'medium',
                                            }}
                                          >
                                            {unit.installmentDiff >= 0 ? '+' : ''}{formatCurrency(unit.installmentDiff)}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      )}
                                      {isComparableUnitColumnVisible('distance') && (
                                      <TableCell align="right" sx={{ color: 'var(--color-text-strong)' }}>
                                        {distance !== null ? `${distance.toFixed(2)} km` : '-'}
                                      </TableCell>
                                      )}
                                    </TableRow>
                                  );
                                })
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <TablePagination
                          component="div"
                          count={topViewingComparableUnitsForDisplay.length}
                          page={topViewingComparableUnitsPage}
                          onPageChange={(_, nextPage) => setTopViewingComparableUnitsPage(nextPage)}
                          rowsPerPage={TOP_VIEWING_PAGE_SIZE}
                          onRowsPerPageChange={() => {}}
                          rowsPerPageOptions={[TOP_VIEWING_PAGE_SIZE]}
                          sx={{
                            borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)',
                            '& .MuiTablePagination-toolbar': {
                              minHeight: 40,
                            },
                          }}
                        />
                        </Box>
                      )}
                      </Collapse>
                    </Box>
                  </Card>
                </Box>
              )}

              {topViewingReferenceUnit && (
                <Accordion
                  data-tutorial="budget-matched"
                  expanded={topViewingBudgetSectionOpen}
                  onChange={(_, expanded) => setTopViewingBudgetSectionOpen(expanded)}
                  sx={{ mt: 2, border: '1px solid rgba(var(--color-text-rgb), 0.12)', boxShadow: 'none' }}
                >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    },
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ color: 'var(--color-text-strong)', fontWeight: 600, letterSpacing: '-0.02em' }}>
                      Budget Matched Units
                    </Typography>
                    {topViewingBudgetSectionOpen && (
                      <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                        Cross Project
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                    {topViewingBudgetUnitsForDistance.length} units
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {!topViewingReferenceUnit || !topViewingBudgetRange ? (
                    <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
                      Select a reference unit with a valid price to load budget-matched units.
                    </Typography>
                  ) : (
                    <>
                      {(() => {
                        const formatBudgetInputValue = (value: string): string => {
                          const digitsOnly = value.replace(/[^\d]/g, '');
                          if (!digitsOnly) return '';
                          const parsed = Number(digitsOnly);
                          return Number.isFinite(parsed) ? parsed.toLocaleString('en-US') : '';
                        };
                        const defaultMin = Math.max(0, topViewingBudgetRange.min);
                        const defaultMax = Math.max(0, topViewingBudgetRange.max);
                        const minInputValue = topViewingBudgetSearchRange.min.trim() !== ''
                          ? formatBudgetInputValue(topViewingBudgetSearchRange.min)
                          : Math.round(defaultMin).toLocaleString('en-US');
                        const maxInputValue = topViewingBudgetSearchRange.max.trim() !== ''
                          ? formatBudgetInputValue(topViewingBudgetSearchRange.max)
                          : Math.round(defaultMax).toLocaleString('en-US');

                        return (
                          <Box sx={{ mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ color: 'var(--color-text-strong)', fontWeight: 600, fontSize: '15.3px' }}>
                              Reference {showBudgetDualPricing ? 'Price / NPS' : 'Price'}: {formatCurrency(topViewingBudgetRange.base)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontWeight: 600, fontSize: '13.3px' }}>
                              Range (+{topViewingBudgetTolerance}%):
                            </Typography>
                            <TextField
                              size="small"
                              value={minInputValue}
                              onChange={(e) => handleTopViewingBudgetSearchRangeChange('min', formatBudgetInputValue(e.target.value))}
                              inputProps={{ inputMode: 'numeric' }}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                              }}
                              sx={{ width: 150, '& .MuiInputBase-input': { py: 0.65 } }}
                            />
                            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontSize: '13.3px' }}>to</Typography>
                            <TextField
                              size="small"
                              value={maxInputValue}
                              onChange={(e) => handleTopViewingBudgetSearchRangeChange('max', formatBudgetInputValue(e.target.value))}
                              inputProps={{ inputMode: 'numeric' }}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                              }}
                              sx={{ width: 150, '& .MuiInputBase-input': { py: 0.65 } }}
                            />
                          </Box>
                        );
                      })()}
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                            Budget range slider (+{topViewingBudgetSliderValue}%)
                          </Typography>
                          <Button
                            size="small"
                            variant="text"
                            onClick={handleBudgetAutoFind}
                            disabled={topViewingBudgetUnitsLoading}
                            sx={{
                              fontSize: '0.65rem',
                              textTransform: 'none',
                              color: 'var(--color-accent)',
                              minWidth: 'auto',
                              py: 0,
                              px: 0.75,
                              '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.1)' },
                            }}
                          >
                            {budgetAutoFindPending ? 'Finding...' : 'Smart Find'}
                          </Button>
                        </Box>
                        <Slider
                          value={topViewingBudgetSliderValue}
                          onChange={(_, value) => setTopViewingBudgetSliderValue(Array.isArray(value) ? value[0] : value)}
                          onChangeCommitted={(_, value) => setTopViewingBudgetTolerance(Array.isArray(value) ? value[0] : value)}
                          min={0}
                          max={30}
                          step={1}
                          valueLabelDisplay="auto"
                          valueLabelFormat={(v) => `+${v}%`}
                          sx={{
                            color: 'var(--color-accent)',
                            mt: 0.5,
                            '& .MuiSlider-thumb': { bgcolor: 'var(--color-accent)' },
                          '& .MuiSlider-rail': { opacity: 0.4 },
                        }}
                      />
                        {showBudgetDualPricing && (
                          <Typography variant="caption" sx={{ color: 'var(--color-warning-strong)', display: 'block', mt: 0.35, fontWeight: 600 }}>
                            Note: Budget filtering is based on NPS price for Executive Condo units.
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block', mb: 0.25 }}>
                          Super Match (from reference unit)
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                          <FormGroup row sx={{ gap: 0.75 }}>
                            <FormControlLabel
                              control={(
                                <Checkbox
                                  size="small"
                                  checked={topViewingBudgetSuperMatch.bedroom}
                                  onChange={(event) => {
                                    const checked = event.target.checked;
                                    setTopViewingBudgetSuperMatch((prev) => ({ ...prev, bedroom: checked }));
                                    if (checked) {
                                      const referenceBedroom = normalizeBedroomValue(topViewingReferenceUnit?.bedroom);
                                      if (referenceBedroom != null) {
                                        handleTopViewingBudgetRangeFilterChange('bedroom', 'min', String(referenceBedroom));
                                        handleTopViewingBudgetRangeFilterChange('bedroom', 'max', '');
                                      }
                                    } else {
                                      // Unchecking the Super Match box should drop the filter
                                      // it applied on check; otherwise the table stays
                                      // restricted to the reference unit's bedroom min.
                                      handleTopViewingBudgetRangeFilterChange('bedroom', 'min', '');
                                    }
                                  }}
                                />
                              )}
                              label={`Bedroom min (${normalizeBedroomValue(topViewingReferenceUnit?.bedroom) ?? 'N/A'} Br)`}
                              sx={{ mr: 1.5 }}
                            />
                            <FormControlLabel
                              control={(
                                <Checkbox
                                  size="small"
                                  checked={topViewingBudgetSuperMatch.size}
                                  onChange={(event) => {
                                    const checked = event.target.checked;
                                    setTopViewingBudgetSuperMatch((prev) => ({ ...prev, size: checked }));
                                    if (checked) {
                                      const referenceAreaSqft = Number(topViewingReferenceUnit?.areaSqft);
                                      if (Number.isFinite(referenceAreaSqft) && referenceAreaSqft > 0) {
                                        handleTopViewingBudgetRangeFilterChange('areaSqft', 'min', String(Math.round(referenceAreaSqft)));
                                      }
                                    } else {
                                      handleTopViewingBudgetRangeFilterChange('areaSqft', 'min', '');
                                    }
                                  }}
                                />
                              )}
                              label={`Size min (${Number.isFinite(Number(topViewingReferenceUnit?.areaSqft)) ? Math.round(Number(topViewingReferenceUnit?.areaSqft)).toLocaleString('en-US') : 'N/A'} sqft)`}
                              sx={{ mr: 1.5 }}
                            />
                            <FormControlLabel
                              control={(
                                <Checkbox
                                  size="small"
                                  checked={topViewingBudgetSuperMatch.floor}
                                  onChange={(event) => {
                                    const checked = event.target.checked;
                                    setTopViewingBudgetSuperMatch((prev) => ({ ...prev, floor: checked }));
                                    if (checked) {
                                      const referenceFloor = parseUnitNumber(topViewingReferenceUnit?.unitNumber ?? '').floor;
                                      if (referenceFloor != null) {
                                        handleTopViewingBudgetRangeFilterChange('floor', 'min', String(referenceFloor));
                                      }
                                    } else {
                                      handleTopViewingBudgetRangeFilterChange('floor', 'min', '');
                                    }
                                  }}
                                />
                              )}
                              label={`Floor min (${(() => {
                                const referenceFloor = parseUnitNumber(topViewingReferenceUnit?.unitNumber ?? '').floor;
                                return referenceFloor != null ? Math.round(referenceFloor) : 'N/A';
                              })()})`}
                            />
                          </FormGroup>
                          {isAdminUser && (
                            <FormControlLabel
                              control={(
                                <Checkbox
                                  size="small"
                                  checked={topViewingBudgetShowReferenceProjectUnits}
                                  onChange={(event) => setTopViewingBudgetShowReferenceProjectUnits(event.target.checked)}
                                />
                              )}
                              label="Show reference project units"
                              sx={{ ml: { xs: 0, md: 'auto' }, mr: 0 }}
                            />
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Tabs
                          value={topViewingBudgetRegionTab}
                          onChange={(_, value) => setTopViewingBudgetRegionTab(value as 'ALL' | 'NEARBY' | 'CCR' | 'OCR' | 'RCR' | 'SUPER')}
                          textColor="inherit"
                          indicatorColor="secondary"
                          sx={{
                            minHeight: 32,
                            '& .MuiTab-root': {
                              minHeight: 32,
                              textTransform: 'none',
                              fontWeight: 600,
                            },
                          }}
                        >
                          <Tab label="All" value="ALL" />
                          <Tab label="Nearby" value="NEARBY" />
                          <Tab label="CCR" value="CCR" />
                           <Tab label="OCR" value="OCR" />
                          <Tab label="RCR" value="RCR" />
                          {/*<Tab label="Super" value="SUPER" /> */}
                        </Tabs>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ToggleButtonGroup
                            value={topViewingBudgetViewMode}
                            exclusive
                            onChange={(_, value) => value && setTopViewingBudgetViewMode(value)}
                            size="small"
                            sx={{
                              '& .MuiToggleButton-root': {
                                color: 'var(--color-text-strong)',
                                borderColor: 'rgba(var(--color-text-rgb), 0.3)',
                                '&.Mui-selected': {
                                  bgcolor: 'var(--color-accent)',
                                  color: 'var(--color-text-inverse)',
                                  '&:hover': { bgcolor: 'var(--color-accent-strong)' },
                                },
                                '&:hover': { bgcolor: 'rgba(var(--color-text-rgb), 0.1)' },
                              },
                            }}
                          >
                            <ToggleButton value="unit">Unit View ({topViewingBudgetUnitsForDistance.length})</ToggleButton>
                            <ToggleButton value="project">Project View ({budgetProjectRowsForDisplay.length})</ToggleButton>
                          </ToggleButtonGroup>
                          <Button
                            size="small"
                            onClick={handleTopViewingBudgetClearAllFilters}
                            sx={{ color: 'var(--color-text-muted)', '&:hover': { color: 'var(--color-accent)' } }}
                          >
                            Clear filters
                          </Button>
                        </Box>
                      </Box>
                      {(() => {
                        const parts: string[] = [];
                        const exFilters = topViewingBudgetExclusionFilters;
                        const uOpts = topViewingBudgetUniqueFilterOptions;
                        const exclusionEntries: { key: keyof typeof exFilters; label: string }[] = [
                          { key: 'projectName', label: 'Project' },
                          { key: 'projectCategory', label: 'Category' },
                          { key: 'district', label: 'District' },
                          { key: 'region', label: 'Region' },
                          { key: 'tenure', label: 'Tenure' },
                          { key: 'nearestMrt', label: 'MRT' },
                        ];
                        exclusionEntries.forEach(({ key, label }) => {
                          const excluded = exFilters[key] ?? [];
                          const options = uOpts[key] ?? [];
                          if (excluded.length > 0 && excluded.length < options.length) {
                            const included = options.filter((o) => !excluded.includes(o));
                            parts.push(`${label}: ${included.join(', ')}`);
                          }
                        });
                        const rangeLabels: Record<string, string> = { floor: 'Floor', stack: 'Stack', bedroom: 'Bedroom', bathroom: 'Bath', areaSqft: 'Area', price: 'Price', psf: 'PSF', outlay: 'Outlay', installment: 'Installment', distance: 'Distance' };
                        Object.entries(rangeLabels).forEach(([key, label]) => {
                          const range = topViewingBudgetRangeFilters[key as keyof typeof topViewingBudgetRangeFilters];
                          if (!range) return;
                          const min = range.min?.trim();
                          const max = range.max?.trim();
                          if (min && max) parts.push(`${label}: ${min}-${max}`);
                          else if (min) parts.push(`${label}: ≥${min}`);
                          else if (max) parts.push(`${label}: ≤${max}`);
                        });
                        const superMatchParts: string[] = [];
                        if (topViewingBudgetSuperMatch.bedroom) {
                          const bedroomValue = normalizeBedroomValue(topViewingReferenceUnit?.bedroom);
                          superMatchParts.push(`Bedroom: ≥${bedroomValue != null ? bedroomValue : 'N/A'}`);
                        }
                        if (topViewingBudgetSuperMatch.size) {
                          const referenceArea = Number(topViewingReferenceUnit?.areaSqft);
                          superMatchParts.push(`Size: ≥${Number.isFinite(referenceArea) ? Math.round(referenceArea).toLocaleString('en-US') : 'N/A'} sqft`);
                        }
                        if (topViewingBudgetSuperMatch.floor) {
                          const referenceFloor = parseUnitNumber(topViewingReferenceUnit?.unitNumber ?? '').floor;
                          superMatchParts.push(`Floor: ≥${referenceFloor != null ? Math.round(referenceFloor) : 'N/A'}`);
                        }
                        if (superMatchParts.length > 0) {
                          parts.push(`Super Match: ${superMatchParts.join(', ')}`);
                        }
                        const textLabels: Record<string, string> = { blockNumber: 'Block', unitNumber: 'Unit', completedDate: 'TOP' };
                        Object.entries(textLabels).forEach(([key, label]) => {
                          const val = ((topViewingBudgetFilters as Record<string, string>)[key] ?? '').trim();
                          if (val) parts.push(`${label}: ${val}`);
                        });
                        // Project-view text/range filters also narrow the unit list (units belonging
                        // to filtered-away projects are hidden), so surface them in both views.
                        {
                          Object.entries(budgetProjectTextFilters).forEach(([key, val]) => {
                            if (val?.trim()) {
                              const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
                              parts.push(`${label}: ${val.trim()}`);
                            }
                          });
                          Object.entries(budgetProjectRangeFilters).forEach(([key, range]) => {
                            const min = range?.min?.trim();
                            const max = range?.max?.trim();
                            const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
                            if (min && max) parts.push(`${label}: ${min}-${max}`);
                            else if (min) parts.push(`${label}: ≥${min}`);
                            else if (max) parts.push(`${label}: ≤${max}`);
                          });
                        }
                        if (parts.length === 0) return null;
                        return (
                          <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ color: 'var(--color-accent)', fontWeight: 600, mr: 0.5 }}>
                              Filtered:
                            </Typography>
                            {parts.map((part, i) => (
                              <Chip key={i} label={part} size="small" sx={{ bgcolor: 'rgba(255, 152, 0, 0.15)', color: 'var(--color-accent)', fontWeight: 500, fontSize: '0.7rem' }} />
                            ))}
                          </Box>
                        );
                      })()}
                      {topViewingBudgetRegionTab === 'NEARBY' && (
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                              Nearby radius: {topViewingBudgetNearbyRadiusKm.toFixed(1)} km (Filtered by distance from reference project)
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Button
                                size="small"
                                variant="text"
                                onClick={handleDistanceAutoFind}
                                sx={{
                                  fontSize: '0.65rem',
                                  textTransform: 'none',
                                  color: 'var(--color-accent)',
                                  minWidth: 'auto',
                                  py: 0,
                                  px: 0.75,
                                  '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.1)' },
                                }}
                              >
                                Smart Find
                              </Button>
                            </Box>
                          </Box>
                          <Slider
                            value={topViewingBudgetNearbyRadiusKm}
                            onChange={(_, value) => setTopViewingBudgetNearbyRadiusKm(Array.isArray(value) ? value[0] : value)}
                            min={0.5}
                            max={10}
                            step={0.1}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(v) => `${Number(v).toFixed(1)} km`}
                            sx={{
                              color: 'var(--color-accent)',
                              mt: 0.5,
                              '& .MuiSlider-thumb': { bgcolor: 'var(--color-accent)' },
                              '& .MuiSlider-rail': { opacity: 0.4 },
                            }}
                          />
                        </Box>
                      )}
                    </>
                  )}

                  {topViewingBudgetUnitsError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {topViewingBudgetUnitsError}
                    </Alert>
                  )}

                  {topViewingBudgetUnitsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    topViewingBudgetRange && (
                      <>
                      {topViewingBudgetViewMode === 'unit' && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                          <ColumnVisibilityMenu
                            anchorEl={budgetUnitColumnsAnchor}
                            columns={budgetUnitColumnsForMenu}
                            getLabel={(column) => (
                              column === 'distance'
                                ? budgetDistanceLabel
                                : formatColumnVisibilityLabel(column)
                            )}
                            onClose={() => setBudgetUnitColumnsAnchor(null)}
                            onOpen={(event) => setBudgetUnitColumnsAnchor(event.currentTarget)}
                            onReset={resetBudgetUnitColumns}
                            onToggle={toggleBudgetUnitColumn}
                            visibility={budgetUnitColumnVisibility}
                          />
                        </Box>
                      )}
                      <TableContainer
                        ref={budgetTableContainerRef}
                        onScroll={handleBudgetTableScroll}
                        sx={{
                          ...scrollableStyles,
                          maxHeight: { xs: 560, md: 680 },
                          overflowY: 'auto',
                          contain: 'layout paint',
                          willChange: 'scroll-position',
                          border: '1px solid rgba(var(--color-text-rgb), 0.08)',
                          borderRadius: 1,
                        }}
                      >
                        {topViewingBudgetViewMode === 'unit' ? (
                          <Table stickyHeader size="small">
                            <TableHead>
                              <TableRow>
                                {isBudgetUnitColumnVisible('projectName') && (
                                  <TableCell
                                    sx={{
                                      position: 'sticky',
                                      left: 0,
                                      zIndex: 4,
                                      minWidth: 190,
                                      backgroundColor: 'var(--color-bg)',
                                      borderRight: '1px solid rgba(var(--color-text-rgb), 0.08)',
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      Project Name
                                      <IconButton size="small" onClick={(e) => handleTopViewingBudgetOpenPopover(e, 'projectName')} sx={getTopViewingBudgetIconButtonSx('projectName')}>
                                        {getSortIndicatorIcon(getTopViewingBudgetSortDirection('projectName'))}
                                      </IconButton>
                                    </Box>
                                  </TableCell>
                                )}
                                {isBudgetUnitColumnVisible('projectCategory') && (
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      Project Category
                                      <IconButton size="small" onClick={(e) => handleTopViewingBudgetOpenPopover(e, 'projectCategory')} sx={getTopViewingBudgetIconButtonSx('projectCategory')}>
                                        {getSortIndicatorIcon(getTopViewingBudgetSortDirection('projectCategory'))}
                                      </IconButton>
                                    </Box>
                                  </TableCell>
                                )}
                                {isBudgetUnitColumnVisible('blockNumber') && (
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      Block
                                      <IconButton size="small" onClick={(e) => handleTopViewingBudgetOpenPopover(e, 'blockNumber')} sx={getTopViewingBudgetIconButtonSx('blockNumber')}>
                                        {getSortIndicatorIcon(getTopViewingBudgetSortDirection('blockNumber'))}
                                      </IconButton>
                                    </Box>
                                  </TableCell>
                                )}
                                {isBudgetUnitColumnVisible('unitNumber') && (
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      Unit
                                      <IconButton size="small" onClick={(e) => handleTopViewingBudgetOpenPopover(e, 'unitNumber')} sx={getTopViewingBudgetIconButtonSx('unitNumber')}>
                                        {getSortIndicatorIcon(getTopViewingBudgetSortDirection('unitNumber'))}
                                      </IconButton>
                                    </Box>
                                  </TableCell>
                                )}
                                {(['floor', 'stack', 'bedroom', 'bathroom', 'areaSqft', 'price', 'psf'] as const).map((column) => (
                                  isBudgetUnitColumnVisible(column) && (
                                    <TableCell
                                      key={column}
                                      align={column === 'price' || column === 'psf' || column === 'areaSqft' ? 'right' : 'left'}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: column === 'price' || column === 'psf' || column === 'areaSqft' ? 'flex-end' : 'flex-start', gap: 0.5 }}>
                                        {column === 'price' && showBudgetDualPricing
                                          ? 'Price / NPS'
                                          : formatColumnVisibilityLabel(column)}
                                        <IconButton size="small" onClick={(e) => handleTopViewingBudgetOpenPopover(e, column)} sx={getTopViewingBudgetIconButtonSx(column)}>
                                          {getSortIndicatorIcon(getTopViewingBudgetSortDirection(column))}
                                        </IconButton>
                                      </Box>
                                    </TableCell>
                                  )
                                ))}
                                {showBudgetDualPricing && isBudgetUnitColumnVisible('dps') && (
                                  <TableCell align="right">
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                      DPS
                                      <IconButton size="small" onClick={(e) => handleTopViewingBudgetOpenPopover(e, 'dps')} sx={getTopViewingBudgetIconButtonSx('dps')}>
                                        {getSortIndicatorIcon(getTopViewingBudgetSortDirection('dps'))}
                                      </IconButton>
                                    </Box>
                                  </TableCell>
                                )}
                                {(['outlay', 'installment', 'distance'] as const).map((column) => (
                                  isBudgetUnitColumnVisible(column) && (
                                    <TableCell
                                      key={column}
                                      align="right"
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                        {column === 'distance'
                                          ? budgetDistanceLabel
                                          : formatColumnVisibilityLabel(column)}
                                        {column === 'outlay' && (
                                          <Tooltip title="How outlay is calculated">
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setTopViewingOutlayInfoAnchor(e.currentTarget);
                                              }}
                                              sx={{ ...getIconButtonSx(), color: 'var(--color-info)', '&:hover': { bgcolor: 'rgba(var(--color-info-rgb), 0.1)' } }}
                                            >
                                              <HelpOutlineIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                        {column === 'installment' && (
                                          <Tooltip title="How installment is calculated">
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setTopViewingInstallmentInfoAnchor(e.currentTarget);
                                              }}
                                              sx={{ ...getIconButtonSx(), color: 'var(--color-info)', '&:hover': { bgcolor: 'rgba(var(--color-info-rgb), 0.1)' } }}
                                            >
                                              <HelpOutlineIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                        <IconButton size="small" onClick={(e) => handleTopViewingBudgetOpenPopover(e, column)} sx={getTopViewingBudgetIconButtonSx(column)}>
                                          {getSortIndicatorIcon(getTopViewingBudgetSortDirection(column))}
                                        </IconButton>
                                      </Box>
                                    </TableCell>
                                  )
                                ))}
                                {(['district', 'region', 'tenure', 'completedDate', 'nearestMrt', 'walkingTimeToMrt'] as const).map((column) => (
                                  isBudgetUnitColumnVisible(column) && (
                                    <TableCell key={column}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {formatColumnVisibilityLabel(column)}
                                        <IconButton size="small" onClick={(e) => handleTopViewingBudgetOpenPopover(e, column)} sx={getTopViewingBudgetIconButtonSx(column)}>
                                          {getSortIndicatorIcon(getTopViewingBudgetSortDirection(column))}
                                        </IconButton>
                                      </Box>
                                    </TableCell>
                                  )
                                ))}
                                {topViewingBudgetRegionTab === 'SUPER' && (
                                  <TableCell>
                                    Why Better
                                  </TableCell>
                                )}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {topViewingBudgetUnitsForDistance.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={budgetUnitColSpan} align="center" sx={{ py: 3, color: 'var(--color-text-muted)' }}>
                                    {topViewingBudgetRegionTab === 'NEARBY'
                                      ? `No available units found within ${topViewingBudgetNearbyRadiusKm.toFixed(1)} km.`
                                      : topViewingBudgetRegionTab === 'ALL'
                                      ? 'No available units found within this budget range.'
                                      : `No available units found within this budget range for ${topViewingBudgetRegionTab}.`}
                                  </TableCell>
                                </TableRow>
                              ) : (
                                <>
                                {shouldVirtualizeBudgetUnitRows && budgetUnitVirtualRows.topSpacer > 0 && (
                                  <TableRow aria-hidden>
                                    <TableCell
                                      colSpan={budgetUnitColSpan}
                                      sx={{ p: 0, border: 0, lineHeight: 0, height: budgetUnitVirtualRows.topSpacer }}
                                    />
                                  </TableRow>
                                )}
                                {budgetUnitVirtualRows.rows.map((unit) => (
                                  <TableRow
                                    key={`budget-unit-${unit.id}`}
                                    hover
                                    onClick={(event) => {
                                      setTopViewingUnitMenuAnchor(null);
                                      setSectionUnitMenuAnchor({
                                        unit: unit as unknown as ComparableUnit,
                                        mouseX: event.clientX - 2,
                                        mouseY: event.clientY - 4,
                                        mode: 'budget',
                                      });
                                    }}
                                    sx={getBudgetUnitRowSx(unit)}
                                  >
                                    {renderBudgetUnitViewTableCells(unit)}
                                  </TableRow>
                                ))}
                                {shouldVirtualizeBudgetUnitRows && budgetUnitVirtualRows.bottomSpacer > 0 && (
                                  <TableRow aria-hidden>
                                    <TableCell
                                      colSpan={budgetUnitColSpan}
                                      sx={{ p: 0, border: 0, lineHeight: 0, height: budgetUnitVirtualRows.bottomSpacer }}
                                    />
                                  </TableRow>
                                )}
                                </>
                              )}
                            </TableBody>
                          </Table>
                        ) : (
                          <Table stickyHeader size="small" sx={{ minWidth: 1200 }}>
                            <TableHead>
                              <TableRow>
                                {PROJECT_VIEW_COLUMN_KEYS.slice(0, 2).map((column) => (
                                  <TableCell
                                    key={`budget-project-${column}`}
                                    sx={{
                                      fontWeight: 600,
                                      ...(column === 'projectName'
                                        ? {
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 4,
                                            minWidth: 160,
                                            backgroundColor: 'var(--color-bg)',
                                            borderRight: '1px solid rgba(var(--color-text-rgb), 0.08)',
                                          }
                                        : {}),
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      {column === 'projectName'
                                        ? 'Project Name'
                                        : column === 'projectCategory'
                                        ? 'Project Category'
                                        : column.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}
                                      <IconButton
                                        size="small"
                                        onClick={(e) => setBudgetProjectPopoverAnchor({ element: e.currentTarget, column })}
                                        sx={getBudgetProjectIconButtonSx(column)}
                                      >
                                        {getSortIndicatorIcon(getBudgetProjectSortDirection(column))}
                                      </IconButton>
                                    </Box>
                                  </TableCell>
                                ))}
                                <TableCell sx={{ fontWeight: 600 }} align="center">Matched</TableCell>
                                {budgetMatchedVisibleBrColumns.br1 && <TableCell sx={{ fontWeight: 600 }} align="center">1BR</TableCell>}
                                {budgetMatchedVisibleBrColumns.br2 && <TableCell sx={{ fontWeight: 600 }} align="center">2BR</TableCell>}
                                {budgetMatchedVisibleBrColumns.br3 && <TableCell sx={{ fontWeight: 600 }} align="center">3BR</TableCell>}
                                {budgetMatchedVisibleBrColumns.br4 && <TableCell sx={{ fontWeight: 600 }} align="center">4BR</TableCell>}
                                {budgetMatchedVisibleBrColumns.br5 && <TableCell sx={{ fontWeight: 600 }} align="center">5BR</TableCell>}
                                {budgetMatchedVisibleBrColumns.ph && <TableCell sx={{ fontWeight: 600 }} align="center">PH</TableCell>}
                                {PROJECT_VIEW_COLUMN_KEYS.slice(2).map((column) => (
                                  <TableCell key={`budget-project-${column}`} sx={{ fontWeight: 600 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      {column === 'distance'
                                        ? budgetDistanceLabel
                                        : column === 'districtLocation'
                                        ? 'District Location'
                                        : column === 'walkingTimeToMrt'
                                        ? 'Walk to MRT'
                                        : column === 'completedDate'
                                        ? 'TOP'
                                        : column.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}
                                      <IconButton
                                        size="small"
                                        onClick={(e) => setBudgetProjectPopoverAnchor({ element: e.currentTarget, column })}
                                        sx={getBudgetProjectIconButtonSx(column)}
                                      >
                                        {getSortIndicatorIcon(getBudgetProjectSortDirection(column))}
                                      </IconButton>
                                    </Box>
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {budgetProjectRowsForDisplay.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={budgetProjectColSpan} align="center" sx={{ py: 3, color: 'var(--color-text-muted)' }}>
                                    {topViewingBudgetRegionTab === 'NEARBY'
                                      ? `No projects found within ${topViewingBudgetNearbyRadiusKm.toFixed(1)} km.`
                                      : topViewingBudgetRegionTab === 'ALL'
                                      ? 'No projects found within this budget range.'
                                      : `No projects found for ${topViewingBudgetRegionTab} within this budget range.`}
                                  </TableCell>
                                </TableRow>
                              ) : (
                                <>
                                {shouldVirtualizeBudgetProjectRows && budgetProjectVirtualRows.topSpacer > 0 && (
                                  <TableRow aria-hidden>
                                    <TableCell
                                      colSpan={budgetProjectColSpan}
                                      sx={{ p: 0, border: 0, lineHeight: 0, height: budgetProjectVirtualRows.topSpacer }}
                                    />
                                  </TableRow>
                                )}
                                {budgetProjectVirtualRows.rows.map((projectName) => {
                                  const matched = topViewingBudgetMatchedCountByProject.get(projectName) ?? { total: 0, br1: 0, br2: 0, br3: 0, br4: 0, br5: 0, ph: 0 };
                                  const renderMatchedBrCountCell = (bucket: BudgetProjectMatchedBrBucket, count: number | undefined) => {
                                    const n = Number(count) || 0;
                                    const isExpanded =
                                      budgetProjectMatchedBrExpand?.projectName === projectName &&
                                      budgetProjectMatchedBrExpand?.bucket === bucket;
                                    if (n <= 0) {
                                      return <TableCell align="center">-</TableCell>;
                                    }
                                    return (
                                      <TableCell align="center">
                                        <Box
                                          component="button"
                                          type="button"
                                          onClick={(e) => handleBudgetProjectMatchedBrClick(e, projectName, bucket, n)}
                                          sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.25,
                                            justifyContent: 'center',
                                            m: 0,
                                            p: 0,
                                            minWidth: 0,
                                            border: 'none',
                                            background: 'transparent',
                                            cursor: 'pointer',
                                            color: 'var(--color-accent)',
                                            fontWeight: 600,
                                            font: 'inherit',
                                            textDecoration: 'underline dotted rgba(255, 152, 0, 0.55)',
                                            textUnderlineOffset: 3,
                                            '&:hover': { opacity: 0.92 },
                                          }}
                                        >
                                          {n}
                                          {isExpanded ? (
                                            <KeyboardArrowUpIcon sx={{ fontSize: '1rem' }} />
                                          ) : (
                                            <KeyboardArrowDownIcon sx={{ fontSize: '1rem' }} />
                                          )}
                                        </Box>
                                      </TableCell>
                                    );
                                  };
                                  const isThisProjectExpanded = budgetProjectMatchedBrExpand?.projectName === projectName;
                                  return (
                                    <Fragment key={`budget-project-row-${projectName}`}>
                                      <TableRow hover>
                                        {PROJECT_VIEW_COLUMN_KEYS.slice(0, 2).map((column) => (
                                          <TableCell
                                            key={`budget-project-${projectName}-${column}`}
                                            sx={
                                              column === 'projectName'
                                                ? {
                                                    position: 'sticky',
                                                    left: 0,
                                                    zIndex: 2,
                                                    minWidth: 160,
                                                    backgroundColor: 'var(--color-bg)',
                                                    borderRight: '1px solid rgba(var(--color-text-rgb), 0.08)',
                                                  }
                                                : undefined
                                            }
                                          >
                                            {getBudgetProjectCellValue(projectName, column)}
                                          </TableCell>
                                        ))}
                                        <TableCell align="center" sx={{ fontWeight: 600 }}>{matched.total}</TableCell>
                                        {budgetMatchedVisibleBrColumns.br1 && renderMatchedBrCountCell('br1', matched.br1)}
                                        {budgetMatchedVisibleBrColumns.br2 && renderMatchedBrCountCell('br2', matched.br2)}
                                        {budgetMatchedVisibleBrColumns.br3 && renderMatchedBrCountCell('br3', matched.br3)}
                                        {budgetMatchedVisibleBrColumns.br4 && renderMatchedBrCountCell('br4', matched.br4)}
                                        {budgetMatchedVisibleBrColumns.br5 && renderMatchedBrCountCell('br5', matched.br5)}
                                        {budgetMatchedVisibleBrColumns.ph && renderMatchedBrCountCell('ph', matched.ph)}
                                        {PROJECT_VIEW_COLUMN_KEYS.slice(2).map((column) => (
                                          <TableCell key={`budget-project-${projectName}-${column}`}>
                                            {getBudgetProjectCellValue(projectName, column)}
                                          </TableCell>
                                        ))}
                                      </TableRow>
                                      {isThisProjectExpanded && (
                                        <TableRow>
                                          <TableCell
                                            colSpan={budgetProjectColSpan}
                                            sx={{
                                              p: 0,
                                              borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)',
                                              bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
                                            }}
                                          >
                                            <Collapse in timeout="auto" unmountOnExit>
                                              <Box sx={{ p: 2 }}>
                                                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'var(--color-text-muted)', fontWeight: 600 }}>
                                                  Matched units (
                                                  {budgetProjectMatchedBrExpand?.bucket === 'ph'
                                                    ? 'PH'
                                                    : `${budgetProjectMatchedBrExpand?.bucket.replace('br', '')}BR`}
                                                  )
                                                </Typography>
                                                {budgetProjectExpandedMatchedUnits.length === 0 ? (
                                                  <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
                                                    No units in this bucket.
                                                  </Typography>
                                                ) : (
                                                  <Table size="small" sx={{ minWidth: 900 }}>
                                                    <TableHead>
                                                      <TableRow>
                                                        {isBudgetUnitColumnVisible('projectName') && (
                                                          <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }}>Project Name</TableCell>
                                                        )}
                                                        {isBudgetUnitColumnVisible('projectCategory') && (
                                                          <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }}>Project Category</TableCell>
                                                        )}
                                                        {isBudgetUnitColumnVisible('blockNumber') && (
                                                          <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }}>Block</TableCell>
                                                        )}
                                                        {isBudgetUnitColumnVisible('unitNumber') && (
                                                          <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }}>Unit</TableCell>
                                                        )}
                                                        {isBudgetUnitColumnVisible('floor') && (
                                                          <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }} align="center">Floor</TableCell>
                                                        )}
                                                        {isBudgetUnitColumnVisible('stack') && (
                                                          <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }} align="center">Stack</TableCell>
                                                        )}
                                                        {isBudgetUnitColumnVisible('bedroom') && (
                                                          <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }} align="center">Bedroom</TableCell>
                                                        )}
                                                        {isBudgetUnitColumnVisible('bathroom') && (
                                                          <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }} align="center">Bathroom</TableCell>
                                                        )}
                                                        {isBudgetUnitColumnVisible('areaSqft') && (
                                                          <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }} align="right">Area (sqft)</TableCell>
                                                        )}
                                                        {isBudgetUnitColumnVisible('price') && (
                                                          <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }} align="right">
                                                            {showBudgetDualPricing ? 'Price / NPS' : formatColumnVisibilityLabel('price')}
                                                          </TableCell>
                                                        )}
                                                        {isBudgetUnitColumnVisible('psf') && (
                                                          <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }} align="right">PSF</TableCell>
                                                        )}
                                                        {showBudgetDualPricing && isBudgetUnitColumnVisible('dps') && (
                                                          <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }} align="right">DPS</TableCell>
                                                        )}
                                                        {isBudgetUnitColumnVisible('outlay') && (
                                                          <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }} align="right">Outlay</TableCell>
                                                        )}
                                                        {isBudgetUnitColumnVisible('installment') && (
                                                          <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }} align="right">Instalment</TableCell>
                                                        )}
                                                        {isBudgetUnitColumnVisible('distance') && (
                                                          <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }} align="right">
                                                            {budgetDistanceLabel}
                                                          </TableCell>
                                                        )}
                                                        {isBudgetUnitColumnVisible('district') && (
                                                          <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }}>District</TableCell>
                                                        )}
                                                        {isBudgetUnitColumnVisible('region') && (
                                                          <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }}>Region</TableCell>
                                                        )}
                                                        {isBudgetUnitColumnVisible('tenure') && (
                                                          <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }}>Tenure</TableCell>
                                                        )}
                                                        {isBudgetUnitColumnVisible('completedDate') && (
                                                          <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }}>TOP</TableCell>
                                                        )}
                                                        {isBudgetUnitColumnVisible('nearestMrt') && (
                                                          <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }}>Nearest Mrt</TableCell>
                                                        )}
                                                        {isBudgetUnitColumnVisible('walkingTimeToMrt') && (
                                                          <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }}>Walk to MRT</TableCell>
                                                        )}
                                                        {topViewingBudgetRegionTab === 'SUPER' && (
                                                          <TableCell sx={{ fontWeight: 600, bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }}>Why Better</TableCell>
                                                        )}
                                                      </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                      {budgetProjectExpandedMatchedUnits.map((unit) => (
                                                        <TableRow
                                                          key={`budget-project-expand-${projectName}-${unit.id}`}
                                                          hover
                                                          onClick={(event) => {
                                                            setTopViewingUnitMenuAnchor(null);
                                                            setSectionUnitMenuAnchor({
                                                              unit: unit as unknown as ComparableUnit,
                                                              mouseX: event.clientX - 2,
                                                              mouseY: event.clientY - 4,
                                                              mode: 'budget',
                                                            });
                                                          }}
                                                          sx={getBudgetUnitRowSx(unit)}
                                                        >
                                                          {renderBudgetUnitViewTableCells(unit)}
                                                        </TableRow>
                                                      ))}
                                                    </TableBody>
                                                  </Table>
                                                )}
                                              </Box>
                                            </Collapse>
                                          </TableCell>
                                        </TableRow>
                                      )}
                                    </Fragment>
                                  );
                                })}
                                {shouldVirtualizeBudgetProjectRows && budgetProjectVirtualRows.bottomSpacer > 0 && (
                                  <TableRow aria-hidden>
                                    <TableCell
                                      colSpan={budgetProjectColSpan}
                                      sx={{ p: 0, border: 0, lineHeight: 0, height: budgetProjectVirtualRows.bottomSpacer }}
                                    />
                                  </TableRow>
                                )}
                                </>
                              )}
                            </TableBody>
                          </Table>
                        )}
                      </TableContainer>
                      <TablePagination
                        component="div"
                        count={
                          topViewingBudgetViewMode === 'unit'
                            ? topViewingBudgetUnitsForDistance.length
                            : budgetProjectRowsForDisplay.length
                        }
                        page={
                          topViewingBudgetViewMode === 'unit'
                            ? topViewingBudgetUnitsPage
                            : topViewingBudgetProjectsPage
                        }
                        onPageChange={(_, nextPage) => {
                          if (topViewingBudgetViewMode === 'unit') {
                            setTopViewingBudgetUnitsPage(nextPage);
                          } else {
                            setTopViewingBudgetProjectsPage(nextPage);
                          }
                        }}
                        rowsPerPage={TOP_VIEWING_PAGE_SIZE}
                        onRowsPerPageChange={() => {}}
                        rowsPerPageOptions={[TOP_VIEWING_PAGE_SIZE]}
                        sx={{
                          borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)',
                          '& .MuiTablePagination-toolbar': {
                            minHeight: 40,
                          },
                        }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 1, flexWrap: 'wrap' }}>
                        {canAccessNearbyDeal && topViewingBudgetRegionTab === 'NEARBY' && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setNearbyDealVisible(true);
                              setNearbyDealSectionOpen(true);
                            }}
                            sx={{
                              borderColor: 'rgba(var(--color-accent-rgb), 0.55)',
                              color: 'var(--color-accent)',
                              '&:hover': {
                                borderColor: 'var(--color-accent)',
                                bgcolor: 'rgba(var(--color-accent-rgb), 0.08)',
                              },
                            }}
                          >
                            Nearby Deals
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => onOpenBudgetInProjectResearch?.(topViewingBudgetSuperMatch)}
                          disabled={!onOpenBudgetInProjectResearch}
                          sx={{
                            borderColor: 'rgba(var(--color-text-rgb), 0.3)',
                            color: 'var(--color-text-strong)',
                            '&:hover': {
                              borderColor: 'var(--color-accent)',
                              color: 'var(--color-accent)',
                            },
                          }}
                        >
                          Open in Project Research
                        </Button>
                      </Box>
                      </>
                    )
                  )}
                  <Popover
                    open={Boolean(topViewingBudgetPopoverAnchor)}
                    anchorEl={topViewingBudgetPopoverAnchor?.element}
                    onClose={handleTopViewingBudgetClosePopover}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  >
                    {topViewingBudgetPopoverAnchor && (
                      <Box sx={{ p: 2, minWidth: 260 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ mb: 1, color: 'var(--color-info)', fontWeight: 500 }}>
                            Sort
                          </Typography>
                          <ToggleButtonGroup
                            value={getTopViewingBudgetSortDirection(topViewingBudgetPopoverAnchor.column)}
                            exclusive
                            onChange={(_, value) => {
                              if (value !== null) {
                                handleTopViewingBudgetSortChange(topViewingBudgetPopoverAnchor.column, value);
                              }
                            }}
                            fullWidth
                            sx={{
                              '& .MuiToggleButtonGroup-grouped': {
                                border: '1px solid rgba(var(--color-shadow-rgb), 0.12)',
                                '&:not(:first-of-type)': {
                                  borderLeft: '1px solid rgba(var(--color-shadow-rgb), 0.12)',
                                  marginLeft: 0,
                                },
                                '&.Mui-selected': {
                                  backgroundColor: 'var(--color-info)',
                                  color: 'var(--color-text-strong)',
                                },
                              },
                            }}
                          >
                            <ToggleButton value="default" sx={{ flex: 1, py: 0.75 }}>Default</ToggleButton>
                            <ToggleButton value="asc" sx={{ flex: 1, py: 0.75 }}>Asc</ToggleButton>
                            <ToggleButton value="desc" sx={{ flex: 1, py: 0.75 }}>Desc</ToggleButton>
                          </ToggleButtonGroup>
                        </Box>

                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2" sx={{ color: 'var(--color-info)', fontWeight: 500, mr: 1 }}>
                              Filter
                            </Typography>
                            <FilterIcon sx={{ fontSize: 18, color: 'var(--color-info)' }} />
                          </Box>
                          {['projectName', 'projectCategory', 'tenure', 'region', 'nearestMrt', 'district'].includes(topViewingBudgetPopoverAnchor.column) ? (
                            <Box>
                              {(() => {
                                const col = topViewingBudgetPopoverAnchor.column;
                                const options = topViewingBudgetUniqueFilterOptions[col as keyof typeof topViewingBudgetUniqueFilterOptions] ?? [];
                                const excluded = topViewingBudgetExclusionFilters[col as keyof typeof topViewingBudgetExclusionFilters] ?? [];
                                const included = options.filter((o) => !excluded.includes(o));
                                const labels: Record<string, string> = {
                                  projectName: 'Select Projects',
                                  projectCategory: 'Select Categories',
                                  tenure: 'Select Tenures',
                                  region: 'Select Regions',
                                  nearestMrt: 'Select MRT Stations',
                                  district: 'Select Districts',
                                };
                                return (
                                  <FormControl fullWidth size="small">
                                    <InputLabel>{labels[col] ?? `Select ${col}`}</InputLabel>
                                    <Select
                                      multiple
                                      label={labels[col] ?? `Select ${col}`}
                                      value={included}
                                      onChange={(event) => {
                                        const rawValue = event.target.value;
                                        const value = typeof rawValue === 'string' ? rawValue.split(',') : rawValue;
                                        if (value.includes('__SELECT_ALL__')) {
                                          if (included.length === options.length) {
                                            handleTopViewingBudgetExclusionFilterSet(col, [...options]);
                                          } else {
                                            handleTopViewingBudgetExclusionFilterSet(col, []);
                                          }
                                          return;
                                        }
                                        handleTopViewingBudgetExclusionFilterSet(col, options.filter((o) => !value.includes(o)));
                                      }}
                                      renderValue={(selected) => {
                                        const selectedValues = selected as string[];
                                        if (excluded.length === 0) return 'All';
                                        if (excluded.length === options.length) return 'None';
                                        return `${selectedValues.length} selected`;
                                      }}
                                      sx={{
                                        bgcolor: 'var(--color-surface)',
                                        '& fieldset': { borderColor: 'rgba(var(--color-text-rgb), 0.2)' },
                                      }}
                                      MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                                    >
                                      <MenuItem value="__SELECT_ALL__">
                                        <Checkbox
                                          checked={included.length === options.length && options.length > 0}
                                          indeterminate={included.length > 0 && included.length < options.length}
                                          sx={{ color: 'var(--color-accent)', '&.Mui-checked': { color: 'var(--color-accent)' } }}
                                        />
                                        <Typography component="span" sx={{ fontWeight: 600 }}>
                                          Select All
                                        </Typography>
                                      </MenuItem>
                                      {options.map((option) => {
                                        const isSelected = included.includes(option);
                                        const optionLabel = col === 'district'
                                          ? getDistrictFilterOptionLabel(option)
                                          : (option || '(empty)');
                                        return (
                                          <MenuItem key={option || '__EMPTY__'} value={option}>
                                            <Checkbox checked={isSelected} sx={{ color: 'var(--color-accent)', '&.Mui-checked': { color: 'var(--color-accent)' } }} />
                                            <Typography component="span">{optionLabel}</Typography>
                                          </MenuItem>
                                        );
                                      })}
                                    </Select>
                                  </FormControl>
                                );
                              })()}
                              <Button
                                size="small"
                                onClick={() => {
                                  handleTopViewingBudgetExclusionFilterSet(topViewingBudgetPopoverAnchor.column, []);
                                }}
                                sx={{ mt: 1 }}
                                fullWidth
                                disabled={!hasTopViewingBudgetActiveFilter(topViewingBudgetPopoverAnchor.column)}
                              >
                                Clear Filter (Show All)
                              </Button>
                            </Box>
                          ) : ['blockNumber', 'unitNumber', 'completedDate'].includes(topViewingBudgetPopoverAnchor.column) ? (
                            <Box>
                              <TextField
                                fullWidth
                                size="small"
                                placeholder={`Filter ${
                                  ({ blockNumber: 'Block', unitNumber: 'Unit', completedDate: 'TOP' } as Record<string, string>)[topViewingBudgetPopoverAnchor.column] ?? topViewingBudgetPopoverAnchor.column
                                }`}
                                value={(topViewingBudgetFilters as Record<string, string>)[topViewingBudgetPopoverAnchor.column] ?? ''}
                                onChange={(e) => handleTopViewingBudgetFilterChange(
                                  topViewingBudgetPopoverAnchor.column,
                                  e.target.value
                                )}
                                InputProps={{
                                  endAdornment: ((topViewingBudgetFilters as Record<string, string>)[topViewingBudgetPopoverAnchor.column] ?? '').trim() !== '' ? (
                                    <InputAdornment position="end">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleTopViewingBudgetFilterChange(
                                          topViewingBudgetPopoverAnchor.column,
                                          ''
                                        )}
                                        sx={{ p: 0.5 }}
                                      >
                                        <Typography variant="caption" sx={{ fontSize: 12 }}>✕</Typography>
                                      </IconButton>
                                    </InputAdornment>
                                  ) : undefined,
                                }}
                              />
                              <Button
                                size="small"
                                onClick={() => {
                                  handleTopViewingBudgetFilterChange(topViewingBudgetPopoverAnchor.column, '');
                                }}
                                sx={{ mt: 1 }}
                                fullWidth
                                disabled={!hasTopViewingBudgetActiveFilter(topViewingBudgetPopoverAnchor.column)}
                              >
                                Clear Filter
                              </Button>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <TextField
                                fullWidth
                                size="small"
                                type={['price', 'psf', 'dps'].includes(topViewingBudgetPopoverAnchor.column) ? 'text' : 'number'}
                                inputProps={['price', 'psf', 'dps'].includes(topViewingBudgetPopoverAnchor.column) ? { inputMode: 'decimal' } : undefined}
                                label="Min"
                                value={(() => {
                                  const raw = ({
                                    floor: topViewingBudgetRangeFilters.floor.min,
                                    stack: topViewingBudgetRangeFilters.stack.min,
                                    bedroom: topViewingBudgetRangeFilters.bedroom.min,
                                    bathroom: topViewingBudgetRangeFilters.bathroom.min,
                                    areaSqft: topViewingBudgetRangeFilters.areaSqft.min,
                                    price: topViewingBudgetRangeFilters.price.min,
                                    dps: topViewingBudgetRangeFilters.dps.min,
                                    psf: topViewingBudgetRangeFilters.psf.min,
                                    outlay: topViewingBudgetRangeFilters.outlay.min,
                                    installment: topViewingBudgetRangeFilters.installment.min,
                                    distance: topViewingBudgetRangeFilters.distance.min,
                                    walkingTimeToMrt: topViewingBudgetRangeFilters.walkingTimeToMrt?.min ?? '',
                                  } as Record<string, string>)[topViewingBudgetPopoverAnchor.column];
                                  return (['price', 'psf', 'dps'].includes(topViewingBudgetPopoverAnchor.column))
                                    ? formatThousandsInput(raw)
                                    : raw;
                                })()}
                                onChange={(e) => {
                                  const nextValue = (['price', 'psf', 'dps'].includes(topViewingBudgetPopoverAnchor.column))
                                    ? e.target.value.replace(/,/g, '').replace(/[^\d.]/g, '')
                                    : e.target.value;
                                  handleTopViewingBudgetRangeFilterChange(topViewingBudgetPopoverAnchor.column as any, 'min', nextValue);
                                }}
                              />
                              <TextField
                                fullWidth
                                size="small"
                                type={['price', 'psf', 'dps'].includes(topViewingBudgetPopoverAnchor.column) ? 'text' : 'number'}
                                inputProps={['price', 'psf', 'dps'].includes(topViewingBudgetPopoverAnchor.column) ? { inputMode: 'decimal' } : undefined}
                                label="Max"
                                value={(() => {
                                  const raw = ({
                                    floor: topViewingBudgetRangeFilters.floor.max,
                                    stack: topViewingBudgetRangeFilters.stack.max,
                                    bedroom: topViewingBudgetRangeFilters.bedroom.max,
                                    bathroom: topViewingBudgetRangeFilters.bathroom.max,
                                    areaSqft: topViewingBudgetRangeFilters.areaSqft.max,
                                    price: topViewingBudgetRangeFilters.price.max,
                                    dps: topViewingBudgetRangeFilters.dps.max,
                                    psf: topViewingBudgetRangeFilters.psf.max,
                                    outlay: topViewingBudgetRangeFilters.outlay.max,
                                    installment: topViewingBudgetRangeFilters.installment.max,
                                    distance: topViewingBudgetRangeFilters.distance.max,
                                    walkingTimeToMrt: topViewingBudgetRangeFilters.walkingTimeToMrt?.max ?? '',
                                  } as Record<string, string>)[topViewingBudgetPopoverAnchor.column];
                                  return (['price', 'psf', 'dps'].includes(topViewingBudgetPopoverAnchor.column))
                                    ? formatThousandsInput(raw)
                                    : raw;
                                })()}
                                onChange={(e) => {
                                  const nextValue = (['price', 'psf', 'dps'].includes(topViewingBudgetPopoverAnchor.column))
                                    ? e.target.value.replace(/,/g, '').replace(/[^\d.]/g, '')
                                    : e.target.value;
                                  handleTopViewingBudgetRangeFilterChange(topViewingBudgetPopoverAnchor.column as any, 'max', nextValue);
                                }}
                              />
                              {(() => {
                                const range = ({
                                  floor: topViewingBudgetRangeFilters.floor,
                                  stack: topViewingBudgetRangeFilters.stack,
                                  bedroom: topViewingBudgetRangeFilters.bedroom,
                                  bathroom: topViewingBudgetRangeFilters.bathroom,
                                  areaSqft: topViewingBudgetRangeFilters.areaSqft,
                                  price: topViewingBudgetRangeFilters.price,
                                  dps: topViewingBudgetRangeFilters.dps,
                                  psf: topViewingBudgetRangeFilters.psf,
                                  outlay: topViewingBudgetRangeFilters.outlay,
                                  installment: topViewingBudgetRangeFilters.installment,
                                  distance: topViewingBudgetRangeFilters.distance,
                                  walkingTimeToMrt: topViewingBudgetRangeFilters.walkingTimeToMrt ?? { min: '', max: '' },
                                } as Record<string, { min: string; max: string }>)[topViewingBudgetPopoverAnchor.column];
                                const hasRangeFilter = range && (range.min.trim() !== '' || range.max.trim() !== '');
                                return (
                                  <Button
                                    size="small"
                                    onClick={() => {
                                      handleTopViewingBudgetRangeFilterChange(topViewingBudgetPopoverAnchor.column as any, 'min', '');
                                      handleTopViewingBudgetRangeFilterChange(topViewingBudgetPopoverAnchor.column as any, 'max', '');
                                    }}
                                    fullWidth
                                    disabled={!hasRangeFilter}
                                  >
                                    Clear Filter
                                  </Button>
                                );
                              })()}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Popover>
                  <Popover
                    open={Boolean(budgetProjectPopoverAnchor)}
                    anchorEl={budgetProjectPopoverAnchor?.element}
                    onClose={() => setBudgetProjectPopoverAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  >
                    {budgetProjectPopoverAnchor && (
                      <Box sx={{ p: 2, minWidth: 260 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ mb: 1, color: 'var(--color-info)', fontWeight: 500 }}>
                            Sort
                          </Typography>
                          <ToggleButtonGroup
                            value={budgetProjectSortConfig.column === budgetProjectPopoverAnchor.column ? budgetProjectSortConfig.direction : 'default'}
                            exclusive
                            onChange={(_, value) => {
                              if (value !== null) {
                                setBudgetProjectSortConfig({ column: budgetProjectPopoverAnchor.column, direction: value });
                              }
                            }}
                            fullWidth
                          >
                            <ToggleButton value="default" sx={{ flex: 1, py: 0.75 }}>Default</ToggleButton>
                            <ToggleButton value="asc" sx={{ flex: 1, py: 0.75 }}>Asc</ToggleButton>
                            <ToggleButton value="desc" sx={{ flex: 1, py: 0.75 }}>Desc</ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                        {['projectName', 'projectCategory', 'tenure', 'region', 'nearestMrt', 'district'].includes(budgetProjectPopoverAnchor.column) ? (
                          <Box>
                            {(() => {
                              const col = budgetProjectPopoverAnchor.column;
                              const options = topViewingBudgetUniqueFilterOptions[col as keyof typeof topViewingBudgetUniqueFilterOptions] ?? [];
                              const excluded = topViewingBudgetExclusionFilters[col as keyof typeof topViewingBudgetExclusionFilters] ?? [];
                              const included = options.filter((o) => !excluded.includes(o));
                              const labels: Record<string, string> = {
                                projectName: 'Select Projects',
                                projectCategory: 'Select Categories',
                                tenure: 'Select Tenures',
                                region: 'Select Regions',
                                nearestMrt: 'Select MRT Stations',
                                district: 'Select Districts',
                              };
                              return (
                                <FormControl fullWidth size="small">
                                  <InputLabel>{labels[col] ?? `Select ${col}`}</InputLabel>
                                  <Select
                                    multiple
                                    label={labels[col] ?? `Select ${col}`}
                                    value={included}
                                    onChange={(event) => {
                                      const rawValue = event.target.value;
                                      const value = typeof rawValue === 'string' ? rawValue.split(',') : rawValue;
                                      if (value.includes('__SELECT_ALL__')) {
                                        if (included.length === options.length) {
                                          handleTopViewingBudgetExclusionFilterSet(col, [...options]);
                                        } else {
                                          handleTopViewingBudgetExclusionFilterSet(col, []);
                                        }
                                        return;
                                      }
                                      handleTopViewingBudgetExclusionFilterSet(col, options.filter((o) => !value.includes(o)));
                                    }}
                                    renderValue={(selected) => {
                                      const selectedValues = selected as string[];
                                      if (excluded.length === 0) return 'All';
                                      if (excluded.length === options.length) return 'None';
                                      return `${selectedValues.length} selected`;
                                    }}
                                    sx={{
                                      bgcolor: 'var(--color-surface)',
                                      '& fieldset': { borderColor: 'rgba(var(--color-text-rgb), 0.2)' },
                                    }}
                                    MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                                  >
                                    <MenuItem value="__SELECT_ALL__">
                                      <Checkbox
                                        checked={included.length === options.length && options.length > 0}
                                        indeterminate={included.length > 0 && included.length < options.length}
                                        sx={{ color: 'var(--color-accent)', '&.Mui-checked': { color: 'var(--color-accent)' } }}
                                      />
                                      <Typography component="span" sx={{ fontWeight: 600 }}>
                                        Select All
                                      </Typography>
                                    </MenuItem>
                                    {options.map((option) => {
                                      const isSelected = included.includes(option);
                                      const optionLabel = col === 'district'
                                        ? getDistrictFilterOptionLabel(option)
                                        : (option || '(empty)');
                                      return (
                                        <MenuItem key={option || '__EMPTY__'} value={option}>
                                          <Checkbox checked={isSelected} sx={{ color: 'var(--color-accent)', '&.Mui-checked': { color: 'var(--color-accent)' } }} />
                                          <Typography component="span">{optionLabel}</Typography>
                                        </MenuItem>
                                      );
                                    })}
                                  </Select>
                                </FormControl>
                              );
                            })()}
                            {(() => {
                              const col = budgetProjectPopoverAnchor.column as keyof typeof topViewingBudgetExclusionFilters;
                              const excluded = topViewingBudgetExclusionFilters[col] ?? [];
                              return (
                                <Button
                                  size="small"
                                  onClick={() => {
                                    handleTopViewingBudgetExclusionFilterSet(col, []);
                                  }}
                                  sx={{ mt: 1 }}
                                  fullWidth
                                  disabled={excluded.length === 0}
                                >
                                  Clear Filter (Show All)
                                </Button>
                              );
                            })()}
                          </Box>
                        ) : projectViewNumericColumns.has(budgetProjectPopoverAnchor.column) ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {(() => {
                              const shouldFormatThousands = /price|psf/i.test(budgetProjectPopoverAnchor.column);
                              return (
                                <>
                            <TextField
                              fullWidth
                              size="small"
                              type={shouldFormatThousands ? 'text' : 'number'}
                              inputProps={shouldFormatThousands ? { inputMode: 'decimal' } : undefined}
                              label="Min"
                              value={shouldFormatThousands
                                ? formatThousandsInput(budgetProjectRangeFilters[budgetProjectPopoverAnchor.column]?.min ?? '')
                                : (budgetProjectRangeFilters[budgetProjectPopoverAnchor.column]?.min ?? '')}
                              onChange={(e) => setBudgetProjectRangeFilters((prev) => ({
                                ...prev,
                                [budgetProjectPopoverAnchor.column]: {
                                  ...(prev[budgetProjectPopoverAnchor.column] ?? { min: '', max: '' }),
                                  min: shouldFormatThousands ? e.target.value.replace(/,/g, '').replace(/[^\d.]/g, '') : e.target.value,
                                },
                              }))}
                            />
                            <TextField
                              fullWidth
                              size="small"
                              type={shouldFormatThousands ? 'text' : 'number'}
                              inputProps={shouldFormatThousands ? { inputMode: 'decimal' } : undefined}
                              label="Max"
                              value={shouldFormatThousands
                                ? formatThousandsInput(budgetProjectRangeFilters[budgetProjectPopoverAnchor.column]?.max ?? '')
                                : (budgetProjectRangeFilters[budgetProjectPopoverAnchor.column]?.max ?? '')}
                              onChange={(e) => setBudgetProjectRangeFilters((prev) => ({
                                ...prev,
                                [budgetProjectPopoverAnchor.column]: {
                                  ...(prev[budgetProjectPopoverAnchor.column] ?? { min: '', max: '' }),
                                  max: shouldFormatThousands ? e.target.value.replace(/,/g, '').replace(/[^\d.]/g, '') : e.target.value,
                                },
                              }))}
                            />
                            {(() => {
                              const range = budgetProjectRangeFilters[budgetProjectPopoverAnchor.column] ?? { min: '', max: '' };
                              const hasRangeFilter = (range.min ?? '').trim() !== '' || (range.max ?? '').trim() !== '';
                              return (
                                <Button
                                  size="small"
                                  onClick={() => setBudgetProjectRangeFilters((prev) => ({
                                    ...prev,
                                    [budgetProjectPopoverAnchor.column]: { min: '', max: '' },
                                  }))}
                                  fullWidth
                                  disabled={!hasRangeFilter}
                                >
                                  Clear Filter
                                </Button>
                              );
                            })()}
                                </>
                              );
                            })()}
                          </Box>
                    ) : (
                          <Box>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder={`Filter ${budgetProjectPopoverAnchor.column}`}
                              value={budgetProjectTextFilters[budgetProjectPopoverAnchor.column] ?? ''}
                              onChange={(e) => setBudgetProjectTextFilters((prev) => ({
                                ...prev,
                                [budgetProjectPopoverAnchor.column]: e.target.value,
                              }))}
                            />
                            <Button
                              size="small"
                              sx={{ mt: 1 }}
                              onClick={() => setBudgetProjectTextFilters((prev) => ({
                                ...prev,
                                [budgetProjectPopoverAnchor.column]: '',
                              }))}
                              fullWidth
                              disabled={(budgetProjectTextFilters[budgetProjectPopoverAnchor.column] ?? '').trim() === ''}
                            >
                              Clear Filter
                            </Button>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Popover>
                </AccordionDetails>
              </Accordion>
              )}

              {/* DEVELOPER INSIGHTS — admin-only pricing recommendation from nearby stock and transactions */}
              {isAdminUser && topViewingReferenceUnit && (
                <React.Suspense fallback={null}>
                <DeveloperInsightsSection
                  projectId={
                    currentProjectId
                    && (projectDetails?.name ?? '').trim().toLowerCase() === (topViewingReferenceUnit.projectName ?? '').trim().toLowerCase()
                      ? currentProjectId
                      : null
                  }
                  projectName={topViewingReferenceUnit.projectName ?? null}
                  referenceUnit={topViewingReferenceUnit}
                />
                </React.Suspense>
              )}

              {/* NEARBY DEAL — thumbsup & price promo units in nearby projects */}
              {canAccessNearbyDeal && nearbyDealVisible && (
              <Accordion
                  expanded={nearbyDealSectionOpen}
                  onChange={(_, expanded) => setNearbyDealSectionOpen(expanded)}
                  sx={{ mt: 2, border: '1px solid rgba(var(--color-text-rgb), 0.12)', boxShadow: 'none' }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      '& .MuiAccordionSummary-content': {
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                      },
                    }}
                  >
                    <Typography variant="h6" sx={{ color: 'var(--color-text-strong)', fontWeight: 600, letterSpacing: '-0.02em' }}>
                      Nearby Deal
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                      {nearbyDealThumbsupGroups.length} thumbsup project{nearbyDealThumbsupGroups.length === 1 ? '' : 's'} and {nearbyDealPricePromoGroups.length} price promo project{nearbyDealPricePromoGroups.length === 1 ? '' : 's'} within {nearbyDealRadiusKm.toFixed(1)} km
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Tabs
                      value={nearbyDealTab}
                      onChange={(_, value) => setNearbyDealTab(value)}
                      variant="fullWidth"
                      sx={{ mb: 2, minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600 } }}
                    >
                      <Tab
                        value="thumbsup"
                        icon={<ThumbUpIcon sx={{ fontSize: 16 }} />}
                        iconPosition="start"
                        label={`Thumbsup Units (${nearbyDealThumbsupGroups.length})`}
                      />
                      <Tab
                        value="pricePromo"
                        icon={<LocalOfferIcon sx={{ fontSize: 16 }} />}
                        iconPosition="start"
                        label={`Price Promo (${nearbyDealPricePromoGroups.length})`}
                      />
                    </Tabs>

                    {nearbyDealTab === 'thumbsup' ? (
                      nearbyDealThumbsupGroups.length === 0 ? (
                        <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
                          No thumbsup units in projects within {nearbyDealRadiusKm.toFixed(1)} km.
                        </Typography>
                      ) : (
                        <Stack spacing={1}>
                          {nearbyDealThumbsupGroups.map((group) => (
                            <Accordion
                              key={`nearby-thumbsup-${group.projectId}`}
                              disableGutters
                              sx={{
                                borderRadius: 2.5,
                                border: '1px solid rgba(var(--color-text-rgb), 0.15)',
                                bgcolor: 'rgba(var(--color-shadow-rgb), 0.2)',
                                '&:before': { display: 'none' },
                                overflow: 'hidden',
                              }}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: 'var(--color-text-strong)' }} />}
                                sx={{
                                  px: 1.5,
                                  py: 1,
                                  minHeight: 48,
                                  '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center' },
                                  '&:hover': { bgcolor: 'rgba(var(--color-sheet-rgb), 0.25)' },
                                }}
                              >
	                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1, gap: 1.5, minWidth: 0 }}>
	                                  <Box sx={{ minWidth: 0, flex: 1 }}>
	                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
	                                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'var(--color-text-strong)' }}>
	                                        {group.projectName}
                                      </Typography>
                                      {group.isLive === 1 && (
                                        <Chip
                                          label="LIVE"
                                          size="small"
                                          sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: 'rgba(244, 67, 54, 0.15)', color: '#f44336', flexShrink: 0 }}
                                        />
	                                      )}
	                                    </Box>
	                                    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)', display: 'block' }}>
	                                      {group.units.length} top-value unit{group.units.length === 1 ? '' : 's'}
	                                    </Typography>
	                                  </Box>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: 'var(--color-text-strong)',
                                        fontWeight: 800,
                                        fontSize: { xs: '0.95rem', md: '1.05rem' },
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0,
                                      }}
                                    >
                                      {formatNumber(group.distance as number, 2)} km away
                                    </Typography>
	                                </Box>
	                              </AccordionSummary>
                              <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5, bgcolor: 'rgba(var(--color-shadow-rgb), 0.12)' }}>
                                <Stack spacing={1}>
                                  {group.units.map((item) => (
                                    <Card
                                      key={`nearby-thumbsup-${item.ProjectId}-${item.id}`}
                                      variant="outlined"
                                      sx={{
                                        borderRadius: 2,
                                        borderColor: 'rgba(var(--color-text-rgb), 0.12)',
                                        backgroundColor: 'rgba(var(--color-shadow-rgb), 0.25)',
                                        boxShadow: 'none',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': { borderColor: 'var(--color-info-navy)', backgroundColor: 'rgba(var(--color-sheet-rgb), 0.35)' },
                                      }}
                                      onClick={() => handleThumbsupUnitClick(item, 'nearby_deal_thumbsup')}
                                    >
                                      <Box sx={{ p: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                        <Box sx={{ minWidth: 0 }}>
                                          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.85)' }}>
                                            Unit {item.UnitNumber} &middot; {item.bedroomNumber}BR &middot; {formatThumbsupUnitSqft(item.size)}
                                          </Typography>
                                        </Box>
                                        <Chip
                                          icon={<ThumbUpIcon sx={{ fontSize: 14 }} />}
                                          label={`${item.thumbsupCount}`}
                                          size="small"
                                          sx={{ bgcolor: 'rgba(var(--color-success-rgb, 76, 175, 80), 0.15)', color: 'var(--color-success)', fontWeight: 700, flexShrink: 0 }}
                                        />
                                      </Box>
                                    </Card>
                                  ))}
                                </Stack>
                              </AccordionDetails>
                            </Accordion>
                          ))}
                        </Stack>
                      )
                    ) : (
                      nearbyDealPricePromoGroups.length === 0 ? (
                        <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
                          No price promo units in projects within {nearbyDealRadiusKm.toFixed(1)} km.
                        </Typography>
                      ) : (
                        <Stack spacing={1}>
                          {nearbyDealPricePromoGroups.map((group) => (
                            <Accordion
                              key={`nearby-promo-${group.projectId}`}
                              disableGutters
                              sx={{
                                borderRadius: 2.5,
                                border: '1px solid rgba(var(--color-text-rgb), 0.15)',
                                bgcolor: 'rgba(var(--color-shadow-rgb), 0.2)',
                                '&:before': { display: 'none' },
                                overflow: 'hidden',
                              }}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: 'var(--color-text-strong)' }} />}
                                sx={{
                                  px: 1.5,
                                  py: 1,
                                  minHeight: 48,
                                  '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center' },
                                  '&:hover': { bgcolor: 'rgba(var(--color-sheet-rgb), 0.25)' },
                                }}
                              >
	                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1, gap: 1.5, minWidth: 0 }}>
	                                  <Box sx={{ minWidth: 0, flex: 1 }}>
	                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
	                                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'var(--color-text-strong)' }}>
	                                        {group.projectName}
                                      </Typography>
                                      {group.isLive === 1 && (
                                        <Chip
                                          label="LIVE"
                                          size="small"
                                          sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: 'rgba(244, 67, 54, 0.15)', color: '#f44336', flexShrink: 0 }}
                                        />
	                                      )}
	                                    </Box>
	                                    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)', display: 'block' }}>
	                                      {group.units.length} unit{group.units.length === 1 ? '' : 's'}
	                                    </Typography>
	                                  </Box>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: 'var(--color-text-strong)',
                                        fontWeight: 800,
                                        fontSize: { xs: '0.95rem', md: '1.05rem' },
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0,
                                      }}
                                    >
                                      {formatNumber(group.distance as number, 2)} km away
                                    </Typography>
	                                </Box>
	                              </AccordionSummary>
                              <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5, bgcolor: 'rgba(var(--color-shadow-rgb), 0.12)' }}>
                                <Stack spacing={1}>
                                  {group.units.map((item) => {
                                    const isBest = pricePromoBestByStackBedroom.has(item.id);
                                    return (
                                      <Card
                                        key={`nearby-promo-${item.ProjectId}-${item.id}`}
                                        variant="outlined"
                                        sx={{
                                          borderRadius: 2,
                                          border: isBest ? '2px solid var(--color-success)' : '1px solid rgba(var(--color-text-rgb), 0.12)',
                                          backgroundColor: 'rgba(var(--color-shadow-rgb), 0.25)',
                                          boxShadow: 'none',
                                          cursor: 'pointer',
                                          transition: 'all 0.2s ease',
                                          '&:hover': { borderColor: isBest ? 'var(--color-success)' : 'var(--color-info-navy)', backgroundColor: 'rgba(var(--color-sheet-rgb), 0.35)' },
                                        }}
                                        onClick={() => handlePricePromoUnitClick(item, 'nearby_deal_price_promo', 'nearby_deal')}
                                      >
                                        <Box sx={{ p: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                          <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.85)' }}>
                                              Unit {item.UnitNumber} &middot; {item.bedroomNumber}BR &middot; {formatThumbsupUnitSqft(item.size)}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'var(--color-success)', fontWeight: 600, display: 'block' }}>
                                              {item.priceChange < 0 ? '-' : ''}${Math.abs(item.priceChange).toLocaleString()}
                                              {item.priceChangePercent ? ` (${item.priceChangePercent.toFixed(1)}%)` : ''}
                                            </Typography>
                                            {item.priceChangeAt && (
                                              <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.5)', display: 'block' }}>
                                                {formatPricePromoDate(item.priceChangeAt)}
                                              </Typography>
                                            )}
                                          </Box>
                                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
                                            <Typography
                                              variant="body2"
                                              onClick={(e) => { e.stopPropagation(); handleOpenPriceChart(item.id, 'price', true, 'nearby_deal_price_promo'); }}
                                              sx={{
                                                color: 'var(--color-text-strong)',
                                                fontWeight: 700,
                                                cursor: 'pointer',
                                                textDecoration: 'underline',
                                                textDecorationStyle: 'dotted',
                                                '&:hover': { color: 'var(--color-info-navy)' },
                                              }}
                                            >
                                              ${Math.round(item.price).toLocaleString()}
                                            </Typography>
                                            <Chip
                                              icon={<LocalOfferIcon sx={{ fontSize: 14 }} />}
                                              label="PRICE PROMO"
                                              size="small"
                                              sx={{ bgcolor: 'rgba(var(--color-success-rgb, 76, 175, 80), 0.15)', color: 'var(--color-success)', fontWeight: 700 }}
                                            />
                                          </Box>
                                        </Box>
                                      </Card>
                                    );
                                  })}
                                </Stack>
                              </AccordionDetails>
                            </Accordion>
                          ))}
                        </Stack>
                      )
                    )}
                  </AccordionDetails>
                </Accordion>
              )}

              {/* TOP VIEWING Thumbs Up Legend Popover */}
              <Popover
                open={Boolean(topViewingThumbsUpLegendAnchor)}
                anchorEl={topViewingThumbsUpLegendAnchor}
                onClose={() => setTopViewingThumbsUpLegendAnchor(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                <Box sx={{ p: 3, maxWidth: 400 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ThumbUpIcon sx={{ color: 'var(--color-success)', mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Thumbs Up Criteria
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    A unit receives a thumbs up icon based on floor comparison:
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1, mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        If Lower Floor:
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                        <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                          Must have <strong>bigger size</strong>
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                          Must have <strong>lower PSF</strong>
                        </Typography>
                        <Typography component="li" variant="body2">
                          Must have <strong>lower price</strong>
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        If Higher Floor:
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                        <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                          Can have <strong>same size or bigger size</strong>
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                          Must have <strong>lower PSF</strong>
                        </Typography>
                        <Typography component="li" variant="body2">
                          Must have <strong>lower price</strong>
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    fullWidth
                    onClick={() => setTopViewingThumbsUpLegendAnchor(null)}
                  >
                    Got it
                  </Button>
                </Box>
              </Popover>

              <Popover
                open={Boolean(topViewingFormulaAnchor)}
                anchorEl={topViewingFormulaAnchor?.element ?? null}
                onClose={closeTopViewingFormulaPopover}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                disableRestoreFocus
                PaperProps={{
                  sx: {
                    bgcolor: 'rgba(60, 60, 60, 0.95)',
                    maxWidth: 360,
                    border: '1px solid rgba(255,255,255,0.12)',
                    p: 2,
                    '& .MuiTypography-root': { color: '#fff' },
                  },
                }}
              >
                <Box onClick={(event) => event.stopPropagation()}>
                  {topViewingFormulaAnchor?.content}
                </Box>
              </Popover>

              {/* TOP VIEWING Outlay Info Popover */}
              <Popover
                open={Boolean(topViewingOutlayInfoAnchor)}
                anchorEl={topViewingOutlayInfoAnchor}
                onClose={() => setTopViewingOutlayInfoAnchor(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                <Box sx={{ p: 3, maxWidth: 400 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <HelpOutlineIcon sx={{ color: 'var(--color-info)', mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Outlay Calculation
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    Outlay represents the total upfront cash required for a property purchase, including down payment and stamp duty.
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1, mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        Formula:
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.95rem' }}>
                        Outlay = Down Payment + BSD
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1.5, fontSize: '0.85rem' }}>
                        Where:
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, mb: 0, mt: 0.5 }}>
                        <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Down Payment</strong> = Price × (100% − LTV%) = Price × {100 - Math.round(loanToValue)}%
                        </Typography>
                        <Typography component="li" variant="body2">
                          <strong>BSD</strong> = Buyer&apos;s Stamp Duty (calculated on tiered rates)
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        BSD Tiered Rates:
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                        <Typography component="li" variant="body2" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                          First $180,000: <strong>1%</strong>
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                          Next $180,000: <strong>2%</strong>
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                          Next $640,000: <strong>3%</strong>
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                          Next $500,000: <strong>4%</strong>
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                          Next $1,500,000: <strong>5%</strong>
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ fontSize: '0.85rem' }}>
                          Remaining amount: <strong>6%</strong>
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 1, mb: 2 }}>
                    <Typography variant="caption" sx={{ fontStyle: 'italic', display: 'block' }}>
                      <strong>Note:</strong> This is an estimate. Actual outlay may vary based on additional fees, legal costs, and other transaction expenses.
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    fullWidth
                    onClick={() => setTopViewingOutlayInfoAnchor(null)}
                  >
                    Got it
                  </Button>
                </Box>
              </Popover>

              {/* TOP VIEWING Installment Info Popover */}
              <Popover
                open={Boolean(topViewingInstallmentInfoAnchor)}
                anchorEl={topViewingInstallmentInfoAnchor}
                onClose={() => setTopViewingInstallmentInfoAnchor(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                <Box sx={{ p: 3, maxWidth: 420 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <HelpOutlineIcon sx={{ color: 'var(--color-info)', mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Installment Calculation
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    Monthly installment is calculated using PMT with loan amount, monthly interest rate and tenure.
                  </Typography>
                  <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1, mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Formula:
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.95rem' }}>
                      Installment = PMT(Loan Amount, Monthly Rate, Months)
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1.5 }}>
                      Current interest rate: <strong>{formatNumber(currentInterestRate ?? 0, 2)}%</strong> p.a.
                    </Typography>
                  </Box>
                  <Button
                    variant="text"
                    fullWidth
                    sx={{ mb: 1, textDecoration: 'underline' }}
                    onClick={() => {
                      setTopViewingInstallmentInfoAnchor(null);
                      onOpenFinancialParameters?.();
                    }}
                  >
                    Open Financial Parameters
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    fullWidth
                    onClick={() => setTopViewingInstallmentInfoAnchor(null)}
                  >
                    Got it
                  </Button>
                </Box>
              </Popover>

              </Grid>

              <Grid item xs={12} sx={{ flexShrink: 0, width: rightPanelCollapsed ? { xs: '100%', md: 48 } : { xs: '100%', md: 320 }, maxWidth: rightPanelCollapsed ? { md: 48 } : { md: 320 } }}>
                <Card data-tutorial="right-panel" sx={{ position: { md: 'sticky' }, top: { md: 16 }, alignSelf: 'flex-start', maxHeight: { md: 'calc(100vh - 32px)' }, overflowY: 'auto', minWidth: rightPanelCollapsed ? { xs: 0, md: 48 } : 0, width: rightPanelCollapsed ? { xs: '100%', md: 48 } : '100%' }}>
                  <CardContent sx={{ p: rightPanelCollapsed ? { md: 0.5 } : undefined }}>
                    {rightPanelCollapsed ? (
                      <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => setRightPanelCollapsed(false)}
                          sx={{ color: 'var(--color-text-strong)' }}
                          title="Expand panel"
                        >
                          <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <>
                      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'flex-end', mb: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => setRightPanelCollapsed(true)}
                          sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}
                          title="Collapse panel"
                        >
                          <ChevronRightIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    {topViewingBlinkerChoiceUnit && (
                      <>
                        <Divider sx={{ my: 1.5 }} />
                        <Accordion
                          expanded={anticipateBlinkerSectionExpanded}
                          onChange={(_, expanded) => setAnticipateBlinkerSectionExpanded(expanded)}
                          disableGutters
                          elevation={0}
                          sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent', width: '100%' }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ px: 0, minHeight: 'unset', '& .MuiAccordionSummary-content': { my: 0.5 } }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: isIPad ? '0.95rem' : undefined }}>
                              Anticipate Blinker Unit Information
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ px: 0, pt: 0 }}>
                        {(() => {
                          const unitTypeLabel = getUnitTypeLabel(topViewingBlinkerChoiceUnit);
                          const npsPrice = topViewingBlinkerChoiceUnit.priceNps ?? topViewingBlinkerChoiceUnit.price;
                          const dpsPrice = topViewingBlinkerChoiceUnit.priceDps ?? null;
                          const npsPsf = topViewingBlinkerChoiceUnit.psfNps ?? topViewingBlinkerChoiceUnit.psf;
                          const dpsPsf = topViewingBlinkerChoiceUnit.psfDps ?? null;
                          const showDualPricing = isExecutiveCondoUnit(topViewingBlinkerChoiceUnit);
                          const blinkerInCompare = topViewingFloorPlansToCompare.some((fp) => fp.unit.id === topViewingBlinkerChoiceUnit.id);
                          const canToggleBlinkerCompare = blinkerInCompare || (
                            Boolean(topViewingBlinkerChoiceUnit.floorplan) && topViewingFloorPlansToCompare.length < 3
                          );
                          return (
                            <Box sx={{ p: isIPadPro129 ? 2.25 : { xs: 1.5, md: 2 }, bgcolor: `rgba(${blinkerAccentRgb}, 0.08)`, borderRadius: 1, border: `1px solid rgba(${blinkerAccentRgb}, 0.3)` }}>
                              <Box sx={{ display: 'grid', gridTemplateColumns: referenceInfoGridColumns, columnGap: isIPadPro129 ? 2 : { xs: 1.25, md: 1.5 }, rowGap: isIPadPro129 ? 0.9 : { xs: 0.5, md: 0.75 }, alignItems: 'start', minWidth: 0 }}>
                                <Typography variant="caption" sx={{ gridColumn: '1 / -1', fontWeight: 700, color: blinkerAccentColor, mb: 0.25 }}>
                                  {topViewingBlinkerChoiceUnit.projectName}
                                </Typography>
                                <Typography variant="caption" sx={referenceUnitLabelSx}>Block:</Typography>
                                <Typography variant="caption" sx={referenceUnitValueSx}>{formatBlockNumber(topViewingBlinkerChoiceUnit.blockNumber ?? null) || '-'}</Typography>
                                <Typography variant="caption" sx={referenceUnitLabelSx}>Unit:</Typography>
                                <Typography variant="caption" sx={referenceUnitValueSx}>{topViewingBlinkerChoiceUnit.unitNumber}</Typography>
                                <Typography variant="caption" sx={referenceUnitLabelSx}>Bedroom:</Typography>
                                <Typography variant="caption" sx={referenceUnitValueSx}>{`${topViewingBlinkerChoiceUnit.bedroom} BR`}</Typography>
                                {unitTypeLabel && (
                                  <>
                                    <Typography variant="caption" sx={referenceUnitLabelSx}>Type:</Typography>
                                    <Typography variant="caption" sx={referenceUnitValueSx}>{unitTypeLabel}</Typography>
                                  </>
                                )}
                                {topViewingBlinkerChoiceUnit.bathroom ? (
                                  <>
                                    <Typography variant="caption" sx={referenceUnitLabelSx}>Bathroom:</Typography>
                                    <Typography variant="caption" sx={referenceUnitValueSx}>{topViewingBlinkerChoiceUnit.bathroom}</Typography>
                                  </>
                                ) : null}
                                <Typography variant="caption" sx={referenceUnitLabelSx}>Area:</Typography>
                                <Typography variant="caption" sx={referenceUnitValueSx}>
                                  {topViewingBlinkerChoiceUnit.areaSqft > 0 ? `${formatNumber(topViewingBlinkerChoiceUnit.areaSqft)} sqft` : '-'}
                                </Typography>
                                {showDualPricing ? (
                                  <>
                                    {npsPrice > 0 && (
                                      <>
                                        <Typography variant="caption" sx={referenceUnitLabelSx}>NPS:</Typography>
                                        <Typography variant="caption" sx={referenceUnitValueSx}>{formatCurrency(npsPrice)}</Typography>
                                      </>
                                    )}
                                    <Typography variant="caption" sx={referenceUnitLabelSx}>NPS PSF:</Typography>
                                    <Typography variant="caption" sx={referenceUnitValueSx}>{npsPsf > 0 ? formatCurrency(npsPsf) : '-'}</Typography>
                                    <Typography variant="caption" sx={referenceUnitLabelSx}>DPS:</Typography>
                                    <Typography variant="caption" sx={referenceUnitValueSx}>{dpsPrice && dpsPrice > 0 ? formatCurrency(dpsPrice) : '-'}</Typography>
                                    <Typography variant="caption" sx={referenceUnitLabelSx}>DPS PSF:</Typography>
                                    <Typography variant="caption" sx={referenceUnitValueSx}>{dpsPsf && dpsPsf > 0 ? formatCurrency(dpsPsf) : '-'}</Typography>
                                  </>
                                ) : (
                                  <>
                                    {npsPrice > 0 && (
                                      <>
                                        <Typography variant="caption" sx={referenceUnitLabelSx}>Price:</Typography>
                                        <Typography variant="caption" sx={referenceUnitValueSx}>{formatCurrency(npsPrice)}</Typography>
                                      </>
                                    )}
                                    <Typography variant="caption" sx={referenceUnitLabelSx}>PSF:</Typography>
                                    <Typography variant="caption" sx={referenceUnitValueSx}>{npsPsf > 0 ? formatCurrency(npsPsf) : '-'}</Typography>
                                  </>
                                )}
                              </Box>
                              <Box sx={{ mt: 1.25 }}>
                                <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block', fontWeight: 600 }}>
                                  Matching Range
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: blinkerRangeRowAlignItems,
                                    gap: 0.5,
                                    width: '100%',
                                    minWidth: 0,
                                    mb: 0.1,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    component="div"
                                    sx={{
                                      color: 'var(--color-text-muted)',
                                      flex: '1 1 auto',
                                      minWidth: 0,
                                      pr: 0.5,
                                      lineHeight: 1.35,
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    Min. {formatCurrency(topViewingBlinkerRange?.min ?? 0)} ({topViewingBlinkerMinTolerance}%)
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      flexShrink: 0,
                                      gap: blinkerRangeTouchFriendly ? 0.35 : 0.2,
                                      pt: blinkerRangeButtonClusterPt,
                                    }}
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        const currentMin = topViewingBlinkerRange?.min ?? 0;
                                        setBlinkerMinPriceInput(currentMin > 0 ? Math.round(currentMin).toLocaleString('en-US') : '');
                                        setBlinkerMinPriceEditAnchor(event.currentTarget);
                                      }}
                                      sx={blinkerRangeControlIconSx}
                                    >
                                      <EditIcon sx={{ fontSize: blinkerRangeEditGlyphRem }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        setTopViewingBlinkerMinPriceOverride(null);
                                        setTopViewingBlinkerMinTolerance(Math.round(Math.max(-50, Math.min(0, topViewingBlinkerMinTolerance - 1))));
                                      }}
                                      disabled={topViewingBlinkerMinTolerance <= -50}
                                      sx={blinkerRangeControlIconSx}
                                      aria-label="Decrease minimum percentage"
                                    >
                                      <RemoveIcon sx={{ fontSize: blinkerRangeStepGlyphRem }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        setTopViewingBlinkerMinPriceOverride(null);
                                        setTopViewingBlinkerMinTolerance(Math.round(Math.max(-50, Math.min(0, topViewingBlinkerMinTolerance + 1))));
                                      }}
                                      disabled={topViewingBlinkerMinTolerance >= 0}
                                      sx={blinkerRangeControlIconSx}
                                      aria-label="Increase minimum percentage"
                                    >
                                      <AddIcon sx={{ fontSize: blinkerRangeStepGlyphRem }} />
                                    </IconButton>
                                  </Box>
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: blinkerRangeRowAlignItems,
                                    gap: 0.5,
                                    width: '100%',
                                    minWidth: 0,
                                    mt: 0.3,
                                    mb: 0.1,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    component="div"
                                    sx={{
                                      color: 'var(--color-text-muted)',
                                      flex: '1 1 auto',
                                      minWidth: 0,
                                      pr: 0.5,
                                      lineHeight: 1.35,
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    Max. {formatCurrency(topViewingBlinkerRange?.max ?? 0)} (+{topViewingBlinkerTolerance}%)
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      flexShrink: 0,
                                      gap: blinkerRangeTouchFriendly ? 0.35 : 0.2,
                                      pt: blinkerRangeButtonClusterPt,
                                    }}
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        const currentMax = topViewingBlinkerRange?.max ?? 0;
                                        setBlinkerMaxPriceInput(currentMax > 0 ? Math.round(currentMax).toLocaleString('en-US') : '');
                                        setBlinkerMaxPriceEditAnchor(event.currentTarget);
                                      }}
                                      sx={blinkerRangeControlIconSx}
                                    >
                                      <EditIcon sx={{ fontSize: blinkerRangeEditGlyphRem }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        setTopViewingBlinkerMaxPriceOverride(null);
                                        setTopViewingBlinkerTolerance(Math.round(Math.max(0, Math.min(50, topViewingBlinkerTolerance - 1))));
                                      }}
                                      disabled={topViewingBlinkerTolerance <= 0}
                                      sx={blinkerRangeControlIconSx}
                                      aria-label="Decrease maximum percentage"
                                    >
                                      <RemoveIcon sx={{ fontSize: blinkerRangeStepGlyphRem }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        setTopViewingBlinkerMaxPriceOverride(null);
                                        setTopViewingBlinkerTolerance(Math.round(Math.max(0, Math.min(50, topViewingBlinkerTolerance + 1))));
                                      }}
                                      disabled={topViewingBlinkerTolerance >= 50}
                                      sx={blinkerRangeControlIconSx}
                                      aria-label="Increase maximum percentage"
                                    >
                                      <AddIcon sx={{ fontSize: blinkerRangeStepGlyphRem }} />
                                    </IconButton>
                                  </Box>
                                </Box>
                                <Slider
                                  value={[topViewingBlinkerMinTolerance, topViewingBlinkerTolerance]}
                                  onChange={(_, value) => {
                                    if (!Array.isArray(value)) return;
                                    const [nextMinRaw, nextMaxRaw] = value;
                                    const nextMin = Math.round(Math.max(-50, Math.min(0, nextMinRaw)));
                                    const nextMax = Math.round(Math.max(0, Math.min(50, nextMaxRaw)));
                                    setTopViewingBlinkerMinPriceOverride(null);
                                    setTopViewingBlinkerMaxPriceOverride(null);
                                    setTopViewingBlinkerMinTolerance(nextMin);
                                    setTopViewingBlinkerTolerance(nextMax);
                                  }}
                                  min={-50}
                                  max={50}
                                  step={1}
                                  disableSwap
                                  valueLabelDisplay="auto"
                                  valueLabelFormat={(v) => `${v > 0 ? '+' : ''}${v}%`}
                                  getAriaLabel={(index) => (index === 0 ? 'Minimum matching tolerance' : 'Maximum matching tolerance')}
                                  sx={{
                                    color: blinkerAccentColor,
                                    mt: 0.1,
                                    '& .MuiSlider-thumb': { bgcolor: blinkerAccentColor },
                                    '& .MuiSlider-track': { border: 'none' },
                                    '& .MuiSlider-valueLabel': { bgcolor: blinkerAccentColor, color: blinkerValueLabelTextColor },
                                  }}
                                />
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={topViewingBlinkerSuperUpgrade}
                                      onChange={(e) => setTopViewingBlinkerSuperUpgrade(e.target.checked)}
                                      size="small"
                                      sx={{ color: blinkerAccentColor, '&.Mui-checked': { color: blinkerAccentColor }, py: 0.25 }}
                                    />
                                  }
                                  label={<Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>Super Upgrade {topViewingBlinkerRange ? `(${superUpgradeMatchCount} units)` : ''}</Typography>}
                                  sx={{ mt: 0.25, ml: -0.5 }}
                                />
                                <Popover
                                  open={Boolean(blinkerMinPriceEditAnchor)}
                                  anchorEl={blinkerMinPriceEditAnchor}
                                  onClose={() => setBlinkerMinPriceEditAnchor(null)}
                                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                >
                                  <Box sx={{ p: 1, minWidth: 230, display: 'grid', gap: 0.75 }}>
                                    <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                                      Enter desired min price
                                    </Typography>
                                    <TextField
                                      size="small"
                                      value={blinkerMinPriceInput}
                                      onChange={(e) => setBlinkerMinPriceInput(formatBlinkerPriceInput(e.target.value))}
                                      onKeyDown={(e) => {
                                        if (e.key !== 'Enter') return;
                                        const base = topViewingBlinkerRange?.base ?? 0;
                                        const parsedValue = Number(blinkerMinPriceInput.replace(/,/g, ''));
                                        if (!base || !Number.isFinite(parsedValue) || parsedValue <= 0) return;
                                        const clampedValue = Math.min(base, Math.max(0, parsedValue));
                                        const nextPercent = ((clampedValue / base) - 1) * 100;
                                        setTopViewingBlinkerMinTolerance(Math.round(Math.min(0, Math.max(-50, nextPercent))));
                                        setTopViewingBlinkerMinPriceOverride(clampedValue);
                                        setBlinkerMinPriceEditAnchor(null);
                                      }}
                                      placeholder="e.g. 1702080"
                                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                    />
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={() => {
                                        const base = topViewingBlinkerRange?.base ?? 0;
                                        const parsedValue = Number(blinkerMinPriceInput.replace(/,/g, ''));
                                        if (!base || !Number.isFinite(parsedValue) || parsedValue <= 0) return;
                                        const clampedValue = Math.min(base, Math.max(0, parsedValue));
                                        const nextPercent = ((clampedValue / base) - 1) * 100;
                                        setTopViewingBlinkerMinTolerance(Math.round(Math.min(0, Math.max(-50, nextPercent))));
                                        setTopViewingBlinkerMinPriceOverride(clampedValue);
                                        setBlinkerMinPriceEditAnchor(null);
                                      }}
                                    >
                                      Apply
                                    </Button>
                                  </Box>
                                </Popover>
                                <Popover
                                  open={Boolean(blinkerMaxPriceEditAnchor)}
                                  anchorEl={blinkerMaxPriceEditAnchor}
                                  onClose={() => setBlinkerMaxPriceEditAnchor(null)}
                                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                >
                                  <Box sx={{ p: 1, minWidth: 230, display: 'grid', gap: 0.75 }}>
                                    <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                                      Enter desired max price
                                    </Typography>
                                    <TextField
                                      size="small"
                                      value={blinkerMaxPriceInput}
                                      onChange={(e) => setBlinkerMaxPriceInput(formatBlinkerPriceInput(e.target.value))}
                                      onKeyDown={(e) => {
                                        if (e.key !== 'Enter') return;
                                        const base = topViewingBlinkerRange?.base ?? 0;
                                        const parsedValue = Number(blinkerMaxPriceInput.replace(/,/g, ''));
                                        if (!base || !Number.isFinite(parsedValue) || parsedValue <= 0) return;
                                        const clampedValue = Math.min(base * 1.5, Math.max(base, parsedValue));
                                        const nextPercent = ((clampedValue / base) - 1) * 100;
                                        setTopViewingBlinkerTolerance(Math.round(Math.max(0, Math.min(50, nextPercent))));
                                        setTopViewingBlinkerMaxPriceOverride(clampedValue);
                                        setBlinkerMaxPriceEditAnchor(null);
                                      }}
                                      placeholder="e.g. 3,719,360"
                                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                    />
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={() => {
                                        const base = topViewingBlinkerRange?.base ?? 0;
                                        const parsedValue = Number(blinkerMaxPriceInput.replace(/,/g, ''));
                                        if (!base || !Number.isFinite(parsedValue) || parsedValue <= 0) return;
                                        const clampedValue = Math.min(base * 1.5, Math.max(base, parsedValue));
                                        const nextPercent = ((clampedValue / base) - 1) * 100;
                                        setTopViewingBlinkerTolerance(Math.round(Math.max(0, Math.min(50, nextPercent))));
                                        setTopViewingBlinkerMaxPriceOverride(clampedValue);
                                        setBlinkerMaxPriceEditAnchor(null);
                                      }}
                                    >
                                      Apply
                                    </Button>
                                  </Box>
                                </Popover>
                                {showDualPricing && (
                                  <Typography variant="caption" sx={{ color: 'var(--color-warning-strong)', display: 'block', mt: 0.35, fontWeight: 600 }}>
                                    Note: Blinker matching uses NPS price for Executive Condo units.
                                  </Typography>
                                )}
                              </Box>
                              <Box sx={{ mt: 1, display: 'grid', gap: 0.5 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  fullWidth
                                  startIcon={<ImageIcon sx={{ fontSize: '0.8rem !important' }} />}
                                  disabled={!topViewingBlinkerChoiceUnit.floorplan}
                                  onClick={() => handleTopViewingUnitMenuViewFloorPlan(topViewingBlinkerChoiceUnit)}
                                  sx={{ fontSize: '0.65rem', py: 0.25 }}
                                >
                                  View Floor Plan
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  fullWidth
                                  startIcon={<ShoppingCartIcon sx={{ fontSize: '0.8rem !important' }} />}
                                  disabled={disableAddToCompareAction || !canToggleBlinkerCompare}
                                  onClick={() => {
                                    if (disableAddToCompareAction) return;
                                    handleTopViewingAddToUnitCompare(topViewingBlinkerChoiceUnit);
                                  }}
                                  sx={{ fontSize: '0.65rem', py: 0.25 }}
                                >
                                  {disableAddToCompareAction
                                    ? 'Add To Compare Cart'
                                    : blinkerInCompare ? 'Remove From Compare Cart' : `Add To Compare Cart (${topViewingFloorPlansToCompare.length}/3)`}
                                </Button>
                                {onOpenFloorPlanCompare && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<ShoppingCartIcon sx={{ fontSize: '0.8rem !important' }} />}
                                    disabled={topViewingFloorPlansToCompare.length === 0}
                                    onClick={onOpenFloorPlanCompare}
                                    sx={{ fontSize: '0.65rem', py: 0.25 }}
                                  >
                                    View Compare Cart
                                  </Button>
                                )}
                                <Button
                                  size="small"
                                  variant="outlined"
                                  fullWidth
                                  onClick={() => setTopViewingBlinkerChoiceUnit(null)}
                                  sx={{ fontSize: '0.65rem', py: 0.25 }}
                                >
                                  Clear
                                </Button>
                              </Box>
                            </Box>
                          );
                        })()}
                          </AccordionDetails>
                        </Accordion>
                      </>
                    )}
                    {/* Reference Unit Display */}
                    {topViewingReferenceUnit && (
                      <>
                        <Divider sx={{ my: 1.5 }} />
                        <Accordion
                          expanded={referenceUnitSectionExpanded}
                          onChange={(_, expanded) => setReferenceUnitSectionExpanded(expanded)}
                          disableGutters
                          elevation={0}
                          sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent', width: '100%' }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ px: 0, minHeight: 'unset', '& .MuiAccordionSummary-content': { my: 0.5 } }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: isIPad ? '0.95rem' : undefined }}>
                              Reference Unit Information
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ px: 0, pt: 0 }}>
                        {(() => {
                          const unitTypeLabel = getUnitTypeLabel(topViewingReferenceUnit);
                          const npsPrice = topViewingReferenceUnit.priceNps ?? topViewingReferenceUnit.price;
                          const dpsPrice = topViewingReferenceUnit.priceDps ?? null;
                          const npsPsf = topViewingReferenceUnit.psfNps ?? topViewingReferenceUnit.psf;
                          const dpsPsf = topViewingReferenceUnit.psfDps ?? null;
                          const showDualPricing = isReferenceExecutiveCondo && !isGuesstimationReference;
                          const isReferencePricePromo = !isGuesstimationReference && topViewingReferenceUnit.id > 0 && (pricePromoUnitIds.has(topViewingReferenceUnit.id) || (topViewingReferenceUnit.priceChange ?? 0) < 0);
                          return (
                        <Box sx={{ p: isIPadPro129 ? 2.25 : { xs: 1.5, md: 2 }, bgcolor: 'rgba(144, 202, 249, 0.2)', borderRadius: 1, border: '1px solid rgba(100, 181, 246, 0.45)' }}>
                          <Box sx={{ display: 'grid', gridTemplateColumns: referenceInfoGridColumns, columnGap: isIPadPro129 ? 2 : { xs: 1.25, md: 1.5 }, rowGap: isIPadPro129 ? 0.9 : { xs: 0.5, md: 0.75 }, alignItems: 'start', minWidth: 0 }}>
                            <Typography variant="caption" sx={{ gridColumn: '1 / -1', fontWeight: 700, color: 'var(--color-accent)', mb: 0.25 }}>
                              {topViewingReferenceUnit.projectName}
                            </Typography>
                            <Typography variant="caption" sx={referenceUnitLabelSx}>Block:</Typography>
                            <Typography variant="caption" sx={referenceUnitValueSx}>
                              {isGuesstimationReference ? 'N/A' : (formatBlockNumber(topViewingReferenceUnit.blockNumber ?? null) || '-')}
                            </Typography>
                            <Typography variant="caption" sx={referenceUnitLabelSx}>Unit:</Typography>
                            <Typography variant="caption" sx={referenceUnitValueSx}>{topViewingReferenceUnit.unitNumber}</Typography>
                            <Typography variant="caption" sx={referenceUnitLabelSx}>Bedroom:</Typography>
                            <Typography variant="caption" sx={referenceUnitValueSx}>
                              {isGuesstimationReference
                                ? (topViewingReferenceUnit.bedroom > 0 ? `${topViewingReferenceUnit.bedroom} BR` : 'N/A')
                                : `${topViewingReferenceUnit.bedroom} BR`}
                            </Typography>
                            {unitTypeLabel && (
                              <>
                                <Typography variant="caption" sx={referenceUnitLabelSx}>Type:</Typography>
                                <Typography variant="caption" sx={referenceUnitValueSx}>{unitTypeLabel}</Typography>
                              </>
                            )}
                            {topViewingReferenceUnit.bathroom ? (
                              <>
                                <Typography variant="caption" sx={referenceUnitLabelSx}>Bathroom:</Typography>
                                <Typography variant="caption" sx={referenceUnitValueSx}>{topViewingReferenceUnit.bathroom}</Typography>
                              </>
                            ) : null}
                            <Typography variant="caption" sx={referenceUnitLabelSx}>Area:</Typography>
                            <Typography variant="caption" sx={referenceUnitValueSx}>
                              {topViewingReferenceUnit.areaSqft > 0 ? `${formatNumber(topViewingReferenceUnit.areaSqft)} sqft` : '-'}
                            </Typography>
                            {showDualPricing ? (
                              <>
                                {npsPrice > 0 && (
                                  <>
                                    <Typography variant="caption" sx={referenceUnitLabelSx}>NPS:</Typography>
                                    <Typography variant="caption" onClick={isReferencePricePromo ? () => handleOpenPriceChart(topViewingReferenceUnit.id, 'price', true) : undefined} sx={{ ...referenceUnitValueSx, ...(isReferencePricePromo ? { color: 'var(--color-success)', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' } : {}) }}>{formatCurrency(npsPrice)}</Typography>
                                  </>
                                )}
                                <Typography variant="caption" sx={referenceUnitLabelSx}>NPS PSF:</Typography>
                                <Typography variant="caption" sx={referenceUnitValueSx}>{npsPsf > 0 ? formatCurrency(npsPsf) : '-'}</Typography>
                                <Typography variant="caption" sx={referenceUnitLabelSx}>DPS:</Typography>
                                <Typography variant="caption" sx={referenceUnitValueSx}>{dpsPrice && dpsPrice > 0 ? formatCurrency(dpsPrice) : '-'}</Typography>
                                <Typography variant="caption" sx={referenceUnitLabelSx}>DPS PSF:</Typography>
                                <Typography variant="caption" sx={referenceUnitValueSx}>{dpsPsf && dpsPsf > 0 ? formatCurrency(dpsPsf) : '-'}</Typography>
                              </>
                            ) : (
                              <>
                                {npsPrice > 0 && (
                                  <>
                                    <Typography variant="caption" sx={referenceUnitLabelSx}>Price:</Typography>
                                    <Typography variant="caption" onClick={isReferencePricePromo ? () => handleOpenPriceChart(topViewingReferenceUnit.id, 'price', true) : undefined} sx={{ ...referenceUnitValueSx, ...(isReferencePricePromo ? { color: 'var(--color-success)', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' } : {}) }}>{formatCurrency(npsPrice)}</Typography>
                                  </>
                                )}
                                <Typography variant="caption" sx={referenceUnitLabelSx}>PSF:</Typography>
                                <Typography variant="caption" sx={referenceUnitValueSx}>{npsPsf > 0 ? formatCurrency(npsPsf) : '-'}</Typography>
                              </>
                            )}
                          </Box>
                          <Box sx={{ mt: 1 }}>
                            {(() => {
                              const referenceUnitInCompare = topViewingFloorPlansToCompare.some((fp) => fp.unit.id === topViewingReferenceUnit.id);
                              const canToggleReferenceCompare = referenceUnitInCompare || (
                                Boolean(topViewingReferenceUnit.floorplan) && topViewingFloorPlansToCompare.length < 3
                              );
                              return (
                                <Box sx={{ display: 'grid', gap: 0.5 }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<ImageIcon sx={{ fontSize: '0.8rem !important' }} />}
                                    disabled={!topViewingReferenceUnit.floorplan}
                                    onClick={() => handleTopViewingUnitMenuViewFloorPlan(topViewingReferenceUnit)}
                                    sx={{ fontSize: '0.65rem', py: 0.25 }}
                                  >
                                    View Floor Plan
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<ShoppingCartIcon sx={{ fontSize: '0.8rem !important' }} />}
                                    disabled={disableAddToCompareAction || !canToggleReferenceCompare}
                                    onClick={() => {
                                      if (disableAddToCompareAction) return;
                                      handleTopViewingAddToUnitCompare(topViewingReferenceUnit);
                                    }}
                                    sx={{ fontSize: '0.65rem', py: 0.25 }}
                                  >
                                    {disableAddToCompareAction
                                      ? 'Add To Compare Cart'
                                      : referenceUnitInCompare ? 'Remove From Compare Cart' : `Add To Compare Cart (${topViewingFloorPlansToCompare.length}/3)`}
                                  </Button>
                                  {onOpenFloorPlanCompare && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      fullWidth
                                      startIcon={<ShoppingCartIcon sx={{ fontSize: '0.8rem !important' }} />}
                                      disabled={topViewingFloorPlansToCompare.length === 0}
                                      onClick={onOpenFloorPlanCompare}
                                      sx={{ fontSize: '0.65rem', py: 0.25 }}
                                    >
                                      View Compare Cart
                                    </Button>
                                  )}
                                </Box>
                              );
                            })()}
                            {onAISummaryClick && (
                              <Box sx={{ display: 'none', mb: 0.5, position: 'relative', overflow: 'hidden' }}>
                                <Box
                                  component="span"
                                  sx={{
                                    position: 'absolute',
                                    top: 6,
                                    right: -18,
                                    zIndex: 1,
                                    px: 2.5,
                                    py: 0.35,
                                    transform: 'rotate(45deg)',
                                    bgcolor: 'var(--color-accent)',
                                    color: 'var(--color-text-inverse)',
                                    fontWeight: 700,
                                    fontSize: '0.45rem',
                                    letterSpacing: '0.02em',
                                    whiteSpace: 'nowrap',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                  }}
                                >
                                  Coming!
                                </Box>
                                <Button
                                  size="small"
                                  variant="contained"
                                  fullWidth
                                  disabled
                                  startIcon={<AutoAwesomeIcon sx={{ fontSize: '0.8rem !important' }} />}
                                  onClick={onAISummaryClick}
                                  sx={{
                                    bgcolor: 'var(--color-accent)',
                                    color: 'var(--color-text-inverse)',
                                    fontWeight: 600,
                                    fontSize: '0.65rem',
                                    py: 0.25,
                                    textTransform: 'none',
                                    '&:hover': { bgcolor: 'var(--color-accent-strong)' },
                                  }}
                                >
                                  SMART AI SUMMARY
                                </Button>
                              </Box>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              fullWidth
                              onClick={() => setTopViewingReferenceUnit(null)}
                              sx={{ mt: 0.5, fontSize: '0.65rem', py: 0.25 }}
                            >
                              Clear
                            </Button>
                          </Box>
                        </Box>
                          );
                        })()}
                          </AccordionDetails>
                        </Accordion>
                      </>
                    )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Dialog
              open={priceMatrixGridOpen}
              onClose={() => setPriceMatrixGridOpen(false)}
              fullWidth
              maxWidth="lg"
              PaperProps={{
                sx: {
                  bgcolor: 'var(--color-surface)',
                  color: 'rgba(var(--color-text-rgb), 0.9)',
                  minHeight: '65vh',
                },
              }}
            >
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Price Matrix Simulation
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
                    Update `STATUS` in-app for demo simulation. Paste a vertical column of values into any status cell. Prices stay sourced from the master price matrix.
                  </Typography>
                </Box>
                <IconButton onClick={() => setPriceMatrixGridOpen(false)} sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent
                dividers
                sx={{
                  borderColor: 'rgba(var(--color-text-rgb), 0.08)',
                  '& .MuiTableCell-root': {
                    borderColor: 'rgba(var(--color-text-rgb), 0.08)',
                    color: 'rgba(var(--color-text-rgb), 0.9)',
                  },
                  '& .MuiTableHead-root .MuiTableCell-head': {
                    backgroundColor: 'var(--color-surface)',
                    color: 'var(--color-text-strong)',
                    fontWeight: 600,
                  },
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Search units"
                    value={priceMatrixGridQuery}
                    onChange={(event) => {
                      setPriceMatrixGridQuery(event.target.value);
                      setPriceMatrixGridPage(0);
                    }}
                    placeholder="Unit / floor / stack / status"
                  />
                  <Button
                    variant="outlined"
                    color="info"
                    onClick={handleResetPriceMatrixSimulation}
                    disabled={!priceMatrixSimulationSession?.units?.length}
                  >
                    Reset Simulation
                  </Button>
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => {
                      if (!currentProjectId || !onCancelPriceMatrixSimulation) return;
                      setPriceMatrixGridOpen(false);
                      void onCancelPriceMatrixSimulation(currentProjectId);
                    }}
                  >
                    Cancel Simulation
                  </Button>
                </Stack>
                {priceMatrixCheckedUnitIds.size > 0 && (
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ alignSelf: 'center', color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                      {priceMatrixCheckedUnitIds.size} selected
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      color="error"
                      onClick={() => {
                        applyPriceMatrixStatuses(
                          Array.from(priceMatrixCheckedUnitIds).map((unitId) => ({ unitId, status: 'SOLD' }))
                        );
                        setPriceMatrixCheckedUnitIds(new Set());
                      }}
                    >
                      Mark as SOLD
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      color="success"
                      onClick={() => {
                        applyPriceMatrixStatuses(
                          Array.from(priceMatrixCheckedUnitIds).map((unitId) => ({ unitId, status: 'AVAILABLE' }))
                        );
                        setPriceMatrixCheckedUnitIds(new Set());
                      }}
                    >
                      Mark as AVAILABLE
                    </Button>
                  </Stack>
                )}
                <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'rgba(var(--color-text-rgb), 0.65)' }}>
                  Accepted values: `SOLD`, `AVAILABLE`. Click one status cell and paste to update it, or shift-click another status cell to select a range and paste a single value to fill the whole selection.
                </Typography>
                <TableContainer sx={{ maxHeight: '52vh' }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={priceMatrixCheckedUnitIds.size > 0 && priceMatrixCheckedUnitIds.size < filteredPriceMatrixGridRows.length}
                            checked={filteredPriceMatrixGridRows.length > 0 && priceMatrixCheckedUnitIds.size === filteredPriceMatrixGridRows.length}
                            onChange={(event) => {
                              if (event.target.checked) {
                                setPriceMatrixCheckedUnitIds(new Set(filteredPriceMatrixGridRows.map((r) => r.id)));
                              } else {
                                setPriceMatrixCheckedUnitIds(new Set());
                              }
                            }}
                            sx={{ color: 'rgba(var(--color-text-rgb), 0.5)' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            Unit
                            <IconButton size="small" onClick={(e) => handlePriceMatrixGridOpenPopover(e, 'unitNumber')} sx={{ color: hasPriceMatrixGridActiveFilter('unitNumber') ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)' }}>
                              <MenuIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            Floor
                            <IconButton size="small" onClick={(e) => handlePriceMatrixGridOpenPopover(e, 'floor')} sx={{ color: hasPriceMatrixGridActiveFilter('floor') ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)' }}>
                              <MenuIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            Stack
                            <IconButton size="small" onClick={(e) => handlePriceMatrixGridOpenPopover(e, 'stack')} sx={{ color: hasPriceMatrixGridActiveFilter('stack') ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)' }}>
                              <MenuIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            Bedroom
                            <IconButton size="small" onClick={(e) => handlePriceMatrixGridOpenPopover(e, 'bedroom')} sx={{ color: hasPriceMatrixGridActiveFilter('bedroom') ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)' }}>
                              <MenuIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            Area
                            <IconButton size="small" onClick={(e) => handlePriceMatrixGridOpenPopover(e, 'areaSqft')} sx={{ color: hasPriceMatrixGridActiveFilter('areaSqft') ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)' }}>
                              <MenuIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            Price
                            <IconButton size="small" onClick={(e) => handlePriceMatrixGridOpenPopover(e, 'price')} sx={{ color: hasPriceMatrixGridActiveFilter('price') ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)' }}>
                              <MenuIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            PSF
                            <IconButton size="small" onClick={(e) => handlePriceMatrixGridOpenPopover(e, 'psf')} sx={{ color: hasPriceMatrixGridActiveFilter('psf') ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)' }}>
                              <MenuIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ minWidth: 150 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            Status
                            <IconButton size="small" onClick={(e) => handlePriceMatrixGridOpenPopover(e, 'status')} sx={{ color: hasPriceMatrixGridActiveFilter('status') ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)' }}>
                              <MenuIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedPriceMatrixGridRows.map((row, localRowIndex) => (
                        <TableRow key={row.id} hover selected={priceMatrixCheckedUnitIds.has(row.id)}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={priceMatrixCheckedUnitIds.has(row.id)}
                              onChange={(event) => {
                                setPriceMatrixCheckedUnitIds((prev) => {
                                  const next = new Set(prev);
                                  if (event.target.checked) {
                                    next.add(row.id);
                                  } else {
                                    next.delete(row.id);
                                  }
                                  return next;
                                });
                              }}
                              sx={{ color: 'rgba(var(--color-text-rgb), 0.5)' }}
                            />
                          </TableCell>
                          <TableCell>{row.unitNumber}</TableCell>
                          <TableCell align="right">{row.floor ?? '-'}</TableCell>
                          <TableCell align="right">{row.stack ?? '-'}</TableCell>
                          <TableCell align="right">{row.bedroom ?? '-'}</TableCell>
                          <TableCell align="right">
                            {row.areaSqft != null && Number.isFinite(row.areaSqft) ? `${formatNumber(row.areaSqft)} sqft` : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {row.price != null && Number.isFinite(row.price) && row.price > 0 ? formatCurrency(row.price) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {row.psf != null && Number.isFinite(row.psf) && row.psf > 0 ? `$${formatNumber(row.psf, 2)}` : '-'}
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              fullWidth
                              value={priceMatrixStatusDrafts[String(row.id)] ?? row.status}
                              placeholder="AVAILABLE / SOLD"
                              onClick={(event) => {
                                const absoluteRowIndex = (priceMatrixGridPage * priceMatrixGridRowsPerPage) + localRowIndex;
                                selectPriceMatrixStatusCell(absoluteRowIndex, event.shiftKey);
                              }}
                              onChange={(event) => {
                                const nextValue = event.target.value.toUpperCase();
                                setPriceMatrixStatusDrafts((prev) => ({
                                  ...prev,
                                  [String(row.id)]: nextValue,
                                }));
                              }}
                              onBlur={(event) => {
                                commitPriceMatrixStatusDraft(row.id, event.target.value);
                              }}
                              onKeyDown={(event) => {
                                if (event.key !== 'Enter') return;
                                event.preventDefault();
                                commitPriceMatrixStatusDraft(row.id, (event.target as HTMLInputElement).value);
                              }}
                              onPaste={(event) => {
                                const pastedText = event.clipboardData.getData('text');
                                const normalizedSingleValue = normalizePriceMatrixStatusInput(pastedText);
                                const absoluteRowIndex = (priceMatrixGridPage * priceMatrixGridRowsPerPage) + localRowIndex;
                                const hasRangeSelection = selectedPriceMatrixStatusUnitIds.size > 1
                                  && selectedPriceMatrixStatusUnitIds.has(row.id);

                                if (normalizedSingleValue && hasRangeSelection) {
                                  event.preventDefault();
                                  applyPriceMatrixStatuses(
                                    filteredPriceMatrixGridRows
                                      .filter((candidate) => selectedPriceMatrixStatusUnitIds.has(candidate.id))
                                      .map((candidate) => ({ unitId: candidate.id, status: normalizedSingleValue }))
                                  );
                                  return;
                                }

                                if (normalizedSingleValue) {
                                  event.preventDefault();
                                  selectPriceMatrixStatusCell(absoluteRowIndex, false);
                                  commitPriceMatrixStatusDraft(row.id, normalizedSingleValue);
                                  return;
                                }

                                if (!pastedText.includes('\n') && !pastedText.includes('\t')) {
                                  return;
                                }
                                event.preventDefault();
                                selectPriceMatrixStatusCell(absoluteRowIndex, false);
                                handlePastePriceMatrixStatuses(absoluteRowIndex, pastedText);
                              }}
                              sx={selectedPriceMatrixStatusUnitIds.has(row.id) ? {
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: 'rgba(var(--color-info-rgb), 0.12)',
                                  '& fieldset': {
                                    borderColor: 'var(--color-info)',
                                  },
                                },
                              } : undefined}
                              inputProps={{
                                style: { textTransform: 'uppercase' },
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {paginatedPriceMatrixGridRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'rgba(var(--color-text-rgb), 0.65)' }}>
                            No matched matrix units found.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Popover
                  open={Boolean(priceMatrixGridPopoverAnchor)}
                  anchorEl={priceMatrixGridPopoverAnchor?.element}
                  onClose={handlePriceMatrixGridClosePopover}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  {priceMatrixGridPopoverAnchor && (
                    <Box sx={{ p: 2, minWidth: 260 }}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1, color: 'var(--color-info)', fontWeight: 500 }}>
                          Sort
                        </Typography>
                        <ToggleButtonGroup
                          value={getPriceMatrixGridSortDirection(priceMatrixGridPopoverAnchor.column)}
                          exclusive
                          onChange={(_, value) => {
                            if (value !== null) {
                              handlePriceMatrixGridSortChange(priceMatrixGridPopoverAnchor.column, value);
                            }
                          }}
                          fullWidth
                          sx={{
                            '& .MuiToggleButtonGroup-grouped': {
                              border: '1px solid rgba(var(--color-shadow-rgb), 0.12)',
                              '&:not(:first-of-type)': {
                                borderLeft: '1px solid rgba(var(--color-shadow-rgb), 0.12)',
                                marginLeft: 0,
                              },
                              '&.Mui-selected': {
                                backgroundColor: 'var(--color-info)',
                                color: 'var(--color-text-strong)',
                              },
                            },
                          }}
                        >
                          <ToggleButton value="default" sx={{ flex: 1, py: 0.75 }}>Default</ToggleButton>
                          <ToggleButton value="asc" sx={{ flex: 1, py: 0.75 }}>Asc</ToggleButton>
                          <ToggleButton value="desc" sx={{ flex: 1, py: 0.75 }}>Desc</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" sx={{ color: 'var(--color-info)', fontWeight: 500, mr: 1 }}>
                            Filter
                          </Typography>
                          <FilterIcon sx={{ fontSize: 18, color: 'var(--color-info)' }} />
                        </Box>
                        {priceMatrixGridPopoverAnchor.column === 'unitNumber' || priceMatrixGridPopoverAnchor.column === 'status' ? (
                          <Box>
                            {(() => {
                              const textColumn = priceMatrixGridPopoverAnchor.column as 'unitNumber' | 'status';
                              return (
                                <>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder={`Filter ${textColumn === 'unitNumber' ? 'Unit' : 'Status'}`}
                              value={textColumn === 'unitNumber'
                                ? priceMatrixGridColumnFilters.unitNumber
                                : priceMatrixGridColumnFilters.status}
                              onChange={(event) => setPriceMatrixGridTextFilter(
                                textColumn,
                                event.target.value
                              )}
                              InputProps={{
                                endAdornment: hasPriceMatrixGridActiveFilter(textColumn) && (
                                  <InputAdornment position="end">
                                    <IconButton
                                      size="small"
                                      onClick={() => setPriceMatrixGridTextFilter(textColumn, '')}
                                      sx={{ p: 0.5 }}
                                    >
                                      <Typography variant="caption" sx={{ fontSize: 12 }}>✕</Typography>
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                            />
                            <Button
                              size="small"
                              onClick={() => setPriceMatrixGridTextFilter(textColumn, '')}
                              sx={{ mt: 1 }}
                              fullWidth
                              disabled={!hasPriceMatrixGridActiveFilter(textColumn)}
                            >
                              Clear Filter
                            </Button>
                                </>
                              );
                            })()}
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {(() => {
                              const column = priceMatrixGridPopoverAnchor.column as 'floor' | 'stack' | 'bedroom' | 'areaSqft' | 'price' | 'psf';
                              const shouldFormatThousands = column === 'price' || column === 'psf';
                              const minKey = `${column}Min` as keyof typeof priceMatrixGridColumnFilters;
                              const maxKey = `${column}Max` as keyof typeof priceMatrixGridColumnFilters;
                              const minRaw = priceMatrixGridColumnFilters[minKey];
                              const maxRaw = priceMatrixGridColumnFilters[maxKey];
                              return (
                                <>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type={shouldFormatThousands ? 'text' : 'number'}
                                    inputProps={shouldFormatThousands ? { inputMode: 'decimal' } : undefined}
                                    label="Min"
                                    value={shouldFormatThousands ? formatThousandsInput(minRaw) : minRaw}
                                    onChange={(event) => setPriceMatrixGridRangeFilter(column, 'min', event.target.value)}
                                  />
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type={shouldFormatThousands ? 'text' : 'number'}
                                    inputProps={shouldFormatThousands ? { inputMode: 'decimal' } : undefined}
                                    label="Max"
                                    value={shouldFormatThousands ? formatThousandsInput(maxRaw) : maxRaw}
                                    onChange={(event) => setPriceMatrixGridRangeFilter(column, 'max', event.target.value)}
                                  />
                                  <Button
                                    size="small"
                                    onClick={() => {
                                      setPriceMatrixGridRangeFilter(column, 'min', '');
                                      setPriceMatrixGridRangeFilter(column, 'max', '');
                                    }}
                                    fullWidth
                                    disabled={!hasPriceMatrixGridActiveFilter(priceMatrixGridPopoverAnchor.column)}
                                  >
                                    Clear Filter
                                  </Button>
                                </>
                              );
                            })()}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                </Popover>
                <TablePagination
                  component="div"
                  count={filteredPriceMatrixGridRows.length}
                  page={priceMatrixGridPage}
                  onPageChange={(_, nextPage) => setPriceMatrixGridPage(nextPage)}
                  rowsPerPage={priceMatrixGridRowsPerPage}
                  onRowsPerPageChange={(event) => {
                    setPriceMatrixGridRowsPerPage(parseInt(event.target.value, 10));
                    setPriceMatrixGridPage(0);
                  }}
                  rowsPerPageOptions={[25, 50, 100]}
                  sx={{ mt: 1, color: 'rgba(var(--color-text-rgb), 0.9)' }}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setPriceMatrixGridOpen(false)}>Close</Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={guesstimationDialogOpen}
              onClose={closeGuesstimationDialog}
              fullWidth
              maxWidth="xs"
            >
              <DialogTitle>
                {guesstimationDialogMode === 'indicative' ? 'Indicative Price' : 'Guess-timation Price'}
                {guesstimationSelectedBedroom ? ` - ${guesstimationSelectedBedroom} BR` : ''}
                {guesstimationDialogMode === 'indicative' && guesstimationIndicativePriceLabel
                  ? ` (${guesstimationIndicativePriceLabel})`
                  : ''}
              </DialogTitle>
              <DialogContent>
                <Typography variant="body2" sx={{ mb: 2, color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                  Choose a size and enter a temporary price reference. Budget matched units will use this price.
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Size (sqft)</InputLabel>
                  <Select
                    value={guesstimationSelectedSize}
                    label="Size (sqft)"
                    onChange={(event) => setGuesstimationSelectedSize(event.target.value as number)}
                  >
                    {(guesstimationBedroomOptions.find((opt) => opt.bedroom === guesstimationSelectedBedroom)?.sizes ?? []).map((size) => (
                      <MenuItem key={size} value={size}>
                        {formatNumber(size)} sqft
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  autoComplete="off"
                  fullWidth
                  label="Indicative Price"
                  value={formatThousandsInput(guesstimationPriceInput)}
                  onChange={(event) => setGuesstimationPriceInput(event.target.value.replace(/,/g, '').replace(/[^\d.]/g, ''))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  inputProps={{
                    autoComplete: 'off',
                  }}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={closeGuesstimationDialog}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={confirmGuesstimationPrice}
                  disabled={
                    !selectedReferenceProjectName
                    || !(Number(guesstimationPriceInput) > 0)
                    || !(Number(guesstimationSelectedSize) > 0)
                  }
                >
                  Confirm
                </Button>
              </DialogActions>
            </Dialog>

            {/* Unit Menu */}
            <Menu
              anchorReference="anchorPosition"
              anchorPosition={topViewingUnitMenuAnchor ? { top: topViewingUnitMenuAnchor.mouseY, left: topViewingUnitMenuAnchor.mouseX } : undefined}
              open={Boolean(topViewingUnitMenuAnchor)}
              onClose={() => setTopViewingUnitMenuAnchor(null)}
            >
              <MenuItem disabled sx={{ opacity: 1 }}>
                <ListItemText
                  primary={`Unit ${topViewingUnitMenuAnchor?.unit?.unitNumber ?? '-'}`}
                  secondary={topViewingUnitMenuAnchor?.unit?.projectName ?? ''}
                  primaryTypographyProps={{ sx: { fontWeight: 700, color: 'var(--color-text-strong)' } }}
                  secondaryTypographyProps={{ sx: { color: 'var(--color-text-muted)' } }}
                />
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  if (!topViewingUnitMenuAnchor) return;
                  handleTopViewingUnitMenuViewFloorPlan(topViewingUnitMenuAnchor.unit);
                  setTopViewingUnitMenuAnchor(null);
                }}
              >
                <ListItemIcon>
                  <ImageIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>View Floor Plan</ListItemText>
              </MenuItem>
              <MenuItem
                data-tutorial="set-reference-menu-item"
                disabled={Boolean(topViewingUnitMenuSetReferenceDisabledReason)}
                onClick={() => {
                  if (!topViewingUnitMenuAnchor) return;
                  if (topViewingUnitMenuSetReferenceDisabledReason) return;
                  handleTopViewingUnitMenuSetReference(topViewingUnitMenuAnchor.unit);
                  setTopViewingUnitMenuAnchor(null);
                }}
              >
                <ListItemIcon>
                  <BookmarkIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={topViewingUnitMenuSetReferenceDisabledReason?.main ?? 'Set as Reference Unit'}
                  secondary={topViewingUnitMenuSetReferenceDisabledReason?.sub}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </MenuItem>
              <MenuItem
                disabled={Boolean(topViewingUnitMenuSetBlinkerDisabledReason)}
                onClick={() => {
                  if (!topViewingUnitMenuAnchor) return;
                  if (topViewingUnitMenuSetBlinkerDisabledReason) return;
                  handleTopViewingUnitMenuSetBlinkerChoice(topViewingUnitMenuAnchor.unit);
                  setTopViewingUnitMenuAnchor(null);
                }}
              >
                <ListItemIcon>
                  <VisibilityIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={topViewingUnitMenuSetBlinkerDisabledReason?.main ?? 'Set as Anticipate Blinker Unit'}
                  secondary={topViewingUnitMenuSetBlinkerDisabledReason?.sub}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </MenuItem>
              <MenuItem
                disabled={
                  disableAddToCompareAction ||
                  !topViewingUnitMenuAnchor?.unit?.floorplan ||
                  (topViewingFloorPlansToCompare.length >= 3 &&
                    !topViewingFloorPlansToCompare.some((fp) => fp.unit.id === topViewingUnitMenuAnchor?.unit.id))
                }
                onClick={() => {
                  if (!topViewingUnitMenuAnchor) return;
                  if (disableAddToCompareAction) return;
                  handleTopViewingAddToUnitCompare(topViewingUnitMenuAnchor.unit);
                  setTopViewingUnitMenuAnchor(null);
                }}
              >
                <ListItemIcon>
                  <ShoppingCartIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  {disableAddToCompareAction
                    ? 'Add to Compare'
                    : topViewingUnitMenuAnchor && topViewingFloorPlansToCompare.some((fp) => fp.unit.id === topViewingUnitMenuAnchor.unit.id)
                      ? 'Remove from Compare'
                      : `Add to Compare (${topViewingFloorPlansToCompare.length}/3)`}
                </ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  if (!topViewingUnitMenuAnchor) return;
                  const unit = topViewingUnitMenuAnchor.unit;
                  handleShortlistAction({
                    unitId: unit.id,
                    label: `${unit.projectName || ''} ${unit.unitNumber || ''}`.trim(),
                    floorplan: unit.floorplan || null,
                  });
                  setTopViewingUnitMenuAnchor(null);
                }}
              >
                <ListItemIcon>
                  {topViewingUnitMenuAnchor && shortlistByUnitId.has(topViewingUnitMenuAnchor.unit.id)
                    ? <StarIcon fontSize="small" sx={{ color: '#ffb300' }} />
                    : <StarBorderIcon fontSize="small" />}
                </ListItemIcon>
                <ListItemText>
                  {topViewingUnitMenuAnchor && shortlistByUnitId.has(topViewingUnitMenuAnchor.unit.id)
                    ? 'Remove from Shortlist'
                    : 'Add to Shortlist'}
                </ListItemText>
              </MenuItem>
              {priceMatrixSimulationSession && topViewingUnitMenuAnchor && (() => {
                const anchoredUnitStatus = (topViewingUnitMenuAnchor.unit.unitStatus || '').toString().toLowerCase();
                const isCurrentlySold = anchoredUnitStatus === 'sold';
                return (
                  <>
                    <Divider />
                    <MenuItem
                      onClick={() => {
                        if (!topViewingUnitMenuAnchor) return;
                        applyPriceMatrixStatuses([{
                          unitId: topViewingUnitMenuAnchor.unit.id,
                          status: isCurrentlySold ? 'AVAILABLE' : 'SOLD',
                        }]);
                        setTopViewingUnitMenuAnchor(null);
                      }}
                    >
                      <ListItemIcon>
                        <ShoppingCartIcon fontSize="small" sx={{ color: isCurrentlySold ? 'var(--color-success)' : 'var(--color-danger)' }} />
                      </ListItemIcon>
                      <ListItemText>
                        {isCurrentlySold ? 'Mark as Available' : 'Mark as Sold'}
                      </ListItemText>
                    </MenuItem>
                  </>
                );
              })()}
            </Menu>

            <Menu
              anchorReference="anchorPosition"
              anchorPosition={sectionUnitMenuAnchor ? { top: sectionUnitMenuAnchor.mouseY, left: sectionUnitMenuAnchor.mouseX } : undefined}
              open={Boolean(sectionUnitMenuAnchor)}
              onClose={() => setSectionUnitMenuAnchor(null)}
            >
              <MenuItem disabled sx={{ opacity: 1 }}>
                <ListItemText
                  primary={`Unit ${sectionUnitMenuAnchor?.unit?.unitNumber ?? '-'}`}
                  secondary={sectionUnitMenuAnchor?.unit?.projectName ?? ''}
                  primaryTypographyProps={{ sx: { fontWeight: 700, color: 'var(--color-text-strong)' } }}
                  secondaryTypographyProps={{ sx: { color: 'var(--color-text-muted)' } }}
                />
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  if (!sectionUnitMenuAnchor) return;
                  handleTopViewingUnitMenuViewFloorPlan(sectionUnitMenuAnchor.unit);
                  setSectionUnitMenuAnchor(null);
                }}
              >
                <ListItemIcon>
                  <ImageIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>View Floor Plan</ListItemText>
              </MenuItem>
              {sectionUnitMenuCanSetReference && (
                <MenuItem
                  disabled={Boolean(sectionUnitMenuSetReferenceDisabledReason)}
                  onClick={() => {
                    if (!sectionUnitMenuAnchor) return;
                    if (sectionUnitMenuSetReferenceDisabledReason) return;
                    handleTopViewingUnitMenuSetReference(sectionUnitMenuAnchor.unit);
                    setSectionUnitMenuAnchor(null);
                  }}
                >
                  <ListItemIcon>
                    <BookmarkIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={sectionUnitMenuSetReferenceDisabledReason?.main ?? 'Set as Reference Unit'}
                    secondary={sectionUnitMenuSetReferenceDisabledReason?.sub}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </MenuItem>
              )}
              <MenuItem
                disabled={
                  disableAddToCompareAction ||
                  !sectionUnitMenuAnchor?.unit?.floorplan ||
                  (topViewingFloorPlansToCompare.length >= 3 &&
                    !topViewingFloorPlansToCompare.some((fp) => fp.unit.id === sectionUnitMenuAnchor?.unit.id))
                }
                onClick={() => {
                  if (!sectionUnitMenuAnchor) return;
                  if (disableAddToCompareAction) return;
                  handleTopViewingAddToUnitCompare(sectionUnitMenuAnchor.unit);
                  setSectionUnitMenuAnchor(null);
                }}
              >
                <ListItemIcon>
                  <ShoppingCartIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  {disableAddToCompareAction
                    ? 'Add to Compare'
                    : sectionUnitMenuAnchor && topViewingFloorPlansToCompare.some((fp) => fp.unit.id === sectionUnitMenuAnchor.unit.id)
                      ? 'Remove from Compare'
                      : `Add to Compare (${topViewingFloorPlansToCompare.length}/3)`}
                </ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  if (!sectionUnitMenuAnchor) return;
                  const unit = sectionUnitMenuAnchor.unit;
                  handleShortlistAction({
                    unitId: unit.id,
                    label: `${unit.projectName || ''} ${unit.unitNumber || ''}`.trim(),
                    floorplan: unit.floorplan || null,
                  });
                  setSectionUnitMenuAnchor(null);
                }}
              >
                <ListItemIcon>
                  {sectionUnitMenuAnchor && shortlistByUnitId.has(sectionUnitMenuAnchor.unit.id)
                    ? <StarIcon fontSize="small" sx={{ color: '#ffb300' }} />
                    : <StarBorderIcon fontSize="small" />}
                </ListItemIcon>
                <ListItemText>
                  {sectionUnitMenuAnchor && shortlistByUnitId.has(sectionUnitMenuAnchor.unit.id)
                    ? 'Remove from Shortlist'
                    : 'Add to Shortlist'}
                </ListItemText>
              </MenuItem>
            </Menu>


            {/* Size Tolerance Slider Modal */}
            <Dialog
              open={topViewingToleranceModalOpen}
              onClose={() => setTopViewingToleranceModalOpen(false)}
              maxWidth="xs"
              fullWidth
              PaperProps={{
                sx: {
                  bgcolor: 'var(--color-surface)',
                  color: 'var(--color-text-strong)',
                  border: '1px solid rgba(var(--color-text-rgb), 0.12)',
                },
              }}
            >
              <DialogTitle sx={{ fontWeight: 600, borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)', pb: 1.5 }}>
                Size tolerance
              </DialogTitle>
              <DialogContent sx={{ pt: 2 }}>
                <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', mb: 2 }}>
                  Include comparable units whose area is within ±{topViewingToleranceSliderValue}% of the reference unit ({topViewingReferenceUnit?.areaSqft != null ? Math.round(topViewingReferenceUnit.areaSqft).toLocaleString() : '—'} sqft). Drag the slider left for exact size only, or right to allow a wider range.
                </Typography>
                <Box sx={{ px: 1 }}>
                  <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                    ±{topViewingToleranceSliderValue}%
                  </Typography>
                  {topViewingReferenceUnit?.areaSqft != null && (
                    <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block', mb: 0.5 }}>
                      Current range: {getAreaRangeLabel(topViewingReferenceUnit.areaSqft, topViewingToleranceSliderValue) ?? '—'}
                    </Typography>
                  )}
                  <Slider
                    value={topViewingToleranceSliderValue}
                    onChange={(_, value) => setTopViewingToleranceSliderValue(Array.isArray(value) ? value[0] : value)}
                    min={0}
                    max={50}
                    step={1}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `±${v}%`}
                    sx={{
                      color: 'var(--color-accent)',
                      mt: 0.5,
                      '& .MuiSlider-thumb': { bgcolor: 'var(--color-accent)' },
                      '& .MuiSlider-rail': { opacity: 0.4 },
                    }}
                  />
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 2, pb: 2, pt: 0, gap: 1 }}>
                <Button onClick={() => setTopViewingToleranceModalOpen(false)} sx={{ color: 'var(--color-text-strong)' }}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    setTopViewingAreaTolerance(topViewingToleranceSliderValue);
                    setTopViewingToleranceModalOpen(false);
                  }}
                  sx={{ bgcolor: 'var(--color-accent)', color: 'var(--color-text-inverse)', '&:hover': { bgcolor: 'var(--color-accent-strong)' } }}
                >
                  Apply
                </Button>
              </DialogActions>
            </Dialog>

            {/* Filter Popover for Comparable Units */}
            <Popover
              open={Boolean(topViewingCompPopoverAnchor)}
              anchorEl={topViewingCompPopoverAnchor?.element}
              onClose={handleTopViewingCompClosePopover}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <Box sx={{ p: 2, minWidth: 280 }}>
                {topViewingCompPopoverAnchor && (
                  <>
                    {/* Sort Section */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1, color: 'var(--color-info)', fontWeight: 500 }}>
                        Sort
                      </Typography>
                      <ToggleButtonGroup
                        value={
                          topViewingCompSortConfig.column === topViewingCompPopoverAnchor.column
                            ? topViewingCompSortConfig.direction === null
                              ? 'default'
                              : topViewingCompSortConfig.direction
                            : 'default'
                        }
                        exclusive
                        onChange={(_, value) => {
                          if (value !== null) {
                            if (value === 'default') {
                              setTopViewingCompSortConfig({ column: null, direction: null });
                            } else {
                              setTopViewingCompSortConfig({
                                column: topViewingCompPopoverAnchor.column,
                                direction: value as 'asc' | 'desc',
                              });
                            }
                          }
                        }}
                        fullWidth
                        sx={{
                          '& .MuiToggleButtonGroup-grouped': {
                            border: '1px solid rgba(var(--color-shadow-rgb), 0.12)',
                            '&:not(:first-of-type)': {
                              borderLeft: '1px solid rgba(var(--color-shadow-rgb), 0.12)',
                              marginLeft: 0,
                            },
                            '&.Mui-selected': {
                              backgroundColor: 'var(--color-info)',
                              color: 'var(--color-text-strong)',
                              '&:hover': {
                                backgroundColor: 'var(--color-info-strong)',
                              },
                            },
                          },
                        }}
                      >
                        <ToggleButton value="default" sx={{ flex: 1, py: 0.75 }}>
                          Default
                        </ToggleButton>
                        <ToggleButton value="asc" sx={{ flex: 1, py: 0.75 }}>
                          Asc
                        </ToggleButton>
                        <ToggleButton value="desc" sx={{ flex: 1, py: 0.75 }}>
                          Desc
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>

                    {/* Filter Section */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ color: 'var(--color-info)', fontWeight: 500, mr: 1 }}>
                          Filter
                        </Typography>
                        <FilterIcon sx={{ fontSize: 18, color: 'var(--color-info)' }} />
                      </Box>

                      {topViewingCompPopoverAnchor.column === 'projectName' ? (
                        <Box>
                          <TextField
                            fullWidth
                            size="small"
                            label="Project"
                            value={topViewingReferenceUnit?.projectName ?? ''}
                            disabled
                            helperText="Best matches are limited to the reference project."
                          />
                        </Box>
                      ) : topViewingCompPopoverAnchor.column === 'projectCategory' ? (
                        <Box>
                          <FormControl fullWidth size="small">
                            <Select
                              multiple
                              displayEmpty
                              value={topViewingCompFilters.projectCategory}
                              onChange={(event) => {
                                const rawValue = event.target.value;
                                const value = typeof rawValue === 'string' ? rawValue.split(',') : rawValue;
                                if (value.includes('__SELECT_ALL__')) {
                                  if (topViewingCompFilters.projectCategory.length === topViewingComparableResultProjectCategories.length) {
                                    handleTopViewingCompFilterChange('projectCategory', []);
                                  } else {
                                    handleTopViewingCompFilterChange('projectCategory', [...topViewingComparableResultProjectCategories]);
                                  }
                                  return;
                                }
                                handleTopViewingCompFilterChange('projectCategory', value);
                              }}
                              renderValue={(selected) => {
                                const selectedValues = selected as string[];
                                if (selectedValues.length === 0) return 'All Categories';
                                if (selectedValues.length === topViewingComparableResultProjectCategories.length) return 'All Selected';
                                return `${selectedValues.length} selected`;
                              }}
                            >
                              <MenuItem value="__SELECT_ALL__">
                                <Checkbox
                                  checked={topViewingCompFilters.projectCategory.length === topViewingComparableResultProjectCategories.length && topViewingComparableResultProjectCategories.length > 0}
                                  indeterminate={topViewingCompFilters.projectCategory.length > 0 && topViewingCompFilters.projectCategory.length < topViewingComparableResultProjectCategories.length}
                                />
                                <Typography component="span" sx={{ fontWeight: 600 }}>Select All</Typography>
                              </MenuItem>
                              {topViewingComparableResultProjectCategories.map((option) => {
                                const isSelected = topViewingCompFilters.projectCategory.includes(option);
                                return (
                                  <MenuItem key={option} value={option}>
                                    <Checkbox checked={isSelected} />
                                    <Typography component="span">{option}</Typography>
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                          <Button
                            size="small"
                            onClick={() =>
                              handleTopViewingCompFilterChange('projectCategory', [
                                ...topViewingComparableResultProjectCategories,
                              ])
                            }
                            sx={{ mt: 1 }}
                            fullWidth
                            disabled={!hasTopViewingCompActiveFilter('projectCategory')}
                          >
                            Clear Filter (Show All)
                          </Button>
                        </Box>
                      ) : topViewingCompPopoverAnchor.column === 'unitTypeDescription' ? (
                        <Box>
                          <FormControl fullWidth size="small">
                            <Select
                              multiple
                              displayEmpty
                              value={topViewingCompFilters.unitTypeDescription}
                              onChange={(event) => {
                                const rawValue = event.target.value;
                                const value = typeof rawValue === 'string' ? rawValue.split(',') : rawValue;
                                if (value.includes('__SELECT_ALL__')) {
                                  if (topViewingCompFilters.unitTypeDescription.length === topViewingComparableResultUnitTypeDescriptions.length) {
                                    handleTopViewingCompFilterChange('unitTypeDescription', []);
                                  } else {
                                    handleTopViewingCompFilterChange('unitTypeDescription', [...topViewingComparableResultUnitTypeDescriptions]);
                                  }
                                  return;
                                }
                                handleTopViewingCompFilterChange('unitTypeDescription', value);
                              }}
                              renderValue={(selected) => {
                                const selectedValues = selected as string[];
                                if (selectedValues.length === 0) return 'All Types';
                                if (selectedValues.length === topViewingComparableResultUnitTypeDescriptions.length) return 'All Selected';
                                return `${selectedValues.length} selected`;
                              }}
                            >
                              <MenuItem value="__SELECT_ALL__">
                                <Checkbox
                                  checked={topViewingCompFilters.unitTypeDescription.length === topViewingComparableResultUnitTypeDescriptions.length && topViewingComparableResultUnitTypeDescriptions.length > 0}
                                  indeterminate={topViewingCompFilters.unitTypeDescription.length > 0 && topViewingCompFilters.unitTypeDescription.length < topViewingComparableResultUnitTypeDescriptions.length}
                                />
                                <Typography component="span" sx={{ fontWeight: 600 }}>Select All</Typography>
                              </MenuItem>
                              {topViewingComparableResultUnitTypeDescriptions.map((option) => {
                                const isSelected = topViewingCompFilters.unitTypeDescription.includes(option);
                                return (
                                  <MenuItem key={option} value={option}>
                                    <Checkbox checked={isSelected} />
                                    <Typography component="span">{option}</Typography>
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                          <Button
                            size="small"
                            onClick={() => handleTopViewingCompFilterChange('unitTypeDescription', [])}
                            sx={{ mt: 1 }}
                            fullWidth
                            disabled={!hasTopViewingCompActiveFilter('unitTypeDescription')}
                          >
                            Clear Filter (Show All)
                          </Button>
                        </Box>
                      ) : topViewingCompPopoverAnchor.column === 'blockNumber' ? (
                        <Box>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="Search..."
                            value={topViewingCompFilters.blockNumber}
                            onChange={(e) => handleTopViewingCompFilterChange('blockNumber', e.target.value)}
                            InputProps={{
                              endAdornment: hasTopViewingCompActiveFilter('blockNumber') && (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      handleTopViewingCompFilterChange('blockNumber', '');
                                    }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                bgcolor: 'var(--color-surface)',
                                '& fieldset': { borderColor: 'rgba(var(--color-text-rgb), 0.2)' },
                              },
                            }}
                          />
                          <Button
                            size="small"
                            onClick={() => {
                              handleTopViewingCompFilterChange('blockNumber', '');
                            }}
                            sx={{ mt: 1 }}
                            fullWidth
                            disabled={!hasTopViewingCompActiveFilter('blockNumber')}
                          >
                            Clear Filter
                          </Button>
                        </Box>
                      ) : (
                    <>
                      {(() => {
                        const shouldFormatThousands = topViewingCompPopoverAnchor.column === 'price' || topViewingCompPopoverAnchor.column === 'psf';
                        return (
                          <>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField
                          size="small"
                          label="Min"
                          placeholder="Min"
                          type={shouldFormatThousands ? 'text' : 'number'}
                          inputProps={shouldFormatThousands ? { inputMode: 'decimal' } : undefined}
                          value={shouldFormatThousands
                            ? formatThousandsInput(topViewingCompRangeFilters[topViewingCompPopoverAnchor.column as keyof typeof topViewingCompRangeFilters].min)
                            : topViewingCompRangeFilters[topViewingCompPopoverAnchor.column as keyof typeof topViewingCompRangeFilters].min}
                          onChange={(e) => handleTopViewingCompRangeFilterChange(
                            topViewingCompPopoverAnchor.column as keyof typeof topViewingCompRangeFilters,
                            'min',
                            shouldFormatThousands ? e.target.value.replace(/,/g, '').replace(/[^\d.]/g, '') : e.target.value
                          )}
                          sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                              bgcolor: 'var(--color-surface)',
                              '& fieldset': { borderColor: 'rgba(var(--color-text-rgb), 0.2)' },
                            },
                          }}
                        />
                        <TextField
                          size="small"
                          label="Max"
                          placeholder="Max"
                          type={shouldFormatThousands ? 'text' : 'number'}
                          inputProps={shouldFormatThousands ? { inputMode: 'decimal' } : undefined}
                          value={shouldFormatThousands
                            ? formatThousandsInput(topViewingCompRangeFilters[topViewingCompPopoverAnchor.column as keyof typeof topViewingCompRangeFilters].max)
                            : topViewingCompRangeFilters[topViewingCompPopoverAnchor.column as keyof typeof topViewingCompRangeFilters].max}
                          onChange={(e) => handleTopViewingCompRangeFilterChange(
                            topViewingCompPopoverAnchor.column as keyof typeof topViewingCompRangeFilters,
                            'max',
                            shouldFormatThousands ? e.target.value.replace(/,/g, '').replace(/[^\d.]/g, '') : e.target.value
                          )}
                          sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                              bgcolor: 'var(--color-surface)',
                              '& fieldset': { borderColor: 'rgba(var(--color-text-rgb), 0.2)' },
                            },
                          }}
                        />
                      </Box>
                      <Button
                        size="small"
                        onClick={() => {
                          handleTopViewingCompRangeFilterChange(
                            topViewingCompPopoverAnchor.column as keyof typeof topViewingCompRangeFilters,
                            'min',
                            ''
                          );
                          handleTopViewingCompRangeFilterChange(
                            topViewingCompPopoverAnchor.column as keyof typeof topViewingCompRangeFilters,
                            'max',
                            ''
                          );
                        }}
                        sx={{ color: 'var(--color-text-strong)' }}
                        fullWidth
                        disabled={!hasTopViewingCompActiveFilter(topViewingCompPopoverAnchor.column)}
                      >
                        Clear Filter
                      </Button>
                          </>
                        );
                      })()}
                    </>
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </Popover>
            <Popover
              open={Boolean(projectViewPopoverAnchor)}
              anchorEl={projectViewPopoverAnchor?.element}
              onClose={() => setProjectViewPopoverAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <Box sx={{ p: 2, minWidth: 220 }}>
                {projectViewPopoverAnchor && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, color: 'var(--color-info)', fontWeight: 500 }}>
                      Sort
                    </Typography>
                    <ToggleButtonGroup
                      value={getProjectViewSortDirection(projectViewPopoverAnchor.column)}
                      exclusive
                      onChange={(_, value) => {
                        if (value === null || !projectViewPopoverAnchor) return;
                        const nextDirection = value as SortDirection;
                        if (nextDirection === 'default') {
                          setProjectViewSortConfig({ column: null, direction: 'default' });
                        } else {
                          setProjectViewSortConfig({ column: projectViewPopoverAnchor.column, direction: nextDirection });
                        }
                      }}
                      fullWidth
                      sx={{
                        '& .MuiToggleButtonGroup-grouped': {
                          border: '1px solid rgba(var(--color-shadow-rgb), 0.12)',
                          '&:not(:first-of-type)': {
                            borderLeft: '1px solid rgba(var(--color-shadow-rgb), 0.12)',
                            marginLeft: 0,
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'var(--color-info)',
                            color: 'var(--color-text-strong)',
                            '&:hover': {
                              backgroundColor: 'var(--color-info-strong)',
                            },
                          },
                        },
                      }}
                    >
                      <ToggleButton value="default" sx={{ flex: 1, py: 0.75 }}>Default</ToggleButton>
                      <ToggleButton value="asc" sx={{ flex: 1, py: 0.75 }}>Asc</ToggleButton>
                      <ToggleButton value="desc" sx={{ flex: 1, py: 0.75 }}>Desc</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <FilterIcon sx={{ color: 'var(--color-info)', mr: 1, fontSize: 18 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Filter {projectViewPopoverAnchor?.column?.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}</Typography>
                </Box>
                {projectViewPopoverAnchor && ['projectName', 'projectCategory', 'district', 'region', 'nearestMrt', 'tenure'].includes(projectViewPopoverAnchor.column) ? (
                  <Box>
                    {(() => {
                      const key = projectViewPopoverAnchor.column as keyof typeof projectViewCategoryFilters;
                      const options = projectViewCategoryOptions[key] ?? [];
                      const selected = projectViewCategoryFilters[key] ?? [];
                      return (
                        <FormControl fullWidth size="small">
                          <InputLabel>
                            {projectViewPopoverAnchor.column === 'nearestMrt'
                              ? 'Nearest MRT'
                              : projectViewPopoverAnchor.column === 'projectCategory'
                              ? 'Project Category'
                              : projectViewPopoverAnchor.column === 'projectName'
                              ? 'Project Name'
                              : projectViewPopoverAnchor.column.charAt(0).toUpperCase() + projectViewPopoverAnchor.column.slice(1)}
                          </InputLabel>
                          <Select
                            multiple
                            label={
                              projectViewPopoverAnchor.column === 'nearestMrt'
                                ? 'Nearest MRT'
                                : projectViewPopoverAnchor.column === 'projectCategory'
                                ? 'Project Category'
                                : projectViewPopoverAnchor.column === 'projectName'
                                ? 'Project Name'
                                : projectViewPopoverAnchor.column.charAt(0).toUpperCase() + projectViewPopoverAnchor.column.slice(1)
                            }
                            value={selected}
                            onChange={(event) => {
                              const rawValue = event.target.value;
                              const value = typeof rawValue === 'string' ? rawValue.split(',') : rawValue;
                              if (value.includes('__SELECT_ALL__')) {
                                const next = selected.length === options.length ? [] : [...options];
                                setProjectViewCategoryFilters((prev) => ({ ...prev, [key]: next }));
                                return;
                              }
                              setProjectViewCategoryFilters((prev) => ({ ...prev, [key]: value }));
                            }}
                            renderValue={(selectedValues) => {
                              const selectedList = selectedValues as string[];
                              if (selectedList.length === 0 || selectedList.length === options.length) return 'All';
                              return `${selectedList.length} selected`;
                            }}
                            MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                          >
                            <MenuItem value="__SELECT_ALL__">
                              <Checkbox
                                checked={selected.length === options.length && options.length > 0}
                                indeterminate={selected.length > 0 && selected.length < options.length}
                                sx={{ color: 'var(--color-accent)', '&.Mui-checked': { color: 'var(--color-accent)' } }}
                              />
                              <Typography component="span" sx={{ fontWeight: 600 }}>
                                Select All
                              </Typography>
                            </MenuItem>
                            {options.map((option) => {
                              const isSelected = selected.includes(option);
                              return (
                                <MenuItem key={option || '__EMPTY__'} value={option}>
                                  <Checkbox
                                    checked={isSelected}
                                    sx={{ color: 'var(--color-accent)', '&.Mui-checked': { color: 'var(--color-accent)' } }}
                                  />
                                  <Typography component="span">{option || '(empty)'}</Typography>
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      );
                    })()}
                    <Button
                      size="small"
                      sx={{ mt: 1 }}
                      onClick={() => {
                        const key = projectViewPopoverAnchor.column as keyof typeof projectViewCategoryFilters;
                        setProjectViewCategoryFilters((prev) => ({ ...prev, [key]: [] }));
                        setProjectViewPopoverAnchor(null);
                      }}
                    >
                      Clear
                    </Button>
                  </Box>
                ) : projectViewPopoverAnchor && projectViewNumericColumns.has(projectViewPopoverAnchor.column) ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {(() => {
                      const shouldFormatThousands = /price|psf/i.test(projectViewPopoverAnchor.column);
                      return (
                        <>
                    <TextField
                      fullWidth
                      size="small"
                      type={shouldFormatThousands ? 'text' : 'number'}
                      inputProps={shouldFormatThousands ? { inputMode: 'decimal' } : undefined}
                      label="Min"
                      placeholder="Min"
                      value={shouldFormatThousands
                        ? formatThousandsInput(projectViewRangeFilters[projectViewPopoverAnchor.column]?.min ?? '')
                        : (projectViewRangeFilters[projectViewPopoverAnchor.column]?.min ?? '')}
                      onChange={(e) => setProjectViewRangeFilters((f) => ({ ...f, [projectViewPopoverAnchor!.column]: { ...(f[projectViewPopoverAnchor!.column] ?? { min: '', max: '' }), min: shouldFormatThousands ? e.target.value.replace(/,/g, '').replace(/[^\d.]/g, '') : e.target.value } }))}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      type={shouldFormatThousands ? 'text' : 'number'}
                      inputProps={shouldFormatThousands ? { inputMode: 'decimal' } : undefined}
                      label="Max"
                      placeholder="Max"
                      value={shouldFormatThousands
                        ? formatThousandsInput(projectViewRangeFilters[projectViewPopoverAnchor.column]?.max ?? '')
                        : (projectViewRangeFilters[projectViewPopoverAnchor.column]?.max ?? '')}
                      onChange={(e) => setProjectViewRangeFilters((f) => ({ ...f, [projectViewPopoverAnchor!.column]: { ...(f[projectViewPopoverAnchor!.column] ?? { min: '', max: '' }), max: shouldFormatThousands ? e.target.value.replace(/,/g, '').replace(/[^\d.]/g, '') : e.target.value } }))}
                    />
                    <Button
                      size="small"
                      onClick={() => {
                        if (projectViewPopoverAnchor) setProjectViewRangeFilters((f) => ({ ...f, [projectViewPopoverAnchor!.column]: { min: '', max: '' } }));
                        setProjectViewPopoverAnchor(null);
                      }}
                    >
                      Clear
                    </Button>
                        </>
                      );
                    })()}
                  </Box>
                ) : (
                  <Box>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={`Filter ${projectViewPopoverAnchor?.column ?? ''}`}
                      value={projectViewFilters[projectViewPopoverAnchor?.column ?? ''] ?? ''}
                      onChange={(e) => projectViewPopoverAnchor && setProjectViewFilters((f) => ({ ...f, [projectViewPopoverAnchor!.column]: e.target.value }))}
                    />
                    <Button size="small" sx={{ mt: 1 }} onClick={() => { if (projectViewPopoverAnchor) setProjectViewFilters((f) => ({ ...f, [projectViewPopoverAnchor!.column]: '' })); setProjectViewPopoverAnchor(null); }}>Clear</Button>
                  </Box>
                )}
              </Box>
            </Popover>

            {/* Refresh Loading Dialog */}
            <Dialog
              open={refreshing}
              PaperProps={{
                sx: {
                  bgcolor: 'var(--color-bg)',
                  color: 'var(--color-text-strong)',
                  boxShadow: 'none',
                  borderRadius: 2,
                  px: 4,
                  py: 3,
                },
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={36} sx={{ color: 'var(--color-accent)' }} />
                <Typography variant="body2" sx={{ color: 'var(--color-text-strong)' }}>Refreshing...</Typography>
              </Box>
            </Dialog>

            {/* Factsheet Preview Modal */}
            {factsheetData && factsheetData.url && (
              <FilePreviewDialog
                open={factsheetModalOpen}
                onClose={() => setFactsheetModalOpen(false)}
                title="Factsheet"
                url={factsheetData.url}
                filename={factsheetDownload?.filename || 'factsheet.pdf'}
              />
            )}

            {/* Brochure Preview Modal */}
            {brochureData && brochureData.url && (
              <FilePreviewDialog
                open={brochureModalOpen}
                onClose={() => setBrochureModalOpen(false)}
                title="Brochure"
                url={brochureData.url}
                filename={brochureDownload?.filename || 'brochure.pdf'}
              />
            )}

            {/* Agent Access Folder — list + (leads only) add / edit / delete curated
                external links for this project. Regular agents see a read-only list;
                Head / Deputy Head of Project see the manage controls. */}
            <Dialog
              open={agentAccessDialogOpen}
              onClose={() => {
                setAgentAccessDialogOpen(false);
                setAgentAccessFormOpen(false);
                setAgentAccessEditingId(null);
                setAgentAccessFormUrl('');
                setAgentAccessFormLabel('');
                setAgentAccessError(null);
                setAgentAccessPage(1);
              }}
              maxWidth="sm"
              fullWidth
              PaperProps={{
                sx: {
                  bgcolor: 'var(--color-surface)',
                  color: 'var(--color-text)',
                  borderRadius: 2,
                },
              }}
            >
              <DialogTitle
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  py: 1.5,
                  px: 2.5,
                  borderBottom: '1px solid var(--color-border)',
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                  <FolderSharedIcon sx={{ color: 'var(--color-accent, #818cf8)' }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--color-text-strong)', lineHeight: 1.2 }}>
                      Agent Access Folder
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'var(--color-text-muted)',
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {currentSitePlan?.projectName || projectDetails?.name || selectedProject || 'Project'}
                    </Typography>
                  </Box>
                </Stack>
                <IconButton
                  onClick={() => {
                    setAgentAccessDialogOpen(false);
                    setAgentAccessFormOpen(false);
                    setAgentAccessEditingId(null);
                    setAgentAccessFormUrl('');
                    setAgentAccessFormLabel('');
                    setAgentAccessError(null);
                    setAgentAccessPage(1);
                  }}
                  size="small"
                  sx={{ color: 'var(--color-text-muted)' }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ p: 2.5 }}>
                {canManageResourceLinks && (
                  <Box sx={{ mb: 2 }}>
                    {!agentAccessFormOpen ? (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          setAgentAccessFormOpen(true);
                          setAgentAccessEditingId(null);
                          setAgentAccessFormUrl('');
                          setAgentAccessFormLabel('');
                          setAgentAccessError(null);
                          setAgentAccessDuplicateNotice(false);
                        }}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                      >
                        Add link
                      </Button>
                    ) : (
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 1.5,
                          border: '1px solid var(--color-border)',
                          bgcolor: 'var(--color-surface-elevated)',
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                          {agentAccessEditingId === null ? 'Add a new link' : 'Edit link'}
                        </Typography>
                        <Stack spacing={1.5}>
                          <TextField
                            label="Link (https://…)"
                            value={agentAccessFormUrl}
                            onChange={(e) => {
                              setAgentAccessFormUrl(e.target.value);
                              setAgentAccessDuplicateNotice(false);
                            }}
                            fullWidth
                            size="small"
                            required
                            placeholder="https://drive.google.com/…"
                          />
                          <TextField
                            label="Display label (optional)"
                            value={agentAccessFormLabel}
                            onChange={(e) => setAgentAccessFormLabel(e.target.value)}
                            fullWidth
                            size="small"
                            helperText="Shown in place of the raw URL. Falls back to the URL if left blank."
                          />
                          {agentAccessDuplicateNotice ? (
                            <Alert severity="info" sx={{ py: 0.5 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {RESOURCE_LINK_DUPLICATE_INFO.title}
                              </Typography>
                              {RESOURCE_LINK_DUPLICATE_INFO.message}
                            </Alert>
                          ) : null}
                          {agentAccessError ? (
                            <Alert severity="error" sx={{ py: 0.5 }}>
                              {agentAccessError}
                            </Alert>
                          ) : null}
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              size="small"
                              onClick={() => {
                                setAgentAccessFormOpen(false);
                                setAgentAccessEditingId(null);
                                setAgentAccessFormUrl('');
                                setAgentAccessFormLabel('');
                                setAgentAccessError(null);
                                setAgentAccessDuplicateNotice(false);
                              }}
                              disabled={agentAccessSaving}
                              sx={{ textTransform: 'none' }}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              disabled={agentAccessSaving || !agentAccessFormUrl.trim()}
                              onClick={async () => {
                                const projectId = projectDetails?.id || currentSitePlan?.projectId;
                                if (!projectId) return;
                                const url = agentAccessFormUrl.trim();
                                const label = agentAccessFormLabel.trim();
                                if (!/^https?:\/\//i.test(url)) {
                                  setAgentAccessError('URL must start with http:// or https://');
                                  return;
                                }
                                if (
                                  isDuplicateProjectResourceLink(
                                    projectResourceLinks,
                                    url,
                                    agentAccessEditingId,
                                  )
                                ) {
                                  setAgentAccessDuplicateNotice(true);
                                  setAgentAccessError(null);
                                  return;
                                }
                                setAgentAccessSaving(true);
                                setAgentAccessError(null);
                                setAgentAccessDuplicateNotice(false);
                                try {
                                  if (agentAccessEditingId === null) {
                                    await api.createProjectResourceLink(projectId, { url, label });
                                  } else {
                                    await api.updateProjectResourceLink(projectId, agentAccessEditingId, { url, label });
                                  }
                                  await reloadProjectResourceLinks();
                                  setAgentAccessFormOpen(false);
                                  setAgentAccessEditingId(null);
                                  setAgentAccessFormUrl('');
                                  setAgentAccessFormLabel('');
                                } catch (err) {
                                  if (isResourceLinkAlreadyExistsError(err)) {
                                    setAgentAccessDuplicateNotice(true);
                                    setAgentAccessError(null);
                                  } else {
                                    setAgentAccessError((err as Error)?.message || 'Failed to save link');
                                  }
                                } finally {
                                  setAgentAccessSaving(false);
                                }
                              }}
                              sx={{ textTransform: 'none', fontWeight: 700 }}
                            >
                              {agentAccessSaving ? 'Saving…' : agentAccessEditingId === null ? 'Add link' : 'Save'}
                            </Button>
                          </Stack>
                        </Stack>
                      </Box>
                    )}
                  </Box>
                )}
                {projectResourceLinksLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : projectResourceLinks.length === 0 ? (
                  <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', py: 2, textAlign: 'center' }}>
                    No links yet.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {(() => {
                      const totalLinks = projectResourceLinks.length;
                      const maxPage = Math.ceil(totalLinks / 10) || 1;
                      const currentPage = Math.min(agentAccessPage, maxPage);
                      
                      if (agentAccessPage > currentPage) {
                        setAgentAccessPage(currentPage);
                      }

                      return (
                        <>
                          {projectResourceLinks
                            .slice((currentPage - 1) * 10, currentPage * 10)
                            .map((link) => {
                              const dateLabel = (() => {
                                if (!link.createdAt) return '';
                                const d = new Date(link.createdAt);
                                if (Number.isNaN(d.getTime())) return '';
                                return d.toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                });
                              })();
                              return (
                                <Box
                                  key={link.id}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.25,
                                    p: 1.5,
                                    borderRadius: 1.5,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'var(--color-surface)',
                                  }}
                                >
                                  <LinkIcon sx={{ color: 'var(--color-accent, #818cf8)' }} />
                                  <Box
                                    component="a"
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      minWidth: 0,
                                      flex: 1,
                                      color: 'inherit',
                                      textDecoration: 'none',
                                      '&:hover .link-title': { textDecoration: 'underline' },
                                    }}
                                  >
                                    <Typography
                                      className="link-title"
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        color: 'var(--color-text-strong)',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                      title={link.label || link.url}
                                    >
                                      {link.label || link.url}
                                    </Typography>
                                    {dateLabel && (
                                      <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                                        Added {dateLabel}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Tooltip title="Open in new tab">
                                    <IconButton
                                      size="small"
                                      component="a"
                                      href={link.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{ color: 'var(--color-text-muted)' }}
                                    >
                                      <OpenInNewIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {canManageResourceLinks && (
                                    <>
                                      <Tooltip title="Edit">
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            setAgentAccessFormOpen(true);
                                            setAgentAccessEditingId(link.id);
                                            setAgentAccessFormUrl(link.url);
                                            setAgentAccessFormLabel(link.label || '');
                                            setAgentAccessError(null);
                                            setAgentAccessDuplicateNotice(false);
                                          }}
                                          sx={{ color: 'var(--color-text-muted)' }}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Delete">
                                        <IconButton
                                          size="small"
                                          onClick={async () => {
                                            const projectId = projectDetails?.id || currentSitePlan?.projectId;
                                            if (!projectId) return;
                                            // eslint-disable-next-line no-alert
                                            const ok = window.confirm(`Delete "${link.label || link.url}"?`);
                                            if (!ok) return;
                                            try {
                                              await api.deleteProjectResourceLink(projectId, link.id);
                                              await reloadProjectResourceLinks();
                                            } catch (err) {
                                              setAgentAccessError((err as Error)?.message || 'Failed to delete link');
                                            }
                                          }}
                                          sx={{ color: 'var(--color-text-muted)' }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  )}
                                </Box>
                              );
                            })}

                          {totalLinks > 10 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5 }}>
                              <Pagination
                                count={maxPage}
                                page={currentPage}
                                onChange={(_, newPage) => setAgentAccessPage(newPage)}
                                size="small"
                                color="primary"
                                sx={{
                                  '& .MuiPaginationItem-root': {
                                    color: 'var(--color-text)',
                                    borderColor: 'var(--color-border)',
                                  },
                                }}
                              />
                            </Box>
                          )}
                        </>
                      );
                    })()}
                  </Stack>
                )}
              </DialogContent>
            </Dialog>

            {/* Custom EDMs & eBooks list dialog */}
            <Dialog
              open={fileListDialogOpen}
              onClose={() => setFileListDialogOpen(false)}
              maxWidth={false}
              PaperProps={{
                sx: {
                  bgcolor: 'var(--color-surface)',
                  color: 'var(--color-text)',
                  borderRadius: 2,
                  width: { xs: '95vw', sm: '75vw', md: '60vw' },
                  maxWidth: '100%',
                },
              }}
            >
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid var(--color-border-strong)' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
                  {fileListDialogType === 'edm'
                    ? `${currentSitePlan?.projectName || projectDetails?.name || selectedProject || 'Project'} EDMs`
                    : `${currentSitePlan?.projectName || projectDetails?.name || selectedProject || 'Project'} eBooks`}
                </Typography>
                <IconButton onClick={() => setFileListDialogOpen(false)} size="small" sx={{ color: 'var(--color-text-muted)' }}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {(() => {
                  const slugifyForFile = (raw: string, fallback: string) =>
                    raw.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || fallback;

                  if (fileListDialogType === 'edm') {
                    const threeMonthsAgo = new Date();
                    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

                    const recentEdms: ProjectEdmFile[] = [];
                    const otherEdms: ProjectEdmFile[] = [];

                    filesEdms.forEach((edm) => {
                      if (!edm.createdAt) {
                        otherEdms.push(edm);
                        return;
                      }
                      const createdDate = new Date(edm.createdAt);
                      if (isNaN(createdDate.getTime())) {
                        otherEdms.push(edm);
                      } else if (createdDate >= threeMonthsAgo) {
                        recentEdms.push(edm);
                      } else {
                        otherEdms.push(edm);
                      }
                    });

                    const renderEdmGrid = (items: ProjectEdmFile[]) => (
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                          gap: 1.5,
                          p: 0.5,
                        }}
                      >
                        {items.map((edm) => {
                          const projId = projectDetails?.id || currentSitePlan?.projectId;
                          const filename = edm.filename || `${slugifyForFile(edm.title, 'edm')}.pdf`;
                          return (
                            <DownloadFileRow
                              key={edm.id}
                              filename={filename}
                              displayName={edm.title}
                              subtitle={edm.category ? `EDM · ${edm.category}` : 'EDM'}
                              url={edm.url && edm.url !== '#' ? edm.url : null}
                              urlPublic={edm.urlPublic}
                              previewImage={edm.cover}
                              uploadedAt={edm.createdAt}
                              onPreview={() => logProjectDetailsPreviewed('edm', projId, edm.id, analyticsCea)}
                              onDownloadClick={() => logProjectDetailsDownloaded('edm', projId, edm.id, analyticsCea)}
                              shareAsset={{ id: edm.id, kind: 'edm', title: edm.title || edm.id, projectId: projId }}
                            />
                          );
                        })}
                      </Box>
                    );

                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 800,
                            color: 'var(--color-accent)',
                            px: 0.5,
                            fontSize: '1.05rem',
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                          }}
                        >
                          Recent Uploads (Last 3 months)
                        </Typography>
                        {recentEdms.length > 0 ? (
                          renderEdmGrid(recentEdms)
                        ) : (
                          <Box
                            sx={{
                              minHeight: '140px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px dashed var(--color-border-strong)',
                              borderRadius: 2,
                              bgcolor: 'rgba(var(--color-text-rgb), 0.02)',
                              p: 3,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'var(--color-text-muted)',
                                fontStyle: 'italic',
                                textAlign: 'center',
                              }}
                            >
                              No EDMs uploaded within the last 3 months
                            </Typography>
                          </Box>
                        )}

                        {otherEdms.length > 0 && (
                          <>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 800,
                                color: 'var(--color-text-muted)',
                                px: 0.5,
                                mt: 3,
                                fontSize: '1.05rem',
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                              }}
                            >
                              Past Uploads
                            </Typography>
                            {renderEdmGrid(otherEdms)}
                          </>
                        )}
                      </Box>
                    );
                  }

                  const threeMonthsAgo = new Date();
                  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

                  const recentEbooks: typeof filesEbooks = [];
                  const otherEbooks: typeof filesEbooks = [];

                  filesEbooks.forEach((ebook) => {
                    if (!ebook.createdAt) {
                      otherEbooks.push(ebook);
                      return;
                    }
                    const createdDate = new Date(ebook.createdAt);
                    if (isNaN(createdDate.getTime())) {
                      otherEbooks.push(ebook);
                    } else if (createdDate >= threeMonthsAgo) {
                      recentEbooks.push(ebook);
                    } else {
                      otherEbooks.push(ebook);
                    }
                  });

                  const renderEbookGrid = (items: typeof filesEbooks) => (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                        gap: 1.5,
                        p: 0.5,
                      }}
                    >
                      {items.map((ebook) => {
                        const projId = projectDetails?.id || currentSitePlan?.projectId;
                        const filename = `${ebook.title || ebook.id}.pdf`;
                        return (
                          <DownloadFileRow
                            key={ebook.id}
                            filename={filename}
                            displayName={ebook.title}
                            subtitle={ebook.type ? `eBook · ${ebook.type}` : 'eBook'}
                            url={ebook.url}
                            previewImage={ebook.cover}
                            uploadedAt={ebook.createdAt}
                            forceBlobDownload
                            onPreview={() => logProjectDetailsPreviewed('ebook', projId, ebook.id, analyticsCea)}
                            onDownloadClick={() => logProjectDetailsDownloaded('ebook', projId, ebook.id, analyticsCea)}
                            shareAsset={{ id: ebook.id, kind: 'ebook', title: ebook.title || ebook.id, projectId: projId }}
                          />
                        );
                      })}
                    </Box>
                  );

                  return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 800,
                          color: 'var(--color-accent)',
                          px: 0.5,
                          fontSize: '1.05rem',
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                        }}
                      >
                        Recent Uploads (Last 3 months)
                      </Typography>
                      {recentEbooks.length > 0 ? (
                        renderEbookGrid(recentEbooks)
                      ) : (
                        <Box
                          sx={{
                            minHeight: '140px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px dashed var(--color-border-strong)',
                            borderRadius: 2,
                            bgcolor: 'rgba(var(--color-text-rgb), 0.02)',
                            p: 3,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'var(--color-text-muted)',
                              fontStyle: 'italic',
                              textAlign: 'center',
                            }}
                          >
                            No eBooks uploaded within the last 3 months
                          </Typography>
                        </Box>
                      )}

                      {otherEbooks.length > 0 && (
                        <>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 800,
                              color: 'var(--color-text-muted)',
                              px: 0.5,
                              mt: 3,
                              fontSize: '1.05rem',
                              letterSpacing: '0.04em',
                              textTransform: 'uppercase',
                            }}
                          >
                            Past Uploads
                          </Typography>
                          {renderEbookGrid(otherEbooks)}
                        </>
                      )}
                    </Box>
                  );
                })()}
              </DialogContent>
            </Dialog>

            {/* Project-scoped podcast videos quick-list dialog — admin only */}
            {isAdminUser && (
              <ProjectPodcastVideosDialog
                open={podcastVideosDialogOpen}
                onClose={() => setPodcastVideosDialogOpen(false)}
                projectId={currentSitePlan?.projectId || projectDetails?.id || null}
                projectName={
                  currentSitePlan?.projectName || projectDetails?.name || selectedProject || null
                }
                videos={projectPodcastVideos}
              />
            )}

            {/* Project-scoped training videos quick-list dialog — admin only */}
            {isAdminUser && (
              <ProjectTrainingVideosDialog
                open={trainingVideosDialogOpen}
                onClose={() => setTrainingVideosDialogOpen(false)}
                projectId={currentSitePlan?.projectId || projectDetails?.id || null}
                projectName={
                  currentSitePlan?.projectName || projectDetails?.name || selectedProject || null
                }
                onAdded={loadProjectTrainingVideos}
                isAdmin={isAdminUser || isProjectDeptUser}
              />
            )}
      <EventRegistrationDialog
        open={projectTrainingRegistrationOpen}
        onClose={() => setProjectTrainingRegistrationOpen(false)}
        projectId={currentProjectId || undefined}
        title={`${currentSitePlan?.projectName || projectDetails?.name || selectedProject || 'Project'} Training and Registration`}
        emptyMessage="No training events are currently available for this project."
        ceaNumber={ceaNumber}
      />
    </>
  );
};

export default TopViewingSitePlanDetail;
